import React from 'react';
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import Dashboard from './Dashboard';
import { scrollElementWithinConsole } from './consoleScroll';
import { AuthProvider, useAuth } from './AuthContext';
import { isNextCompliAdmin } from './platformAdminNav';
import ProtectedRoute from './ProtectedRoute';
import {
  createLocalAccount,
} from './accountSecurity';
import { loadWebsiteLogo, WEBSITE_LOGO_STORAGE_KEY } from './themeRuntime';
import BrandLockup from './BrandLockup';
import { BRAND_NAME, withBrand } from './brand';
import { GoogleMark, LoginEmailScene, MicrosoftMark, SsoMark } from './components/LoginEmailScene';
import {
  APPEARANCE_UPDATED_EVENT,
  type AppearancePreferences,
  type AppearanceThemeMode,
  loadAppearancePreferences,
  resolveThemeMode,
  saveAppearancePreferences,
} from './appearanceRuntime';
import {
  applyUiChromeLocally,
  buildUiChromePreference,
  loadUiChromeFromAccount,
  saveUiChromeToAccount,
} from './chromePreferences';
import { SOCIAL_LINKS } from './socialLinks';
import './App.css';
import {
  PLAN_CATEGORY_TABS,
  PRICING_TIER_BY_ID,
  PRICING_TIERS,
  SUPPORT_LEVEL_DETAILS,
  SUPPORT_LEVELS,
  type PricingTierId,
} from './constants/pricingTiers';
import {
  canonicalizeConsolePath,
  isCrmNavHighlightPath,
  isInletOperatorPath,
} from './consolePaths';
import { SCREEN_COPY } from './i18n/screenCopy';
import { PolicyBody } from './i18n/PolicyBody';
import DodTermText from './DodTermText';
import NextCompliAssistant from './NextCompliAssistant';
import OfacVenezuelaWindow from './OfacVenezuelaWindow';
import OfacPlanCoverage from './OfacPlanCoverage';
import { captureAttributionFromWindow, getAttribution } from './marketingAttribution';
import {
  clearFunnel,
  installUxTelemetry,
  markFunnel,
  noteLocation,
  trackUxEvent,
} from './uxTelemetry';
import { installPostludeVisit, notePostludeVisit } from './postludeVisit';
import { identifySiteVisitor, installSiteTraffic, noteSitePageview } from './siteTraffic';
import LinkedInInsightTag from './LinkedInInsightTag';
import AssentCapture from './AssentCapture';
import PostludeEmbedPage from './PostludeEmbedPage';
import RazzmatazzEmbedPage from './RazzmatazzEmbedPage';
import { HelpCenterPage } from './HelpCenterPage';

const WEBSITE_ROOT = '';
const POSTLUDE_EMBED_PATH = '/embed/postlude';
const RAZZMATAZZ_EMBED_PATH = '/embed/razzmatazz';
const CONSOLE_PATH = '/console';
const CONSOLE_EMAIL_ACCOUNTS_PATH = '/console/email-accounts';
const CONSOLE_RESEARCH_PATH = '/console/research';
const CONSOLE_PURSUIT_PATH = '/console/pursuit';
const LEGACY_CONSOLE_CRM_PATH = '/console/crm';
const CONSOLE_OFAC_PATH = '/console/ofac';
const CONSOLE_OFAC_COMPLIANCE_PATH = '/console/ofac-compliance';
const CONSOLE_OFAC_DASHBOARDS_PATH = '/console/ofac-dashboards';
const CONSOLE_OFAC_LIVE_FEED_PATH = '/console/ofac-live-feed';
const CONSOLE_OFAC_SCREENING_PATH = '/console/ofac-screening';
const CONSOLE_OFAC_INVESTIGATIONS_PATH = '/console/ofac-investigations';
const CONSOLE_OFAC_WHITELIST_PATH = '/console/ofac-whitelist';
const CONSOLE_OFAC_CAPTA_PATH = '/console/ofac-capta';
const CONSOLE_WEBSITE_COMPLIANCE_PATH = '/console/website-compliance';
const CONSOLE_CLOUD_COMPLIANCE_PATH = '/console/cloud-compliance';
const CONSOLE_DOD_CONTRACTS_PATH = '/console/dod-contracts-compliance';
const CONSOLE_GOVERNMENT_COMPLIANCE_PATH = '/console/government-compliance';
const CONSOLE_EDUCATION_COMPLIANCE_PATH = '/console/education-compliance';
const CONSOLE_AI_COMPLIANCE_PATH = '/console/ai-compliance';
const CONSOLE_BUSINESS_FORMATION_PATH = '/console/business-formation';
const CONSOLE_AUDITS_PATH = '/console/audits';
const CONSOLE_COMPLIDESK_PATH = '/console/complidesk';
const CONSOLE_POSTLUDE_PATH = '/console/postlude';
const CONSOLE_AGENT_PATH = '/console/agent';
const APP_DASHBOARD_PATH = '/dashboard';

const hasPlatformAdminAccess = (user: ReturnType<typeof useAuth>['user']): boolean => isNextCompliAdmin(user);

const hasAdminConsoleAccess = (user: ReturnType<typeof useAuth>['user']): boolean => {
  if (!user) {
    return false;
  }
  if (hasPlatformAdminAccess(user)) {
    return true;
  }
  // A subscription admin manages people on one paying account. That job does not
  // open NextCompli operator tools (Access Management, the subscription catalog).
  const permissions = ((((user as unknown as { effective_permissions?: string[] }).effective_permissions) || [])).map(
    (permission) => String(permission || '').trim().toLowerCase(),
  );
  const permissionMarkers = [
    'users.read',
    'users.create',
    'groups.read',
    'roles.read',
    'assign groups users',
    'assign permissions groups',
  ];
  return permissionMarkers.some((marker) => permissions.some((permission) => permission === marker || permission.includes(marker)));
};
const CONSOLE_STYLE_PATH = '/console/style';
const CONSOLE_STYLE_DESIGNER_PATH = '/console/style/designer';
const CONSOLE_SUBSCRIPTIONS_PATH = '/console/subscriptions';
const CONSOLE_SUBSCRIPTION_HUB_PATH = '/console/subscription-hub';
const CONSOLE_SUBSCRIPTION_LEVELS_PATH = '/console/subscription-levels';
const CONSOLE_PAYING_SUBSCRIPTIONS_PATH = '/console/paying-subscriptions';
const CONSOLE_EXPIRED_SUBSCRIPTIONS_PATH = '/console/expired-subscriptions';
const CONSOLE_MY_SUBSCRIPTIONS_PATH = '/console/my-subscriptions';
const CONSOLE_TEAM_PATH = '/console/team';
const CONSOLE_BILLING_PATH = '/console/billing';
const ADMIN_CONSOLE_PATH = '/console/admin';
const ADMIN_ROLES_PATH = '/console/admin/roles';
const ADMIN_USERS_PATH = '/console/admin/users';
const ADMIN_GROUPS_PATH = '/console/admin/groups';
const ADMIN_COST_OPTIMIZATION_PATH = '/console/admin/cost-optimization';
const ADMIN_VOICE_MESSAGES_PATH = '/console/admin/voice-messages';
const ADMIN_KEYCLOAK_PATH = '/console/admin/keycloak';
const ADMIN_OBSERVABILITY_PATH = '/console/admin/observability';
const ADMIN_EXPERIENCE_PATH = '/console/admin/experience';
const ACCEPT_INVITE_PATH = '/accept-invite';

const websitePath = (path = '') => {
  if (!path) {
    return WEBSITE_ROOT || '/';
  }

  return `${WEBSITE_ROOT}${path}`;
};
const ARCHITECTURE_DESTINATION = websitePath('/architecture');
const DEMO_REQUEST_DESTINATION = websitePath('/demo-request');
const SALES_ASSISTANT_DESTINATION = websitePath('/sales-assistant');
const SIGNUP_DESTINATION = websitePath('/signup');
const SUBSCRIBE_DESTINATION = websitePath('/subscribe');
const DASHBOARDS_VALUE_DESTINATION = websitePath('/dashboards');
const LIABILITY_MONITORING_DESTINATION = websitePath('/liability-monitoring');
const DOD_CONTRACTS_DESTINATION = websitePath('/dod-contracts');

type SlideoutItem = {
  id: string;
  to: string;
  label: string;
  summary: string;
  subItems: { label: string; to: string; description?: string; opensInNewWindow?: boolean }[];
  ctaLabel?: string;
  iconKey?: IndustryIconKey;
  iconTone?: string;
};

type SlideoutGroup = {
  header: string;
  items: SlideoutItem[];
};

type GatewayChatResponse = {
  provider: string;
  reply: string;
  timestamp: string;
  session_id?: string;
  citations?: string[];
  intake_route?: string | null;
  intake_reason?: string | null;
  intake_desk?: string | null;
  intake_marketing?: string | null;
};

type WebsiteLanguage = 'en' | 'es';

const LANGUAGE_STORAGE_KEY = 'nextcompli.website.language';
const LANGUAGE_COOKIE_KEY = 'nextcompli.lang';
const GEO_COUNTRY_COOKIE_KEY = 'nextcompli.geo_country';
const LATAM_COUNTRY_CODES = new Set([
  'AR', 'BO', 'BR', 'CL', 'CO', 'CR', 'CU', 'DO', 'EC', 'SV', 'GT', 'HN', 'MX', 'NI', 'PA', 'PY', 'PE', 'PR', 'UY', 'VE',
]);
const PRODUCTION_WEBSITE_HOSTS = new Set(['nextcompli.com', 'www.nextcompli.com']);
const GEO_LANGUAGE_DEFAULT_PATH = '/api/v1/public/geo-language-default';

const getCookieValue = (key: string): string => {
  if (typeof document === 'undefined') {
    return '';
  }
  const encodedKey = `${encodeURIComponent(key)}=`;
  const raw = document.cookie.split(';').map((entry) => entry.trim()).find((entry) => entry.startsWith(encodedKey));
  if (!raw) {
    return '';
  }
  return decodeURIComponent(raw.slice(encodedKey.length));
};

const detectInitialWebsiteLanguage = (): WebsiteLanguage => {
  if (typeof window === 'undefined') {
    return 'en';
  }

  const urlLanguage = new URLSearchParams(window.location.search).get('lang');
  if (urlLanguage === 'es' || urlLanguage === 'en') {
    return urlLanguage;
  }

  const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (storedLanguage === 'es' || storedLanguage === 'en') {
    return storedLanguage;
  }

  const cookieLanguage = getCookieValue(LANGUAGE_COOKIE_KEY).toLowerCase();
  if (cookieLanguage === 'es' || cookieLanguage === 'en') {
    return cookieLanguage;
  }

  const geoCountry = (
    getCookieValue(GEO_COUNTRY_COOKIE_KEY) ||
    ((window as Window & { __NEXTCOMPLI_GEO_COUNTRY?: string }).__NEXTCOMPLI_GEO_COUNTRY || '')
  )
    .toUpperCase()
    .trim();
  if (LATAM_COUNTRY_CODES.has(geoCountry)) {
    return 'es';
  }

  const browserLanguages = [
    ...(window.navigator.languages || []),
    window.navigator.language || '',
  ]
    .map((value) => value.toLowerCase())
    .filter(Boolean);
  if (browserLanguages.some((value) => value.startsWith('es'))) {
    return 'es';
  }

  return 'en';
};
const detectProductionWebsiteRuntime = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const host = window.location.hostname.toLowerCase();
  if (!host) {
    return false;
  }

  if (PRODUCTION_WEBSITE_HOSTS.has(host)) {
    return true;
  }

  return host.endsWith('.nextcompli.com');
};

const IS_PRODUCTION_WEBSITE_RUNTIME = detectProductionWebsiteRuntime();
const CANONICAL_SUPPORT_PHONE = '1-866-776-5961';
const SUPPORT_PHONE_DISPLAY = (
  IS_PRODUCTION_WEBSITE_RUNTIME
    ? CANONICAL_SUPPORT_PHONE
    : (process.env.REACT_APP_SUPPORT_PHONE || CANONICAL_SUPPORT_PHONE)
).trim();
const SUPPORT_PHONE_TEL_NUMBER = SUPPORT_PHONE_DISPLAY.replace(/[^\d+]/g, '');
const SUPPORT_PHONE_TEL = SUPPORT_PHONE_TEL_NUMBER ? `tel:${SUPPORT_PHONE_TEL_NUMBER}` : '';
const HAS_SUPPORT_PHONE = SUPPORT_PHONE_DISPLAY.length > 0 && SUPPORT_PHONE_TEL_NUMBER.length > 0;
const WEBSITE_TEXT_ONLY_MODE = process.env.REACT_APP_WEBSITE_TEXT_ONLY === 'true';
const WEBSITE_SUBMENU_TEXT_ONLY_MODE = WEBSITE_TEXT_ONLY_MODE && IS_PRODUCTION_WEBSITE_RUNTIME;

export const WEBSITE_COPY: Record<WebsiteLanguage, Record<string, string>> = {
  en: {
    'nav.products': 'Products',
    'nav.industries': 'Industries',
    'nav.customers': 'Customers',
    'nav.learning': 'Learning',
    'nav.support': 'Support',
    'nav.company': 'Company',
    	'nav.dashboard': 'Dashboard',
    'header.askPrompt': 'Ask the assistant anything',
    'header.askLabel': 'Ask NextCompli AI',
    'header.askPlaceholder': 'Pricing, security, integrations…',
    'header.askScope': 'AI assistant for product and compliance questions.',
    'header.askPrivacy': 'Do not include secrets or personal data.',
    'header.askFallback': 'Need a guaranteed answer? Contact support.',
    'header.askAction': 'Ask',
    'header.askThinking': 'Thinking...',
    'header.askingAgentforce': 'Consulting assistant…',
    'header.contactUs': 'Contact us',
    'header.phone': SUPPORT_PHONE_DISPLAY,
    'header.login': 'Login',
    'header.logout': 'Sign out',
    'header.signedInAs': 'Signed in as',
    'header.settingsMenu': 'Account menu',
    'header.consoleMenu': 'Console menu',
    'header.console': 'Console',
    'header.menu': 'Menu',
    'header.settingsChrome': 'Language and appearance',
    'header.actions': 'Header actions',
    'header.theme': 'Theme',
    'header.themeMenu': 'Choose appearance',
    'header.themeSystem': 'System',
    'header.themeLight': 'Light',
    'header.themeDark': 'Dark',
    'header.themeSystemHint': 'Match device setting',
    'header.accountSettings': 'Account settings',
    'header.dashboard': 'Dashboard',
    'header.adminConsole': 'Admin console',
    'header.keycloakAdmin': 'Keycloak Admin',
    'header.designSystem': 'Website Theming',
    'header.tryFree': 'Try for free',
    'header.startPlan': 'Start CompliCore',
    'header.languageMenu': 'Change language',
    'header.closeSlideoutMenu': 'Close menu',
    'header.languageCode': 'EN',
    'header.languageEnglish': 'English',
    'header.languageSpanish': 'Español',
    'home.alert': '',
    'home.askHeading': 'Product needs? Pricing questions? Ask away.',
    'home.askPlaceholder': 'I run compliance for a 40-person team. What should I automate first?',
    'home.askScope': 'AI assistant for website product guidance.',
    'home.askTrust': 'AI can make mistakes. Verify important details and avoid secrets.',
    'home.askStarterOne': 'Compare plans',
    'home.askStarterTwo': 'Security & compliance',
    'home.askStarterThree': 'Implementation timeline',
    'home.connectSales': 'Connect me with sales',
    'home.tryFree': 'Start CompliCore',
    'home.startPlan': 'Start CompliCore',
    'home.seePlans': 'See plans',
    'home.choosePlan': 'Help me choose a plan',
    'home.heroKicker': 'NextCompli. Compliance data intelligence, modernized.',
    'home.heroTitleLead': 'Welcome to',
    'home.heroTitleAccent': 'Operational Compliance Redefined.',
    'home.heroCopy':
      'NextCompli helps your team run sanctions and compliance checks in one place, with clear results you can act on quickly.',
    'home.heroCopyMuted':
      'You get alerts, case tracking, and audit-ready records so operations, legal, and leadership can see what happened and what to do next.',
    'home.viewArchitecture': 'View architecture',
    'page.platformTitle': 'Platform',
    'page.platformSubtitle': 'Enable only the services you need and orchestrate them through one gateway.',
    'page.requestDemo': 'Request demo',
    'page.deploymentTitle': 'Deployment',
    'page.deploymentSubtitle': 'Run NextCompli where your team and regulators require.',
    'page.requestDeploymentPlan': 'Request deployment planning',
    'page.securityTitle': 'Security & Compliance',
    'page.securitySubtitle': 'Auditability, observability, and controlled configuration management.',
    'page.talkCompliance': 'Talk to compliance specialist',
    'page.developersTitle': 'Developers',
    'page.developersSubtitle': 'Build and integrate with a modular architecture designed for practical implementation.',
    'page.bookTechnical': 'Book technical session',
    'page.resourcesTitle': 'Resources',
    'page.resourcesSubtitle': 'Explore implementation references and external data portals used in the NextCompli workflow.',
    'page.architectureTitle': 'Architecture Center',
    'page.architectureSubtitle': 'Reference architecture patterns for deployment, security, resiliency, and compliance evidence.',
    'page.architectureDisclaimer':
      'This page summarizes current architecture patterns and operating practices. Some capabilities are generally available today, while others may vary by environment, region, or service tier.',
    'page.privacyTitle': 'Privacy Policy',
    'page.privacySubtitle': 'NextCompli processes operational and configuration data to provide platform functionality and support.',
    'page.termsTitle': 'Terms of Service',
    'page.termsSubtitle': 'Use of NextCompli is subject to service terms covering acceptable use, access controls, and deployment scope.',
    'page.contactTitle': 'Request Demo',
    'page.contactSubtitle': 'Tell us your deployment model and use case; we will map the best rollout path.',
    'page.releaseNotes': 'Release Notes',
    'page.releaseNotesSubtitle': 'Recent release and regulatory feature updates previously listed in Newsroom.',
    'page.newsroom': 'Newsroom',
    'page.newsroomSubtitle': 'Live internet news for compliance, sanctions, and regulatory operations.',
    'page.newsroomLoading': 'Loading latest headlines…',
    'page.learnMore': 'Learn more',
    'page.legalTitle': 'Legal',
    'page.legalSubtitle': 'Legal policies, disclosures, and platform terms for using NextCompli services.',
    'page.version': 'Version',
    'page.lastUpdated': 'Last updated',
    'login.title': 'Log in',
    'login.subtitle': 'Use Google, company SSO, or email. Microsoft 365 and Okta go through SSO — we do not offer a Microsoft GCC High login.',
    'login.visualKicker': 'Government compliance',
    'login.visualHeadline': 'Screen names, track homework, and keep proof in one place',
    'login.visualBody': 'One place for screening, government homework, and website checks.',
    'login.continueEmail': 'Continue with email',
    'login.ssoMicrosoftNote': 'Microsoft 365, Okta, and other company directories use this button.',
    'login.trouble': 'Trouble signing in?',
    'login.notCustomer': 'Not a customer? Start CompliCore',
    'login.createTab': 'Create account',
    'login.signInTab': 'Sign in',
    'login.createTitle': 'Start CompliCore',
    'login.createLead': 'Pick a plan, type your name and password, and continue. No card is required. You become the subscription admin. The company name can wait until you want it on invoices.',
    'login.planLegend': 'Subscription',
    'login.planContact': 'Contact us',
    'login.createLoginOnly': 'Create a password login without a plan',
    'login.checkingSession': 'Checking session',
    'login.emailLabel': 'Email or username',
    'login.emailPlaceholder': 'Email address or username',
    'login.password': 'Password',
    'login.showPassword': 'Show password',
    'login.hidePassword': 'Hide password',
    'login.show': 'Show',
    'login.hide': 'Hide',
    'login.signIn': 'Sign in',
    'login.continueSso': 'Continue with SSO',
    'login.google': 'Sign in with Google',
    'login.googleUnavailable': 'Google sign-in is not ready yet. Use your password for now.',
    'login.microsoft': 'Sign in with Microsoft',
    'login.microsoftUnavailable': 'Microsoft sign-in is not ready yet. Use your password for now.',
    'login.identityConflict': 'That sign-in is already linked to a different NextCompli account. Ask an administrator before connecting it here.',
    'login.or': 'or',
    'login.byContinuing': 'By continuing, you agree to the',
    'login.googleNotWorkspace': 'Use a company Google account on gosslife.biz, nextcompli.com, or jfgoss.com.',
    'login.googleNoAccount': 'That Google address is not a NextCompli user yet. An administrator must add it, or use your password.',
    'login.accountDisabled': 'This Google address belongs to a disabled NextCompli account. Ask an administrator to turn it back on.',
    'login.googleNotVerified': 'Google has not confirmed that email address yet.',
    'login.ssoHint': 'Single Sign-On is enabled. Continue with your organization identity provider.',
    'login.agreePrefix': 'I agree to the',
    'login.agreeAnd': 'and',
    'login.termsLink': 'Terms of Service',
    'login.privacyLink': 'Privacy Policy',
    'login.mustAgree': 'You must accept the Terms of Service and Privacy Policy to continue.',
    'login.authFailed': 'Unable to authenticate with the provided credentials.',
    'login.continueDashboard': 'Continue to Dashboard',
    'login.signingIn': 'Signing in…',
    'login.forgotPassword': 'Forgot Password?',
    'login.passwordResetRequired': 'Password reset is required for this account.',
    'login.ssoUnavailable': 'Single Sign-On is temporarily unavailable due to an identity provider credential mismatch. Please contact your administrator.',
    'login.ssoExpired': 'Single Sign-On session expired before completion. Please try again.',
    'login.ssoIncomplete': 'Single Sign-On did not complete. Please try again.',
    'login.forgotTitle': 'Forgot password',
    'login.forgotSubtitle': 'Enter the email for your account. If it matches an active user, we will send a reset link.',
    'login.sendReset': 'Send reset link',
    'login.sending': 'Sending…',
    'login.backToSignIn': 'Back to sign in',
    'login.email': 'Email',
    'contact.fullName': 'Full name',
    'contact.workEmail': 'Account email',
    'contact.company': 'Company',
    'contact.role': 'Role',
    'contact.deploymentPreference': 'Deployment preference',
    'contact.submit': 'Submit request',
    'product.watchDemo': 'Workflow readiness',
    'product.whatThisIs': 'What this is',
    'product.whyNeeded': 'Why this is needed',
    'product.ofacFlowStory': 'OFAC flow story',
    'product.testimonials': 'Testimonials',
    'product.businessValue': 'Business value',
    'product.businessRisk': 'Business risk',
    'product.capabilities': 'Capabilities',
    'product.workflow': 'Workflow',
    'product.expectedOutcomes': 'Expected outcomes',
    'product.faq': 'Frequently asked questions',
    'support.header': 'Support',
    'support.helpDocs': 'Help & Documentation',
    'support.communities': 'Communities',
    'support.services': 'Services & Plans',
    'support.accountMgmt': 'Account Management',
    'home.deploymentPlan': 'Get a deployment plan tailored to your compliance and operations needs.',
    'home.requestDemoCta': 'Request demo',
    'home.signupKicker': 'Email updates',
    'home.signupPrompt': 'Get product updates in your inbox',
    'home.signupHint': 'Release notes and launch news. Unsubscribe anytime.',
    'home.signupPlaceholder': 'Work email',
    'home.signupButton': 'Get updates',
    'home.signupSuccessTitle': 'You’re subscribed.',
    'home.signupSuccessBody': 'We’ll send product updates and release announcements to this inbox.',
    'home.signupAddAnother': 'Add another email',
    'footer.socialLinks': 'Social links',
    'footer.callUsAt': HAS_SUPPORT_PHONE ? `Call us at ${SUPPORT_PHONE_DISPLAY}` : '',
    'footer.supportOptions': 'See all support options',
    'footer.newTitle': 'New to NextCompli?',
    'footer.new.whatPlatform': 'Dashboards',
    'footer.new.liabilityMonitoring': 'Liability Monitoring',
    'footer.new.agenticEnterprise': 'Agentic Enterprise',
    'footer.new.digitalCompliance': 'Government Compliance',
    'footer.new.continuousMonitoring': 'Continuous Monitoring',
    'footer.new.caseAudit': 'Case & Audit Workspace',
    'footer.new.developerSandbox': 'Developer Sandbox',
    'footer.new.sanctionsIntelligence': 'Sanctions Intelligence',
    'footer.new.researchWorkflows': 'Research Workflows',
    'footer.aboutTitle': 'About NextCompli',
    'footer.about.whatIs': 'What is NextCompli?',
    'footer.about.newsroom': 'Newsroom',
    'footer.about.customerSuccess': 'Customer Success',
    'footer.about.blog': 'Blog',
    'footer.about.careers': 'Careers',
    'footer.about.trust': 'Trust',
    'footer.about.sustainability': 'Sustainability',
    'footer.about.investors': 'Investors',
    'footer.popularTitle': 'Popular Links',
    'footer.popular.login': 'NextCompli Login',
    'footer.popular.features': 'New Release Features',
    'footer.popular.partner': 'Find or Become a Partner',
    'footer.popular.dreamforce': 'Industries',
    'footer.popular.platformOverview': 'Platform Overview',
    'footer.popular.pricing': 'Product Pricing',
    'footer.popular.allProducts': 'All Platform Products',
    'footer.popular.newsletter': 'Email updates',
    'footer.worldwide': 'Worldwide',
    'footer.legal': 'Legal',
    'footer.privacy': 'Privacy',
    'footer.terms': 'Terms',
    'footer.responsibleDisclosure': 'Responsible Disclosure',
    'footer.contact': 'Contact',
    'footer.cookiePreferences': 'Cookie Preferences',
    'footer.dataChoices': 'Your Data Choices',
    'footer.copyright': '© Copyright 2026 NextCompli, Inc. All rights reserved. Various trademarks held by their respective owners.',
    'footer.col.products': 'Products',
    'footer.col.solutions': 'Solutions',
    'footer.col.frameworks': 'Frameworks',
    'footer.col.resources': 'Resources',
    'footer.col.support': 'Support',
    'footer.products.ofac': 'OFAC screening',
    'footer.products.government': 'Government compliance',
    'footer.products.website': 'Website compliance',
    'footer.products.education': 'Education compliance',
    'footer.products.ai': 'AI compliance',
    'footer.products.plans': 'Plans and pricing',
    'footer.solutions.smallBiz': 'Small business',
    'footer.solutions.enterprise': 'Enterprise',
    'footer.solutions.defense': 'Defense contractors',
    'footer.frameworks.ofac': 'OFAC / SDN',
    'footer.frameworks.cmmc': 'CMMC Level 1 (FCI)',
    'footer.frameworks.website': 'Website and privacy',
    'footer.frameworks.ferpa': 'FERPA / GLBA campus',
    'footer.resources.blog': 'Blog',
    'footer.resources.news': 'News',
    'footer.resources.trust': 'Trust',
    'footer.resources.release': 'Release notes',
    'footer.resources.company': 'About',
    'footer.resources.dashboards': 'Dashboards',
    'footer.resources.liability': 'Liability monitoring',
    'footer.support.help': 'Help',
    'footer.support.demo': 'Schedule a demo',
    'footer.support.login': 'Sign in',
    'footer.support.contact': 'Contact',
  },
  es: {
    'nav.products': 'Productos',
    'nav.industries': 'Industrias',
    'nav.customers': 'Clientes',
    'nav.learning': 'Aprendizaje',
    'nav.support': 'Soporte',
    'nav.company': 'Compañía',
    	'nav.dashboard': 'Panel',
    'header.askPrompt': 'Pregunta al asistente',
    'header.askLabel': 'Pregunta a la IA de NextCompli',
    'header.askPlaceholder': 'Precios, seguridad, integraciones…',
    'header.askScope': 'Asistente de IA para preguntas de producto y cumplimiento.',
    'header.askPrivacy': 'No incluyas secretos ni datos personales.',
    'header.askFallback': '¿Necesitas una respuesta garantizada? Contacta soporte.',
    'header.askAction': 'Preguntar',
    'header.askThinking': 'Pensando...',
    'header.askingAgentforce': 'Consultando al asistente…',
    'header.contactUs': 'Contáctanos',
    'header.phone': SUPPORT_PHONE_DISPLAY,
    'header.login': 'Iniciar sesión',
    'header.logout': 'Cerrar sesión',
    'header.signedInAs': 'Conectado como',
    'header.settingsMenu': 'Menú de cuenta',
    'header.consoleMenu': 'Menú de consola',
    'header.console': 'Consola',
    'header.menu': 'Menú',
    'header.settingsChrome': 'Idioma y apariencia',
    'header.actions': 'Acciones del encabezado',
    'header.theme': 'Tema',
    'header.themeMenu': 'Elegir apariencia',
    'header.themeSystem': 'Sistema',
    'header.themeLight': 'Claro',
    'header.themeDark': 'Oscuro',
    'header.themeSystemHint': 'Usar configuración del dispositivo',
    'header.accountSettings': 'Configuración de la cuenta',
    'header.dashboard': 'Panel',
    'header.adminConsole': 'Consola de administracion',
    'header.keycloakAdmin': 'Admin de Keycloak',
    'header.designSystem': 'Tematizacion del sitio web',
    'header.tryFree': 'Probar gratis',
    'header.startPlan': 'Empezar CompliCore',
    'header.languageMenu': 'Cambiar idioma',
    'header.closeSlideoutMenu': 'Cerrar menu',
    'header.languageCode': 'ES',
    'header.languageEnglish': 'English',
    'header.languageSpanish': 'Español',
    'home.alert': '',
    'home.askHeading': '¿Necesitas productos? ¿Preguntas de precios? Pregunta aquí.',
    'home.askPlaceholder': 'Lidero cumplimiento en un equipo de 40 personas. ¿Qué debo automatizar primero?',
    'home.askScope': 'Asistente de IA para orientación de producto en el sitio web.',
    'home.askTrust': 'La IA puede equivocarse. Verifica los detalles importantes y evita compartir secretos.',
    'home.askStarterOne': 'Comparar planes',
    'home.askStarterTwo': 'Seguridad y cumplimiento',
    'home.askStarterThree': 'Cronograma de implementación',
    'home.connectSales': 'Conectar con ventas',
    'home.tryFree': 'Empezar CompliCore',
    'home.startPlan': 'Empezar CompliCore',
    'home.seePlans': 'Ver planes',
    'home.choosePlan': 'Ayúdame a elegir un plan',
    'home.heroKicker': 'NextCompli. Inteligencia de datos de cumplimiento, modernizada.',
    'home.heroTitleLead': 'Bienvenido a',
    'home.heroTitleAccent': 'Cumplimiento Operacional Redefinido.',
    'home.heroCopy':
      'NextCompli ayuda a tu equipo a ejecutar controles de sanciones y cumplimiento en un solo lugar, con resultados claros para actuar rápido.',
    'home.heroCopyMuted':
      'Obtienes alertas, seguimiento de casos y registros listos para auditoría para que operaciones, legal y liderazgo sepan qué ocurrió y qué hacer después.',
    'home.viewArchitecture': 'Ver arquitectura',
    'page.platformTitle': 'Plataforma',
    'page.platformSubtitle': 'Activa solo los servicios que necesitas y orquéstalos a través de un gateway único.',
    'page.requestDemo': 'Solicitar demo',
    'page.deploymentTitle': 'Despliegue',
    'page.deploymentSubtitle': 'Ejecuta NextCompli donde tu equipo y reguladores lo requieran.',
    'page.requestDeploymentPlan': 'Solicitar plan de despliegue',
    'page.securityTitle': 'Seguridad y Cumplimiento',
    'page.securitySubtitle': 'Auditabilidad, observabilidad y gestión de configuración con control.',
    'page.talkCompliance': 'Hablar con especialista de cumplimiento',
    'page.developersTitle': 'Desarrolladores',
    'page.developersSubtitle': 'Construye e integra con una arquitectura modular diseñada para implementación práctica.',
    'page.bookTechnical': 'Reservar sesión técnica',
    'page.resourcesTitle': 'Recursos',
    'page.resourcesSubtitle': 'Explora referencias de implementación y portales externos de datos usados en el flujo de NextCompli.',
    'page.architectureTitle': 'Centro de Arquitectura',
    'page.architectureSubtitle': 'Patrones de arquitectura de referencia para despliegue, seguridad, resiliencia y evidencia de cumplimiento.',
    'page.architectureDisclaimer':
      'Esta página resume patrones de arquitectura y prácticas operativas actuales. Algunas capacidades están disponibles hoy y otras pueden variar por entorno, región o nivel de servicio.',
    'page.privacyTitle': 'Política de Privacidad',
    'page.privacySubtitle': 'NextCompli procesa datos operativos y de configuración para brindar funcionalidad y soporte de plataforma.',
    'page.termsTitle': 'Términos del Servicio',
    'page.termsSubtitle': 'El uso de NextCompli está sujeto a términos de servicio sobre uso aceptable, control de acceso y alcance de despliegue.',
    'page.contactTitle': 'Solicitar Demo',
    'page.contactSubtitle': 'Cuéntanos tu modelo de despliegue y caso de uso; definiremos la mejor ruta de implementación.',
    'page.releaseNotes': 'Notas de la versión',
    'page.releaseNotesSubtitle': 'Actualizaciones recientes de producto y cumplimiento que antes aparecían en Sala de prensa.',
    'page.newsroom': 'Sala de prensa',
    'page.newsroomSubtitle': 'Noticias en vivo sobre cumplimiento, sanciones y operaciones regulatorias.',
    'page.newsroomLoading': 'Cargando titulares…',
    'page.learnMore': 'Más información',
    'page.legalTitle': 'Legal',
    'page.legalSubtitle': 'Políticas legales, divulgaciones y términos de la plataforma para usar los servicios de NextCompli.',
    'page.version': 'Versión',
    'page.lastUpdated': 'Última actualización',
    'login.title': 'Iniciar sesión',
    'login.subtitle': 'Usa Google, SSO de la empresa o correo. Microsoft 365 y Okta entran por SSO — no ofrecemos inicio de sesión Microsoft GCC High.',
    'login.visualKicker': 'Cumplimiento gubernamental',
    'login.visualHeadline': 'Criba nombres, sigue la tarea y guarda la prueba en un solo lugar',
    'login.visualBody': 'Un solo lugar para cribado, tarea gubernamental y revisiones del sitio.',
    'login.continueEmail': 'Continuar con correo',
    'login.ssoMicrosoftNote': 'Microsoft 365, Okta y otros directorios de empresa usan este botón.',
    'login.trouble': '¿Problemas para entrar?',
    'login.notCustomer': '¿Aún no eres cliente? Empieza CompliCore',
    'login.createTab': 'Crear cuenta',
    'login.signInTab': 'Iniciar sesión',
    'login.createTitle': 'Empezar CompliCore',
    'login.createLead': 'Elige un plan, escribe tu nombre y tu contraseña, y continúa. No se necesita una tarjeta. Tú serás admin de la suscripción. El nombre de la empresa puede esperar hasta que quieras verlo en las facturas.',
    'login.planLegend': 'Suscripción',
    'login.planContact': 'Contáctanos',
    'login.createLoginOnly': 'Crear un acceso con contraseña sin un plan',
    'login.checkingSession': 'Comprobando sesión',
    'login.emailLabel': 'Correo o usuario',
    'login.emailPlaceholder': 'Correo o nombre de usuario',
    'login.password': 'Contraseña',
    'login.showPassword': 'Mostrar contraseña',
    'login.hidePassword': 'Ocultar contraseña',
    'login.show': 'Mostrar',
    'login.hide': 'Ocultar',
    'login.signIn': 'Iniciar sesión',
    'login.continueSso': 'Continuar con SSO',
    'login.google': 'Iniciar sesión con Google',
    'login.googleUnavailable': 'El inicio con Google todavía no está listo. Usa tu contraseña por ahora.',
    'login.microsoft': 'Iniciar sesión con Microsoft',
    'login.microsoftUnavailable': 'El inicio con Microsoft todavía no está listo. Usa tu contraseña por ahora.',
    'login.identityConflict': 'Ese inicio de sesión ya está vinculado a otra cuenta de NextCompli. Pide a un administrador antes de conectarlo aquí.',
    'login.or': 'o',
    'login.byContinuing': 'Al continuar, aceptas los',
    'login.googleNotWorkspace': 'Usa una cuenta de Google de la empresa en gosslife.biz, nextcompli.com o jfgoss.com.',
    'login.googleNoAccount': 'Esa dirección de Google todavía no es un usuario de NextCompli. Un administrador debe añadirla, o usa tu contraseña.',
    'login.accountDisabled': 'Esa dirección de Google pertenece a una cuenta de NextCompli desactivada. Pide a un administrador que la reactive.',
    'login.googleNotVerified': 'Google todavía no ha confirmado esa dirección de correo.',
    'login.ssoHint': 'El inicio de sesión único está activado. Continúa con el proveedor de identidad de tu organización.',
    'login.agreePrefix': 'Acepto los',
    'login.agreeAnd': 'y la',
    'login.termsLink': 'Términos del Servicio',
    'login.privacyLink': 'Política de Privacidad',
    'login.mustAgree': 'Debes aceptar los Términos del Servicio y la Política de Privacidad para continuar.',
    'login.authFailed': 'No se pudo autenticar con las credenciales proporcionadas.',
    'login.continueDashboard': 'Continuar al panel',
    'login.signingIn': 'Iniciando sesión…',
    'login.forgotPassword': '¿Olvidaste la contraseña?',
    'login.passwordResetRequired': 'Esta cuenta requiere restablecer la contraseña.',
    'login.ssoUnavailable': 'El inicio de sesión único no está disponible temporalmente por un desajuste de credenciales del proveedor de identidad. Contacta a tu administrador.',
    'login.ssoExpired': 'La sesión de inicio de sesión único caducó antes de completarse. Inténtalo de nuevo.',
    'login.ssoIncomplete': 'El inicio de sesión único no se completó. Inténtalo de nuevo.',
    'login.forgotTitle': 'Olvidé la contraseña',
    'login.forgotSubtitle': 'Ingresa el correo de tu cuenta. Si coincide con un usuario activo, enviaremos un enlace de restablecimiento.',
    'login.sendReset': 'Enviar enlace',
    'login.sending': 'Enviando…',
    'login.backToSignIn': 'Volver a iniciar sesión',
    'login.email': 'Correo',
    'contact.fullName': 'Nombre completo',
    'contact.workEmail': 'Correo de la cuenta',
    'contact.company': 'Empresa',
    'contact.role': 'Cargo',
    'contact.deploymentPreference': 'Preferencia de despliegue',
    'contact.submit': 'Enviar solicitud',
    'product.watchDemo': 'Preparación del flujo de trabajo',
    'product.whatThisIs': 'Qué es esto',
    'product.whyNeeded': 'Por qué se necesita',
    'product.ofacFlowStory': 'Historia del flujo OFAC',
    'product.testimonials': 'Testimonios',
    'product.businessValue': 'Valor de negocio',
    'product.businessRisk': 'Riesgo de negocio',
    'product.capabilities': 'Capacidades',
    'product.workflow': 'Flujo de trabajo',
    'product.expectedOutcomes': 'Resultados esperados',
    'product.faq': 'Preguntas frecuentes',
    'support.header': 'Soporte',
    'support.helpDocs': 'Ayuda y Documentación',
    'support.communities': 'Comunidades',
    'support.services': 'Servicios y Planes',
    'support.accountMgmt': 'Gestión de Cuenta',
    'home.deploymentPlan': 'Obtén un plan de despliegue adaptado a tus necesidades de cumplimiento y operaciones.',
    'home.requestDemoCta': 'Solicitar demo',
    'home.signupKicker': 'Actualizaciones por email',
    'home.signupPrompt': 'Recibe novedades de producto en tu bandeja',
    'home.signupHint': 'Notas de versión y lanzamientos. Cancela cuando quieras.',
    'home.signupPlaceholder': 'Correo laboral',
    'home.signupButton': 'Recibir actualizaciones',
    'home.signupSuccessTitle': 'Ya estás suscrito.',
    'home.signupSuccessBody': 'Te enviaremos actualizaciones de producto y anuncios de nuevas versiones a este correo.',
    'home.signupAddAnother': 'Agregar otro correo',
    'footer.socialLinks': 'Enlaces sociales',
    'footer.callUsAt': HAS_SUPPORT_PHONE ? `Llamanos al ${SUPPORT_PHONE_DISPLAY}` : '',
    'footer.supportOptions': 'Ver todas las opciones de soporte',
    'footer.newTitle': '¿Nuevo en NextCompli?',
    'footer.new.whatPlatform': 'Paneles',
    'footer.new.liabilityMonitoring': 'Monitoreo de Responsabilidad',
    'footer.new.agenticEnterprise': 'Empresa Agéntica',
    'footer.new.digitalCompliance': 'Cumplimiento gubernamental',
    'footer.new.continuousMonitoring': 'Monitoreo Continuo',
    'footer.new.caseAudit': 'Espacio de Casos y Auditoría',
    'footer.new.developerSandbox': 'Sandbox para Desarrolladores',
    'footer.new.sanctionsIntelligence': 'Inteligencia de Sanciones',
    'footer.new.researchWorkflows': 'Flujos de Investigación',
    'footer.aboutTitle': 'Acerca de NextCompli',
    'footer.about.whatIs': '¿Qué es NextCompli?',
    'footer.about.newsroom': 'Noticias',
    'footer.about.customerSuccess': 'Éxito del Cliente',
    'footer.about.blog': 'Blog',
    'footer.about.careers': 'Carreras',
    'footer.about.trust': 'Confianza',
    'footer.about.sustainability': 'Sostenibilidad',
    'footer.about.investors': 'Inversionistas',
    'footer.popularTitle': 'Enlaces Populares',
    'footer.popular.login': 'Acceso NextCompli',
    'footer.popular.features': 'Nuevas Funcionalidades',
    'footer.popular.partner': 'Encontrar o Ser Socio',
    'footer.popular.dreamforce': 'Industrias',
    'footer.popular.platformOverview': 'Resumen de la Plataforma',
    'footer.popular.pricing': 'Precios del Producto',
    'footer.popular.allProducts': 'Todos los Productos de la Plataforma',
    'footer.popular.newsletter': 'Actualizaciones por email',
    'footer.worldwide': 'Mundial',
    'footer.legal': 'Legal',
    'footer.privacy': 'Privacidad',
    'footer.terms': 'Términos',
    'footer.responsibleDisclosure': 'Divulgación Responsable',
    'footer.contact': 'Contacto',
    'footer.cookiePreferences': 'Preferencias de Cookies',
    'footer.dataChoices': 'Opciones de tus Datos',
    'footer.copyright': '© Copyright 2026 NextCompli, Inc. Todos los derechos reservados. Diversas marcas registradas pertenecen a sus respectivos propietarios.',
    'footer.col.products': 'Productos',
    'footer.col.solutions': 'Soluciones',
    'footer.col.frameworks': 'Marcos',
    'footer.col.resources': 'Recursos',
    'footer.col.support': 'Soporte',
    'footer.products.ofac': 'Cribado OFAC',
    'footer.products.government': 'Cumplimiento gubernamental',
    'footer.products.website': 'Cumplimiento del sitio',
    'footer.products.education': 'Cumplimiento educativo',
    'footer.products.ai': 'Cumplimiento de IA',
    'footer.products.plans': 'Planes y precios',
    'footer.solutions.smallBiz': 'Pequeña empresa',
    'footer.solutions.enterprise': 'Empresa',
    'footer.solutions.defense': 'Contratistas de defensa',
    'footer.frameworks.ofac': 'OFAC / SDN',
    'footer.frameworks.cmmc': 'CMMC Nivel 1 (FCI)',
    'footer.frameworks.website': 'Sitio y privacidad',
    'footer.frameworks.ferpa': 'FERPA / GLBA campus',
    'footer.resources.blog': 'Blog',
    'footer.resources.news': 'Noticias',
    'footer.resources.trust': 'Confianza',
    'footer.resources.release': 'Notas de versión',
    'footer.resources.company': 'Acerca de',
    'footer.resources.dashboards': 'Tableros',
    'footer.resources.liability': 'Monitoreo de responsabilidad',
    'footer.support.help': 'Ayuda',
    'footer.support.demo': 'Agendar una demo',
    'footer.support.login': 'Iniciar sesión',
    'footer.support.contact': 'Contacto',
  },
};

type WebsiteLanguageContextValue = {
  language: WebsiteLanguage;
  setLanguage: React.Dispatch<React.SetStateAction<WebsiteLanguage>>;
  t: (key: string) => string;
};

const WebsiteLanguageContext = React.createContext<WebsiteLanguageContextValue | null>(null);

const useWebsiteLanguage = (): WebsiteLanguageContextValue => {
  const context = React.useContext(WebsiteLanguageContext);
  if (!context) {
    throw new Error('useWebsiteLanguage must be used within WebsiteLanguageContext.');
  }
  return context;
};

const COMPANY_DESTINATION = websitePath('/company');
const LEARNING_DESTINATION = websitePath('/blog');
// SupportPage still renders products/pricing at /plans; enterprise help tickets live at /help.
const SUPPORT_DESTINATION = websitePath('/plans');
const HELP_DESTINATION = websitePath('/help');
const FORGOT_PASSWORD_DESTINATION = websitePath('/forgot-password');
const RESET_PASSWORD_DESTINATION = websitePath('/reset-password');
const CONTACT_DESTINATION = websitePath('/contact-us');
const RELEASE_DESTINATION = websitePath('/release');
const LEGAL_DESTINATION = websitePath('/legal');
const PRIVACY_DESTINATION = websitePath('/privacy');
const TERMS_DESTINATION = websitePath('/terms');
const COOKIE_PREFERENCES_DESTINATION = websitePath('/cookie-preferences');
const DATA_CHOICES_DESTINATION = websitePath('/data-choices');
const TRUST_DESTINATION = websitePath('/trust');
const RESPONSIBLE_DISCLOSURE_DESTINATION = websitePath('/responsible-disclosure');
const DMCA_DESTINATION = websitePath('/dmca');
const ACCESSIBILITY_DESTINATION = websitePath('/accessibility');
const CLAIMS_EVIDENCE_DESTINATION = websitePath('/claims-evidence');

const LEGAL_POLICY_METADATA = {
  privacy: {
    version: '2026.02.24.1',
    updatedAt: 'February 24, 2026',
  },
  terms: {
    version: '2026.02.24.1',
    updatedAt: 'February 24, 2026',
  },
};

const TERMS_VERSION_HISTORY = [
  {
    version: '2026.01.15.0',
    publishedAt: 'January 15, 2026',
    summary: 'Initial legal route publication and baseline service terms.',
  },
  {
    version: LEGAL_POLICY_METADATA.terms.version,
    publishedAt: LEGAL_POLICY_METADATA.terms.updatedAt,
    summary: 'Expanded billing/renewal, governing law, dispute process, and assent traceability language.',
  },
];

const TERMS_VERSION_HASH = `tos-${LEGAL_POLICY_METADATA.terms.version}`;
const ASSENT_EVENTS_STORAGE_KEY = 'nextcompli.legal.assentEvents';
const DSAR_REQUESTS_STORAGE_KEY = 'nextcompli.privacy.dsarRequests';
const COOKIE_PREFERENCES_STORAGE_KEY = 'nextcompli.cookie.preferences';
const COOKIE_PREFERENCES_VERSION = '2026.02.25.1';
const inferDefaultMetricsBaseUrl = (): string => {
  const configured = (process.env.REACT_APP_METRICS_URL || process.env.REACT_APP_METRICS_SERVICE_URL || '').trim();
  if (configured) {
    return configured;
  }
  if (typeof window === 'undefined') {
    return '';
  }

  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1';
  if (isLocal) {
    return '';
  }

  return `${window.location.origin}/api/v1/metrics`;
};

const LEGAL_METRICS_BASE_URL = (process.env.REACT_APP_METRICS_SERVICE_URL || inferDefaultMetricsBaseUrl()).replace(/\/$/, '');

type ClaimsEvidenceItem = {
  claimId: string;
  claimText: string;
  evidenceUrl: string;
  lastReviewed: string;
};

const CLAIM_SUBSTANTIATION_REGISTRY: ClaimsEvidenceItem[] = [
  {
    claimId: 'CLAIM-OPS-001',
    claimText: 'Operational Compliance Cloud unifies risk visibility and sanctions intelligence workflows.',
    evidenceUrl: 'https://nextcompli.com/evidence/claim-ops-001',
    lastReviewed: '2026-02-24',
  },
  {
    claimId: 'CLAIM-DEPLOY-002',
    claimText: 'NextCompli supports local, Kubernetes, and cloud deployment pathways with architecture parity.',
    evidenceUrl: 'https://nextcompli.com/evidence/claim-deploy-002',
    lastReviewed: '2026-02-24',
  },
  {
    claimId: 'CLAIM-COMP-003',
    claimText: 'Compliance controls include legal routes, privacy rights intake, and accessibility evidence workflows.',
    evidenceUrl: 'https://nextcompli.com/evidence/claim-comp-003',
    lastReviewed: '2026-02-24',
  },
];

const ACCESSIBILITY_AUDIT_SUMMARY = {
  reportId: 'A11Y-Q1-2026',
  reportUrl: 'https://nextcompli.com/audits/accessibility-q1-2026',
  auditedAt: '2026-02-20',
  remediationCompletionPercent: 96,
  openCriticalIssues: 0,
  openSeriousIssues: 0,
};

type AssentEvent = {
  eventId: string;
  username: string;
  acceptedAt: string;
  termsVersion: string;
  termsVersionHash: string;
  privacyVersion: string;
  sourcePath: string;
};

const parseStoredArray = <T,>(key: string): T[] => {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const value = localStorage.getItem(key);
    if (!value) {
      return [];
    }
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
};

const publishAssentEvent = async (event: AssentEvent): Promise<void> => {
  if (!LEGAL_METRICS_BASE_URL) {
    return;
  }
  try {
    await fetch(`${LEGAL_METRICS_BASE_URL}/metrics/legal/assent-events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: event.username,
        account_id: event.username,
        source_path: event.sourcePath,
        terms_version: event.termsVersion,
        terms_version_hash: event.termsVersionHash,
        privacy_version: event.privacyVersion,
        accepted_at: event.acceptedAt,
      }),
    });
  } catch {
  }
};

const recordAssentEvent = (username: string, sourcePath: string): AssentEvent | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const event: AssentEvent = {
    eventId: `ASSENT-${Date.now()}`,
    username,
    acceptedAt: new Date().toISOString(),
    termsVersion: LEGAL_POLICY_METADATA.terms.version,
    termsVersionHash: TERMS_VERSION_HASH,
    privacyVersion: LEGAL_POLICY_METADATA.privacy.version,
    sourcePath,
  };
  try {
    const existing = parseStoredArray<AssentEvent>(ASSENT_EVENTS_STORAGE_KEY);
    const next = [event, ...existing].slice(0, 200);
    localStorage.setItem(ASSENT_EVENTS_STORAGE_KEY, JSON.stringify(next));
    localStorage.setItem('nextcompli.legal.latestAssent', JSON.stringify(event));
  } catch {
    // Do not block login when localStorage is full (research catalog can exhaust quota).
  }
  void publishAssentEvent(event);
  return event;
};

type DsarRequest = {
  requestId: string;
  requestType: string;
  submittedAt: string;
  dueAt: string;
  status: 'queued' | 'in_review' | 'completed';
};

const getStoredDsarRequests = (): DsarRequest[] => parseStoredArray<DsarRequest>(DSAR_REQUESTS_STORAGE_KEY);

const saveStoredDsarRequest = (request: DsarRequest): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const existing = getStoredDsarRequests();
  localStorage.setItem(DSAR_REQUESTS_STORAGE_KEY, JSON.stringify([request, ...existing].slice(0, 500)));
};

const isValidEvidenceUrl = (value: string): boolean => /^https?:\/\//i.test(value);

const PRISM_PRODUCT_MENU_IMAGE_VERSION = 'v=20260413-refresh-12';

const COMPANY_SLIDEOUT_ITEMS: SlideoutItem[] = [
  {
    id: 'about-nextcompli',
    to: COMPANY_DESTINATION,
    label: 'About NextCompli',
    summary: 'Learn about our history, products, and values.',
    ctaLabel: 'See our story',
    subItems: [
      { label: 'What Is NextCompli?', to: COMPANY_DESTINATION },
      { label: 'Leadership', to: COMPANY_DESTINATION },
      { label: 'Global Offices', to: COMPANY_DESTINATION },
      { label: 'Swag Store(opens in a new window)', to: COMPANY_DESTINATION, opensInNewWindow: true },
    ],
  },
  {
    id: 'our-values',
    to: COMPANY_DESTINATION,
    label: 'Our Values',
    summary: 'We are guided by our values and strive to do well in business while doing good in our communities.',
    ctaLabel: 'See what drives us',
    subItems: [
      { label: 'Trust(opens in a new window)', to: COMPANY_DESTINATION, opensInNewWindow: true },
      { label: 'Customer success', to: COMPANY_DESTINATION },
      { label: 'Innovation', to: COMPANY_DESTINATION },
    ],
  },
  {
    id: 'legal',
    to: COMPANY_DESTINATION,
    label: 'Legal',
    summary: withBrand(
      'Find answers to your questions about {brand} agreement terms, policies, intellectual property, corporate governance and compliance for customers, employees, partners and suppliers.',
    ),
    ctaLabel: 'See legal',
    subItems: [
      { label: 'For Customers', to: COMPANY_DESTINATION },
      { label: 'For Partners and Alliances', to: COMPANY_DESTINATION },
      { label: 'For Suppliers', to: COMPANY_DESTINATION },
      { label: 'Terms of Service', to: TERMS_DESTINATION },
      { label: 'TMCU Guidelines', to: COMPANY_DESTINATION },
      { label: 'DMCA', to: DMCA_DESTINATION },
      { label: 'Privacy Info', to: PRIVACY_DESTINATION },
      { label: 'Claim Evidence Registry', to: CLAIMS_EVIDENCE_DESTINATION },
      { label: 'Compliance', to: COMPANY_DESTINATION },
      { label: 'Product Accessibility Status', to: ACCESSIBILITY_DESTINATION },
      { label: 'Governance', to: COMPANY_DESTINATION },
    ],
  },
];

const COMPANY_SLIDEOUT_ITEMS_ES: SlideoutItem[] = [
  {
    id: 'about-nextcompli',
    to: COMPANY_DESTINATION,
    label: 'Acerca de NextCompli',
    summary: 'Conoce nuestra historia, productos y valores.',
    ctaLabel: 'Ver nuestra historia',
    subItems: [
      { label: '¿Qué es NextCompli?', to: COMPANY_DESTINATION },
      { label: 'Liderazgo', to: COMPANY_DESTINATION },
      { label: 'Oficinas globales', to: COMPANY_DESTINATION },
      { label: 'Tienda de regalos (se abre en una ventana nueva)', to: COMPANY_DESTINATION, opensInNewWindow: true },
    ],
  },
  {
    id: 'our-values',
    to: COMPANY_DESTINATION,
    label: 'Nuestros Valores',
    summary: 'Nos guían nuestros valores y buscamos hacer bien los negocios mientras hacemos el bien en nuestras comunidades.',
    ctaLabel: 'Ver lo que nos impulsa',
    subItems: [
      { label: 'Confianza (se abre en una ventana nueva)', to: COMPANY_DESTINATION, opensInNewWindow: true },
      { label: 'Éxito del cliente', to: COMPANY_DESTINATION },
      { label: 'Innovación', to: COMPANY_DESTINATION },
    ],
  },
  {
    id: 'legal',
    to: COMPANY_DESTINATION,
    label: 'Legal',
    summary:
      'Encuentra respuestas sobre términos de acuerdos, políticas, propiedad intelectual, gobierno corporativo y cumplimiento para clientes, empleados, socios y proveedores.',
    ctaLabel: 'Ver legal',
    subItems: [
      { label: 'Para clientes', to: COMPANY_DESTINATION },
      { label: 'Para socios y alianzas', to: COMPANY_DESTINATION },
      { label: 'Para proveedores', to: COMPANY_DESTINATION },
      { label: 'Términos del servicio', to: TERMS_DESTINATION },
      { label: 'Guías TMCU', to: COMPANY_DESTINATION },
      { label: 'DMCA', to: DMCA_DESTINATION },
      { label: 'Información de privacidad', to: PRIVACY_DESTINATION },
      { label: 'Registro de evidencias de reclamos', to: CLAIMS_EVIDENCE_DESTINATION },
      { label: 'Cumplimiento', to: COMPANY_DESTINATION },
      { label: 'Estado de accesibilidad del producto', to: ACCESSIBILITY_DESTINATION },
      { label: 'Gobernanza', to: COMPANY_DESTINATION },
    ],
  },
];

const SUPPORT_SLIDEOUT_ITEMS: SlideoutItem[] = [
  {
    id: 'help-documentation',
    to: HELP_DESTINATION,
    label: 'Help & Documentation',
    summary:
      'Need help? Ask the assistant. Find documentation, access learning resources, log cases, and more – all the support you need, wherever you need it.',
    ctaLabel: withBrand('Ask on {brand} Help'),
    subItems: [
      { label: 'Known Issues', to: `${HELP_DESTINATION}?topic=known-issues`, description: 'Find up-to-date information and status about our technology bugs and workarounds.' },
      {
        label: 'Developer Documentation',
        to: `${HELP_DESTINATION}?topic=documentation`,
        description: 'Find tutorials, sample code, developer guides, and API references.',
      },
      {
        label: 'Architecture Center',
        to: ARCHITECTURE_DESTINATION,
        description: 'Explore diagrams, decision guides, and resources to build trusted, easy, and adaptable solutions.',
      },
    ],
  },
  {
    id: 'communities',
    to: HELP_DESTINATION,
    label: 'Communities',
    summary: withBrand(
      'Meet people, learn skills, find experts, and share feedback in {brand} communities.',
    ),
    ctaLabel: 'Explore communities',
    subItems: [
      { label: 'Customer community', to: `${HELP_DESTINATION}?topic=other`, description: 'Connect with peers who run the same compliance work.' },
      {
        label: 'Learning',
        to: LEARNING_DESTINATION,
        description: 'Courses and guides for the work on your plan.',
      },
      {
        label: 'Partner community',
        to: `${HELP_DESTINATION}?topic=other`,
        description: 'Collaborate, learn, and manage your partner business.',
      },
    ],
  },
  {
    id: 'services-plans',
    to: SUPPORT_DESTINATION,
    label: 'Services & Plans',
    summary: 'Reach your goals with our customer success experts.',
    ctaLabel: 'See all services',
    subItems: [
      {
        label: 'Success Plans',
        to: `${HELP_DESTINATION}?topic=success-plans`,
        description: 'Get the right level of support and guidance to grow your business.',
      },
      {
        label: 'Professional Services',
        to: `${HELP_DESTINATION}?topic=success-plans`,
        description: withBrand('Implement, innovate, and transform your enterprise with help from {brand} experts.'),
      },
    ],
  },
  {
    id: 'account-management',
    to: HELP_DESTINATION,
    label: 'Account Management',
    summary: withBrand('Manage your {brand} subscriptions and contracts.'),
    ctaLabel: 'Explore Your Account features.',
    subItems: [
      {
        label: 'Your Account App',
        to: `${HELP_DESTINATION}?topic=login`,
        description: withBrand('Make payments, buy products, and manage renewals ({brand} sign-in required).'),
      },
      {
        label: 'Billing Portal',
        to: `${HELP_DESTINATION}?topic=billing`,
        description: withBrand('Access invoices and make payments ({brand} sign-in required).'),
      },
      {
        label: 'Digital Wallet',
        to: `${HELP_DESTINATION}?topic=billing`,
        description: 'Track and manage consumption with near-real time usage data.',
      },
    ],
  },
];

const SUPPORT_SLIDEOUT_ITEMS_ES: SlideoutItem[] = [
  {
    id: 'help-documentation',
    to: HELP_DESTINATION,
    label: 'Ayuda y Documentación',
    summary:
      '¿Necesitas ayuda? Pregunta al asistente. Encuentra documentación, recursos de aprendizaje, casos y más, todo el soporte que necesitas.',
    ctaLabel: withBrand('Preguntar en Ayuda de {brand}'),
    subItems: [
      {
        label: 'Problemas conocidos',
        to: `${HELP_DESTINATION}?topic=known-issues`,
        description: 'Encuentra información actualizada sobre errores y alternativas de nuestra tecnología.',
      },
      {
        label: 'Documentación para desarrolladores',
        to: `${HELP_DESTINATION}?topic=documentation`,
        description: 'Encuentra tutoriales, código de ejemplo, guías para desarrolladores y referencias de API.',
      },
      {
        label: 'Centro de arquitectura',
        to: ARCHITECTURE_DESTINATION,
        description: 'Explora diagramas, guías de decisión y recursos para crear soluciones confiables y adaptables.',
      },
    ],
  },
  {
    id: 'communities',
    to: HELP_DESTINATION,
    label: 'Comunidades',
    summary: withBrand('Conoce personas, aprende habilidades, encuentra expertos y comparte comentarios en comunidades de {brand}.'),
    ctaLabel: 'Explorar comunidades',
    subItems: [
      { label: 'Comunidad de clientes', to: `${HELP_DESTINATION}?topic=other`, description: 'Conéctate con pares que hacen el mismo trabajo de cumplimiento.' },
      {
        label: 'Aprendizaje',
        to: LEARNING_DESTINATION,
        description: 'Cursos y guías para el trabajo de tu plan.',
      },
      {
        label: 'Comunidad de socios',
        to: `${HELP_DESTINATION}?topic=other`,
        description: 'Colabora, aprende y gestiona tu negocio de partner.',
      },
    ],
  },
  {
    id: 'services-plans',
    to: SUPPORT_DESTINATION,
    label: 'Servicios y Planes',
    summary: 'Alcanza tus objetivos con nuestros expertos de éxito del cliente.',
    ctaLabel: 'Ver todos los servicios',
    subItems: [
      {
        label: 'Planes de éxito',
        to: `${HELP_DESTINATION}?topic=success-plans`,
        description: 'Obtén el nivel correcto de soporte y guía para hacer crecer tu negocio.',
      },
      {
        label: 'Servicios profesionales',
        to: `${HELP_DESTINATION}?topic=success-plans`,
        description: withBrand('Implementa, innova y transforma tu empresa con ayuda de expertos de {brand}.'),
      },
    ],
  },
  {
    id: 'account-management',
    to: HELP_DESTINATION,
    label: 'Gestión de Cuenta',
    summary: withBrand('Administra tus suscripciones y contratos de {brand}.'),
    ctaLabel: 'Explorar funciones de tu cuenta',
    subItems: [
      {
        label: 'App Tu Cuenta',
        to: `${HELP_DESTINATION}?topic=login`,
        description: withBrand('Realiza pagos, compra productos y gestiona renovaciones (requiere inicio de sesión de {brand}).'),
      },
      {
        label: 'Portal de facturación',
        to: `${HELP_DESTINATION}?topic=billing`,
        description: withBrand('Accede a facturas y realiza pagos (requiere inicio de sesión de {brand}).'),
      },
      {
        label: 'Billetera digital',
        to: `${HELP_DESTINATION}?topic=billing`,
        description: 'Monitorea y administra consumo con datos de uso casi en tiempo real.',
      },
    ],
  },
];

const LEARNING_SLIDEOUT_ITEMS: SlideoutItem[] = [
  {
    id: 'try-nextcompli-for-free',
    to: LEARNING_DESTINATION,
    label: 'Try NextCompli for Free',
    summary: '',
    ctaLabel: 'Get started',
    subItems: [
      {
        label: 'Free Suite(opens in a new window)',
        to: LEARNING_DESTINATION,
        description: 'The free CRM suite with sales, service, marketing, and Slack for a maximum of 2 users.',
        opensInNewWindow: true,
      },
      {
        label: 'Starter Suite(opens in a new window)',
        to: LEARNING_DESTINATION,
        description:
          'The marketing, sales, service, and commerce tools you need to succeed — all in a single CRM suite.',
        opensInNewWindow: true,
      },
      {
        label: 'Developer Edition(opens in a new window)',
        to: LEARNING_DESTINATION,
        description: 'Build enterprise-quality apps fast and get hands-on with AI and Data Cloud.',
        opensInNewWindow: true,
      },
    ],
  },
  {
    id: 'new-to-nextcompli',
    to: LEARNING_DESTINATION,
    label: 'New to NextCompli',
    summary: '',
    ctaLabel: 'Learn more about NextCompli',
    subItems: [
      {
        label: 'What is CRM?',
        to: LEARNING_DESTINATION,
        description: 'Learn all about what CRM is, what it does, and how it can improve customer relationships.',
      },
      {
        label: 'What is Customer 360?',
        to: LEARNING_DESTINATION,
        description: 'NextCompli’s category-leading apps for sales, service, marketing, commerce, IT, and more.',
      },
      {
        label: 'What are AI agents?',
        to: LEARNING_DESTINATION,
        description: 'Learn more about AI agents and how they can help your business.',
      },
      {
        label: 'What is the AI assistant?',
        to: LEARNING_DESTINATION,
        description:
          'It’s always-on digital labor augmenting every employee, department, and business process to improve customer experiences.',
      },
    ],
  },
  {
    id: 'blogs',
    to: LEARNING_DESTINATION,
    label: 'Blogs',
    summary: 'Discover tips and insights from experts to supercharge your digital journey.',
    ctaLabel: 'Explore all blogs',
    subItems: [
      {
        label: 'The 360 Blog',
        to: LEARNING_DESTINATION,
        description: 'Get the latest on AI, data, digital labor, and more.',
      },
      {
        label: 'AI Research Blog',
        to: LEARNING_DESTINATION,
        description: 'Keep up with NextCompli’s AI research team and stay on the cutting edge of technology.',
      },
      {
        label: 'Developer Blog',
        to: LEARNING_DESTINATION,
        description: 'Get the latest tooling innovation news, insights, and inspiration.',
      },
      {
        label: 'Salesblazer',
        to: LEARNING_DESTINATION,
        description:
          'Grow your career with the latest in sales content, training, and events — all part of the world’s leading sales community.',
      },
    ],
  },
  {
    id: 'resources',
    to: LEARNING_DESTINATION,
    label: 'Resources',
    summary: 'Find free articles, guides, best practices, research, and more.',
    ctaLabel: 'Explore all resources',
    subItems: [
      {
        label: 'Analyst Reports',
        to: LEARNING_DESTINATION,
        description: 'Read the latest studies and reports from the world’s best-known business analysts.',
      },
      {
        label: 'Partners',
        to: LEARNING_DESTINATION,
        description: 'Explore industry-leading NextCompli partner apps and experts.',
      },
      {
        label: 'Developer Documentation',
        to: LEARNING_DESTINATION,
        description: 'Get developer guides, tutorials, sample code, and API references.',
      },
      {
        label: 'Newsletter(opens in a new window)',
        to: LEARNING_DESTINATION,
        description: 'Sign up to receive the latest NextCompli tips and insights, tailored just for you.',
        opensInNewWindow: true,
      },
    ],
  },
];

const LEARNING_SLIDEOUT_ITEMS_ES: SlideoutItem[] = [
  {
    id: 'try-nextcompli-for-free',
    to: LEARNING_DESTINATION,
    label: 'Prueba NextCompli gratis',
    summary: '',
    ctaLabel: 'Comenzar',
    subItems: [
      {
        label: 'Suite gratuita (se abre en una ventana nueva)',
        to: LEARNING_DESTINATION,
        description: 'La suite CRM gratuita con ventas, servicio, marketing y Slack para un máximo de 2 usuarios.',
        opensInNewWindow: true,
      },
      {
        label: 'Suite Starter (se abre en una ventana nueva)',
        to: LEARNING_DESTINATION,
        description:
          'Las herramientas de marketing, ventas, servicio y comercio que necesitas para crecer, todo en una sola suite CRM.',
        opensInNewWindow: true,
      },
      {
        label: 'Edición para desarrolladores (se abre en una ventana nueva)',
        to: LEARNING_DESTINATION,
        description: 'Crea aplicaciones empresariales de alta calidad rápidamente y trabaja con IA y Data Cloud.',
        opensInNewWindow: true,
      },
    ],
  },
  {
    id: 'new-to-nextcompli',
    to: LEARNING_DESTINATION,
    label: 'Nuevo en NextCompli',
    summary: '',
    ctaLabel: 'Conocer más sobre NextCompli',
    subItems: [
      {
        label: '¿Qué es CRM?',
        to: LEARNING_DESTINATION,
        description: 'Aprende qué es un CRM, qué hace y cómo puede mejorar las relaciones con tus clientes.',
      },
      {
        label: '¿Qué es Customer 360?',
        to: LEARNING_DESTINATION,
        description: 'Las aplicaciones líderes de NextCompli para ventas, servicio, marketing, comercio, TI y más.',
      },
      {
        label: '¿Qué son los agentes de IA?',
        to: LEARNING_DESTINATION,
        description: 'Descubre más sobre los agentes de IA y cómo pueden ayudar a tu empresa.',
      },
      {
        label: '¿Qué es el asistente de IA?',
        to: LEARNING_DESTINATION,
        description:
          'Es trabajo digital siempre activo que potencia a cada empleado, departamento y proceso para mejorar la experiencia del cliente.',
      },
    ],
  },
  {
    id: 'blogs',
    to: LEARNING_DESTINATION,
    label: 'Blogs',
    summary: 'Descubre consejos e ideas de expertos para impulsar tu recorrido digital.',
    ctaLabel: 'Explorar todos los blogs',
    subItems: [
      {
        label: 'Blog 360',
        to: LEARNING_DESTINATION,
        description: 'Obtén lo último sobre IA, datos, trabajo digital y más.',
      },
      {
        label: 'Blog de investigación en IA',
        to: LEARNING_DESTINATION,
        description: 'Sigue al equipo de investigación de IA de NextCompli y mantente en la vanguardia tecnológica.',
      },
      {
        label: 'Blog de desarrolladores',
        to: LEARNING_DESTINATION,
        description: 'Conoce novedades de herramientas, ideas y recursos para desarrolladores.',
      },
      {
        label: 'Salesblazer',
        to: LEARNING_DESTINATION,
        description:
          'Impulsa tu carrera con el contenido, la formación y los eventos de ventas de la comunidad líder del mundo.',
      },
    ],
  },
  {
    id: 'resources',
    to: LEARNING_DESTINATION,
    label: 'Recursos',
    summary: 'Encuentra artículos, guías, mejores prácticas, investigación y más, todo gratis.',
    ctaLabel: 'Explorar todos los recursos',
    subItems: [
      {
        label: 'Informes de analistas',
        to: LEARNING_DESTINATION,
        description: 'Lee estudios e informes recientes de analistas empresariales reconocidos.',
      },
      {
        label: 'Socios',
        to: LEARNING_DESTINATION,
        description: 'Explora aplicaciones de socios líderes y expertos de NextCompli.',
      },
      {
        label: 'Documentación para desarrolladores',
        to: LEARNING_DESTINATION,
        description: 'Accede a guías, tutoriales, código de ejemplo y referencias de API.',
      },
      {
        label: 'Boletín (se abre en una ventana nueva)',
        to: LEARNING_DESTINATION,
        description: 'Regístrate para recibir los últimos consejos e insights de NextCompli, adaptados para ti.',
        opensInNewWindow: true,
      },
    ],
  },
];

const CUSTOMER_SLIDEOUT_ITEMS: SlideoutItem[] = [
  {
    id: 'customer-stories',
    to: websitePath('/customers'),
    label: 'Customer Stories',
    summary: 'Explore success stories from customers of all sizes, in every industry.',
    subItems: [
      { label: 'Customer Highlights from Earnings', to: websitePath('/customers/customer-highlights-from-earnings') },
      { label: 'Analytics Stories', to: websitePath('/customers/analytics-stories') },
      { label: 'Integration Stories', to: websitePath('/customers/integration-stories') },
      { label: 'Collaboration Stories', to: websitePath('/customers/collaboration-stories') },
      { label: 'Platform Stories', to: websitePath('/customers/platform-stories') },
    ],
    ctaLabel: 'See all stories',
  },
];

type ProductDetailContent = {
  slug: string;
  label: string;
  title: string;
  summary: string;
  highlights: string[];
  details: string[];
  sources: { label: string; href: string }[];
};

type ProductSubDetailContent = {
  id: string;
  parentSlug: string;
  parentLabel: string;
  subSlug: string;
  label: string;
  title: string;
  to: string;
  summary: string;
  businessValue: string;
  businessRisk: string[];
  capabilities: string[];
  workflow: string[];
  outcomes: string[];
  sources: { label: string; href: string }[];
};

type CustomerStoryContent = {
  slug: string;
  label: string;
  title: string;
  subtitle: string;
  industry: string;
  challenge: string;
  approach: string[];
  outcomes: string[];
  sources: { label: string; href: string }[];
};

type IndustryDetailContent = {
  slug: string;
  label: string;
  title: string;
  summary: string;
  heroSubtitle: string;
  ctaLabel: string;
  ctaTo?: string;
};

type IndustrySubDetailContent = {
  id: string;
  parentSlug: string;
  parentLabel: string;
  subSlug: string;
  label: string;
  title: string;
  to: string;
  summary: string;
  pillars: string[];
  details: string[];
};

type IndustryIconKey =
  | 'automotive'
  | 'communications'
  | 'construction'
  | 'consumer'
  | 'education'
  | 'energy'
  | 'finance'
  | 'health'
  | 'science'
  | 'manufacturing'
  | 'media'
  | 'nonprofit'
  | 'professional'
  | 'public'
  | 'retail'
  | 'technology'
  | 'travel';

const toSlug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const toProductSubSlug = (productSlug: string, highlight: string): string => {
  if (productSlug === 'usage-based-pricing-plans') {
    const match = PRICING_TIERS.find((tier) => {
      const name = String(tier.name || '').toLowerCase();
      const label = String(tier.marketingLabel || '').toLowerCase();
      const haystack = highlight.toLowerCase();
      return (name && haystack.includes(name)) || (label && haystack.includes(label));
    });
    if (match?.usagePricingSubSlug) {
      return match.usagePricingSubSlug;
    }
  }
  return toSlug(highlight);
};

const toExplorePlatformLabel = (label: string): string =>
  /platform/i.test(label) ? `Explore ${label}` : `Explore ${label} Platform`;

const toExplorePageTitle = (title: string): string =>
  /platform/i.test(title) ? `Explore ${title}` : `Explore ${title} Platform`;

export const MENU_TEXT_ES: Record<string, string> = {
  Home: 'Inicio',
  Products: 'Productos',
  Services: 'Servicios',
  Features: 'Funciones',
  'By job': 'Por trabajo',
  'Screen a name': 'Cribar un nombre',
  'Run government homework': 'Hacer la tarea gubernamental',
  'Check a public website': 'Revisar un sitio público',
  'Check the cloud': 'Revisar la nube',
  'Legal Entity Formation': 'Formación de entidad legal',
  'Wills & Trusts': 'Testamentos y fideicomisos',
  'Invite the team': 'Invitar al equipo',
  'Cloud Compliance': 'Cumplimiento de la nube',
  Organization: 'Organización',
  Industries: 'Industrias',
  Customers: 'Clientes',
  Learning: 'Aprendizaje',
  Company: 'Compañía',
  'Discover NextCompli products': 'Descubre los productos de NextCompli',
  'Browse products, services, and features.': 'Explora productos, servicios y funcionalidades.',
  'Explore Platform': 'Explorar Plataforma',
  'Explore industries': 'Explorar industrias',
  'Get industry-specific innovations to modernize your business and lower costs.':
    'Obtén innovaciones específicas por industria para modernizar tu negocio y reducir costos.',
  'Customer Stories': 'Historias de clientes',
  'Explore success stories from customers of all sizes, in every industry.':
    'Explora historias de éxito de clientes de todos los tamaños en cada industria.',
  'See all stories': 'Ver todas las historias',
  'Explore Learning': 'Explorar aprendizaje',
  'Learn more': 'Conocer más',

  'Screening API Platform': 'Plataforma API de Evaluación',
  'Continuous Monitoring': 'Monitoreo Continuo',
  'Case & Audit Workspace': 'Espacio de Casos y Auditoría',
  'Developer Sandbox & Docs': 'Sandbox y Documentación para Desarrolladores',
  'Basic Support Package': 'Paquete de Soporte Básico',
  'Advanced Support Package': 'Paquete de Soporte Avanzado',
  'Enterprise Support Package': 'Paquete de Soporte Empresarial',
  'CompliCore (Starting at $99/month)': 'CompliCore (Desde $99/mes)',
  'CompliAdvantage (Starting at $999/month)': 'CompliAdvantage (Desde $999/mes)',
  'Professional (Starting at $999/month)': 'CompliAdvantage (Desde $999/mes)',
  'Basic (Starting at $99/month)': 'Esenciales (Desde $99/mes)',
  'Advanced (Starting at $999/month)': 'Profesional (Desde $999/mes)',
  'Enterprise (Contact for estimate)': 'CompliComplete (Contactar para estimación)',
  'CompliComplete (Contact for estimate)': 'CompliComplete (Contactar para estimación)',
  'Basic starting at $99/month': 'Básico desde $99/mes',
  'Advanced starting at $999/month': 'Avanzado desde $999/mes',
  'Enterprise contact for estimate': 'CompliComplete: contactar para estimación',
  'CompliComplete contact for estimate': 'CompliComplete: contactar para estimación',
  'Everything in Basic': 'Todo lo de Básico',
  'Everything in Advanced': 'Todo lo de Avanzado',
  'Live Environment Monitoring': 'Monitoreo de Entorno en Vivo',
  'Real-Time Alerts': 'Alertas en Tiempo Real',
  'Embedded Consultants': 'Consultores Integrados',
  'Scheduled Audits': 'Auditorías Programadas',
  'OFAC Compliance': 'Cumplimiento OFAC',
  'Website Compliance': 'Cumplimiento del Sitio Web',
  'DoD Contracts Compliance': 'Cumplimiento gubernamental',
  'Government Compliance': 'Cumplimiento gubernamental',
  'Education Compliance': 'Cumplimiento educativo',
  'Open Education Compliance': 'Abrir cumplimiento educativo',
  'Campus Restricted-Party Screening': 'Evaluación de partes restringidas en campus',
  'Research Export and CUI': 'Exportación de investigación y CUI',
  'HECVAT and Vendor Trust': 'HECVAT y confianza de proveedores',
  'FERPA and GLBA Program': 'Programa FERPA y GLBA',
  Dashboards: 'Paneles',
  'Risk-scoring': 'Puntuación de Riesgo',
  'Screening Operations': 'Operaciones de Evaluación',
  'Data Source Health Monitoring': 'Monitoreo de Salud de Fuentes de Datos',
  'Risk & Exposure Insights': 'Perspectivas de Riesgo y Exposición',
  'Liability Compliance Monitoring': 'Monitoreo de Cumplimiento de Responsabilidad',
  'Audit Evidence Management': 'Gestión de Evidencia de Auditoría',
  'Alerts & Incident Workflow': 'Flujo de Alertas e Incidentes',
  'Executive Compliance Reporting': 'Reportes Ejecutivos de Cumplimiento',
  'OFAC SDN & General Licenses': 'OFAC SDN y Licencias Generales',
  'CAPTA + Consolidated Lists': 'CAPTA y Listas Consolidadas',
  'SEC Filings + EDGAR Search': 'Presentaciones SEC y Búsqueda EDGAR',
  'DOE / State Sanctions Context': 'Contexto de Sanciones DOE / Departamento de Estado',
  'Real-time + Batch Screening': 'Evaluación en Tiempo Real y por Lotes',
  'Fuzzy Matching & Transliteration': 'Coincidencia Difusa y Transliteración',
  'Audit Trail + Alert Workflow': 'Rastro de Auditoría y Flujo de Alertas',
  'Usage-based Pricing Plans': 'Planes de Precios por Uso',
  'Accessibility & User Experience': 'Accesibilidad y Experiencia de Usuario',
  'Board-ready compliance reporting': 'Reportes de cumplimiento listos para directorio',
  'Company and people background dossier': 'Dossier de antecedentes de empresas y personas',
  'Compliance Posture Overview': 'Resumen de postura de cumplimiento',
  'Contract Compliance': 'Cumplimiento contractual',
  Cybersecurity: 'Ciberseguridad',
  'Evidence-linked decision logs': 'Registros de decisiones vinculados a evidencia',
  'Evidence-linked investigation package': 'Paquete de investigacion vinculado a evidencia',
  'Execution support with your teams': 'Soporte de ejecucion con tus equipos',
  'Financial & Ethics Controls': 'Controles financieros y eticos',
  'Governance operating model': 'Modelo operativo de gobernanza',
  'Incident response actions': 'Acciones de respuesta a incidentes',
  'Leadership dashboard snapshots': 'Vistas ejecutivas de panel',
  'Monitoring Operations Console': 'Consola de operaciones de monitoreo',
  'Priority alert pipelines': 'Canales de alertas prioritarias',
  'Intellectual Property & DMCA': 'Propiedad intelectual y DMCA',
  'Marketing, Terms & Consumer Rules': 'Marketing, términos y normas al consumidor',
  'Privacy & Data Governance': 'Privacidad y gobernanza de datos',
  'Program design guidance': 'Guia de diseno de programa',
  'Research Reports & Investigations': 'Reportes de investigacion e investigaciones',
  'Research Reports workspace': 'Espacio de trabajo de reportes de investigacion',
  'Security & Risk Protection': 'Proteccion de seguridad y riesgo',
  'Email Authenticity & Deliverability': 'Autenticidad y entregabilidad de correo',
  'Zero Trust & System Hardening': 'Zero Trust y endurecimiento del sistema',
  'Submit people and entities': 'Registrar personas y entidades',
  'Supply Chain Assurance': 'Aseguramiento de cadena de suministro',
  'Trend and KPI rollups': 'Consolidado de tendencias y KPI',

  'Single screening endpoint design': 'Diseño de endpoint único de evaluación',
  'Normalized response schema': 'Esquema de respuesta normalizado',
  'Config-first deployment controls': 'Controles de despliegue basados en configuración',
  'Ongoing rescreen schedules': 'Programaciones de reevaluación continua',
  'Delta alerts on source updates': 'Alertas delta por actualizaciones de fuentes',
  'Escalation-ready change events': 'Eventos de cambio listos para escalación',
  'Case queue and disposition states': 'Cola de casos y estados de resolución',
  'Analyst notes and evidence attachments': 'Notas de analista y adjuntos de evidencia',
  'Regulator-friendly timeline view': 'Vista de línea de tiempo para reguladores',
  'Endpoint examples and payloads': 'Ejemplos de endpoints y payloads',
  'Environment parity from local to cloud': 'Paridad de entornos de local a nube',
  'Test-first integration patterns': 'Patrones de integración guiados por pruebas',
  'SDN list ingestion patterns': 'Patrones de ingestión de lista SDN',
  'General-license applicability context': 'Contexto de aplicabilidad de licencias generales',
  'Program-level sanctions metadata': 'Metadatos de sanciones a nivel programa',
  'CAPTA list handling': 'Gestión de lista CAPTA',
  'Consolidated-list search alignment': 'Alineación de búsqueda de listas consolidadas',
  'Program code classification': 'Clasificación por código de programa',
  '20+ year filing search depth': 'Profundidad de búsqueda de presentaciones de más de 20 años',
  'Issuer-level disclosure context': 'Contexto de divulgación a nivel emisor',
  'Evidence links for case review': 'Enlaces de evidencia para revisión de casos',
  'Foreign-policy sanctions context': 'Contexto de sanciones de política exterior',
  'Sector and jurisdiction review': 'Revisión por sector y jurisdicción',
  'Cross-source analyst references': 'Referencias de analista entre fuentes',
  'Low-latency API checks': 'Validaciones API de baja latencia',
  'Bulk screening jobs': 'Procesos masivos de evaluación',
  'Consistent scoring outputs': 'Resultados de puntuación consistentes',
  'Approximate-name matching': 'Coincidencia aproximada de nombres',
  'Cross-script transliteration support': 'Soporte de transliteración entre alfabetos',
  'Configurable sensitivity tuning': 'Ajuste configurable de sensibilidad',
  'Structured alert lifecycle': 'Ciclo de vida estructurado de alertas',
  'Immutable decision timeline': 'Línea de tiempo inmutable de decisiones',
  'Reviewer accountability controls': 'Controles de responsabilidad del revisor',
  'Volume-scaled tiers': 'Niveles escalados por volumen',
  'Deployment-aware packaging': 'Empaquetado según despliegue',
  'Growth path from pilot to enterprise': 'Ruta de crecimiento de piloto a empresa',

  Automotive: 'Automotriz',
  Communications: 'Comunicaciones',
  'Construction & Real Estate': 'Construcción e Inmobiliaria',
  'Consumer Goods': 'Bienes de Consumo',
  Education: 'Educación',
  'Energy & Utilities': 'Energía y Servicios Públicos',
  'Financial Services': 'Servicios Financieros',
  Healthcare: 'Salud',
  'Life Sciences': 'Ciencias de la Vida',
  Manufacturing: 'Manufactura',
  Media: 'Medios',
  Nonprofit: 'Sin fines de lucro',
  'Professional Services': 'Servicios Profesionales',
  'Public Sector': 'Sector Público',
  Retail: 'Retail',
  Technology: 'Tecnología',
  'Travel, Transportation & Hospitality': 'Viajes, Transporte y Hospitalidad',

  OEM: 'Fabricantes OEM',
  'Dealer/Retailers': 'Distribuidores / Minoristas',
  Captive: 'Financieras cautivas',
  'Software-Defined Vehicle (SDV)': 'Vehículo Definido por Software (SDV)',
  'Automotive Cloud': 'Nube Automotriz',
  'Automotive Pricing': 'Precios Automotrices',
  'Unify the Telecom Customer Journey': 'Unificar el recorrido del cliente de telecom',
  'Accelerate Communication Sales': 'Acelerar ventas de comunicaciones',
  'Simplify Wholesale Transactions': 'Simplificar transacciones mayoristas',
  'Light Up Fiber Experiences': 'Impulsar experiencias de fibra',
  'Fulfill the Perfect Order': 'Cumplir con el pedido perfecto',
  'Optimize Telecom Customer Service': 'Optimizar servicio al cliente en telecom',
  'Real Estate': 'Bienes raíces',
  'Engineering & Construction': 'Ingeniería y Construcción',
  'Residential & Commercial Services': 'Servicios residenciales y comerciales',
  'Fast-Moving Consumer Goods': 'Bienes de consumo de alta rotación',
  'Home Durables': 'Bienes durables para el hogar',
  Distributors: 'Distribuidores',
  'Apparel, Footwear, and Accessories': 'Ropa, calzado y accesorios',
  Beauty: 'Belleza',
  'Recruitment & Admissions': 'Reclutamiento y admisiones',
  'Academic Operations': 'Operaciones académicas',
  'Student Success': 'Éxito estudiantil',
  'Advancement & Alumni Relations': 'Desarrollo y relación con exalumnos',
  'Communications, Marketing, and Engagement': 'Comunicaciones, marketing y compromiso',
  'Lifelong Learning': 'Aprendizaje continuo',
  'K-12': 'K-12',
  'Power & Utilities': 'Energía y servicios públicos',
  'Oil & Gas': 'Petróleo y gas',
  'Scale Service': 'Escalar servicio',
  'Streamline Onboarding': 'Optimizar incorporación',
  'Personalize Engagement': 'Personalizar interacción',
  'Modernize Insurance Sales': 'Modernizar ventas de seguros',
  'Next Gen Service for Banking': 'Servicio de nueva generación para banca',
  'Next Gen Advisor for Wealth': 'Asesor de nueva generación para patrimonio',
  'Provider Solutions': 'Soluciones para proveedores',
  'Payer and Insurance Solutions': 'Soluciones para pagadores y aseguradoras',
  'Public Health and Human Services': 'Salud pública y servicios humanos',
  Pharmaceuticals: 'Farmacéuticas',
  MedTech: 'Tecnología médica',
  Biotech: 'Biotecnología',
  Chemicals: 'Químicos',
  'Automotive Suppliers': 'Proveedores automotrices',
  'Industrial Manufacturing': 'Manufactura industrial',
  'Consumer Electronics': 'Electrónica de consumo',
  Advertising: 'Publicidad',
  Broadcasting: 'Radiodifusión',
  Publishing: 'Publicación',
  'Arts & Culture': 'Arte y cultura',
  'Fundraising & Development': 'Recaudación y desarrollo',
  'Program Delivery': 'Entrega de programas',
  'Volunteer Management': 'Gestión de voluntariado',
  Consulting: 'Consultoría',
  'Staffing and Recruiting': 'Dotación y reclutamiento',
  Legal: 'Legal',
  Government: 'Gobierno',
  'Public Safety': 'Seguridad pública',
  'Citizen Services': 'Servicios al ciudadano',
  'Store Operations': 'Operaciones de tienda',
  Merchandising: 'Merchandising',
  'Digital Commerce': 'Comercio digital',
  'Software & Platforms': 'Software y plataformas',
  'High Tech Manufacturing': 'Manufactura de alta tecnología',
  'IT Services': 'Servicios de TI',
  Travel: 'Viajes',
  'Transportation and Logistics': 'Transporte y logística',
  Hospitality: 'Hospitalidad',

  'Customer Highlights from Earnings': 'Casos destacados de resultados',
  'Analytics Stories': 'Historias de analítica',
  'Integration Stories': 'Historias de integración',
  'Collaboration Stories': 'Historias de colaboración',
  'Platform Stories': 'Historias de plataforma',

  'Try NextCompli for Free': 'Prueba NextCompli gratis',
  'New to NextCompli': 'Nuevo en NextCompli',
  Blogs: 'Blogs',
  Resources: 'Recursos',
  'Get started': 'Comenzar',
  'Learn more about NextCompli': 'Conocer más sobre NextCompli',
  'Explore all blogs': 'Explorar todos los blogs',
  'Explore all resources': 'Explorar todos los recursos',

  'About NextCompli': 'Acerca de NextCompli',
  'Our Values': 'Nuestros Valores',
  'See our story': 'Ver nuestra historia',
  'See what drives us': 'Ver lo que nos impulsa',
  'See legal': 'Ver legal',
  'Free Suite': 'Suite gratuita',
  'Starter Suite': 'Suite Starter',
  'Developer Edition': 'Edición para desarrolladores',
  Newsletter: 'Boletín',
  'Swag Store': 'Tienda de regalos',
  Trust: 'Confianza',
};

const OPEN_NEW_WINDOW_SUFFIX_REGEX = /\s*\(opens in a new window\)\s*$/i;
const OPEN_NEW_WINDOW_SUFFIX_ES = ' (se abre en una ventana nueva)';

const localizeMenuText = (text: string, language: WebsiteLanguage): string => {
  if (language === 'en') {
    return text;
  }
  const directMatch = MENU_TEXT_ES[text];
  if (directMatch) {
    return directMatch;
  }

  if (OPEN_NEW_WINDOW_SUFFIX_REGEX.test(text)) {
    const baseLabel = text.replace(OPEN_NEW_WINDOW_SUFFIX_REGEX, '').trim();
    const localizedBaseLabel = MENU_TEXT_ES[baseLabel] ?? baseLabel;
    return `${localizedBaseLabel}${OPEN_NEW_WINDOW_SUFFIX_ES}`;
  }

  return text;
};

const localizeSlideoutItems = (items: SlideoutItem[], language: WebsiteLanguage): SlideoutItem[] =>
  items.map((item) => {
    const localizedLabel = localizeMenuText(item.label, language);
    return {
      ...item,
      label: localizedLabel,
      summary: item.summary
        ? (localizeMenuText(item.summary, language) === item.summary && language === 'es'
          ? `Información y recursos sobre ${localizedLabel}.`
          : localizeMenuText(item.summary, language))
        : item.summary,
      ctaLabel: item.ctaLabel
        ? (localizeMenuText(item.ctaLabel, language) === item.ctaLabel && language === 'es'
          ? `Explorar ${localizedLabel}`
          : localizeMenuText(item.ctaLabel, language))
        : item.ctaLabel,
      subItems: item.subItems.map((subItem) => {
        const localizedSubItemLabel = localizeMenuText(subItem.label, language);
        const localizedDescription = subItem.description
          ? (localizeMenuText(subItem.description, language) === subItem.description && language === 'es'
            ? `Más información sobre ${localizedSubItemLabel}.`
            : localizeMenuText(subItem.description, language))
          : undefined;
        return {
          ...subItem,
          label: localizedSubItemLabel,
          description: localizedDescription,
        };
      }),
    };
  });

const localizeSlideoutGroups = (groups: SlideoutGroup[], language: WebsiteLanguage): SlideoutGroup[] =>
  groups.map((group) => ({
    ...group,
    header: localizeMenuText(group.header, language),
    items: localizeSlideoutItems(group.items, language),
  }));

const PRODUCT_TILE_TONES = ['teal', 'violet', 'indigo', 'cyan', 'green', 'pink', 'orange', 'blue'];
const INDUSTRY_TILE_TONES = ['violet', 'indigo', 'blue', 'cyan', 'teal'];

type ProductTileIconKey =
  | 'ingestion'
  | 'listCoverage'
  | 'localChecks'
  | 'publicChecks'
  | 'governance'
  | 'license'
  | 'metadata'
  | 'monitoring'
  | 'checks'
  | 'alerts'
  | 'audit'
  | 'api'
  | 'analytics'
  | 'pricing'
  | 'default';

const productTileIconKey = (productSlug: string, highlight: string): ProductTileIconKey => {
  const text = `${productSlug} ${highlight}`.toLowerCase();
  if (/local\s+content\s+compliance\s+checks|local\s+content\s+checks|local\s+file/.test(text)) return 'localChecks';
  if (/public\s+website\s+compliance\s+checks|public\s+website\s+checks|public\s+site/.test(text)) return 'publicChecks';
  if (/policy\s+exception\s+governance|exception\s+governance|policy\s+exception/.test(text)) return 'governance';
  if (/general-license|license|policy|applicability/.test(text)) return 'license';
  if (/real\s*time|batch|screening|check/.test(text)) return 'checks';
  if (/ingestion|list|source|capta|consolidated|coverage/.test(text)) return 'listCoverage';
  if (/ingestion|list|source|batch/.test(text)) return 'ingestion';
  if (/program|metadata|taxonomy|context/.test(text)) return 'metadata';
  if (/monitor|continuous|live|rescreen/.test(text)) return 'monitoring';
  if (/alert|incident|escalation|workflow/.test(text)) return 'alerts';
  if (/audit|evidence|timeline|report/.test(text)) return 'audit';
  if (/api|endpoint|schema|gateway|payload/.test(text)) return 'api';
  if (/dashboard|risk|insight|score|trend/.test(text)) return 'analytics';
  if (/pricing|cost|tier|usage/.test(text)) return 'pricing';
  return 'default';
};

const ProductTileGlyph: React.FC<{ productSlug: string; highlight: string; className?: string }> = ({
  productSlug,
  highlight,
  className,
}) => {
  const iconKey = productTileIconKey(productSlug, highlight);
  const strokeProps = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.9,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  const iconPaths = (() => {
    switch (iconKey) {
      case 'ingestion':
        return <><path d="M4.5 7.5h15" {...strokeProps} /><path d="M4.5 12h10" {...strokeProps} /><path d="M4.5 16.5h6" {...strokeProps} /><path d="m14.5 9 4 3-4 3" {...strokeProps} /></>;
      case 'listCoverage':
        return <><rect x="5.2" y="6" width="13.6" height="12" rx="2" {...strokeProps} /><path d="M8 9.4h8" {...strokeProps} /><path d="M8 12h6.4" {...strokeProps} /><path d="M8 14.6h7.3" {...strokeProps} /><path d="m15.2 8.2 1.2 1.2 2.1-2.1" {...strokeProps} /></>;
      case 'localChecks':
        return <><rect x="5.7" y="4.8" width="12.6" height="14.4" rx="2" {...strokeProps} /><path d="M8.8 9.1h6.6" {...strokeProps} /><path d="M8.8 12.1h5.2" {...strokeProps} /><path d="m8.9 15.1 1.6 1.6 2.8-2.8" {...strokeProps} /></>;
      case 'publicChecks':
        return <><circle cx="12" cy="11.2" r="5.8" {...strokeProps} /><path d="M6.4 11.2h11.2" {...strokeProps} /><path d="M12 5.4a8.4 8.4 0 0 1 0 11.6" {...strokeProps} /><path d="M12 5.4a8.4 8.4 0 0 0 0 11.6" {...strokeProps} /><path d="m9 18.2 1.8 1.8 4-4" {...strokeProps} /></>;
      case 'governance':
        return <><path d="M12 4.6 6 7v4.6c0 3.3 2.1 5.9 6 7.2 3.9-1.3 6-3.9 6-7.2V7l-6-2.4Z" {...strokeProps} /><path d="M9.1 11.5h5.8" {...strokeProps} /><path d="m9.9 14.1 1.6 1.6 2.7-2.7" {...strokeProps} /></>;
      case 'license':
        return <><rect x="6" y="5" width="12" height="14" rx="2" {...strokeProps} /><path d="M9 9h6" {...strokeProps} /><path d="M9 12h6" {...strokeProps} /><path d="m10 15 1.4 1.4 2.8-2.8" {...strokeProps} /></>;
      case 'metadata':
        return <><circle cx="8" cy="8" r="2" {...strokeProps} /><circle cx="16" cy="8" r="2" {...strokeProps} /><circle cx="12" cy="16" r="2" {...strokeProps} /><path d="M9.8 9.4 11 14" {...strokeProps} /><path d="M14.2 9.4 13 14" {...strokeProps} /></>;
      case 'monitoring':
        return <><rect x="4.5" y="6" width="15" height="10" rx="2" {...strokeProps} /><path d="M9.5 18.5h5" {...strokeProps} /><path d="m7.3 12 2.3-2.2 1.8 1.8 3.2-3" {...strokeProps} /></>;
      case 'checks':
        return <><circle cx="12" cy="12" r="6.6" {...strokeProps} /><path d="M12 8.4v3.7" {...strokeProps} /><path d="M12 12l2.3 1.6" {...strokeProps} /><path d="m8.1 15.3 1.8 1.8 3.9-3.9" {...strokeProps} /></>;
      case 'alerts':
        return <><path d="M12 4.8 5 17h14L12 4.8Z" {...strokeProps} /><path d="M12 9.2v4" {...strokeProps} /><circle cx="12" cy="15.2" r="0.85" fill="currentColor" stroke="none" /></>;
      case 'audit':
        return <><rect x="6" y="5" width="12" height="14" rx="2" {...strokeProps} /><path d="M9 9h6" {...strokeProps} /><path d="M9 12h4.4" {...strokeProps} /><path d="m13.5 15.2 1.2 1.2 2-2.1" {...strokeProps} /></>;
      case 'api':
        return <><rect x="4.8" y="6" width="14.4" height="12" rx="2" {...strokeProps} /><path d="M9 10.2 7.2 12 9 13.8" {...strokeProps} /><path d="M15 10.2 16.8 12 15 13.8" {...strokeProps} /><path d="M11 15.8h2" {...strokeProps} /></>;
      case 'analytics':
        return <><path d="M5 18h14" {...strokeProps} /><path d="M8 15v-3.5" {...strokeProps} /><path d="M12 15V9" {...strokeProps} /><path d="M16 15v-5.5" {...strokeProps} /></>;
      case 'pricing':
        return <><rect x="5" y="6" width="14" height="12" rx="2" {...strokeProps} /><path d="M8.5 10h7" {...strokeProps} /><path d="M12 10v4.8" {...strokeProps} /><path d="M9.7 14.8h4.6" {...strokeProps} /></>;
      default:
        return <><circle cx="12" cy="12" r="6" {...strokeProps} /><path d="M12 8.8v6.4" {...strokeProps} /><path d="M8.8 12h6.4" {...strokeProps} /></>;
    }
  })();

  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {iconPaths}
    </svg>
  );
};

type SiteBreadcrumb = {
  label: string;
  to?: string;
};

const STATIC_BREADCRUMB_LABELS: Record<string, string> = {
  '/platform': 'Platform',
  '/products': 'Products',
  '/industries': 'Industries',
  '/customers': 'Customers',
  '/learning': 'Learning',
  '/news': 'News',
  '/blog': 'Blog',
  '/support': 'Support',
  '/help': 'Help',
  '/company': 'Company',
  '/demo-request': 'Request Demo',
  '/sales-assistant': 'Sales Assistant',
  '/signup': 'Sign up',
  '/subscribe': 'Subscribe',
  '/plans': 'Plans',
  '/dashboards': 'Dashboards',
  '/liability-monitoring': 'Liability Monitoring',
  '/dod-contracts': 'Government Compliance',
  '/legal': 'Legal',
  '/privacy': 'Privacy Policy',
  '/terms': 'Terms of Service',
  '/contact-us': 'Contact',
  '/trust': 'Trust',
  '/cookie-preferences': 'Cookie Preferences',
  '/data-choices': 'Your Data Choices',
  '/claims-evidence': 'Claims & Evidence',
  '/responsible-disclosure': 'Responsible Disclosure',
  '/dmca': 'DMCA',
  '/accessibility': 'Accessibility',
};

const formatBreadcrumbSegment = (segment: string): string => {
  const acronymMap: Record<string, string> = {
    api: 'API',
    ofac: 'OFAC',
    sdn: 'SDN',
    sec: 'SEC',
    dod: 'DoD',
    doe: 'DOE',
  };

  return segment
    .split('-')
    .filter(Boolean)
    .map((part) => acronymMap[part] ?? (part.charAt(0).toUpperCase() + part.slice(1)))
    .join(' ');
};

const productTileTone = (productSlug: string, highlight: string): string => {
  const key = `${highlight}:${productSlug}`;
  const hash = key.split('').reduce((total, character) => total + character.charCodeAt(0), 0);
  return PRODUCT_TILE_TONES[hash % PRODUCT_TILE_TONES.length];
};

const industryTileTone = (industryLabel: string): string => {
  const hash = industryLabel.split('').reduce((total, character) => total + character.charCodeAt(0), 0);
  return INDUSTRY_TILE_TONES[hash % INDUSTRY_TILE_TONES.length];
};

const INDUSTRY_ICON_KEY_BY_SLUG: Record<string, IndustryIconKey> = {
  automotive: 'automotive',
  communications: 'communications',
  'construction-and-real-estate': 'construction',
  'consumer-goods': 'consumer',
  education: 'education',
  'energy-and-utilities': 'energy',
  'financial-services': 'finance',
  healthcare: 'health',
  'life-sciences': 'science',
  manufacturing: 'manufacturing',
  media: 'media',
  nonprofit: 'nonprofit',
  'professional-services': 'professional',
  'public-sector': 'public',
  retail: 'retail',
  technology: 'technology',
  'travel-transportation-and-hospitality': 'travel',
};

const IndustryIcon: React.FC<{ iconKey: IndustryIconKey; className?: string }> = ({ iconKey, className }) => {
  const strokeProps = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.95,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  const iconPaths = (() => {
    switch (iconKey) {
      case 'automotive':
        return <><rect x="5" y="10" width="14" height="6" rx="2" {...strokeProps} /><circle cx="8" cy="16" r="1.5" {...strokeProps} /><circle cx="16" cy="16" r="1.5" {...strokeProps} /></>;
      case 'communications':
        return <><path d="M12 18v-5" {...strokeProps} /><circle cx="12" cy="9" r="2" {...strokeProps} /><path d="M8.5 6.5a5 5 0 0 1 7 0" {...strokeProps} /><path d="M6 4a8 8 0 0 1 12 0" {...strokeProps} /></>;
      case 'construction':
        return <><rect x="6" y="11" width="5" height="7" rx="1" {...strokeProps} /><rect x="13" y="8" width="5" height="10" rx="1" {...strokeProps} /><path d="M4.5 10.5h15" {...strokeProps} /></>;
      case 'consumer':
        return <><path d="M7 9h10l-1 9H8z" {...strokeProps} /><path d="M9 9a3 3 0 1 1 6 0" {...strokeProps} /></>;
      case 'education':
        return <><path d="M3.5 9 12 5l8.5 4-8.5 4z" {...strokeProps} /><path d="M6 11v3.5c3.2 2.3 6.8 2.3 12 0V11" {...strokeProps} /></>;
      case 'energy':
        return <><path d="M12 4 7 12h4l-1 8 7-10h-4l1-6z" {...strokeProps} /></>;
      case 'finance':
        return <><rect x="5" y="6" width="14" height="12" rx="2" {...strokeProps} /><path d="M8 12h8" {...strokeProps} /><path d="M8 9h5" {...strokeProps} /></>;
      case 'health':
        return <><path d="M12 18s-6-3.6-6-8a3.2 3.2 0 0 1 6-1.5A3.2 3.2 0 0 1 18 10c0 4.4-6 8-6 8z" {...strokeProps} /></>;
      case 'science':
        return <><path d="M9 5h6" {...strokeProps} /><path d="M10 5v4l-4.5 7a2 2 0 0 0 1.8 3h9.4a2 2 0 0 0 1.8-3L14 9V5" {...strokeProps} /><path d="M8.5 14h7" {...strokeProps} /></>;
      case 'manufacturing':
        return <><circle cx="12" cy="12" r="3" {...strokeProps} /><path d="M12 4v2M12 18v2M4 12h2M18 12h2M6.3 6.3l1.4 1.4M16.3 16.3l1.4 1.4M17.7 6.3l-1.4 1.4M7.7 16.3l-1.4 1.4" {...strokeProps} /></>;
      case 'media':
        return <><rect x="4" y="7" width="16" height="10" rx="2" {...strokeProps} /><path d="M10 9.5v5l4-2.5z" {...strokeProps} /></>;
      case 'nonprofit':
        return <><circle cx="12" cy="8.5" r="2.5" {...strokeProps} /><path d="M6.5 18c.9-2.7 2.6-4 5.5-4s4.6 1.3 5.5 4" {...strokeProps} /></>;
      case 'professional':
        return <><rect x="5" y="8" width="14" height="10" rx="2" {...strokeProps} /><path d="M9 8V6h6v2" {...strokeProps} /><path d="M5 12h14" {...strokeProps} /></>;
      case 'public':
        return <><path d="M4 9.5 12 5l8 4.5" {...strokeProps} /><path d="M6 10v7M10 10v7M14 10v7M18 10v7" {...strokeProps} /><path d="M4 17.5h16" {...strokeProps} /></>;
      case 'retail':
        return <><path d="M6.5 9h11l-1 9h-9z" {...strokeProps} /><path d="M9 9V7a3 3 0 0 1 6 0v2" {...strokeProps} /></>;
      case 'technology':
        return <><rect x="7" y="7" width="10" height="10" rx="2" {...strokeProps} /><path d="M10 1v4M14 1v4M10 19v4M14 19v4M1 10h4M1 14h4M19 10h4M19 14h4" {...strokeProps} /></>;
      case 'travel':
        return <><path d="M4 13h16" {...strokeProps} /><path d="M6 13 4 9l2-.5 3 3.5h3L15.5 7l2-.5L20 12" {...strokeProps} /></>;
      default:
        return <circle cx="12" cy="12" r="5" {...strokeProps} />;
    }
  })();

  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {iconPaths}
    </svg>
  );
};

type ProductSlideoutIconKey =
  | 'starter'
  | 'advanced'
  | 'enterprise'
  | 'shield'
  | 'web'
  | 'contract'
  | 'dashboard'
  | 'monitor'
  | 'alert'
  | 'consulting'
  | 'audit'
  | 'report'
  | 'investigation'
  | 'pricing'
  | 'analysis';

type ProductSlideoutVisual = {
  icon: ProductSlideoutIconKey;
  tone: 'blue' | 'teal' | 'indigo' | 'orange' | 'emerald' | 'slate' | 'cyan';
  motif: 'grid' | 'signal' | 'steps' | 'radar' | 'orbit';
};

type ProductVisualTile = {
  icon: ProductSlideoutIconKey;
  label: string;
  emphasize?: boolean;
};

const DEFAULT_PRODUCT_SLIDEOUT_VISUAL: ProductSlideoutVisual = {
  icon: 'dashboard',
  tone: 'blue',
  motif: 'grid',
};

const PRODUCT_SLIDEOUT_VISUAL_BY_ID: Record<string, ProductSlideoutVisual> = {
  'support-basic': { icon: 'starter', tone: 'teal', motif: 'steps' },
  'job-ofac': { icon: 'shield', tone: 'indigo', motif: 'grid' },
  'job-government': { icon: 'contract', tone: 'slate', motif: 'steps' },
  'job-website': { icon: 'web', tone: 'blue', motif: 'signal' },
  'job-cloud': { icon: 'monitor', tone: 'teal', motif: 'signal' },
  'job-organization': { icon: 'contract', tone: 'slate', motif: 'steps' },
  'job-team': { icon: 'consulting', tone: 'emerald', motif: 'steps' },
  'support-advanced': { icon: 'advanced', tone: 'blue', motif: 'signal' },
  'support-enterprise': { icon: 'enterprise', tone: 'indigo', motif: 'radar' },
  'service-ofac-compliance': { icon: 'shield', tone: 'indigo', motif: 'grid' },
  'service-website-compliance': { icon: 'web', tone: 'blue', motif: 'signal' },
  'service-cloud-compliance': { icon: 'monitor', tone: 'teal', motif: 'signal' },
  'service-organization': { icon: 'contract', tone: 'slate', motif: 'steps' },
  'service-dod-contracts-compliance': { icon: 'contract', tone: 'slate', motif: 'steps' },
  'service-education-compliance': { icon: 'audit', tone: 'teal', motif: 'grid' },
  'feature-dashboards': { icon: 'dashboard', tone: 'blue', motif: 'grid' },
  'feature-live-monitoring': { icon: 'monitor', tone: 'teal', motif: 'signal' },
  'feature-real-time-alerts': { icon: 'alert', tone: 'orange', motif: 'radar' },
  'feature-embedded-consultants': { icon: 'consulting', tone: 'emerald', motif: 'steps' },
  'feature-scheduled-audits': { icon: 'audit', tone: 'slate', motif: 'grid' },
  'feature-exec-reporting': { icon: 'report', tone: 'indigo', motif: 'signal' },
  'feature-research-reports': { icon: 'analysis', tone: 'cyan', motif: 'orbit' },
  'feature-usage-pricing': { icon: 'pricing', tone: 'orange', motif: 'steps' },
};

const getProductSlideoutVisual = (itemId: string): ProductSlideoutVisual =>
  PRODUCT_SLIDEOUT_VISUAL_BY_ID[itemId] ?? DEFAULT_PRODUCT_SLIDEOUT_VISUAL;

const inferProductTileIcon = (label: string, fallbackIcon: ProductSlideoutIconKey): ProductSlideoutIconKey => {
  const value = label.toLowerCase();

  if (/pricing|cost|billing|usage|plan/.test(value)) {
    return 'pricing';
  }
  if (/background|investigat|dossier|research|due\s*diligence/.test(value)) {
    return 'analysis';
  }
  if (/alert|incident|notification|watchlist|sanction/.test(value)) {
    return 'alert';
  }
  if (/monitor|monitoring|health|real\s*time|signal/.test(value)) {
    return 'monitor';
  }
  if (/audit|evidence|policy|compliance|control/.test(value)) {
    return 'audit';
  }
  if (/report|executive|kpi|metric|dashboard/.test(value)) {
    return 'report';
  }
  if (/consult|analyst|advis|expert/.test(value)) {
    return 'consulting';
  }
  if (/contract|agreement|federal|dod/.test(value)) {
    return 'contract';
  }
  if (/website|web|page|site/.test(value)) {
    return 'web';
  }
  if (/security|shield|protect/.test(value)) {
    return 'shield';
  }
  return fallbackIcon;
};

const toMenuTileLabel = (label: string): string => {
  const words = label
    .replace(/[()]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

  if (words.length <= 2) {
    return words.join(' ');
  }

  return `${words[0]} ${words[1]}`;
};

const buildProductVisualTiles = (
  item: SlideoutItem | null,
  fallbackIcon: ProductSlideoutIconKey,
  language: WebsiteLanguage,
): ProductVisualTile[] => {
  const defaultLabels = language === 'es'
    ? ['Detectar', 'Evaluar', 'Actuar', 'Auditar']
    : ['Detect', 'Assess', 'Act', 'Audit'];

  const sourceLabels = item?.subItems.map((subItem) => subItem.label).filter(Boolean) || [];
  const padded = [...sourceLabels];
  while (padded.length < 4) {
    padded.push(defaultLabels[padded.length]);
  }

  return padded.slice(0, 4).map((label, index) => ({
    icon: inferProductTileIcon(label, index === 1 ? fallbackIcon : 'monitor'),
    label: toMenuTileLabel(label),
    emphasize: index === 1,
  }));
};

const PRODUCT_MENU_MEME_COPY: Record<string, { en: [string, string]; es: [string, string] }> = {
  'support-basic': { en: ['Need fast setup?', 'Screen keeps teams shipping.'], es: ['Necesitas comenzar rapido?', 'Screen mantiene al equipo avanzando.'] },
  'support-advanced': { en: ['Ops getting noisy?', 'Advanced adds proactive controls.'], es: ['La operacion esta ruidosa?', 'Advanced agrega controles proactivos.'] },
  'support-enterprise': { en: ['High-stakes workflows?', 'CompliComplete locks in governance.'], es: ['Flujos de alto riesgo?', 'CompliComplete asegura gobernanza.'] },
  'service-ofac-compliance': { en: ['Sanctions risk rising?', 'OFAC checks every entity.'], es: ['Sube el riesgo de sanciones?', 'OFAC revisa cada entidad.'] },
  'service-website-compliance': { en: ['Compliance gaps on your site?', 'Privacy, a11y, security, email authentication, and Zero Trust for cloud, containers, and git.'], es: ['Brechas de cumplimiento en tu sitio?', 'Privacidad, a11y, seguridad, autenticación de correo y Zero Trust para nube, contenedores y git.'] },
  'service-dod-contracts-compliance': { en: ['Government vendor lists stacking up?', 'Six pillars take you from SAM to city, county, and state boards.'], es: ['Se acumulan las listas de proveedores?', 'Seis pilares van de SAM a tableros de ciudad, condado y estado.'] },
  'service-education-compliance': { en: ['Campus sanctions and CUI?', 'Four pillars cover scholars, export, HECVAT, and FERPA.'], es: ['Sanciones y CUI en campus?', 'Cuatro pilares cubren académicos, exportación, HECVAT y FERPA.'] },
  'feature-dashboards': { en: ['Need one source of truth?', 'Dashboards align every team.'], es: ['Necesitas una sola verdad?', 'Paneles alinean a todo el equipo.'] },
  'feature-live-monitoring': { en: ['Issues hiding in prod?', 'Live monitoring spots them now.'], es: ['Problemas en produccion?', 'Monitoreo en vivo los detecta ya.'] },
  'feature-real-time-alerts': { en: ['Response too slow?', 'Alerts trigger action instantly.'], es: ['Respuesta lenta?', 'Alertas activan acciones al instante.'] },
  'feature-embedded-consultants': { en: ['Policy gaps unclear?', 'Consultants guide every decision.'], es: ['Brechas de politica?', 'Consultores guian cada decision.'] },
  'feature-scheduled-audits': { en: ['Audit prep painful?', 'Scheduled audits remove surprises.'], es: ['Auditorias dolorosas?', 'Auditorias programadas evitan sorpresas.'] },
  'feature-exec-reporting': { en: ['Need board clarity?', 'Exec reporting makes risk visible.'], es: ['Necesitas claridad ejecutiva?', 'Reportes ejecutivos muestran el riesgo.'] },
  'feature-research-reports': { en: ['Case evidence scattered?', 'Research reports unify investigations and background diligence.'], es: ['Evidencia dispersa?', 'Reportes de investigación unifican investigación y diligencia de antecedentes.'] },
  'feature-usage-pricing': { en: ['Costs unpredictable?', 'Usage pricing tracks real value.'], es: ['Costos impredecibles?', 'Precios por uso reflejan valor real.'] },
};

const PRODUCT_PNG_HERO_BY_SLUG: Record<string, string> = {
  'ofac-compliance': `/images/prism/product-menu-unique/service-ofac-compliance.webp?${PRISM_PRODUCT_MENU_IMAGE_VERSION}`,
  'screening-api-platform': `/images/prism/product-menu-unique/feature-prism.webp?${PRISM_PRODUCT_MENU_IMAGE_VERSION}`,
  'usage-based-pricing-plans': `/images/prism/product-menu-unique/feature-usage-pricing.webp?${PRISM_PRODUCT_MENU_IMAGE_VERSION}`,
};

const PRODUCT_SUBITEM_PNG_HERO_BY_KEY: Record<string, string> = {
  'ofac-compliance/sdn-and-general-license-screening': `/images/prism/product-menu-unique/service-ofac-compliance.webp?${PRISM_PRODUCT_MENU_IMAGE_VERSION}`,
  'ofac-compliance/consolidated-and-capta-list-coverage': `/images/prism/product-menu-unique/service-ofac-compliance.webp?${PRISM_PRODUCT_MENU_IMAGE_VERSION}`,
  'ofac-compliance/continuous-ofac-monitoring': `/images/prism/product-menu-unique/service-ofac-compliance.webp?${PRISM_PRODUCT_MENU_IMAGE_VERSION}`,
  'ofac-compliance/real-time-and-batch-ofac-checks': `/images/prism/product-menu-unique/service-ofac-compliance.webp?${PRISM_PRODUCT_MENU_IMAGE_VERSION}`,
  'screening-api-platform/text-decomposition': `/images/prism/product-menu-unique/feature-prism.webp?${PRISM_PRODUCT_MENU_IMAGE_VERSION}`,
  'screening-api-platform/cinematic-framing': `/images/prism/product-menu-unique/feature-prism.webp?${PRISM_PRODUCT_MENU_IMAGE_VERSION}`,
  'screening-api-platform/generation-output-artifacts': `/images/prism/product-menu-unique/feature-prism.webp?${PRISM_PRODUCT_MENU_IMAGE_VERSION}`,
  'usage-based-pricing-plans/basic-starting-at-99-month': `/images/prism/product-menu-unique/feature-usage-pricing.webp?${PRISM_PRODUCT_MENU_IMAGE_VERSION}`,
  'usage-based-pricing-plans/advanced-starting-at-999-month': `/images/prism/product-menu-unique/feature-usage-pricing.webp?${PRISM_PRODUCT_MENU_IMAGE_VERSION}`,
  'usage-based-pricing-plans/enterprise-contact-for-estimate': `/images/prism/product-menu-unique/feature-usage-pricing.webp?${PRISM_PRODUCT_MENU_IMAGE_VERSION}`,
};

type SemanticGraphicPalette = {
  shellFrom: string;
  shellTo: string;
  orbFrom: string;
  orbTo: string;
  accent: string;
  accentSoft: string;
  ink: string;
};

type SemanticPageGraphicInput = {
  title: string;
  summary: string;
  bullets: string[];
  contextLabel?: string;
  pageKey?: string;
  forcedMotif?: SemanticGraphicMotif;
};

const SEMANTIC_GRAPHIC_PALETTES: SemanticGraphicPalette[] = [
  {
    shellFrom: '#f5f9ff',
    shellTo: '#e6f0ff',
    orbFrom: '#8de6d2',
    orbTo: '#1aa3a0',
    accent: '#2f6fde',
    accentSoft: '#9bc6ff',
    ink: '#0f2e57',
  },
  {
    shellFrom: '#f4fbff',
    shellTo: '#e3f6ff',
    orbFrom: '#8dd8ff',
    orbTo: '#3a96d9',
    accent: '#0f7bba',
    accentSoft: '#8fd3ff',
    ink: '#103860',
  },
  {
    shellFrom: '#f6fbf7',
    shellTo: '#e8f5ef',
    orbFrom: '#9ce9bd',
    orbTo: '#28a36d',
    accent: '#1b8f67',
    accentSoft: '#9ae4c4',
    ink: '#124334',
  },
  {
    shellFrom: '#f7f8ff',
    shellTo: '#ecebff',
    orbFrom: '#b0bcff',
    orbTo: '#6c79de',
    accent: '#4f5dd1',
    accentSoft: '#b8bfff',
    ink: '#212f6f',
  },
];

const hashString = (value: string): number => {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash >>> 0);
};

const clampValue = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const pickPalette = (seed: number): SemanticGraphicPalette => SEMANTIC_GRAPHIC_PALETTES[seed % SEMANTIC_GRAPHIC_PALETTES.length];

const escapeSvgText = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

const trimSvgLabel = (value: string, maxLength: number): string => {
  const normalized = value.trim();
  if (normalized.length <= maxLength) {
    return normalized;
  }
  return `${normalized.slice(0, maxLength - 3).trim()}...`;
};

type SemanticGraphicMotif = 'shield' | 'monitor' | 'report' | 'pricing' | 'alerts' | 'network' | 'search' | 'workflow';

type LockedSemanticTemplate =
  | 'ofac-screening'
  | 'dod-governance'
  | 'executive-board'
  | 'dashboards-ops'
  | 'investigative-dossier'
  | 'pricing-plans';

const detectForcedMotifByPageKey = (pageKey?: string): SemanticGraphicMotif | undefined => {
  if (!pageKey) {
    return undefined;
  }

  if (/sdn-and-general-license-screening|ofac-sdn-general-licenses/.test(pageKey)) {
    return 'shield';
  }
  if (/real-time-and-batch-ofac-checks|real-time-batch-screening/.test(pageKey)) {
    return 'workflow';
  }
  if (/continuous-monitoring/.test(pageKey)) {
    return 'monitor';
  }
  if (/capta-and-consolidated-lists|capta-consolidated-lists/.test(pageKey)) {
    return 'search';
  }
  if (/sec-filings-and-edgar-search|sec-filings-edgar-search/.test(pageKey)) {
    return 'report';
  }
  if (/doe-and-state-sanctions-context|doe-state-sanctions-context/.test(pageKey)) {
    return 'network';
  }

  if (/real-time-alerts|priority-alert-pipelines|incident-response-actions|alerts-incident-workflow/.test(pageKey)) {
    return 'alerts';
  }
  if (/executive-compliance-reporting|leadership-dashboard-snapshots|board-ready-compliance-reporting|trend-and-kpi-rollups/.test(pageKey)) {
    return 'report';
  }
  if (/usage-based-pricing-plans|basic-starting-at-99-month|advanced-starting-at-999-month|enterprise-contact-for-estimate/.test(pageKey)) {
    return 'pricing';
  }
  if (/embedded-consultants|program-design-guidance|execution-support-with-your-teams|governance-operating-model/.test(pageKey)) {
    return 'network';
  }
  if (/investigative-reporting|submit-people-and-entities|background-dossier|evidence-linked-investigation-package|case-audit-workspace/.test(pageKey)) {
    return 'search';
  }
  if (/ofac|sanction|capta|dod-contracts-compliance|education-compliance|ai-compliance|hecvat|ferpa|restricted-party|cybersecurity|contract-compliance|supply-chain-assurance|financial-and-ethics-controls|entity-eligibility|pursuit-readiness/.test(pageKey)) {
    return 'shield';
  }

  return undefined;
};

const detectLockedSemanticTemplate = (pageKey?: string): LockedSemanticTemplate | undefined => {
  if (!pageKey) {
    return undefined;
  }

  const isSubItemPage = /^(product-sub:|industry-sub:)/.test(pageKey);
  if (isSubItemPage) {
    return undefined;
  }

  if (/ofac-compliance|ofac-sdn-general-licenses|capta-consolidated-lists|sec-filings-edgar-search|doe-state-sanctions-context/.test(pageKey)) {
    return 'ofac-screening';
  }
  if (/dod-contracts-compliance|education-compliance|ai-compliance|hecvat|ferpa-and-glba|restricted-party|cybersecurity|contract-compliance|supply-chain-assurance|financial-and-ethics-controls|entity-eligibility|pursuit-readiness/.test(pageKey)) {
    return 'dod-governance';
  }
  if (/executive-compliance-reporting|leadership-dashboard-snapshots|board-ready-compliance-reporting|trend-and-kpi-rollups/.test(pageKey)) {
    return 'executive-board';
  }
  if (/dashboards|risk-scoring|data-source-health-monitoring|compliance-posture-overview/.test(pageKey)) {
    return 'dashboards-ops';
  }
  if (/investigative-reporting|submit-people-and-entities|evidence-linked-investigation-package|exhaustive-background-reports/.test(pageKey)) {
    return 'investigative-dossier';
  }
  if (/usage-based-pricing-plans|basic-starting-at-99-month|advanced-starting-at-999-month|enterprise-contact-for-estimate/.test(pageKey)) {
    return 'pricing-plans';
  }

  return undefined;
};

const detectSemanticMotif = (title: string, summary: string, bullets: string[]): SemanticGraphicMotif => {
  const text = `${title} ${summary} ${bullets.join(' ')}`.toLowerCase();

  if (/price|cost|plan|usage|budget|billing|estimate/.test(text)) {
    return 'pricing';
  }
  if (/alert|incident|urgent|priority|escalation|respond/.test(text)) {
    return 'alerts';
  }
  if (/report|board|executive|kpi|trend|insight|dashboard/.test(text)) {
    return 'report';
  }
  if (/investig|research|case|dossier|evidence|background/.test(text)) {
    return 'search';
  }
  if (/consult|advisor|team|collaboration|partner|service/.test(text)) {
    return 'network';
  }
  if (/contract|privacy|security|risk|audit|compliance|sanction|ofac|dod/.test(text)) {
    return 'shield';
  }
  if (/workflow|operate|process|automation|screening|monitor/.test(text)) {
    return 'workflow';
  }
  return 'monitor';
};

const motifGlyphMarkup = (motif: SemanticGraphicMotif, accent: string, accentSoft: string, ink: string): string => {
  switch (motif) {
    case 'shield':
      return `<path d="M58 18l34 12v27c0 22-14 35-34 45C38 92 24 79 24 57V30l34-12z" fill="${accent}"/><path d="M58 32l16 6v17c0 12-7 19-16 25-9-6-16-13-16-25V38l16-6z" fill="#ffffff" opacity="0.9"/>`;
    case 'alerts':
      return `<path d="M58 23c-14 0-24 10-24 23v13l-7 11h62l-7-11V46c0-13-10-23-24-23z" fill="${accent}"/><rect x="49" y="73" width="18" height="8" rx="4" fill="${ink}"/>`;
    case 'report':
      return `<rect x="23" y="20" width="70" height="64" rx="12" fill="#ffffff"/><rect x="30" y="28" width="42" height="6" rx="3" fill="${accentSoft}"/><rect x="30" y="42" width="56" height="6" rx="3" fill="${accentSoft}"/><rect x="30" y="56" width="26" height="18" rx="4" fill="${accent}"/><rect x="61" y="51" width="8" height="23" rx="3" fill="${ink}"/><rect x="73" y="46" width="8" height="28" rx="3" fill="${accent}"/>`;
    case 'pricing':
      return `<circle cx="44" cy="52" r="22" fill="#ffffff"/><path d="M44 37v30M34 43c2-4 6-6 10-6 6 0 10 3 10 8s-3 7-9 8-9 3-9 8 4 8 10 8c4 0 8-2 10-6" stroke="${accent}" stroke-width="4" stroke-linecap="round" fill="none"/><rect x="66" y="32" width="22" height="14" rx="5" fill="${accent}"/><rect x="66" y="52" width="26" height="14" rx="5" fill="${ink}"/>`;
    case 'network':
      return `<circle cx="32" cy="36" r="10" fill="${accent}"/><circle cx="84" cy="36" r="10" fill="${ink}"/><circle cx="58" cy="76" r="11" fill="${accentSoft}"/><path d="M38 42l14 22M78 42l-14 22M42 36h32" stroke="#ffffff" stroke-width="4" stroke-linecap="round"/>`;
    case 'search':
      return `<circle cx="48" cy="46" r="22" fill="#ffffff"/><circle cx="48" cy="46" r="14" fill="none" stroke="${accent}" stroke-width="4"/><path d="M63 61l16 16" stroke="${ink}" stroke-width="6" stroke-linecap="round"/><rect x="33" y="42" width="16" height="4" rx="2" fill="${accentSoft}"/><rect x="33" y="50" width="22" height="4" rx="2" fill="${accentSoft}"/>`;
    case 'workflow':
      return `<rect x="20" y="25" width="30" height="18" rx="6" fill="${accent}"/><rect x="66" y="25" width="30" height="18" rx="6" fill="#ffffff"/><rect x="20" y="61" width="30" height="18" rx="6" fill="#ffffff"/><rect x="66" y="61" width="30" height="18" rx="6" fill="${ink}"/><path d="M50 34h16M34 43v18M50 70h16" stroke="${accentSoft}" stroke-width="4" stroke-linecap="round"/>`;
    default:
      return `<rect x="20" y="25" width="76" height="56" rx="11" fill="#ffffff"/><path d="M26 36h64M36 44v28" stroke="${accentSoft}" stroke-width="3"/><path d="M42 66l12-11 10 7 16-15" stroke="${accent}" stroke-width="4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`;
  }
};

const buildLockedSemanticGraphicDataUri = (
  template: LockedSemanticTemplate,
  input: SemanticPageGraphicInput,
  palette: SemanticGraphicPalette,
): string => {
  const titleLabel = escapeSvgText(trimSvgLabel(input.title, 40));
  const subtitleLabel = escapeSvgText(trimSvgLabel(input.summary, 58));
  const bulletRows = (input.bullets.length > 0 ? input.bullets : ['Operational control', 'Decision quality', 'Audit traceability'])
    .slice(0, 3)
    .map((entry, index) => {
      const y = 306 + (index * 26);
      return `<text x="44" y="${y}" font-family="system-ui,-apple-system,sans-serif" font-size="13" font-weight="600" fill="#365f88">- ${escapeSvgText(trimSvgLabel(entry, 44))}</text>`;
    })
    .join('');

  if (template === 'ofac-screening') {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="760" height="500" viewBox="0 0 760 500" role="img" aria-label="${titleLabel} OFAC screening graphic">
      <defs>
        <linearGradient id="storyBg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#edf4ff"/>
          <stop offset="100%" stop-color="#dce9fc"/>
        </linearGradient>
      </defs>
      <rect width="760" height="500" rx="24" fill="url(#storyBg)"/>

      <rect x="34" y="34" width="692" height="432" rx="20" fill="#ffffff" stroke="#c9ddf7"/>
      <rect x="34" y="34" width="692" height="56" rx="20" fill="#0f3f90"/>
      <text x="56" y="68" font-family="system-ui,-apple-system,sans-serif" font-size="18" font-weight="800" fill="#ffffff">OFAC element story flow</text>
      <rect x="612" y="48" width="96" height="28" rx="14" fill="#0abf63"/>
      <text x="660" y="66" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#ffffff">ALWAYS ON</text>

      <rect x="56" y="108" width="310" height="124" rx="14" fill="#f7fbff" stroke="#d5e6fb"/>
      <rect x="392" y="108" width="310" height="124" rx="14" fill="#f7fbff" stroke="#d5e6fb"/>
      <rect x="56" y="254" width="310" height="124" rx="14" fill="#f7fbff" stroke="#d5e6fb"/>
      <rect x="392" y="254" width="310" height="124" rx="14" fill="#f7fbff" stroke="#d5e6fb"/>

      <circle cx="79" cy="132" r="13" fill="#2f6fde"/>
      <text x="79" y="137" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#ffffff">1</text>
      <text x="100" y="134" font-family="system-ui,-apple-system,sans-serif" font-size="16" font-weight="800" fill="#143e6a">OFAC SDN &amp; General Licenses</text>
      <text x="76" y="162" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#3f6794">Find blocked-party hits and evaluate</text>
      <text x="76" y="180" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#3f6794">license context before escalation.</text>

      <circle cx="415" cy="132" r="13" fill="#2f6fde"/>
      <text x="415" y="137" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#ffffff">2</text>
      <text x="436" y="134" font-family="system-ui,-apple-system,sans-serif" font-size="16" font-weight="800" fill="#143e6a">CAPTA + Consolidated Lists</text>
      <text x="412" y="162" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#3f6794">Add non-SDN and foreign-bank</text>
      <text x="412" y="180" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#3f6794">restriction coverage to close gaps.</text>

      <circle cx="79" cy="278" r="13" fill="#2f6fde"/>
      <text x="79" y="283" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#ffffff">3</text>
      <text x="100" y="280" font-family="system-ui,-apple-system,sans-serif" font-size="16" font-weight="800" fill="#143e6a">Continuous Monitoring</text>
      <text x="76" y="308" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#3f6794">Watch list and policy changes, detect</text>
      <text x="76" y="326" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#3f6794">deltas, and trigger rescreen actions.</text>

      <circle cx="415" cy="278" r="13" fill="#2f6fde"/>
      <text x="415" y="283" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#ffffff">4</text>
      <text x="436" y="280" font-family="system-ui,-apple-system,sans-serif" font-size="16" font-weight="800" fill="#143e6a">Real-time + Batch Screening</text>
      <text x="412" y="308" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#3f6794">Use one scoring model for immediate</text>
      <text x="412" y="326" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#3f6794">checks and high-volume portfolio jobs.</text>

      <path d="M366 170h24M379 164l11 6-11 6" fill="none" stroke="#2f6fde" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M548 232v18M542 237l6-6 6 6" fill="none" stroke="#2f6fde" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M392 316h-24M379 322l-11-6 11-6" fill="none" stroke="#2f6fde" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>

      <rect x="56" y="392" width="646" height="56" rx="12" fill="#f3f9ff" stroke="#d5e6fb"/>
      <text x="76" y="415" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#3e6793">Outcome story</text>
      <text x="76" y="434" font-family="system-ui,-apple-system,sans-serif" font-size="13" font-weight="700" fill="#1f4d81">Fewer false escalations, stronger evidence trails, and faster policy-aligned decisions across operations, legal, and risk teams.</text>
    </svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  if (template === 'dod-governance') {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="760" height="500" viewBox="0 0 760 500" role="img" aria-label="${titleLabel} DoD governance graphic">
      <rect width="760" height="500" rx="24" fill="#eef4ff"/>
      <circle cx="584" cy="182" r="170" fill="${palette.orbTo}" opacity="0.9"/>
      <rect x="74" y="74" width="612" height="348" rx="18" fill="#ffffff" stroke="#d0e0f5"/>
      <text x="106" y="118" font-family="system-ui,-apple-system,sans-serif" font-size="22" font-weight="800" fill="#13355d">${titleLabel}</text>
      <text x="106" y="146" font-family="system-ui,-apple-system,sans-serif" font-size="13" font-weight="600" fill="#547193">${subtitleLabel}</text>

      <rect x="106" y="176" width="128" height="102" rx="14" fill="#f5f9ff" stroke="#d4e4f8"/>
      <rect x="250" y="176" width="128" height="102" rx="14" fill="#f5f9ff" stroke="#d4e4f8"/>
      <rect x="394" y="176" width="128" height="102" rx="14" fill="#f5f9ff" stroke="#d4e4f8"/>
      <rect x="538" y="176" width="128" height="102" rx="14" fill="#f5f9ff" stroke="#d4e4f8"/>
      <text x="170" y="232" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#244d7a">Cyber</text>
      <text x="314" y="232" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#244d7a">Contracts</text>
      <text x="458" y="232" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#244d7a">Supply Chain</text>
      <text x="602" y="232" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#244d7a">Ethics</text>

      <path d="M170 278v44M314 278v44M458 278v44M602 278v44" stroke="#8fb3df" stroke-width="3" stroke-linecap="round"/>
      <rect x="144" y="324" width="484" height="68" rx="12" fill="#f8fbff" stroke="#d8e8fb"/>
      <text x="164" y="350" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#2d527d">Governance control loop</text>
      <text x="164" y="372" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="600" fill="#5b7a9f">Policy mapping -> readiness checks -> contract evidence -> board review</text>
      ${bulletRows.replace('x="44"', 'x="164"').replace('x="44"', 'x="164"').replace('x="44"', 'x="164"')}
    </svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  if (template === 'dashboards-ops') {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="760" height="500" viewBox="0 0 760 500" role="img" aria-label="${titleLabel} dashboards operations graphic">
      <rect width="760" height="500" rx="24" fill="#edf6ff"/>
      <circle cx="610" cy="170" r="165" fill="${palette.orbTo}" opacity="0.88"/>
      <rect x="70" y="70" width="620" height="360" rx="18" fill="#ffffff" stroke="#d2e3f7"/>
      <rect x="70" y="70" width="620" height="44" rx="18" fill="#f2f8ff"/>
      <text x="98" y="98" font-family="system-ui,-apple-system,sans-serif" font-size="13" font-weight="800" fill="#254b77">Dashboards Operations</text>
      <rect x="96" y="132" width="188" height="126" rx="12" fill="#f9fcff" stroke="#d8e8fb"/>
      <rect x="300" y="132" width="188" height="126" rx="12" fill="#f9fcff" stroke="#d8e8fb"/>
      <rect x="504" y="132" width="160" height="126" rx="12" fill="#f9fcff" stroke="#d8e8fb"/>
      <text x="190" y="158" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#4c6b8f">Risk Score</text>
      <text x="394" y="158" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#4c6b8f">Source Health</text>
      <text x="584" y="158" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#4c6b8f">Alerts</text>
      <circle cx="190" cy="203" r="32" fill="#ffffff" stroke="${palette.accentSoft}" stroke-width="10"/>
      <path d="M190 171 A32 32 0 1 1 166 228" stroke="${palette.accent}" stroke-width="10" fill="none" stroke-linecap="round"/>
      <text x="190" y="209" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="16" font-weight="800" fill="#1c4f83">81</text>
      <circle cx="394" cy="203" r="32" fill="#ffffff" stroke="${palette.accentSoft}" stroke-width="10"/>
      <path d="M394 171 A32 32 0 1 1 374 231" stroke="${palette.accent}" stroke-width="10" fill="none" stroke-linecap="round"/>
      <text x="394" y="209" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="16" font-weight="800" fill="#1c4f83">76</text>
      <rect x="534" y="184" width="102" height="14" rx="7" fill="#deecff"/>
      <rect x="534" y="206" width="76" height="14" rx="7" fill="#deecff"/>
      <rect x="534" y="228" width="56" height="14" rx="7" fill="#deecff"/>
      <rect x="96" y="276" width="568" height="126" rx="12" fill="#f8fbff" stroke="#d8e8fb"/>
      <text x="118" y="304" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#32557e">${titleLabel}</text>
      <text x="118" y="324" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="600" fill="#5c7b9f">${subtitleLabel}</text>
      ${bulletRows.replace('x="44"', 'x="118"').replace('x="44"', 'x="118"').replace('x="44"', 'x="118"')}
    </svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  if (template === 'investigative-dossier') {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="760" height="500" viewBox="0 0 760 500" role="img" aria-label="${titleLabel} investigative dossier graphic">
      <rect width="760" height="500" rx="24" fill="#f0f7ff"/>
      <circle cx="602" cy="162" r="160" fill="${palette.orbTo}" opacity="0.86"/>
      <rect x="86" y="66" width="590" height="366" rx="18" fill="#ffffff" stroke="#d4e4f7"/>
      <rect x="112" y="108" width="244" height="292" rx="14" fill="#f9fcff" stroke="#dbe9fb"/>
      <rect x="380" y="108" width="270" height="152" rx="14" fill="#f9fcff" stroke="#dbe9fb"/>
      <rect x="380" y="274" width="270" height="126" rx="14" fill="#f9fcff" stroke="#dbe9fb"/>
      <text x="130" y="138" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#4a6c91">Subject dossier</text>
      <text x="400" y="138" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#4a6c91">Evidence index</text>
      <text x="400" y="304" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#4a6c91">Investigation package</text>
      <circle cx="232" cy="190" r="42" fill="#ffffff" stroke="${palette.accentSoft}" stroke-width="10"/>
      <path d="M252 210l20 20" stroke="${palette.ink}" stroke-width="7" stroke-linecap="round"/>
      <circle cx="232" cy="190" r="20" fill="none" stroke="${palette.accent}" stroke-width="5"/>
      <rect x="130" y="254" width="200" height="12" rx="6" fill="#deecff"/>
      <rect x="130" y="276" width="170" height="12" rx="6" fill="#deecff"/>
      <rect x="130" y="298" width="146" height="12" rx="6" fill="#deecff"/>
      <rect x="402" y="160" width="226" height="16" rx="8" fill="#e2efff"/>
      <rect x="402" y="186" width="206" height="16" rx="8" fill="#e2efff"/>
      <rect x="402" y="212" width="166" height="16" rx="8" fill="#e2efff"/>
      <text x="130" y="354" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#31567f">${titleLabel}</text>
      <text x="130" y="374" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="600" fill="#5d7da0">${subtitleLabel}</text>
      ${bulletRows.replace('x="44"', 'x="402"').replace('x="44"', 'x="402"').replace('x="44"', 'x="402"')}
    </svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  if (template === 'pricing-plans') {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="760" height="500" viewBox="0 0 760 500" role="img" aria-label="${titleLabel} usage pricing plans graphic">
      <rect width="760" height="500" rx="24" fill="#eef7ff"/>
      <circle cx="602" cy="170" r="165" fill="${palette.orbTo}" opacity="0.84"/>
      <rect x="70" y="70" width="620" height="360" rx="18" fill="#ffffff" stroke="#d3e3f7"/>
      <text x="98" y="108" font-family="system-ui,-apple-system,sans-serif" font-size="21" font-weight="800" fill="#173e68">${titleLabel}</text>
      <text x="98" y="132" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="600" fill="#5b7b9d">${subtitleLabel}</text>
      <rect x="98" y="160" width="176" height="208" rx="14" fill="#f8fbff" stroke="#dae8fb"/>
      <rect x="292" y="148" width="186" height="232" rx="14" fill="#f4f9ff" stroke="#cfdff5"/>
      <rect x="496" y="138" width="176" height="242" rx="14" fill="#f0f7ff" stroke="#c7daf4"/>
      <text x="186" y="190" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#4c6e92">Basic</text>
      <text x="385" y="182" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#3f648d">Advanced</text>
      <text x="584" y="172" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#355c88">CompliComplete</text>
      <text x="186" y="224" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="23" font-weight="800" fill="#1f5fa7">$99</text>
      <text x="385" y="220" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="23" font-weight="800" fill="#1f5fa7">$999</text>
      <text x="584" y="216" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="20" font-weight="800" fill="#1f5fa7">Custom</text>
      <rect x="126" y="246" width="120" height="10" rx="5" fill="#deecff"/>
      <rect x="126" y="266" width="104" height="10" rx="5" fill="#deecff"/>
      <rect x="126" y="286" width="92" height="10" rx="5" fill="#deecff"/>
      <rect x="322" y="248" width="128" height="10" rx="5" fill="#deecff"/>
      <rect x="322" y="268" width="116" height="10" rx="5" fill="#deecff"/>
      <rect x="322" y="288" width="108" height="10" rx="5" fill="#deecff"/>
      <rect x="526" y="246" width="118" height="10" rx="5" fill="#deecff"/>
      <rect x="526" y="266" width="106" height="10" rx="5" fill="#deecff"/>
      <rect x="526" y="286" width="98" height="10" rx="5" fill="#deecff"/>
      <rect x="98" y="388" width="574" height="28" rx="10" fill="#f7fbff" stroke="#d8e8fb"/>
      <text x="118" y="406" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#3b5f87">Usage scales with value delivery across all plans</text>
    </svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="760" height="500" viewBox="0 0 760 500" role="img" aria-label="${titleLabel} executive reporting graphic">
    <rect width="760" height="500" rx="24" fill="#edf5ff"/>
    <rect x="60" y="72" width="640" height="352" rx="18" fill="#ffffff" stroke="#d2e2f7"/>
    <text x="92" y="112" font-family="system-ui,-apple-system,sans-serif" font-size="21" font-weight="800" fill="#123a65">${titleLabel}</text>
    <text x="92" y="138" font-family="system-ui,-apple-system,sans-serif" font-size="13" font-weight="600" fill="#557598">${subtitleLabel}</text>

    <rect x="92" y="164" width="182" height="110" rx="12" fill="#f8fbff" stroke="#d9e8fb"/>
    <rect x="292" y="164" width="182" height="110" rx="12" fill="#f8fbff" stroke="#d9e8fb"/>
    <rect x="492" y="164" width="182" height="110" rx="12" fill="#f8fbff" stroke="#d9e8fb"/>
    <text x="182" y="224" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="26" font-weight="800" fill="#1f5ea8">84</text>
    <text x="182" y="244" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="700" fill="#5a7698">Risk Score</text>
    <text x="382" y="224" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="26" font-weight="800" fill="#1f5ea8">31</text>
    <text x="382" y="244" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="700" fill="#5a7698">Open Actions</text>
    <text x="582" y="224" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="26" font-weight="800" fill="#1f5ea8">97%</text>
    <text x="582" y="244" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="700" fill="#5a7698">Policy Coverage</text>

    <rect x="92" y="290" width="582" height="108" rx="12" fill="#f7fbff" stroke="#d7e7fb"/>
    <text x="114" y="318" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#31557f">Board summary highlights</text>
    ${bulletRows.replace('x="44"', 'x="114"').replace('x="44"', 'x="114"').replace('x="44"', 'x="114"')}
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const buildSemanticPageGraphicDataUri = ({
  title,
  summary,
  bullets,
  contextLabel,
  pageKey,
  forcedMotif,
}: SemanticPageGraphicInput): string => {
  const safeBullets = bullets.length > 0 ? bullets : ['Operational visibility', 'Decision workflow', 'Evidence tracking'];
  const seedText = `${title}|${summary}|${safeBullets.join('|')}|${contextLabel ?? ''}|${pageKey ?? ''}`;
  const seed = hashString(seedText);
  const palette = pickPalette(seed);
  const lockedTemplate = detectLockedSemanticTemplate(pageKey);
  if (lockedTemplate) {
    return buildLockedSemanticGraphicDataUri(lockedTemplate, {
      title,
      summary,
      bullets: safeBullets,
      contextLabel,
      pageKey,
      forcedMotif,
    }, palette);
  }

  const motif = forcedMotif ?? detectForcedMotifByPageKey(pageKey) ?? detectSemanticMotif(title, summary, safeBullets);
  const ringA = clampValue(40 + (seed % 42), 38, 81);
  const ringB = clampValue(34 + ((seed >> 4) % 45), 30, 82);
  const ringC = clampValue(28 + ((seed >> 8) % 49), 24, 84);
  const titleLabel = trimSvgLabel(title, 46);
  const subtitleLabel = trimSvgLabel(summary, 68);
  const rows = safeBullets.slice(0, 3).map((entry, index) => {
    const y = 346 + (index * 30);
    return `<text x="52" y="${y}" font-family="system-ui,-apple-system,sans-serif" font-size="14" font-weight="600" fill="#24507a">- ${escapeSvgText(trimSvgLabel(entry, 48))}</text>`;
  }).join('');

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="760" height="500" viewBox="0 0 760 500" role="img" aria-label="${escapeSvgText(title)} semantic overview graphic">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="${palette.shellFrom}"/>
      <stop offset="100%" stop-color="${palette.shellTo}"/>
    </linearGradient>
    <radialGradient id="orb" cx="33%" cy="28%" r="72%">
      <stop offset="0%" stop-color="${palette.orbFrom}" stop-opacity="0.88"/>
      <stop offset="100%" stop-color="${palette.orbTo}" stop-opacity="0.96"/>
    </radialGradient>
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="10" stdDeviation="11" flood-color="#0e2b4a" flood-opacity="0.18"/>
    </filter>
  </defs>
  <rect width="760" height="500" rx="26" fill="url(#bg)"/>
  <circle cx="594" cy="190" r="166" fill="url(#orb)"/>
  <rect x="236" y="86" width="470" height="316" rx="20" fill="#ffffff" filter="url(#shadow)"/>
  <rect x="236" y="86" width="470" height="42" rx="20" fill="#f2f7ff"/>
  <circle cx="262" cy="108" r="6" fill="#d7e5f9"/>
  <circle cx="280" cy="108" r="6" fill="#d7e5f9"/>
  <circle cx="298" cy="108" r="6" fill="#d7e5f9"/>
  <text x="324" y="113" font-family="system-ui,-apple-system,sans-serif" font-size="13" font-weight="700" fill="${palette.ink}">${escapeSvgText(trimSvgLabel(contextLabel ?? 'NextCompli', 32))}</text>

  <g transform="translate(266 144)">
    <circle cx="0" cy="0" r="0" fill="none"/>
    <circle cx="62" cy="58" r="48" fill="#ffffff" stroke="${palette.accentSoft}" stroke-width="14"/>
    <path d="M62 10 A48 48 0 ${ringA > 50 ? 1 : 0} 1 ${62 + Math.cos((Math.PI * 2 * ringA) / 100) * 48} ${58 + Math.sin((Math.PI * 2 * ringA) / 100) * 48}" stroke="${palette.accent}" stroke-width="14" fill="none" stroke-linecap="round"/>
    <text x="62" y="55" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="20" font-weight="800" fill="${palette.ink}">${ringA}%</text>
    <text x="62" y="76" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="700" fill="#4f6f92">Coverage</text>

    <circle cx="198" cy="58" r="48" fill="#ffffff" stroke="${palette.accentSoft}" stroke-width="14"/>
    <path d="M198 10 A48 48 0 ${ringB > 50 ? 1 : 0} 1 ${198 + Math.cos((Math.PI * 2 * ringB) / 100) * 48} ${58 + Math.sin((Math.PI * 2 * ringB) / 100) * 48}" stroke="${palette.accent}" stroke-width="14" fill="none" stroke-linecap="round"/>
    <text x="198" y="55" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="20" font-weight="800" fill="${palette.ink}">${ringB}%</text>
    <text x="198" y="76" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="700" fill="#4f6f92">Signal</text>

    <circle cx="334" cy="58" r="48" fill="#ffffff" stroke="${palette.accentSoft}" stroke-width="14"/>
    <path d="M334 10 A48 48 0 ${ringC > 50 ? 1 : 0} 1 ${334 + Math.cos((Math.PI * 2 * ringC) / 100) * 48} ${58 + Math.sin((Math.PI * 2 * ringC) / 100) * 48}" stroke="${palette.accent}" stroke-width="14" fill="none" stroke-linecap="round"/>
    <text x="334" y="55" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="20" font-weight="800" fill="${palette.ink}">${ringC}%</text>
    <text x="334" y="76" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="700" fill="#4f6f92">Readiness</text>
  </g>

  <rect x="272" y="250" width="398" height="128" rx="14" fill="#f8fbff" stroke="#d8e6f8"/>
  <text x="292" y="278" font-family="system-ui,-apple-system,sans-serif" font-size="15" font-weight="800" fill="${palette.ink}">${escapeSvgText(titleLabel)}</text>
  <text x="292" y="300" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="600" fill="#5e7da1">${escapeSvgText(subtitleLabel)}</text>
  ${rows}

  <g transform="translate(80 150)">
    <rect x="0" y="0" width="118" height="118" rx="24" fill="#ffffff" stroke="${palette.accentSoft}" stroke-width="3"/>
    ${motifGlyphMarkup(motif, palette.accent, palette.accentSoft, palette.ink)}
  </g>
</svg>`;

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

type SubitemExplainerInput = {
  pageKey: string;
  title: string;
  summary: string;
  parentLabel: string;
  workflow: string[];
  capabilities: string[];
  outcomes: string[];
};

type SubitemDiagramMode = 'linear' | 'dual-lane' | 'cycle' | 'hub';

const normalizeDiagramLabel = (value: string, maxLength = 28): string => {
  const cleaned = value
    .replace(/^\s*\d+[.)-]\s*/, '')
    .replace(/\s*\([^)]*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return trimSvgLabel(cleaned || value, maxLength);
};

const determineSubitemDiagramMode = (pageKey: string): SubitemDiagramMode => {
  if (/real-time-and-batch-ofac-checks|real-time-batch-screening/.test(pageKey)) {
    return 'dual-lane';
  }
  if (/continuous-monitoring/.test(pageKey)) {
    return 'cycle';
  }
  if (/capta-and-consolidated-lists|capta-consolidated-lists/.test(pageKey)) {
    return 'hub';
  }
  return 'linear';
};

const deriveSubitemDiagramStages = (input: SubitemExplainerInput): string[] => {
  const { pageKey } = input;
  if (/sdn-and-general-license-screening|ofac-sdn-general-licenses/.test(pageKey)) {
    return ['Screen SDN lists', 'Assess general licenses', 'Analyst adjudication', 'Publish disposition'];
  }
  if (/real-time-and-batch-ofac-checks|real-time-batch-screening/.test(pageKey)) {
    return ['Real-time stream', 'Batch queue', 'Unified match engine', 'Decision and audit trail'];
  }
  if (/continuous-monitoring/.test(pageKey)) {
    return ['Watch source updates', 'Detect delta events', 'Investigate risk changes', 'Rescreen and close'];
  }
  if (/capta-and-consolidated-lists|capta-consolidated-lists/.test(pageKey)) {
    return ['CAPTA datasets', 'Consolidated lists', 'Program tag context', 'Entity match engine'];
  }
  if (/sec-filings-and-edgar-search|sec-filings-edgar-search/.test(pageKey)) {
    return ['Fetch filings', 'Extract entities', 'Link risk context', 'Create evidence dossier'];
  }
  if (/doe-and-state-sanctions-context|doe-state-sanctions-context/.test(pageKey)) {
    return ['Collect policy updates', 'Map country context', 'Apply screening rules', 'Issue guidance'];
  }

  const merged = [...input.workflow, ...input.capabilities, ...input.outcomes]
    .map((item) => normalizeDiagramLabel(item, 30))
    .filter(Boolean);

  const unique: string[] = [];
  for (const item of merged) {
    if (!unique.includes(item)) {
      unique.push(item);
    }
  }

  const defaults = ['Collect inputs', 'Evaluate controls', 'Review outcomes', 'Publish evidence'];
  while (unique.length < 4) {
    unique.push(defaults[unique.length]);
  }

  return unique.slice(0, 4);
};

const buildSubitemExplainerGraphicDataUri = (input: SubitemExplainerInput): string => {
  const seed = hashString(`${input.pageKey}|${input.title}|${input.summary}`);
  const palette = pickPalette(seed);
  const motif = detectForcedMotifByPageKey(input.pageKey) ?? detectSemanticMotif(input.title, input.summary, input.workflow);
  const mode = determineSubitemDiagramMode(input.pageKey);
  const stages = deriveSubitemDiagramStages(input).map((stage) => escapeSvgText(normalizeDiagramLabel(stage, 27)));
  const titleLabel = escapeSvgText(trimSvgLabel(input.title, 44));
  const subtitleLabel = escapeSvgText(trimSvgLabel(input.summary, 78));
  const parentLabel = escapeSvgText(trimSvgLabel(input.parentLabel, 24));

  if (/sdn-and-general-license-screening|ofac-sdn-general-licenses/.test(input.pageKey)) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="760" height="500" viewBox="0 0 760 500" role="img" aria-label="${titleLabel} action explainer graphic">
      <defs>
        <linearGradient id="ofacBg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="${palette.shellFrom}"/>
          <stop offset="100%" stop-color="${palette.shellTo}"/>
        </linearGradient>
        <radialGradient id="ofacOrb" cx="34%" cy="30%" r="70%">
          <stop offset="0%" stop-color="${palette.orbFrom}" stop-opacity="0.84"/>
          <stop offset="100%" stop-color="${palette.orbTo}" stop-opacity="0.94"/>
        </radialGradient>
      </defs>
      <rect width="760" height="500" rx="24" fill="url(#ofacBg)"/>
      <circle cx="636" cy="154" r="146" fill="url(#ofacOrb)"/>
      <text x="44" y="56" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#24537f">${parentLabel}</text>
      <text x="44" y="84" font-family="system-ui,-apple-system,sans-serif" font-size="23" font-weight="800" fill="${palette.ink}">${titleLabel}</text>
      <text x="44" y="108" font-family="system-ui,-apple-system,sans-serif" font-size="13" font-weight="600" fill="#57779a">${subtitleLabel}</text>

      <rect x="42" y="132" width="676" height="278" rx="16" fill="#ffffff" stroke="#cfe1f6"/>
      <rect x="62" y="156" width="148" height="200" rx="12" fill="#f8fcff" stroke="#d6e7fa"/>
      <rect x="238" y="156" width="148" height="200" rx="12" fill="#f8fcff" stroke="#d6e7fa"/>
      <rect x="414" y="156" width="148" height="200" rx="12" fill="#f8fcff" stroke="#d6e7fa"/>
      <rect x="590" y="156" width="108" height="200" rx="12" fill="#f8fcff" stroke="#d6e7fa"/>

      <path d="M210 256h24M386 256h24M562 256h24" stroke="${palette.accent}" stroke-width="3" stroke-linecap="round"/>
      <path d="M228 249l8 7-8 7M404 249l8 7-8 7M580 249l8 7-8 7" fill="none" stroke="${palette.accent}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>

      <text x="76" y="182" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#4f729b">INPUTS</text>
      <text x="252" y="182" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#4f729b">ACTIONS</text>
      <text x="428" y="182" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#4f729b">DECISION</text>
      <text x="604" y="182" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#4f729b">OUTPUTS</text>

      <text x="76" y="210" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Entity profile</text>
      <text x="76" y="232" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">SDN + consolidated</text>
      <text x="76" y="254" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">General licenses</text>

      <text x="252" y="210" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Exact + fuzzy match</text>
      <text x="252" y="232" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Threshold scoring</text>
      <text x="252" y="254" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">License applicability</text>

      <text x="428" y="210" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Analyst adjudication</text>
      <text x="428" y="232" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Rationale capture</text>
      <text x="428" y="254" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Policy ownership</text>

      <text x="604" y="210" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Clear</text>
      <text x="604" y="232" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Escalate</text>
      <text x="604" y="254" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Block</text>

      <rect x="76" y="284" width="608" height="52" rx="10" fill="#f7fbff" stroke="#d8e8fb"/>
      <text x="94" y="306" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#355d88">Result artifacts: disposition record, evidence links, and automated rescreen cadence.</text>
      <text x="94" y="325" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="600" fill="#5a7ca2">Generated from workflow, capabilities, and outcomes for this feature page.</text>

      <rect x="44" y="424" width="672" height="36" rx="10" fill="#ffffff" stroke="#cfe1f6"/>
      <text x="64" y="447" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#3a628b">Action chain: ingest -> evaluate -> adjudicate -> publish</text>
    </svg>`;

    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  if (/capta-and-consolidated-lists|capta-consolidated-lists/.test(input.pageKey)) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="760" height="500" viewBox="0 0 760 500" role="img" aria-label="${titleLabel} CAPTA and consolidated lists explainer graphic">
      <defs>
        <linearGradient id="captaBg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="${palette.shellFrom}"/>
          <stop offset="100%" stop-color="${palette.shellTo}"/>
        </linearGradient>
      </defs>
      <rect width="760" height="500" rx="24" fill="url(#captaBg)"/>
      <text x="44" y="56" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#24537f">${parentLabel}</text>
      <text x="44" y="84" font-family="system-ui,-apple-system,sans-serif" font-size="23" font-weight="800" fill="${palette.ink}">${titleLabel}</text>
      <text x="44" y="108" font-family="system-ui,-apple-system,sans-serif" font-size="13" font-weight="600" fill="#57779a">${subtitleLabel}</text>

      <rect x="42" y="132" width="676" height="282" rx="16" fill="#ffffff" stroke="#cfe1f6"/>
      <rect x="62" y="160" width="178" height="220" rx="12" fill="#f8fcff" stroke="#d6e7fa"/>
      <rect x="260" y="160" width="200" height="220" rx="12" fill="#f8fcff" stroke="#d6e7fa"/>
      <rect x="480" y="160" width="218" height="220" rx="12" fill="#f8fcff" stroke="#d6e7fa"/>

      <text x="76" y="186" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#4f729b">WHAT CAPTA IS</text>
      <text x="76" y="210" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Foreign financial institutions</text>
      <text x="76" y="230" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">with correspondent account</text>
      <text x="76" y="250" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">or payable-through</text>
      <text x="76" y="270" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">account restrictions.</text>

      <text x="276" y="186" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#4f729b">SCREENING ACTIONS</text>
      <text x="276" y="210" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">1. Check CAPTA list entries</text>
      <text x="276" y="232" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">2. Check non-SDN consolidated</text>
      <text x="276" y="254" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">3. Identify institution type</text>
      <text x="276" y="276" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">4. Apply account restrictions</text>
      <text x="276" y="298" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">5. Publish policy-safe decision</text>

      <text x="496" y="186" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#4f729b">WHY IT MATTERS</text>
      <text x="496" y="210" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Prevents prohibited US</text>
      <text x="496" y="230" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">correspondent account access.</text>
      <text x="496" y="252" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Reduces bank-partner and</text>
      <text x="496" y="272" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">regulatory enforcement risk.</text>
      <text x="496" y="294" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Improves audit defensibility.</text>

      <path d="M240 272h20M460 272h20" stroke="${palette.accent}" stroke-width="3" stroke-linecap="round"/>
      <path d="M254 266l6 6-6 6M474 266l6 6-6 6" fill="none" stroke="${palette.accent}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>

      <rect x="44" y="426" width="672" height="34" rx="10" fill="#ffffff" stroke="#cfe1f6"/>
      <text x="64" y="448" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#3a628b">Story: CAPTA + consolidated checks close non-SDN banking exposure gaps.</text>
    </svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  if (/continuous-monitoring/.test(input.pageKey)) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="760" height="500" viewBox="0 0 760 500" role="img" aria-label="${titleLabel} continuous monitoring explainer graphic">
      <defs>
        <linearGradient id="contBg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="${palette.shellFrom}"/>
          <stop offset="100%" stop-color="${palette.shellTo}"/>
        </linearGradient>
      </defs>
      <rect width="760" height="500" rx="24" fill="url(#contBg)"/>
      <text x="44" y="56" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#24537f">${parentLabel}</text>
      <text x="44" y="84" font-family="system-ui,-apple-system,sans-serif" font-size="23" font-weight="800" fill="${palette.ink}">${titleLabel}</text>
      <text x="44" y="108" font-family="system-ui,-apple-system,sans-serif" font-size="13" font-weight="600" fill="#57779a">${subtitleLabel}</text>

      <rect x="42" y="132" width="676" height="282" rx="16" fill="#ffffff" stroke="#cfe1f6"/>
      <circle cx="380" cy="274" r="102" fill="none" stroke="#cfe1f6" stroke-width="12"/>
      <path d="M380 172a102 102 0 0 1 87 49" fill="none" stroke="${palette.accent}" stroke-width="5" stroke-linecap="round"/>
      <path d="M467 220l8 2-5 7" fill="none" stroke="${palette.accent}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M485 274a102 102 0 0 1-49 87" fill="none" stroke="${palette.accent}" stroke-width="5" stroke-linecap="round"/>
      <path d="M438 361l-2 8-7-5" fill="none" stroke="${palette.accent}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M380 376a102 102 0 0 1-87-49" fill="none" stroke="${palette.accent}" stroke-width="5" stroke-linecap="round"/>
      <path d="M293 328l-8-2 5-7" fill="none" stroke="${palette.accent}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M275 274a102 102 0 0 1 49-87" fill="none" stroke="${palette.accent}" stroke-width="5" stroke-linecap="round"/>
      <path d="M322 187l2-8 7 5" fill="none" stroke="${palette.accent}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>

      <rect x="334" y="246" width="92" height="56" rx="10" fill="#f8fcff" stroke="#d6e7fa"/>
      <text x="350" y="267" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#2c577f">Always-on</text>
      <text x="350" y="285" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#2c577f">control loop</text>

      <text x="102" y="214" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#4f729b">WATCH</text>
      <text x="102" y="232" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">List and policy updates</text>
      <text x="548" y="214" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#4f729b">DETECT</text>
      <text x="548" y="232" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Delta events and drift</text>
      <text x="102" y="338" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#4f729b">INVESTIGATE</text>
      <text x="102" y="356" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Analyst triage + rationale</text>
      <text x="548" y="338" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#4f729b">RESCREEN</text>
      <text x="548" y="356" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Close and schedule next run</text>

      <rect x="44" y="426" width="672" height="34" rx="10" fill="#ffffff" stroke="#cfe1f6"/>
      <text x="64" y="448" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#3a628b">Story: continuous checks catch new risk between onboarding and periodic reviews.</text>
    </svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  if (/real-time-and-batch-ofac-checks|real-time-batch-screening/.test(input.pageKey)) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="760" height="500" viewBox="0 0 760 500" role="img" aria-label="${titleLabel} real-time and batch screening explainer graphic">
      <defs>
        <linearGradient id="rtBg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="${palette.shellFrom}"/>
          <stop offset="100%" stop-color="${palette.shellTo}"/>
        </linearGradient>
      </defs>
      <rect width="760" height="500" rx="24" fill="url(#rtBg)"/>
      <text x="44" y="56" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#24537f">${parentLabel}</text>
      <text x="44" y="84" font-family="system-ui,-apple-system,sans-serif" font-size="23" font-weight="800" fill="${palette.ink}">${titleLabel}</text>
      <text x="44" y="108" font-family="system-ui,-apple-system,sans-serif" font-size="13" font-weight="600" fill="#57779a">${subtitleLabel}</text>

      <rect x="42" y="132" width="676" height="282" rx="16" fill="#ffffff" stroke="#cfe1f6"/>
      <rect x="66" y="166" width="204" height="200" rx="12" fill="#f8fcff" stroke="#d6e7fa"/>
      <rect x="288" y="166" width="184" height="200" rx="12" fill="#f8fcff" stroke="#d6e7fa"/>
      <rect x="490" y="166" width="204" height="200" rx="12" fill="#f8fcff" stroke="#d6e7fa"/>

      <text x="82" y="192" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#4f729b">REAL-TIME LANE</text>
      <text x="82" y="216" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Low-latency API checks</text>
      <text x="82" y="236" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Transaction authorization</text>
      <text x="82" y="256" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Immediate allow/hold signal</text>

      <text x="304" y="192" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#4f729b">UNIFIED ENGINE</text>
      <text x="304" y="216" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Shared matching rules</text>
      <text x="304" y="236" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Shared scoring outputs</text>
      <text x="304" y="256" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Shared evidence schema</text>

      <text x="506" y="192" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="800" fill="#4f729b">BATCH LANE</text>
      <text x="506" y="216" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Portfolio rescreen jobs</text>
      <text x="506" y="236" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">High-volume processing</text>
      <text x="506" y="256" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#184069">Consistent risk outcomes</text>

      <path d="M270 264h18M472 264h18" stroke="${palette.accent}" stroke-width="3" stroke-linecap="round"/>
      <path d="M282 258l6 6-6 6M484 258l6 6-6 6" fill="none" stroke="${palette.accent}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>

      <rect x="66" y="306" width="628" height="46" rx="10" fill="#f7fbff" stroke="#d8e8fb"/>
      <text x="84" y="334" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#355d88">Story: one control model supports immediate decisions and scheduled scale without score drift.</text>

      <rect x="44" y="426" width="672" height="34" rx="10" fill="#ffffff" stroke="#cfe1f6"/>
      <text x="64" y="448" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#3a628b">Action chain: real-time lane + batch lane -> unified decision contract.</text>
    </svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  const linearBody = `
    <g>
      <rect x="150" y="150" width="130" height="76" rx="12" fill="#ffffff" stroke="#cfe1f6"/>
      <rect x="306" y="150" width="130" height="76" rx="12" fill="#ffffff" stroke="#cfe1f6"/>
      <rect x="462" y="150" width="130" height="76" rx="12" fill="#ffffff" stroke="#cfe1f6"/>
      <rect x="618" y="150" width="130" height="76" rx="12" fill="#ffffff" stroke="#cfe1f6"/>
      <path d="M280 188h22M436 188h22M592 188h22" stroke="${palette.accent}" stroke-width="3" stroke-linecap="round"/>
      <path d="M299 181l8 7-8 7M455 181l8 7-8 7M611 181l8 7-8 7" fill="none" stroke="${palette.accent}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      <text x="166" y="179" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="${palette.ink}">${stages[0]}</text>
      <text x="322" y="179" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="${palette.ink}">${stages[1]}</text>
      <text x="478" y="179" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="${palette.ink}">${stages[2]}</text>
      <text x="634" y="179" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="${palette.ink}">${stages[3]}</text>
    </g>
  `;

  const dualLaneBody = `
    <g>
      <rect x="160" y="138" width="180" height="62" rx="12" fill="#ffffff" stroke="#cfe1f6"/>
      <rect x="160" y="236" width="180" height="62" rx="12" fill="#ffffff" stroke="#cfe1f6"/>
      <rect x="396" y="184" width="184" height="78" rx="12" fill="#ffffff" stroke="#cfe1f6"/>
      <rect x="620" y="184" width="124" height="78" rx="12" fill="#ffffff" stroke="#cfe1f6"/>
      <path d="M340 169h38M340 267h38M580 223h40" stroke="${palette.accent}" stroke-width="3" stroke-linecap="round"/>
      <path d="M370 162l8 7-8 7M370 260l8 7-8 7M612 216l8 7-8 7" fill="none" stroke="${palette.accent}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      <text x="178" y="174" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="${palette.ink}">${stages[0]}</text>
      <text x="178" y="272" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="${palette.ink}">${stages[1]}</text>
      <text x="414" y="224" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="${palette.ink}">${stages[2]}</text>
      <text x="636" y="224" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="${palette.ink}">${stages[3]}</text>
    </g>
  `;

  const cycleBody = `
    <g>
      <circle cx="450" cy="218" r="108" fill="none" stroke="#cfe1f6" stroke-width="12"/>
      <rect x="392" y="86" width="116" height="54" rx="12" fill="#ffffff" stroke="#cfe1f6"/>
      <rect x="566" y="188" width="116" height="54" rx="12" fill="#ffffff" stroke="#cfe1f6"/>
      <rect x="392" y="302" width="116" height="54" rx="12" fill="#ffffff" stroke="#cfe1f6"/>
      <rect x="218" y="188" width="116" height="54" rx="12" fill="#ffffff" stroke="#cfe1f6"/>
      <path d="M507 118l36 20M594 244l-36 20M393 330l-36-20M306 188l36-20" stroke="${palette.accent}" stroke-width="3" stroke-linecap="round"/>
      <text x="404" y="117" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="700" fill="${palette.ink}">${stages[0]}</text>
      <text x="578" y="219" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="700" fill="${palette.ink}">${stages[1]}</text>
      <text x="404" y="333" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="700" fill="${palette.ink}">${stages[2]}</text>
      <text x="230" y="219" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="700" fill="${palette.ink}">${stages[3]}</text>
    </g>
  `;

  const hubBody = `
    <g>
      <rect x="420" y="178" width="176" height="88" rx="14" fill="#ffffff" stroke="#cfe1f6"/>
      <rect x="192" y="108" width="168" height="58" rx="12" fill="#ffffff" stroke="#cfe1f6"/>
      <rect x="192" y="202" width="168" height="58" rx="12" fill="#ffffff" stroke="#cfe1f6"/>
      <rect x="192" y="296" width="168" height="58" rx="12" fill="#ffffff" stroke="#cfe1f6"/>
      <path d="M360 137h60M360 231h60M360 325h60" stroke="${palette.accent}" stroke-width="3" stroke-linecap="round"/>
      <path d="M412 130l8 7-8 7M412 224l8 7-8 7M412 318l8 7-8 7" fill="none" stroke="${palette.accent}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      <text x="208" y="142" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="${palette.ink}">${stages[0]}</text>
      <text x="208" y="236" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="${palette.ink}">${stages[1]}</text>
      <text x="208" y="330" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="${palette.ink}">${stages[2]}</text>
      <text x="438" y="226" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="${palette.ink}">${stages[3]}</text>
    </g>
  `;

  const diagramBody = mode === 'dual-lane'
    ? dualLaneBody
    : mode === 'cycle'
      ? cycleBody
      : mode === 'hub'
        ? hubBody
        : linearBody;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="760" height="500" viewBox="0 0 760 500" role="img" aria-label="${titleLabel} concept explainer graphic">
    <defs>
      <linearGradient id="bgConcept" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="${palette.shellFrom}"/>
        <stop offset="100%" stop-color="${palette.shellTo}"/>
      </linearGradient>
      <radialGradient id="orbConcept" cx="34%" cy="30%" r="70%">
        <stop offset="0%" stop-color="${palette.orbFrom}" stop-opacity="0.85"/>
        <stop offset="100%" stop-color="${palette.orbTo}" stop-opacity="0.95"/>
      </radialGradient>
    </defs>
    <rect width="760" height="500" rx="24" fill="url(#bgConcept)"/>
    <circle cx="638" cy="150" r="148" fill="url(#orbConcept)"/>
    <text x="44" y="56" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="800" fill="#24537f">${parentLabel}</text>
    <text x="44" y="84" font-family="system-ui,-apple-system,sans-serif" font-size="24" font-weight="800" fill="${palette.ink}">${titleLabel}</text>
    <text x="44" y="108" font-family="system-ui,-apple-system,sans-serif" font-size="13" font-weight="600" fill="#57779a">${subtitleLabel}</text>
    <rect x="42" y="140" width="90" height="90" rx="22" fill="#ffffff" stroke="#cfe1f6"/>
    <g transform="translate(28 130)">
      ${motifGlyphMarkup(motif, palette.accent, palette.accentSoft, palette.ink)}
    </g>
    ${diagramBody}
    <rect x="44" y="418" width="672" height="40" rx="12" fill="#ffffff" stroke="#cfe1f6"/>
    <text x="64" y="443" font-family="system-ui,-apple-system,sans-serif" font-size="12" font-weight="700" fill="#3a628b">Conceptual flow generated from page workflow, capabilities, and outcomes</text>
  </svg>`;

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const buildBusinessRiskIcebergGraphicDataUri = (): string => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="900" height="520" viewBox="0 0 900 520" role="img" aria-label="Business risk iceberg showing visible and hidden cost of non-compliance">
    <defs>
      <linearGradient id="riskSky" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#1b66b6"/>
        <stop offset="100%" stop-color="#1456a1"/>
      </linearGradient>
      <linearGradient id="riskSea" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#205da8"/>
        <stop offset="100%" stop-color="#17498a"/>
      </linearGradient>
    </defs>
    <rect width="900" height="520" rx="24" fill="url(#riskSky)"/>
    <rect y="196" width="900" height="324" fill="url(#riskSea)"/>

    <text x="450" y="64" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="34" font-weight="800" fill="#ffffff">True Cost Of Non-Compliance</text>
    <text x="94" y="146" font-family="system-ui,-apple-system,sans-serif" font-size="30" font-weight="800" fill="#d9ecff">VISIBLE</text>
    <text x="668" y="472" font-family="system-ui,-apple-system,sans-serif" font-size="34" font-weight="800" fill="#d9ecff">INVISIBLE</text>

    <path d="M454 108l56 70 56 24 24 38-14 38H324l-14-38 24-38 56-24z" fill="#d6e4ec"/>
    <path d="M324 278h252l14 50-22 34H332l-22-34z" fill="#1f5a9a"/>
    <path d="M332 362h236l-10 48-52 38-18 34-52-38-18-34-52-38z" fill="#0e3f77"/>

    <text x="620" y="154" font-family="system-ui,-apple-system,sans-serif" font-size="17" font-weight="700" fill="#ffffff">Regulatory fines and penalties</text>
    <text x="148" y="236" font-family="system-ui,-apple-system,sans-serif" font-size="17" font-weight="700" fill="#ffffff">Business disruption</text>
    <text x="634" y="330" font-family="system-ui,-apple-system,sans-serif" font-size="17" font-weight="700" fill="#ffffff">Legal fees</text>
    <text x="196" y="394" font-family="system-ui,-apple-system,sans-serif" font-size="17" font-weight="700" fill="#ffffff">Reputational damage</text>
    <text x="570" y="454" font-family="system-ui,-apple-system,sans-serif" font-size="17" font-weight="700" fill="#ffffff">Revenue loss</text>

    <path d="M572 154h40M266 236h44M586 330h40M298 394h44M516 454h38" stroke="#d9ecff" stroke-width="3" stroke-dasharray="6 6"/>
    <circle cx="572" cy="154" r="5" fill="#d9ecff"/>
    <circle cx="266" cy="236" r="5" fill="#d9ecff"/>
    <circle cx="586" cy="330" r="5" fill="#d9ecff"/>
    <circle cx="298" cy="394" r="5" fill="#d9ecff"/>
    <circle cx="516" cy="454" r="5" fill="#d9ecff"/>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

type OfacCollageVariant = 'overview' | 'sdn' | 'capta' | 'monitoring' | 'realtime-batch';

const OFAC_OVERVIEW_GRAPHIC_PATH = '/images/ofac-compliance-hero-modern.webp?v=20260318b';

const OFAC_SUBPAGE_VARIANT_BY_SLUG: Record<string, OfacCollageVariant> = {
  'sdn-and-general-license-screening': 'sdn',
  'consolidated-and-capta-list-coverage': 'capta',
  'continuous-ofac-monitoring': 'monitoring',
  'real-time-and-batch-ofac-checks': 'realtime-batch',
};

const buildOfacIconCollageGraphicDataUri = (variant: OfacCollageVariant): string => {
  let svg = '';

  if (variant === 'overview') {
    svg = `<svg xmlns="http://www.w3.org/2000/svg" width="760" height="500" viewBox="0 0 760 500" role="img" aria-label="OFAC overview hero with global exposure, asset screening, and compliance control motifs">
      <defs>
        <linearGradient id="ofacHeroBg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#071f45"/>
          <stop offset="100%" stop-color="#103f71"/>
        </linearGradient>
        <radialGradient id="ofacHeroGlow" cx="50%" cy="45%" r="55%">
          <stop offset="0%" stop-color="#4dc8ff" stop-opacity="0.58"/>
          <stop offset="100%" stop-color="#1d73ac" stop-opacity="0.08"/>
        </radialGradient>
        <linearGradient id="ofacPanel" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="rgba(255,255,255,0.16)"/>
          <stop offset="100%" stop-color="rgba(255,255,255,0.06)"/>
        </linearGradient>
      </defs>
      <rect width="760" height="500" rx="24" fill="url(#ofacHeroBg)"/>
      <rect x="44" y="52" width="672" height="396" rx="28" fill="rgba(255,255,255,0.04)" stroke="rgba(142,232,255,0.16)"/>
      <path d="M72 386c75-34 144-48 216-46 77 2 132 21 205 44 72 23 144 23 194 14" fill="none" stroke="#83ddff" stroke-opacity="0.2"/>
      <path d="M84 418c111-31 201-31 308 8 90 33 176 34 262 12" fill="none" stroke="#83ddff" stroke-opacity="0.28"/>
      <circle cx="382" cy="210" r="118" fill="url(#ofacHeroGlow)"/>
      <circle cx="382" cy="210" r="95" fill="none" stroke="#8ee8ff" stroke-opacity="0.58" stroke-width="2"/>
      <ellipse cx="382" cy="210" rx="95" ry="37" fill="none" stroke="#8ee8ff" stroke-opacity="0.46"/>
      <ellipse cx="382" cy="210" rx="95" ry="66" fill="none" stroke="#8ee8ff" stroke-opacity="0.28"/>
      <ellipse cx="382" cy="210" rx="41" ry="95" fill="none" stroke="#8ee8ff" stroke-opacity="0.3"/>
      <ellipse cx="382" cy="210" rx="67" ry="95" fill="none" stroke="#8ee8ff" stroke-opacity="0.22"/>
      <g fill="#8fdfff" opacity="0.18">
        <path d="M334 184l26-34 44 10 28 25-10 46-49 10-39-19z"/>
        <path d="M398 162l50-20 44 33-8 44-43 17-41-26z"/>
      </g>
      <rect x="96" y="118" width="162" height="244" rx="20" fill="url(#ofacPanel)" stroke="rgba(142,232,255,0.28)"/>
      <text x="126" y="152" font-family="Georgia, 'Times New Roman', serif" font-size="18" font-weight="700" fill="#dff7ff">Foreign exposure</text>
      <circle cx="177" cy="226" r="46" fill="rgba(10,48,86,0.92)" stroke="#8ee8ff" stroke-width="2.4"/>
      <ellipse cx="177" cy="226" rx="46" ry="18" fill="none" stroke="#8ee8ff" stroke-opacity="0.58"/>
      <ellipse cx="177" cy="226" rx="46" ry="31" fill="none" stroke="#8ee8ff" stroke-opacity="0.36"/>
      <ellipse cx="177" cy="226" rx="18" ry="46" fill="none" stroke="#8ee8ff" stroke-opacity="0.36"/>
      <path d="M133 298h88M133 320h76" stroke="#95ddff" stroke-width="7" stroke-linecap="round"/>
      <rect x="300" y="156" width="164" height="180" rx="24" fill="rgba(8,33,66,0.5)" stroke="rgba(142,232,255,0.32)"/>
      <path d="M336 286h92l-8 16h-76z" fill="#9fd7fb"/>
      <path d="M344 214h76v62h-76z" fill="#d6eeff"/>
      <path d="M326 214l56-36 56 36z" fill="#edf8ff"/>
      <path d="M354 226v44M372 226v44M390 226v44M408 226v44" stroke="#7eb7e0" stroke-width="6"/>
      <rect x="376" y="246" width="12" height="24" fill="#6c9dc6"/>
      <rect x="502" y="118" width="162" height="244" rx="20" fill="url(#ofacPanel)" stroke="rgba(142,232,255,0.28)"/>
      <text x="532" y="152" font-family="Georgia, 'Times New Roman', serif" font-size="18" font-weight="700" fill="#dff7ff">Assets and control</text>
      <rect x="532" y="188" width="106" height="64" rx="16" fill="rgba(8,33,66,0.88)" stroke="#8ee8ff" stroke-width="2"/>
      <circle cx="564" cy="220" r="14" fill="#c9a14a"/>
      <path d="M559 220h10M564 214v12" stroke="#17375d" stroke-width="2.5" stroke-linecap="round"/>
      <path d="M590 210h28M590 228h22" stroke="#9adfff" stroke-width="6" stroke-linecap="round"/>
      <path d="M536 304h34l14-14h34l12 14h8a12 12 0 0 1 12 12v4h-18a16 16 0 0 0-32 0h-48a16 16 0 0 0-32 0h-16v-4a12 12 0 0 1 12-12z" fill="#95d7ff"/>
      <circle cx="566" cy="320" r="16" fill="#123f6c"/><circle cx="566" cy="320" r="6" fill="#95d7ff"/>
      <circle cx="622" cy="320" r="16" fill="#123f6c"/><circle cx="622" cy="320" r="6" fill="#95d7ff"/>
      <circle cx="612" cy="100" r="32" fill="rgba(8,33,66,0.92)" stroke="#8ee8ff" stroke-width="2"/>
      <path d="M598 100l10 10 18-22" fill="none" stroke="#8ff2b0" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
      <rect x="600" y="68" width="24" height="8" rx="4" fill="#8ee8ff"/>
      <g opacity="0.45" stroke="#86ddff" stroke-width="1.6">
        <line x1="258" y1="226" x2="300" y2="226"/>
        <line x1="464" y1="226" x2="502" y2="226"/>
        <line x1="612" y1="132" x2="462" y2="208"/>
      </g>
    </svg>`;
  } else if (variant === 'sdn') {
    svg = `<svg xmlns="http://www.w3.org/2000/svg" width="760" height="500" viewBox="0 0 760 500" role="img" aria-label="SDN and general license screening collage">
      <defs>
        <linearGradient id="sdnBg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#0a2955"/>
          <stop offset="100%" stop-color="#16508b"/>
        </linearGradient>
      </defs>
      <rect width="760" height="500" rx="24" fill="url(#sdnBg)"/>
      <rect x="70" y="96" width="212" height="278" rx="20" fill="rgba(255,255,255,0.08)" stroke="#78d7ff" stroke-opacity="0.48"/>
      <rect x="102" y="132" width="148" height="190" rx="14" fill="#f3fbff"/>
      <path d="M124 170h104M124 196h92M124 222h100M124 248h78M124 274h94" stroke="#6d9ec6" stroke-width="8" stroke-linecap="round"/>
      <circle cx="298" cy="210" r="54" fill="rgba(15,53,92,0.94)" stroke="#8be6ff" stroke-width="3"/>
      <circle cx="286" cy="200" r="18" fill="none" stroke="#9ee8ff" stroke-width="4"/>
      <path d="M299 213l18 18" stroke="#9ee8ff" stroke-width="5" stroke-linecap="round"/>
      <path d="M420 110h170l30 30v176H420z" fill="rgba(255,255,255,0.09)" stroke="#7fd9ff" stroke-opacity="0.52"/>
      <path d="M590 110v30h30" fill="none" stroke="#7fd9ff" stroke-width="2"/>
      <rect x="456" y="164" width="128" height="74" rx="12" fill="#f4fbff"/>
      <path d="M486 198h70M486 220h52" stroke="#6a9bc4" stroke-width="8" stroke-linecap="round"/>
      <path d="M470 278l20 20 42-42" fill="none" stroke="#8ff2b0" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/>
      <g>
        <circle cx="550" cy="338" r="46" fill="rgba(10,44,78,0.92)" stroke="#86ddff" stroke-width="2"/>
        <circle cx="550" cy="324" r="14" fill="none" stroke="#9ee8ff" stroke-width="4"/>
        <path d="M526 370c8-18 16-28 24-28s16 10 24 28" fill="none" stroke="#9ee8ff" stroke-width="4" stroke-linecap="round"/>
        <circle cx="581" cy="354" r="12" fill="none" stroke="#8ff2b0" stroke-width="3"/>
        <path d="M575 354l4 4 8-8" fill="none" stroke="#8ff2b0" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      </g>
      <g>
        <rect x="304" y="344" width="148" height="46" rx="12" fill="#9ee8ff"/>
        <path d="M332 344v-24a20 20 0 0 1 40 0v24" fill="none" stroke="#9ee8ff" stroke-width="6"/>
        <path d="M334 408h88" stroke="#8ff2b0" stroke-width="6" stroke-linecap="round"/>
      </g>
      <path d="M252 230H404M404 230L454 204" fill="none" stroke="#84dbff" stroke-width="3" stroke-linecap="round"/>
      <path d="M520 292v-24" fill="none" stroke="#84dbff" stroke-width="3" stroke-linecap="round"/>
      <path d="M338 344l-18-44" fill="none" stroke="#84dbff" stroke-width="3" stroke-linecap="round"/>
    </svg>`;
  } else if (variant === 'capta') {
    svg = `<svg xmlns="http://www.w3.org/2000/svg" width="760" height="500" viewBox="0 0 760 500" role="img" aria-label="CAPTA and consolidated list coverage collage with foreign bank and sanctions list motifs">
      <defs>
        <linearGradient id="captaBg2" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#082140"/>
          <stop offset="100%" stop-color="#114f79"/>
        </linearGradient>
      </defs>
      <rect width="760" height="500" rx="24" fill="url(#captaBg2)"/>
      <rect x="82" y="96" width="236" height="308" rx="22" fill="rgba(255,255,255,0.06)" stroke="rgba(138,223,255,0.32)"/>
      <text x="114" y="132" font-family="Georgia, 'Times New Roman', serif" font-size="18" font-weight="700" fill="#dff7ff">CAPTA foreign bank controls</text>
      <path d="M120 194h160l-12 18H132z" fill="#a8dcff"/>
      <path d="M134 152l66-34 66 34z" fill="#edf8ff"/>
      <rect x="146" y="152" width="108" height="74" fill="#d7efff"/>
      <path d="M162 166v46M181 166v46M200 166v46M219 166v46" stroke="#7caed8" stroke-width="6"/>
      <rect x="190" y="188" width="18" height="38" fill="#6d9dc4"/>
      <path d="M126 272h132M126 302h154" stroke="#9ee8ff" stroke-width="7" stroke-linecap="round"/>
      <path d="M126 342h74l18-18h54l10 18h6" fill="none" stroke="#8ff2b0" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="422" cy="200" r="72" fill="rgba(10,50,82,0.92)" stroke="#8adfff" stroke-width="3"/>
      <circle cx="422" cy="200" r="24" fill="none" stroke="#8ff2b0" stroke-width="5"/>
      <circle cx="360" cy="200" r="12" fill="#9ee8ff"/>
      <circle cx="484" cy="200" r="12" fill="#9ee8ff"/>
      <circle cx="422" cy="138" r="12" fill="#9ee8ff"/>
      <circle cx="422" cy="262" r="12" fill="#9ee8ff"/>
      <path d="M372 200h38M434 200h38M422 150v36M422 214v34" stroke="#9ee8ff" stroke-width="5" stroke-linecap="round"/>
      <path d="M320 200h-36M524 200h36" stroke="#84dbff" stroke-width="4" stroke-linecap="round"/>
      <circle cx="284" cy="200" r="8" fill="#84dbff"/>
      <circle cx="560" cy="200" r="8" fill="#84dbff"/>
      <rect x="528" y="104" width="152" height="96" rx="18" fill="rgba(255,255,255,0.08)" stroke="rgba(138,223,255,0.32)"/>
      <text x="558" y="138" font-family="Georgia, 'Times New Roman', serif" font-size="17" font-weight="700" fill="#dff7ff">Non-SDN lists</text>
      <path d="M560 164h86M560 184h72" stroke="#9ee8ff" stroke-width="7" stroke-linecap="round"/>
      <rect x="498" y="286" width="190" height="122" rx="18" fill="rgba(255,255,255,0.08)" stroke="rgba(138,223,255,0.32)"/>
      <path d="M534 318h120M534 348h98M534 378h76" stroke="#9ee8ff" stroke-width="8" stroke-linecap="round"/>
      <path d="M360 336l30 30 64-64" fill="none" stroke="#8ff2b0" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  } else if (variant === 'monitoring') {
    svg = `<svg xmlns="http://www.w3.org/2000/svg" width="760" height="500" viewBox="0 0 760 500" role="img" aria-label="Continuous OFAC monitoring collage showing watch, change detection, and rescreening workflow">
      <defs>
        <linearGradient id="monitorBg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#081f40"/>
          <stop offset="100%" stop-color="#12527c"/>
        </linearGradient>
      </defs>
      <rect width="760" height="500" rx="24" fill="url(#monitorBg)"/>
      <rect x="84" y="110" width="170" height="282" rx="22" fill="rgba(255,255,255,0.06)" stroke="rgba(136,225,255,0.3)"/>
      <text x="118" y="146" font-family="Georgia, 'Times New Roman', serif" font-size="18" font-weight="700" fill="#dff7ff">Watch list state</text>
      <circle cx="170" cy="228" r="62" fill="rgba(19,73,117,0.92)" stroke="#88e1ff" stroke-width="3"/>
      <circle cx="170" cy="228" r="38" fill="none" stroke="#88e1ff" stroke-width="3" stroke-opacity="0.72"/>
      <path d="M170 228l30-16" stroke="#8ff2b0" stroke-width="5" stroke-linecap="round"/>
      <circle cx="170" cy="228" r="6" fill="#8ff2b0"/>
      <path d="M122 316h96M122 340h74" stroke="#9ee8ff" stroke-width="7" stroke-linecap="round"/>
      <rect x="296" y="148" width="164" height="160" rx="22" fill="rgba(255,255,255,0.06)" stroke="rgba(136,225,255,0.3)"/>
      <text x="324" y="184" font-family="Georgia, 'Times New Roman', serif" font-size="18" font-weight="700" fill="#dff7ff">Source changes</text>
      <path d="M330 248h30l18-28 26 46 18-22h8" fill="none" stroke="#9ee8ff" stroke-width="7" stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="424" cy="228" r="24" fill="rgba(10,48,86,0.92)" stroke="#8fe5ff" stroke-width="3"/>
      <path d="M412 228h24M424 216v12" stroke="#8ff2b0" stroke-width="4" stroke-linecap="round"/>
      <path d="M424 228l8 8" stroke="#8ff2b0" stroke-width="4" stroke-linecap="round"/>
      <rect x="502" y="110" width="174" height="282" rx="22" fill="rgba(255,255,255,0.06)" stroke="rgba(136,225,255,0.3)"/>
      <text x="534" y="146" font-family="Georgia, 'Times New Roman', serif" font-size="18" font-weight="700" fill="#dff7ff">Alerts and rescreen</text>
      <path d="M536 206h96" stroke="#9ee8ff" stroke-width="7" stroke-linecap="round"/>
      <circle cx="560" cy="206" r="8" fill="#9ee8ff"/>
      <circle cx="604" cy="206" r="8" fill="#9ee8ff"/>
      <circle cx="636" cy="206" r="8" fill="#8ff2b0"/>
      <rect x="536" y="252" width="104" height="74" rx="14" fill="rgba(8,33,66,0.88)" stroke="#88e1ff" stroke-width="2"/>
      <path d="M560 286l18 18 34-38" fill="none" stroke="#8ff2b0" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>
      <g>
        <circle cx="170" cy="380" r="30" fill="rgba(11,54,94,0.92)" stroke="#89e1ff" stroke-width="3"/>
        <path d="M184 370a22 22 0 1 0 2 18" fill="none" stroke="#9ee8ff" stroke-width="4" stroke-linecap="round"/>
        <path d="M186 360v10h-10" fill="none" stroke="#8ff2b0" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
      </g>
      <path d="M254 228h42M460 228h42" stroke="#85ddff" stroke-width="3" stroke-linecap="round"/>
    </svg>`;
  } else {
    svg = `<svg xmlns="http://www.w3.org/2000/svg" width="760" height="500" viewBox="0 0 760 500" role="img" aria-label="Real-time and batch OFAC checks collage showing live screening and batch rescreening feeding one decision engine">
      <defs>
        <linearGradient id="rtBg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#082040"/>
          <stop offset="100%" stop-color="#12517b"/>
        </linearGradient>
      </defs>
      <rect width="760" height="500" rx="24" fill="url(#rtBg)"/>
      <rect x="82" y="118" width="188" height="122" rx="20" fill="rgba(255,255,255,0.07)" stroke="rgba(123,216,255,0.34)"/>
      <text x="114" y="154" font-family="Georgia, 'Times New Roman', serif" font-size="18" font-weight="700" fill="#dff7ff">Real-time</text>
      <path d="M116 198h34l18-22 24 42 22-20h22" fill="none" stroke="#9ee8ff" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="116" cy="198" r="8" fill="#8ff2b0"/>
      <rect x="82" y="278" width="188" height="126" rx="20" fill="rgba(255,255,255,0.07)" stroke="rgba(123,216,255,0.34)"/>
      <text x="114" y="314" font-family="Georgia, 'Times New Roman', serif" font-size="18" font-weight="700" fill="#dff7ff">Batch queue</text>
      <rect x="118" y="334" width="116" height="20" rx="10" fill="#bde8ff"/>
      <rect x="118" y="364" width="92" height="20" rx="10" fill="#98dcff"/>
      <rect x="118" y="394" width="68" height="20" rx="10" fill="#70cbfb"/>
      <circle cx="388" cy="252" r="84" fill="rgba(14,66,110,0.92)" stroke="#8de5ff" stroke-width="3"/>
      <circle cx="388" cy="252" r="28" fill="none" stroke="#9ee8ff" stroke-width="5"/>
      <path d="M388 194v18M388 292v18M330 252h18M428 252h18M350 214l12 12M414 278l12 12M426 216l-12 12M362 280l-12 12" stroke="#9ee8ff" stroke-width="5" stroke-linecap="round"/>
      <text x="344" y="348" font-family="Georgia, 'Times New Roman', serif" font-size="18" font-weight="700" fill="#dff7ff">Shared decision engine</text>
      <path d="M270 180h42M270 340h42" stroke="#84dcff" stroke-width="4" stroke-linecap="round"/>
      <rect x="506" y="150" width="168" height="198" rx="20" fill="rgba(255,255,255,0.07)" stroke="rgba(123,216,255,0.34)"/>
      <text x="538" y="186" font-family="Georgia, 'Times New Roman', serif" font-size="18" font-weight="700" fill="#dff7ff">Decision record</text>
      <rect x="544" y="212" width="92" height="114" rx="14" fill="#f3fbff"/>
      <path d="M564 238h52M564 264h48" stroke="#6b9dc6" stroke-width="8" stroke-linecap="round"/>
      <circle cx="590" cy="294" r="16" fill="none" stroke="#8ff2b0" stroke-width="4"/>
      <path d="M590 286v8l6 4" stroke="#8ff2b0" stroke-width="4" stroke-linecap="round"/>
      <path d="M472 252h18M674 252h18" stroke="#84dcff" stroke-width="4" stroke-linecap="round"/>
    </svg>`;
  }

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

type PlainLanguageExplainer = {
  what: string;
  why: string;
};

const PRODUCT_PLAIN_LANGUAGE_EXPLAINERS: Record<string, PlainLanguageExplainer> = {
  basic: {
    what:
      'CompliCore is the starting operating package for smaller teams that need sanctions, website, FCI / CMMC Level 1 DoD coaching, and campus education coverage without stitching together separate point tools.',
    why:
      'It is needed when organizations want practical control coverage, cleaner handoffs, and a baseline they can expand without rebuilding the stack later. DoD CUI work is not in this SaaS.',
  },
  advanced: {
    what:
      'CompliAdvantage is the growth package for teams that need faster monitoring, clearer alert routing, and a CMMC Level 2 tracker without storing CUI in NextCompli.',
    why:
      'It is needed when manual coordination starts slowing incident response, increasing analyst noise, and creating inconsistent escalation paths, including DFARS 252.204-7012 readiness.',
  },
  enterprise: {
    what:
      'CompliComplete is the cross-functional governance package for organizations that need embedded guidance, executive reporting, scheduled assurance, and optional CUI-enclave pointers around production controls.',
    why:
      'It is needed when compliance operations span multiple teams, leaders need board-ready reporting, and a CUI program requires SSP/POA&M plus GCC High or PreVeil pointers — still without treating NextCompli as CMMC certified.',
  },
  'ofac-compliance': {
    what:
      'OFAC stands for the Office of Foreign Assets Control, a U.S. Treasury office that administers and enforces economic sanctions programs. In plain language, this page is about using one workflow to screen parties against OFAC sanctions data, evaluate license context, and document decisions.',
    why:
      'Teams need this because sanctions obligations change quickly, and split tools create missed risk and inconsistent decisions. A single OFAC operating flow lowers false escalations, improves response speed, and creates cleaner audit evidence.',
  },
  'ofac-sdn-general-licenses': {
    what:
      'This capability combines SDN match detection with general-license context in one review path, so teams do not treat potential sanctions matches and licensing relief as separate disconnected tasks. Venezuela GL 52C is the current example: certain PdVSA activity may be authorized for established U.S. entities without clearing every person, bank, or affiliate.',
    why:
      'It is needed to reduce both false positives and missed true exposure by forcing analysts to evaluate match quality and license applicability together before publishing disposition. A market movement is not a permitted counterparty.',
  },
  'capta-consolidated-lists': {
    what:
      'The CAPTA List identifies foreign financial institutions subject to correspondent account or payable-through account sanctions. In practical terms, it flags institutions that may be barred from, or restricted in, access to U.S. correspondent banking channels, and must be screened alongside OFAC non-SDN consolidated lists.',
    why:
      'It is needed because SDN-only screening can miss banking restrictions that still create material enforcement and partner risk. CAPTA plus consolidated checks close those non-SDN exposure gaps.',
  },
  'continuous-monitoring': {
    what:
      'Continuous monitoring is a CompliAdvantage always-on control loop that rechecks entities when list data, sanctions actions, or policy context changes between scheduled reviews. CompliCore uses operator Rescreen book after the same notices.',
    why:
      'It is needed because one-time screening becomes stale. Ongoing checks reduce the chance that newly sanctioned or newly restricted exposure goes undetected until the next manual cycle.',
  },
  'real-time-batch-screening': {
    what:
      'Real-time plus batch screening is a CompliAdvantage control model for immediate transaction decisions and scheduled high-volume portfolio or CSV rescreening. CompliCore screens named parties one at a time.',
    why:
      'It is needed to keep scoring and policy interpretation consistent across live operations and offline bulk reviews, preventing drift in risk outcomes.',
  },
  'live-environment-monitoring': {
    what:
      'Live Environment Monitoring is the feature set that keeps operational screening, alert routing, and monitoring-console activity connected in one production workflow.',
    why:
      'It is needed when teams cannot afford blind spots between immediate checks, analyst review, and bulk operational monitoring across active environments.',
  },
  'education-compliance': {
    what:
      'Education Compliance is the campus program that screens restricted parties, evidences research-export and CUI controls, collects HECVAT vendor packets, and names FERPA / GLBA program owners.',
    why:
      'Research offices already buy Maxient for conduct and Workiva for IPEDS. They still need OFAC-grade scholar screening, CUI evidence, and a Trust Center that answers HECVAT without forking EDUCAUSE’s workbook.',
  },
  'website-compliance': {
    what:
      'Website Compliance is the operating console for the customer’s public site and the systems behind it: privacy, accessibility, website security, email authentication so mail is not treated as spam, Zero Trust checks of cloud, containers, git, and the website itself, and AI/ML input guardrails when the site exposes a chat or other generative assistant.',
    why:
      'A cookie banner alone does not keep password-reset mail out of spam or stop a leaked git token. Teams need one evidence file that scales from CompliCore hygiene to CompliAdvantage monitoring to CompliComplete governance.',
  },
  'dod-contracts-compliance': {
    what:
      'Government Compliance is the eligibility and evidence binder for federal, state, county, and city vendor work, including unclassified Defense awards. CompliCore covers FCI / CMMC Level 1 plus the vendor lists a Texas IT consultancy needs. CompliAdvantage adds the Level 2 tracker without storing CUI. CompliComplete adds SSP/POA&M, GCC High/PreVeil pointers, and an optional CUI enclave.',
    why:
      'New contractors mix entity paperwork, CMMC, and city/county/state vendor portals into one pile. Splitting by subscription keeps CUI out of the commercial SaaS while still coaching FCI first. NextCompli does not file SPRS or certify the customer.',
  },
};

const PRODUCT_SUBITEM_PLAIN_LANGUAGE_EXPLAINERS: Record<string, PlainLanguageExplainer> = {
  'ofac-compliance/sdn-and-general-license-screening': {
    what:
      'This step evaluates whether a party matches sanctions data and whether any applicable general license, including Venezuela GL 52C for certain PdVSA activity, changes the operational decision.',
    why:
      'It is needed to avoid treating all potential matches as equal risk and to ensure compliant, evidence-backed dispositions. GL 52C still requires contracting conditions, payment routing, reporting, and SDN screening.',
  },
  'ofac-compliance/consolidated-and-capta-list-coverage': {
    what:
      'This step screens beyond the SDN list into OFAC consolidated non-SDN data and the CAPTA List, where CAPTA stands for correspondent account or payable-through account restrictions applied to foreign financial institutions and banks.',
    why:
      'It is needed because a bank or foreign financial institution can be restricted from U.S. correspondent banking access without appearing as an SDN, and those non-SDN restrictions still create real payment, partner, and enforcement risk.',
  },
  'ofac-compliance/continuous-ofac-monitoring': {
    what:
      'This CompliAdvantage step continuously watches for sanctions-data changes and triggers rescreen actions when risk context shifts. CompliCore operators run Rescreen book from Live Feed after the same GovDelivery notice.',
    why:
      'It is needed to catch newly relevant risk between onboarding and periodic manual review windows.',
  },
  'ofac-compliance/real-time-and-batch-ofac-checks': {
    what:
      'This CompliAdvantage step runs low-latency checks for immediate payment-party operations while also running batch jobs for large-scale CSV or portfolio review. CompliCore adds one named party at a time.',
    why:
      'It is needed to support both transaction-speed controls and enterprise-scale coverage under one policy model.',
  },
  'website-compliance/email-authenticity-and-deliverability': {
    what:
      'This step publishes SPF that lists every real sender, one DKIM key, and DMARC at p=none with a report mailbox someone reads. It also sends a short action note so operators are not stuck in the inbox, then tightens the policy as the subscription rises.',
    why:
      'It is needed because unauthenticated mail is filtered or rejected, and spoofed From: addresses burn the brand. CAN-SPAM covers the legal footer; this pillar covers whether the message arrives.',
  },
  'website-compliance/zero-trust-and-system-hardening': {
    what:
      'This step applies Zero Trust to the cloud accounts, containers, git repos, and public website that serve the customer: verify identity, least privilege, no secrets in source or images, then monitor and govern as the plan rises.',
    why:
      'It is needed because attackers skip the marketing homepage and walk in through an open bucket, a leaked token, or an unsigned image. CIS Implementation Groups and the CISA maturity model are the public ladders we map to CompliCore / CompliAdvantage / CompliComplete.',
  },
  'website-compliance/ai-ml-zero-trust-and-guardrails': {
    what:
      'This step inventories every public chat or generative assistant on the website, then requires input rails that block toxic, political/off-topic, and prompt-injection traffic before it reaches the LLM, plus output filtering and owner-led evaluation as the plan rises.',
    why:
      'It is needed because a website assistant that trusts the visitor (or the model) by default will answer off-topic or abusive prompts and can be steered by injection. OWASP’s LLM Top 10 and open stacks such as NVIDIA NeMo Guardrails with Meta Llama Guard are the practical pattern: filter first, then call the model.',
  },
};

const OFAC_FLOW_STORY: Array<{ title: string; meaning: string }> = [
  {
    title: 'OFAC SDN & General Licenses',
    meaning: 'Start with blocked-party detection, then apply current license context (including Venezuela GL 52C) so a new authorization is not treated as a skip of SDN, payment routing, or reporting.',
  },
  {
    title: 'CAPTA + Consolidated Lists',
    meaning: 'Extend beyond SDN to capture foreign-bank and non-SDN restrictions that still create real sanctions exposure.',
  },
  {
    title: 'Continuous Monitoring',
    meaning: 'CompliAdvantage: keep watching for source updates and new risk so the program does not drift between formal reviews. CompliCore operators rescreen the book by hand after a notice.',
  },
  {
    title: 'Real-time + Batch Screening',
    meaning: 'CompliAdvantage: one control framework for immediate payment-party checks and scheduled high-volume CSV screening. CompliCore adds named parties one at a time.',
  },
];

const SUPPORT_SLIDEOUT_ITEM_IDS = new Set(['support-basic', 'support-advanced', 'support-enterprise']);

const isSupportSlideoutItem = (itemId?: string | null): boolean => Boolean(itemId && SUPPORT_SLIDEOUT_ITEM_IDS.has(itemId));

const getSlideoutProductLabelParts = (label: string, itemId?: string | null): { title: string; subtitle?: string } => {
  const parentheticalMatch = label.match(/^([^()]+?)\s*(\(.+\))$/);
  if (parentheticalMatch) {
    return {
      title: parentheticalMatch[1].trim(),
      subtitle: parentheticalMatch[2].trim(),
    };
  }

  if (!isSupportSlideoutItem(itemId)) {
    return { title: label };
  }

  const firstSpaceIndex = label.indexOf(' ');
  if (firstSpaceIndex <= 0) {
    return { title: label };
  }

  return {
    title: label.slice(0, firstSpaceIndex),
    subtitle: label.slice(firstSpaceIndex + 1),
  };
};

const formatSlideoutProductLabel = (label: string, itemId?: string | null): React.ReactNode => {
  const parts = getSlideoutProductLabelParts(label, itemId);
  if (!parts.subtitle) {
    if (isSupportSlideoutItem(itemId)) {
      const firstSpaceIndex = label.indexOf(' ');
      if (firstSpaceIndex > 0) {
        return (
          <>
            {label.slice(0, firstSpaceIndex)}
            <br />
            {label.slice(firstSpaceIndex + 1)}
          </>
        );
      }
    }
    return label;
  }
  return (
    <span className="products-slideout-link-label">
      <span className="products-slideout-link-label-title">{parts.title}</span>
      <span className="products-slideout-link-label-subtitle">{parts.subtitle}</span>
    </span>
  );
};

const getProductMenuMemeCopy = (itemId: string, label: string, language: WebsiteLanguage): [string, string] => {
  const entry = PRODUCT_MENU_MEME_COPY[itemId];
  if (!entry) {
    return language === 'es'
      ? ['Que problema resuelve?', `${label} convierte riesgo en accion.`]
      : ['What problem does it solve?', `${label} turns risk into action.`];
  }
  return language === 'es' ? entry.es : entry.en;
};

const ProductSlideoutIcon: React.FC<{ iconKey: ProductSlideoutIconKey; className?: string }> = ({ iconKey, className }) => {
  const strokeProps = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.9,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  const iconPaths = (() => {
    switch (iconKey) {
      case 'starter':
        return <><rect x="4.5" y="6" width="15" height="12" rx="2" {...strokeProps} /><path d="M8 9.5h8" {...strokeProps} /><path d="M8 13h4.5" {...strokeProps} /></>;
      case 'advanced':
        return <><path d="M4.5 14.5h3l2-4 3 6 2.2-4h4.8" {...strokeProps} /><path d="M4.5 18h15" {...strokeProps} /></>;
      case 'enterprise':
        return <><path d="M12 4.3 5 7.4v4.8c0 3.5 2.4 6.2 7 7.5 4.6-1.3 7-4 7-7.5V7.4L12 4.3Z" {...strokeProps} /><path d="m9.2 12.2 1.8 1.7 3.8-3.8" {...strokeProps} /></>;
      case 'shield':
        return <><path d="M12 4.5 6 7v4.5c0 3.4 2.2 6 6 7.4 3.8-1.4 6-4 6-7.4V7l-6-2.5Z" {...strokeProps} /><path d="M9.2 12h5.6" {...strokeProps} /></>;
      case 'web':
        return <><circle cx="12" cy="12" r="7" {...strokeProps} /><path d="M5.3 12h13.4" {...strokeProps} /><path d="M12 5a10.5 10.5 0 0 1 0 14" {...strokeProps} /><path d="M12 5a10.5 10.5 0 0 0 0 14" {...strokeProps} /></>;
      case 'contract':
        return <><rect x="6" y="4.8" width="12" height="14.4" rx="1.8" {...strokeProps} /><path d="M9 9h6" {...strokeProps} /><path d="M9 12h6" {...strokeProps} /><path d="M9 15h3.8" {...strokeProps} /></>;
      case 'dashboard':
        return <><rect x="4.2" y="5" width="15.6" height="14" rx="2" {...strokeProps} /><path d="M8 10h3.2v6H8z" {...strokeProps} /><path d="M12.5 8h3.5v8h-3.5z" {...strokeProps} /></>;
      case 'monitor':
        return <><rect x="4" y="6.2" width="16" height="10" rx="1.8" {...strokeProps} /><path d="M10 18.5h4" {...strokeProps} /><path d="m7.2 11 2.2-2.2 2 2 3-3" {...strokeProps} /></>;
      case 'alert':
        return <><path d="M12 4.6 4.8 18h14.4L12 4.6Z" {...strokeProps} /><path d="M12 9v4.2" {...strokeProps} /><circle cx="12" cy="15.8" r="0.8" fill="currentColor" stroke="none" /></>;
      case 'consulting':
        return <><circle cx="9" cy="9.2" r="2.2" {...strokeProps} /><path d="M5.7 16.8c.8-2 2-3 3.3-3s2.5 1 3.3 3" {...strokeProps} /><path d="M15 8.2h4.8" {...strokeProps} /><path d="M15 11.4h3.8" {...strokeProps} /></>;
      case 'audit':
        return <><rect x="5.2" y="5.2" width="13.6" height="13.6" rx="2" {...strokeProps} /><path d="M8.4 10h7.2" {...strokeProps} /><path d="M8.4 13h5.1" {...strokeProps} /><path d="m14.8 15.4 1.1 1.1 2.1-2.2" {...strokeProps} /></>;
      case 'report':
        return <><path d="M6 17.8V6.2" {...strokeProps} /><path d="M10 17.8v-6.5" {...strokeProps} /><path d="M14 17.8V9.3" {...strokeProps} /><path d="M18 17.8v-4.4" {...strokeProps} /><path d="M4.5 17.8h15" {...strokeProps} /></>;
      case 'investigation':
        return <><circle cx="10.5" cy="10.5" r="4.2" {...strokeProps} /><path d="m13.7 13.7 4.2 4.2" {...strokeProps} /><path d="M10.5 8.6v3.8" {...strokeProps} /><path d="M8.6 10.5h3.8" {...strokeProps} /></>;
      case 'pricing':
        return <><rect x="5" y="6" width="14" height="12" rx="2" {...strokeProps} /><path d="M8.2 10.2h7.6" {...strokeProps} /><path d="M12 10.2v4.8" {...strokeProps} /><path d="M9.4 15h5.2" {...strokeProps} /></>;
      default:
        return <circle cx="12" cy="12" r="5" {...strokeProps} />;
    }
  })();

  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {iconPaths}
    </svg>
  );
};

const PRODUCT_DETAIL_CONTENT: ProductDetailContent[] = [
  {
    slug: 'basic',
    label: 'CompliCore',
    title: 'CompliCore',
    summary: 'For smaller teams building a reliable baseline, CompliCore combines sanctions coverage, website controls, DoD monitoring, and campus education compliance into one practical launch package.',
    highlights: ['OFAC Compliance', 'Website Compliance', 'Government Compliance', 'Education Compliance'],
    details: [
      'CompliCore is designed to move a team out of spreadsheet-driven coordination and into a repeatable operating model with clear priorities.',
      'It gives smaller organizations a defensible starting point they can implement quickly and expand without reworking core workflows later.',
      'Government Compliance on CompliCore is FCI / CMMC Level 1 plus local, county, state, and federal vendor lists: entity, SAM, insurance, 15 FAR 52.204-21 practices, and SPRS affirmation. Do not store CUI in this SaaS. Level 2, SSP/POA&M, and CUI enclaves are CompliAdvantage and CompliComplete.',
    ],
    sources: [
      { label: 'OFAC Compliance Resources', href: 'https://ofac.treasury.gov/compliance' },
      { label: 'FTC Business Guidance', href: 'https://www.ftc.gov/business-guidance' },
      { label: 'Acquisition.gov', href: 'https://www.acquisition.gov/' },
    ],
  },
  {
    slug: 'advanced',
    label: 'CompliAdvantage',
    title: 'CompliAdvantage',
    summary: 'For teams scaling beyond manual review, CompliAdvantage adds live monitoring, real-time alerts, and usage-aware operating controls for faster response.',
    highlights: ['Live Environment Monitoring', 'Real-Time Alerts', 'Usage-based Pricing Plans'],
    details: [
      'CompliAdvantage is built for teams where alert volume, environment sprawl, and response expectations have outgrown starter workflows.',
      'It emphasizes operational speed without losing auditability, so analysts and leaders can act on the same live risk picture.',
      'Government Compliance on CompliAdvantage adds the 110-practice CMMC Level 2 tracker, POA&M workspace, and DFARS 252.204-7012 72-hour incident playbook. CUI stays in the client enclave, not in NextCompli.',
    ],
    sources: [
      { label: 'NIST Cybersecurity Framework', href: 'https://www.nist.gov/cyberframework' },
      { label: 'NIST Risk Management Framework', href: 'https://csrc.nist.gov/projects/risk-management/about-rmf' },
    ],
  },
  {
    slug: 'enterprise',
    label: 'CompliComplete',
    title: 'CompliComplete',
    summary: 'For organizations with broader oversight demands, CompliComplete layers embedded support, executive reporting, and scheduled audits onto production controls.',
    highlights: ['Embedded Consultants', 'Executive Compliance Reporting', 'Scheduled Audits'],
    details: [
      'CompliComplete is optimized for organizations that need stronger governance rhythm, executive visibility, and cross-functional rollout support.',
      'It helps convert fragmented compliance work into a board-ready operating model with clearer accountability and evidence quality.',
      'Government Compliance on CompliComplete adds SSP export, GCC High/Entra/Intune evidence pointers, a PreVeil CUI mailbox pointer, and an optional FedRAMP Moderate enclave. NextCompli does not file SPRS or certify the customer as CMMC certified.',
    ],
    sources: [
      { label: 'NIST Privacy Framework', href: 'https://www.nist.gov/privacy-framework' },
      { label: 'NIST Risk Management Framework', href: 'https://csrc.nist.gov/projects/risk-management/about-rmf' },
    ],
  },
  {
    slug: 'screening-api-platform',
    label: 'Screening API Platform',
    title: 'Screening API Platform',
    summary: 'For compliance engineering teams replacing fragmented tools, one API combines sanctions checks and watchlist retrieval into decision-ready risk output from a single integration.',
    highlights: ['Text decomposition', 'Cinematic framing', 'Generation output artifacts'],
    details: [
      'Designed around a gateway model so teams integrate once and expand source coverage without rewriting client code.',
      'Aligns with market expectations for unified screening interfaces and operationally consistent workflows.',
    ],
    sources: [
      { label: 'OFAC Sanctions Programs and Information', href: 'https://ofac.treasury.gov/sanctions-programs-and-country-information' },
      { label: 'SEC EDGAR Search', href: 'https://www.sec.gov/edgar/search/' },
    ],
  },
  {
    slug: 'continuous-monitoring',
    label: 'Continuous Monitoring',
    title: 'Continuous Monitoring',
    summary: 'For CompliAdvantage operations teams that cannot rely on one-time screening, continuous monitoring re-checks the client file when OFAC lists change so new hits are caught before the next manual review.',
    highlights: ['Automatic book rescreen on OFAC notices', 'Delta alerts on source updates', 'Escalation-ready change events'],
    details: [
      'GovDelivery / Live Feed notices refresh SLS caches and re-screen the Screening portfolio without waiting for an operator.',
      'CompliCore still sees the same notices and uses Rescreen book. Automatic portfolio rescreen is CompliAdvantage.',
    ],
    sources: [{ label: 'OFAC Recent Actions', href: 'https://ofac.treasury.gov/recent-actions' }],
  },
  {
    slug: 'case-audit-workspace',
    label: 'Case & Audit Workspace',
    title: 'Case & Audit Workspace',
    summary: 'For investigation and compliance analysts, one workspace turns triage, evidence capture, and case decisions into audit-ready records without spreadsheet handoffs.',
    highlights: ['Case queue and disposition states', 'Analyst notes and evidence attachments', 'Regulator-friendly timeline view', 'Exhaustive background reports'],
    details: [
      'Decision workflows centralize hit review with structured notes and status transitions.',
      'Audit posture improves when each resolution includes rationale, timestamps, and reviewer identity.',
      'Company and people research workflows package adverse media, sanctions context, and evidence-backed recommendations into one dossier.',
    ],
    sources: [{ label: 'OFAC Compliance Resources', href: 'https://ofac.treasury.gov/compliance' }],
  },
  {
    slug: 'developer-sandbox-docs',
    label: 'Developer Sandbox & Docs',
    title: 'Developer Sandbox & Docs',
    summary: 'For developer teams under delivery pressure, sandbox workflows and plain-language docs shorten time to first successful integration before production rollout.',
    highlights: ['Endpoint examples and payloads', 'Environment parity from local to cloud', 'Test-first integration patterns'],
    details: [
      'Developer experience focuses on repeatable API behavior and environment portability.',
      'Supports local development, Kubernetes staging, and cloud deployment with consistent contracts.',
    ],
    sources: [{ label: 'SEC Developer APIs', href: 'https://www.sec.gov/search-filings' }],
  },
  {
    slug: 'pursuit',
    label: 'Pursuit CRM',
    title: 'Pursuit CRM',
    summary:
      'Pursuit is a separate multi-tenant CRM SaaS. NextCompli is the first tenant and operator. John Forrest & Goss and other GossLed programs connect as workspaces through the API instead of forking the product.',
    highlights: ['Leads, accounts, contacts, and deals', 'MEDDPICC stage gates', 'Tenant isolation', 'Partner and OEM workspaces'],
    details: [
      'One codebase (nextcompli/pursuit). Tenants are configuration, not forks.',
      'NextCompli hosts the control plane. A Pursuit subscription is a SaaS agreement; consulting remains an MSA/SOW with the selling firm.',
      'Operators open Pursuit at /console/pursuit. Partner brands such as John Forrest & Goss sell and operate their own workspace via X-Tenant-Slug.',
    ],
    sources: [
      { label: 'Open Pursuit console', href: '/console/pursuit' },
      { label: 'John Forrest & Goss', href: 'https://jfgoss.com/products/pursuit' },
    ],
  },
  {
    slug: 'ofac-compliance',
    label: 'OFAC Compliance',
    title: 'OFAC Compliance',
    summary:
      'For sanctions and compliance teams, a single OFAC workflow combines SDN and non-SDN list coverage with license context. CompliCore covers named-party onboard and operator rescreen after list notices. CompliAdvantage adds continuous monitoring and real-time/batch checks.',
    highlights: ['SDN and general license screening', 'Consolidated and CAPTA list coverage', 'Continuous OFAC monitoring', 'Real-time and batch OFAC checks'],
    details: [
      'The OFAC workflow unifies SDN and non-SDN controls so teams do not rely on fragmented point checks across separate screens and processes.',
      'CompliCore is Screen now on named counterparties plus an operator Rescreen book after GovDelivery / Live Feed notices. Continuous monitoring and real-time/batch screening are CompliAdvantage.',
    ],
    sources: [
      { label: 'OFAC Sanctions Programs and Information', href: 'https://ofac.treasury.gov/sanctions-programs-and-country-information' },
      { label: 'OFAC Consolidated Sanctions List (non-SDN lists)', href: 'https://ofac.treasury.gov/consolidated-sanctions-list-non-sdn-lists' },
      { label: 'OFAC Recent Actions', href: 'https://ofac.treasury.gov/recent-actions' },
    ],
  },
  {
    slug: 'ofac-sdn-general-licenses',
    label: 'OFAC SDN & General Licenses',
    title: 'OFAC SDN & General Licenses',
    summary: 'For sanctions programs that must separate true hits from permitted activity, SDN screening includes general-license context from official U.S. Treasury sources, including Venezuela GL 52C for certain PdVSA transactions.',
    highlights: ['SDN list ingestion patterns', 'General-license applicability context', 'Program-level sanctions metadata'],
    details: [
      'General licenses can change operational obligations and must be evaluated alongside sanctions list hits. GL 52C authorizes certain established U.S. entity activity with PdVSA; it does not skip SDN, payment routing, or reporting.',
      'The SDN list remains a critical blocked-party screening source for compliance operations.',
    ],
    sources: [
      { label: 'OFAC SDN Human-Readable Lists', href: 'https://ofac.treasury.gov/specially-designated-nationals-and-blocked-persons-list-sdn-human-readable-lists' },
      { label: 'OFAC Selected General Licenses', href: 'https://ofac.treasury.gov/selected-general-licenses-issued-ofac' },
      { label: 'OFAC General License 52C (PdVSA)', href: 'https://ofac.treasury.gov/media/936926/download?inline=' },
    ],
  },
  {
    slug: 'capta-consolidated-lists',
    label: 'CAPTA + Consolidated Lists',
    title: 'CAPTA + Consolidated Lists',
    summary: 'For teams that need more than SDN-only checks, consolidated and CAPTA coverage reduces missed exposure across non-SDN sanctions lists.',
    highlights: ['CAPTA list handling', 'Consolidated-list search alignment', 'Program code classification'],
    details: [
      'CAPTA entries identify foreign financial institutions with correspondent account restrictions.',
      'Consolidated list workflows reduce missed matches across non-SDN sanctions sources.',
    ],
    sources: [{ label: 'OFAC Consolidated Sanctions List', href: 'https://sanctionslist.ofac.treas.gov/Home/ConsolidatedList' }],
  },
  {
    slug: 'sec-filings-edgar-search',
    label: 'SEC Filings + EDGAR Search',
    title: 'SEC Filings + EDGAR Search',
    summary: 'For due-diligence teams validating counterparties, EDGAR and SEC filing analysis surfaces disclosure risk that basic sanctions screening can miss.',
    highlights: ['20+ year filing search depth', 'Issuer-level disclosure context', 'Evidence links for case review'],
    details: [
      'EDGAR search supports contextual due diligence and adverse disclosure checks.',
      'Filing-derived risk context strengthens analyst decisions beyond pure list matches.',
    ],
    sources: [
      { label: 'SEC EDGAR Search', href: 'https://www.sec.gov/edgar/search/' },
      { label: 'SEC Search Filings', href: 'https://www.sec.gov/search-filings' },
    ],
  },
  {
    slug: 'doe-state-sanctions-context',
    label: 'DOE / State Sanctions Context',
    title: 'DOE / State Sanctions Context',
    summary: 'For analysts working complex jurisdiction questions, policy context from U.S. agency sources helps resolve ambiguous sanctions matches with defensible reasoning.',
    highlights: ['Foreign-policy sanctions context', 'Sector and jurisdiction review', 'Cross-source analyst references'],
    details: [
      'Regulatory context helps interpret potentially ambiguous matches and jurisdictional implications.',
      'Supports policy-aware triage in highly regulated operational settings.',
    ],
    sources: [{ label: 'U.S. Department of State Sanctions Topics', href: 'https://www.state.gov/?s=sanctions' }],
  },
  {
    slug: 'real-time-batch-screening',
    label: 'Real-time + Batch Screening',
    title: 'Real-time + Batch Screening',
    summary: 'For CompliAdvantage teams balancing transaction speed and portfolio scale, one platform supports both real-time payment-party decisions and scheduled high-volume CSV screening. CompliCore adds named parties one at a time.',
    highlights: ['Low-latency API checks', 'CSV and payment-party batch', 'Consistent scoring outputs'],
    details: [
      'Real-time mode fits transactional controls while batch mode supports periodic portfolio reviews.',
      'Shared rules and scoring reduce drift between live and offline screening outcomes. CSV import is gated to this entitlement.',
    ],
    sources: [{ label: 'OFAC Compliance', href: 'https://ofac.treasury.gov/compliance' }],
  },
  {
    slug: 'fuzzy-matching-transliteration',
    label: 'Fuzzy Matching & Transliteration',
    title: 'Fuzzy Matching & Transliteration',
    summary: 'For multinational data sets with inconsistent names, fuzzy matching and transliteration improve near-match detection without forcing exact-spelling dependence.',
    highlights: ['Approximate-name matching', 'Cross-script transliteration support', 'Configurable sensitivity tuning'],
    details: [
      'Approximate matching helps capture relevant candidates when names vary by spelling or script.',
      'Sensitivity controls allow balancing false positives and false negatives by workflow.',
    ],
    sources: [{ label: 'OFAC Sanctions List Search', href: 'https://sanctionssearch.ofac.treas.gov/' }],
  },
  {
    slug: 'audit-trail-alert-workflow',
    label: 'Audit Trail + Alert Workflow',
    title: 'Audit Trail + Alert Workflow',
    summary: 'For organizations that must prove every decision, alert workflows maintain an evidence trail from detection through closure for faster internal and external review.',
    highlights: ['Structured alert lifecycle', 'Immutable decision timeline', 'Reviewer accountability controls'],
    details: [
      'Alert-to-case transitions preserve context, ownership, and disposition history for every event.',
      'Audit trails make internal reviews and external examinations faster and more defensible.',
    ],
    sources: [{ label: 'OFAC Enforcement and Compliance', href: 'https://ofac.treasury.gov/compliance' }],
  },
  {
    slug: 'website-compliance',
    label: 'Website Compliance',
    title: 'Website Compliance',
    summary: 'For legal, product, and digital teams, website compliance covers privacy, accessibility, website security, email authenticity so mail does not go to spam, Zero Trust hygiene for cloud, containers, git, and the public site, AI/ML input guardrails when the site has a chat or assistant, intellectual property and DMCA, and marketing/consumer rules. Coverage deepens from CompliCore to CompliAdvantage to CompliComplete.',
    highlights: ['Privacy & Data Governance', 'Accessibility & User Experience', 'Security & Risk Protection', 'Email Authenticity & Deliverability', 'Zero Trust & System Hardening', 'AI/ML Zero Trust & Guardrails', 'Intellectual Property & DMCA', 'Marketing, Terms & Consumer Rules'],
    details: [
      'Privacy and data governance covers transparent collection and usage, consent and cookie controls, classified cookie/tracker inventory, script blocking and consent logs, Google Consent Mode v2 and geo banners, scheduled rescans, user rights (access, deletion, correction, opt-out), COPPA when children are in scope, and required legal policies.',
      'Accessibility and user experience covers ADA/WCAG expectations including keyboard navigation, alt text, readable layouts, a published accessibility statement, and clear consent flows without dark patterns. Overlay widgets are not treated as WCAG compliance.',
      'Security and risk protection covers HTTPS, security headers, vulnerability protection and patching, incident contact, PCI DSS when the site takes cards, plus WAF/recurring scans on CompliAdvantage and ASVS-depth review on CompliComplete.',
      'Email authenticity and deliverability covers SPF that matches the mail host, one DKIM key, and DMARC with a report mailbox so website and product mail authenticates at Gmail, Yahoo, and Microsoft. CompliCore also records an action note: trash spam, mark ordinary mail read, and say what to do when a message needs a decision. CompliAdvantage adds quarantine, one-click unsubscribe, and complaint-rate watch; CompliComplete adds reject and BIMI.',
      'Zero Trust and system hardening checks the cloud accounts, containers, git repos, and public website that serve the customer: MFA and inventories on CompliCore, CIS Benchmark logging and image/SBOM scans on CompliAdvantage, CSPM and signed supply chain on CompliComplete.',
      'AI/ML Zero Trust and guardrails inventory public chats and assistants, then require pre-LLM input rails for toxic, political/off-topic, and injection prompts (NeMo Guardrails, Llama Guard, or equivalent), plus output filtering and owner-led evaluation on higher plans.',
      'Intellectual property and DMCA covers copyright notices, designated-agent registration under 17 U.S.C. § 512, notice and counter-notice intake, repeat-infringer policy, and open-source attribution.',
      'Marketing, terms, and consumer rules covers E-SIGN clickwrap, FTC claim substantiation and endorsements, auto-renewal and cancellation disclosures, CAN-SPAM, and acceptable-use boundaries.',
    ],
    sources: [
      { label: 'U.S. Copyright Office — DMCA § 512', href: 'https://www.copyright.gov/512/' },
      { label: 'Gmail email sender guidelines', href: 'https://support.google.com/mail/answer/81126' },
      { label: 'CISA Zero Trust Maturity Model', href: 'https://www.cisa.gov/zero-trust-maturity-model' },
      { label: 'CIS Controls Implementation Groups', href: 'https://www.cisecurity.org/controls/implementation-groups' },
      { label: 'OWASP Top 10 for LLM Applications', href: 'https://genai.owasp.org/llm-top-10/' },
      { label: 'NVIDIA NeMo Guardrails', href: 'https://github.com/NVIDIA-NeMo/Guardrails' },
      { label: 'FTC Advertising and Marketing Basics', href: 'https://www.ftc.gov/business-guidance/advertising-marketing' },
      { label: 'California Consumer Privacy Act (CCPA)', href: 'https://oag.ca.gov/privacy/ccpa' },
      { label: 'WCAG Overview (W3C)', href: 'https://www.w3.org/WAI/standards-guidelines/wcag/' },
    ],
  },
  {
    slug: 'dod-contracts-compliance',
    label: 'Government Compliance',
    title: 'Government Compliance',
    summary: 'For defense, state, county, and city vendors, compliance is organized by company, project, and contract. CompliCore covers FCI / CMMC Level 1 plus the vendor lists a Texas IT consultancy needs. CompliAdvantage adds the Level 2 tracker without storing CUI. CompliComplete adds SSP/POA&M, GCC High/PreVeil pointers, and an optional CUI enclave.',
    highlights: ['Entity Eligibility', 'Cybersecurity', 'Contract Compliance', 'Supply Chain Assurance', 'Financial & Ethics Controls', 'Pursuit Readiness'],
    details: [
      'Entity eligibility covers SAM.gov UEI/CAGE, NAICS and representations, banking, insurance, W-9, Texas franchise tax account status, and legal-entity standing before a contracting officer can award.',
      'Cybersecurity covers CMMC Phase I self-assessment for FCI (FAR 52.204-21, 15 practices, SPRS affirmation) on CompliCore. CompliAdvantage unlocks the NIST 800-171 Level 2 tracker and DFARS 252.204-7012 playbook without storing CUI. CompliComplete adds SSP/POA&M, GCC High/Entra/Intune evidence pointers, and a PreVeil CUI mailbox pointer. CMMC Phase II C3PAO requirements are paused as of July 2026.',
      'Contract compliance covers FAR/DFARS obligations, Section 889, US-person access / FOCI, clause flow-downs, and export controls.',
      'Supply chain assurance covers subcontractor compliance, prohibited-source checks, vendor vetting, and deliverable traceability.',
      'Financial and ethics controls cover first-award contract-type choice (FFP/T&M), timekeeping, and mandatory disclosure.',
      'Pursuit readiness covers capability statements, SBA Small Business Search, SAM opportunity watch, SBIR and STTR Phase I, Phase II, and Phase III records, Texas ESBD/CMBL, DIR RFOs, cooperatives, city/county/school portals, APEX Accelerator counseling, and a subcontract-first path to past performance. NextCompli does not submit a research proposal or invent an award number.',
    ],
    sources: [
      { label: 'DoD CMMC program', href: 'https://dodcio.defense.gov/cmmc/' },
      { label: 'SAM.gov', href: 'https://sam.gov/' },
      { label: 'SBIR.gov', href: 'https://www.sbir.gov/about' },
      { label: 'Acquisition.gov DFARS', href: 'https://www.acquisition.gov/dfars' },
      { label: 'Defense Contract Audit Agency', href: 'https://www.dcaa.mil/' },
    ],
  },
  {
    slug: 'ai-compliance',
    label: 'AI Compliance',
    title: 'AI Compliance',
    summary: 'For teams already using AI tools, AI Compliance keeps an inventory, a one-page use rule, and a named owner. Higher plans add vendor terms, a human check before a decision, and an impact note. NextCompli does not certify NIST AI RMF or ISO/IEC 42001.',
    highlights: ['Inventory and use rule', 'Vendor review and human check', 'Impact and scope'],
    details: [
      'The inventory lists every AI tool, including features inside software the company already owns, plus what data each tool can see and who owns the account. Customer text stays out of this console.',
      'The use rule names the data that never goes into a public model: personal data, health records, payment cards, government contract files, and passwords.',
      'CompliAdvantage adds vendor terms, a person reviewing the output before a decision about a person or a payment, a way to stop a bad system, and a quarterly review.',
      'CompliComplete adds an impact note and the questions of whether a federal agency inventory or the European Union AI Act even applies. A commercial chatbot is not a certificate.',
    ],
    sources: [
      { label: 'NIST AI RMF 1.0', href: 'https://doi.org/10.6028/NIST.AI.100-1' },
      { label: 'ISO/IEC 42001', href: 'https://www.iso.org/standard/81230.html' },
      { label: 'European Union AI Act', href: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32024R1689' },
    ],
  },
  {
    slug: 'education-compliance',
    label: 'Education Compliance',
    title: 'Education Compliance',
    summary: 'For campus research offices and CISOs, Education Compliance packages restricted-party screening, research-export / CUI evidence, HECVAT vendor trust, and FERPA / GLBA program hygiene — without building a Title IX or Clery case system.',
    highlights: ['Campus Restricted-Party Screening', 'Research Export and CUI', 'HECVAT and Vendor Trust', 'FERPA and GLBA Program'],
    details: [
      'Campus restricted-party screening covers visiting scholars, MOUs, foreign universities, and shipments against OFAC lists, then keeps cleared parties on monitoring.',
      'Research export and CUI reuse the DoD console for deemed-export access, NIST 800-171, and CMMC evidence when a grant or contract requires it.',
      'HECVAT and vendor trust collect official EDUCAUSE packets from EdTech vendors and publish NextCompli’s own campus assurance pack. The HECVAT workbook is not copied into the product.',
      'FERPA and GLBA program hygiene names owners for the annual notice, 45-day access path, 34 CFR 99.31 disclosure log, and Title IV Safeguards WISP.',
    ],
    sources: [
      { label: 'Student Privacy Policy Office', href: 'https://studentprivacy.ed.gov/' },
      { label: 'EDUCAUSE HECVAT toolkit', href: 'https://www.educause.edu/higher-education-community-vendor-assessment-toolkit' },
      { label: 'FSA GLBA cybersecurity update', href: 'https://fsapartners.ed.gov/knowledge-center/library/electronic-announcements/2023-02-09/updates-gramm-leach-bliley-act-cybersecurity-requirements' },
      { label: 'OFAC compliance', href: 'https://ofac.treasury.gov/compliance' },
    ],
  },
  {
    slug: 'cloud-compliance',
    label: 'Cloud Compliance',
    title: 'Cloud Compliance',
    summary: 'For the cloud account the client owns, Cloud Compliance is a written checklist on CompliCore. CompliAdvantage adds a once-a-day read-only review after the client grants a narrow audit role. This is not the public website scan, and it does not use the account that runs NextCompli.',
    highlights: ['Written cloud checklist', 'The client cloud account', 'Once-a-day read on a higher plan'],
    details: [
      'The checklist covers sign-in, audit logs, network exposure, encryption, backups, and findings.',
      'The client grants access to their own cloud account. Do not point this console at the account that runs NextCompli.',
      'A higher plan can add a once-a-day read-only review. The public website stays in Website Compliance.',
    ],
    sources: [
      { label: 'CIS Controls Implementation Groups', href: 'https://www.cisecurity.org/controls/implementation-groups' },
      { label: 'NIST Cybersecurity Framework', href: 'https://www.nist.gov/cyberframework' },
    ],
  },
  {
    slug: 'business-creation-compliance',
    label: 'Legal Entity Formation',
    title: 'Legal Entity Formation',
    summary: 'Name the organization first. A business, a nonprofit, and a campus all start here. The answers become the filing, tax, license, and report steps. NextCompli does not send those papers to a government office.',
    highlights: ['Business, nonprofit, or campus', 'One question at a time', 'A checklist, not a filing desk'],
    details: [
      'The first question is what kind of organization you are creating.',
      'Each answer writes the next step. Fees, forms, and deadlines still have to be checked with the state or with counsel.',
      'NextCompli keeps the checklist. It does not file articles, a tax-ID application, or a license.',
    ],
    sources: [
      { label: 'IRS employer identification number', href: 'https://www.irs.gov/businesses/small-businesses-self-employed/employer-id-numbers' },
    ],
  },
  {
    slug: 'wills-trusts-compliance',
    label: 'Wills & Trusts',
    title: 'Wills & Trusts',
    summary: 'Name the person first. The answers become a checklist for a will or a revocable trust. A lawyer licensed in that state writes and reviews the papers. NextCompli does not write them and does not file them.',
    highlights: ['Will or revocable trust', 'One question at a time', 'A checklist, not a law office'],
    details: [
      'The first question is will only, or a revocable trust plus a pour-over will.',
      'A will does not skip probate. A trust does no work until property is placed in it, and a home deed goes through the lawyer.',
      'NextCompli keeps the checklist. It does not write a will or a trust, notarize a signature, or file with a court.',
    ],
    sources: [
      { label: 'Texas Estates Code chapter 251', href: 'https://statutes.capitol.texas.gov/Docs/ES/htm/ES.251.htm' },
      { label: 'TexasLawHelp wills and estates', href: 'https://texaslawhelp.org/article/wills-and-estates' },
    ],
  },
  {
    slug: 'dashboards',
    label: 'Dashboards',
    title: 'Dashboards',
    summary: 'For leaders and operators who need one source of truth, dashboards connect source health, risk posture, and compliance trend performance in one view.',
    highlights: ['Data source health monitoring', 'Risk-scoring', 'Executive KPI rollups'],
    details: [
      'Unified dashboards help teams detect instability and prioritize remediation based on measurable compliance impact.',
      'Role-based views keep analysts and leaders aligned on the same operational truth without duplicative reporting effort.',
    ],
    sources: [{ label: 'NIST Risk Management Framework', href: 'https://csrc.nist.gov/projects/risk-management/about-rmf' }],
  },
  {
    slug: 'live-environment-monitoring',
    label: 'Live Environment Monitoring',
    title: 'Live Environment Monitoring',
    summary: 'For production teams that need both immediacy and traceability, live environment monitoring connects screening, alerting, and monitoring-console workflows into one operating lane.',
    highlights: ['Real-time + Batch Screening', 'Audit Trail + Alert Workflow', 'Monitoring Operations Console'],
    details: [
      'This feature keeps immediate checks, queued jobs, and analyst review aligned so environment-wide monitoring does not split into disconnected systems.',
      'It helps teams see where events are happening, what changed, and which actions are still pending across live operational surfaces.',
    ],
    sources: [
      { label: 'OFAC Compliance Resources', href: 'https://ofac.treasury.gov/compliance' },
      { label: 'NIST Cybersecurity Framework', href: 'https://www.nist.gov/cyberframework' },
    ],
  },
  {
    slug: 'real-time-alerts',
    label: 'Real-Time Alerts',
    title: 'Real-Time Alerts',
    summary: 'For response teams handling time-sensitive policy events, real-time alerts route the highest-priority incidents quickly with clear ownership and next actions.',
    highlights: ['Alerts & incident workflow', 'Priority alert pipelines', 'Incident response actions'],
    details: [
      'Prioritized alert channels reduce noise while ensuring high-risk events reach the right responders quickly.',
      'Integrated response actions keep investigations accountable and traceable from detection through closure.',
    ],
    sources: [{ label: 'NIST Cybersecurity Framework', href: 'https://www.nist.gov/cyberframework' }],
  },
  {
    slug: 'embedded-consultants',
    label: 'Embedded Consultants',
    title: 'Embedded Consultants',
    summary: 'For organizations launching or rebuilding compliance programs, embedded consultants reduce rollout friction with hands-on control design and team execution support.',
    highlights: ['Program design guidance', 'Execution support with your teams', 'Governance operating model'],
    details: [
      'Hands-on consultant engagement reduces rollout friction and transfers repeatable compliance operating practices to internal teams.',
      'Advisory support aligns policy intent, implementation mechanics, and stakeholder accountability across functions.',
    ],
    sources: [{ label: 'ISO 37301 Compliance Management Systems', href: 'https://www.iso.org/standard/75080.html' }],
  },
  {
    slug: 'scheduled-audits',
    label: 'Scheduled Audits',
    title: 'Scheduled Audits',
    summary: 'For teams accountable to recurring audits, scheduled workflows verify controls on cadence, capture evidence continuously, and reduce last-minute audit scramble.',
    highlights: ['Liability compliance monitoring', 'Local file liability checks', 'Public website liability checks', 'Audit evidence management'],
    details: [
      'Scheduling and cadence controls help teams prove that critical checks execute predictably and on time.',
      'Evidence management standardizes the artifacts auditors need and shortens preparation windows for formal reviews.',
    ],
    sources: [{ label: 'AICPA SOC Guidance', href: 'https://www.aicpa-cima.com/topic/audit-assurance/service-organization-controls-soc' }],
  },
  {
    slug: 'executive-compliance-reporting',
    label: 'Executive Compliance Reporting',
    title: 'Executive Compliance Reporting',
    summary: 'For executives and boards that need fast risk visibility, reporting translates operational compliance data into decision-ready updates with accountable owners.',
    highlights: ['Leadership dashboard snapshots', 'Board-ready compliance reporting', 'Compliance trend commentary'],
    details: [
      'Executive reporting packages focus attention on current risk posture, material change, and action ownership.',
      'Narrative-ready outputs improve communication with legal, audit committees, and senior stakeholders.',
    ],
    sources: [{ label: 'OECD Corporate Governance Principles', href: 'https://www.oecd.org/corporate/principles-corporate-governance/' }],
  },
  {
    slug: 'investigative-reporting',
    label: 'Research Reports & Investigations',
    title: 'Research Reports & Investigations',
    summary: 'For due-diligence teams assessing people and entities, one research-report workflow links investigation findings and background evidence for defensible escalations.',
    highlights: ['Submit people and entities', 'Company and people background dossiers', 'Evidence-linked investigation package'],
    details: [
      'Investigation reports unify diligence findings, source links, and recommendation logic into one auditable narrative.',
      'Teams can escalate complex cases with complete context and defensible evidence packaging.',
    ],
    sources: [{ label: 'FATF Guidance and Recommendations', href: 'https://www.fatf-gafi.org/en/publications/Fatfrecommendations/Fatf-recommendations.html' }],
  },
  {
    slug: 'usage-based-pricing-plans',
    label: 'Usage-based Pricing Plans',
    title: 'Usage-based Pricing Plans',
    summary: 'For teams at different compliance maturity levels, transparent usage-based plans start at $99/month and scale to advanced automation and enterprise governance.',
    highlights: ['CompliCore starting at $99/month', 'CompliAdvantage starting at $999/month', 'CompliComplete contact for estimate'],
    details: [
      'Pricing is designed around operational maturity, monitoring depth, and response workflow requirements.',
      'CompliCore and CompliAdvantage provide clear starting price points, while CompliComplete is quote-based for customized compliance programs.',
    ],
    sources: [{ label: 'NextCompli Plans', href: websitePath('/plans') }],
  },
];

const PRODUCT_SUBITEM_REFERENCE_SOURCES: Record<string, { label: string; href: string }[]> = {
  'screening-api-platform': [
    { label: 'Confluence: Prism Image Generation Runbook', href: 'https://winanscreek.atlassian.net/wiki/spaces/NC/pages/52232193' },
    { label: 'Confluence Search: Prism image generation', href: 'https://winanscreek.atlassian.net/wiki/search?text=Prism%20image%20generation' },
    { label: 'Confluence Search: menu video generation', href: 'https://winanscreek.atlassian.net/wiki/search?text=menu%20video%20generation' },
    { label: 'Prompt Engineering Guide', href: 'https://www.promptingguide.ai/' },
  ],
  'continuous-monitoring': [
    { label: 'OFAC Recent Actions', href: 'https://ofac.treasury.gov/recent-actions' },
    { label: 'OFAC General Licenses', href: 'https://ofac.treasury.gov/selected-general-licenses-issued-ofac' },
  ],
  'case-audit-workspace': [
    { label: 'OFAC Recent Actions Categories', href: 'https://ofac.treasury.gov/recent-actions' },
    { label: 'OFAC Enforcement and Compliance', href: 'https://ofac.treasury.gov/civil-penalties-and-enforcement-information' },
  ],
  'developer-sandbox-docs': [
    { label: 'SEC EDGAR Search', href: 'https://www.sec.gov/edgar/search/' },
    { label: 'SEC EDGAR APIs', href: 'https://www.sec.gov/search-filings/edgar-application-programming-interfaces' },
  ],
  'ofac-sdn-general-licenses': [
    { label: 'OFAC SDN Data Center', href: 'https://sanctionslist.ofac.treas.gov/Home/SdnList' },
    { label: 'OFAC Selected General Licenses', href: 'https://ofac.treasury.gov/selected-general-licenses-issued-ofac' },
    { label: 'OFAC General License 52C (PdVSA)', href: 'https://ofac.treasury.gov/media/936926/download?inline=' },
  ],
  'capta-consolidated-lists': [
    { label: 'OFAC Consolidated Sanctions List', href: 'https://ofac.treasury.gov/consolidated-sanctions-list-non-sdn-lists' },
    { label: 'OFAC Program Tag Definitions', href: 'https://ofac.treasury.gov/specially-designated-nationals-list-sdn-list/program-tag-definitions-for-ofac-sanctions-lists' },
  ],
  'sec-filings-edgar-search': [
    { label: 'SEC Search Filings', href: 'https://www.sec.gov/search-filings' },
    { label: 'SEC EDGAR Full Text Search', href: 'https://www.sec.gov/edgar/search/' },
  ],
  'doe-state-sanctions-context': [
    { label: 'U.S. State Sanctions News', href: 'https://www.state.gov/?s=sanctions' },
    { label: 'OFAC Sanctions Programs', href: 'https://ofac.treasury.gov/sanctions-programs-and-country-information' },
  ],
  'real-time-batch-screening': [
    { label: 'OFAC Search Tool Guidance', href: 'https://ofac.treasury.gov/faqs/topic/1636' },
    { label: 'OFAC SDN Data Formats', href: 'https://sanctionslist.ofac.treas.gov/Home/SdnList' },
  ],
  'fuzzy-matching-transliteration': [
    { label: 'OFAC Sanctions List Search', href: 'https://sanctionssearch.ofac.treas.gov/' },
    { label: 'OFAC FAQ: Score and Thresholds', href: 'https://ofac.treasury.gov/faqs/topic/1636' },
  ],
  'audit-trail-alert-workflow': [
    { label: 'OFAC Recent Actions', href: 'https://ofac.treasury.gov/recent-actions' },
    { label: 'OFAC Reporting and Enforcement', href: 'https://ofac.treasury.gov/civil-penalties-and-enforcement-information' },
  ],
  'usage-based-pricing-plans': [
    { label: withBrand('{brand} pricing'), href: websitePath('/products/usage-based-pricing-plans') },
    { label: 'SEC Search Filings', href: 'https://www.sec.gov/search-filings' },
  ],
};

type ProductSubitemContentOverride = Pick<
  ProductSubDetailContent,
  'summary' | 'businessValue' | 'businessRisk' | 'capabilities' | 'workflow' | 'outcomes' | 'sources'
>;

const PRODUCT_SUBITEM_RISK_SOURCE_REFERENCES: { label: string; href: string }[] = [
  { label: 'OFAC Civil Penalties and Enforcement Information', href: 'https://ofac.treasury.gov/civil-penalties-and-enforcement-information' },
  { label: 'FTC Advertising and Marketing Basics', href: 'https://www.ftc.gov/business-guidance/advertising-marketing/advertising-marketing-basics' },
  { label: 'NIST Cybersecurity Framework (CSF 2.0)', href: 'https://www.nist.gov/cyberframework' },
  { label: 'OFAC Sanctions List Service', href: 'https://ofac.treasury.gov/sanctions-list-service' },
  { label: 'OFAC Search FAQs', href: 'https://ofac.treasury.gov/faqs/topic/1636' },
];

const inferProductRiskDomain = (parentSlug: string): 'sanctions' | 'website' | 'security' | 'governance' | 'pricing' | 'platform' => {
  if (
    [
      'ofac-compliance',
      'ofac-sdn-general-licenses',
      'capta-consolidated-lists',
      'continuous-monitoring',
      'real-time-batch-screening',
      'fuzzy-matching-transliteration',
      'audit-trail-alert-workflow',
      'doe-state-sanctions-context',
    ].includes(parentSlug)
  ) {
    return 'sanctions';
  }

  if (['website-compliance', 'scheduled-audits', 'dod-contracts-compliance', 'education-compliance', 'ai-compliance'].includes(parentSlug)) {
    return 'website';
  }

  if (['dashboards', 'real-time-alerts'].includes(parentSlug)) {
    return 'security';
  }

  if (['executive-compliance-reporting', 'investigative-reporting', 'case-audit-workspace', 'embedded-consultants'].includes(parentSlug)) {
    return 'governance';
  }

  if (parentSlug === 'usage-based-pricing-plans') {
    return 'pricing';
  }

  return 'platform';
};

const buildProductSubitemBusinessRisk = (parentSlug: string, highlight: string): string[] => {
  const lane = highlight.toLowerCase();
  const domain = inferProductRiskDomain(parentSlug);

  if (domain === 'sanctions') {
    return [
      `Without ${lane}, teams increase exposure to missed restricted-party and non-SDN matches that can trigger blocked transactions, enforcement actions, and monetary penalties.`,
      'If list, license, and policy updates are not operationalized quickly, investigators face backlog growth, delayed decisions, and inconsistent escalations across teams.',
      'When evidence is fragmented, the business has weaker defense during regulator, banking-partner, or auditor review and can incur higher remediation cost.',
    ];
  }

  if (domain === 'website') {
    return [
      `Without ${lane}, organizations are more likely to publish unsubstantiated or non-compliant claims that lead to forced takedowns, legal dispute, and enforcement exposure.`,
      'Manual review-only controls create inconsistent approvals, missed deadlines, and avoidable rework across legal, marketing, and compliance stakeholders.',
      'Missing traceable evidence packages increases litigation and audit burden because teams cannot quickly prove what was reviewed, approved, and remediated.',
    ];
  }

  if (domain === 'security') {
    return [
      `Without ${lane}, high-risk operational and cyber events are detected later, increasing incident impact and recovery cost.`,
      'Weak monitoring and alert routing reduces ownership clarity, which extends response time and creates avoidable customer-facing disruption.',
      'Lack of framework-aligned telemetry makes board, regulator, and customer reporting less defensible and can slow renewals or procurement approvals.',
    ];
  }

  if (domain === 'governance') {
    return [
      `Without ${lane}, leadership decisions rely on incomplete evidence, increasing the chance of inconsistent risk acceptance and delayed escalation.`,
      'Investigation and audit teams spend more time reconstructing context, which raises operating cost and lengthens cycle time for high-priority cases.',
      'Inadequate reporting and accountability trails can weaken board confidence and increase findings during formal audits or external diligence.',
    ];
  }

  if (domain === 'pricing') {
    return [
      `Without ${lane}, buyers face pricing ambiguity that slows procurement and increases stalled opportunities late in the sales cycle.`,
      'Unclear package boundaries can produce scope disputes, margin leakage, and customer dissatisfaction after contract signature.',
      'If cost-to-capability mapping is not explicit, expansion planning becomes reactive and finance forecasting confidence drops.',
    ];
  }

  return [
    `Without ${lane}, teams maintain fragmented workflows that increase manual rework and reduce decision consistency.`,
    'Disconnected tooling makes it harder to demonstrate control effectiveness and evidence continuity during audit or customer review.',
    'As complexity grows, operating risk rises because ownership, thresholds, and escalation paths are not standardized.',
  ];
};

const buildProductBusinessRiskOverview = (productSlug: string, productLabel: string): string[] => {
  const domain = inferProductRiskDomain(productSlug);

  if (domain === 'sanctions') {
    return [
      `Without standardized ${productLabel} controls, teams are more likely to miss sanctioned-party exposure and face enforcement or payment disruption risk.`,
      'Fragmented sanctions workflows increase manual rework and slow high-confidence decisions when list and license context changes.',
      'Incomplete evidence continuity weakens the organization during regulator and banking-partner examinations.',
    ];
  }

  if (domain === 'website') {
    return [
      `Without ${productLabel}, publishing workflows can release non-compliant or unsubstantiated claims that increase legal and enforcement exposure.`,
      'Manual compliance review creates inconsistent approvals and avoidable launch delays across legal and marketing teams.',
      'Weak remediation traceability increases audit and litigation response costs when issues are challenged externally.',
    ];
  }

  if (domain === 'security') {
    return [
      `Without ${productLabel}, teams detect high-risk incidents later, increasing operational disruption and recovery cost.`,
      'Insufficient telemetry and ownership routing prolongs incident response and weakens control accountability.',
      'Leadership reporting quality declines when risk posture cannot be measured consistently across systems.',
    ];
  }

  if (domain === 'governance') {
    return [
      `Without ${productLabel}, executive and audit decisions rely on incomplete context, increasing governance and escalation risk.`,
      'Analysts spend more time reconstructing case history, which raises cycle time and operating cost.',
      'Stakeholder confidence decreases when accountability and evidence are not consistently structured.',
    ];
  }

  if (domain === 'pricing') {
    return [
      `Without ${productLabel}, buyers may misread cost-to-capability fit, slowing procurement and delaying revenue recognition.`,
      'Ambiguous package boundaries increase post-sale scope disputes and implementation friction.',
      'Forecasting reliability drops when tier adoption and expansion paths are not clearly defined.',
    ];
  }

  return [
    `Without ${productLabel}, teams keep fragmented controls that increase manual effort and reduce operating consistency.`,
    'Risk decisions become less defensible when evidence and ownership are split across disconnected tools.',
    'Scaling programs becomes harder because policy execution and measurement are not standardized.',
  ];
};

const PRODUCT_SUBITEM_CONTENT_OVERRIDES: Record<string, Partial<ProductSubitemContentOverride>> = {
  'screening-api-platform/text-decomposition': {
    summary:
      'Text decomposition converts menu summaries and subitems into explicit scene intent: subject, environment, action, and evidence cues that can be validated before generation.',
    businessValue:
      'Teams reduce prompt ambiguity and generation churn by translating product copy into structured semantic units before rendering graphics or video frames.',
    capabilities: [
      'Token-level extraction of nouns, verbs, constraints, and required visual evidence from input text',
      'Schema-backed prompt assembly that preserves source attribution for each semantic element',
      'Preflight validation that flags missing actors, actions, or context before image/video generation begins',
    ],
    workflow: [
      'Parse menu summary and subitems into normalized semantic blocks',
      'Map extracted blocks into a scene-spec object with explicit fields and confidence checks',
      'Compile validated scene-spec into generation-ready instruction text with provenance metadata',
    ],
    outcomes: [
      'Higher first-pass generation quality with fewer re-runs caused by underspecified prompts',
      'Clearer cross-team review because scene intent is visible prior to render time',
      'Better auditability through one-to-one mapping from input text to prompt instructions',
    ],
    sources: [
      { label: 'Prompt Engineering Guide', href: 'https://www.promptingguide.ai/' },
      { label: 'OpenAI Prompt Engineering Best Practices', href: 'https://platform.openai.com/docs/guides/prompt-engineering' },
      { label: 'Confluence: Prism Image Generation Runbook', href: 'https://winanscreek.atlassian.net/wiki/spaces/NC/pages/52232193' },
    ],
  },
  'screening-api-platform/cinematic-framing': {
    summary:
      'Cinematic framing turns decomposed text intent into production-ready visual composition decisions: camera language, blocking, pacing, and lighting hierarchy.',
    businessValue:
      'Brand and product teams get consistent campaign language across stills and video sequences while preserving scene uniqueness by menu context.',
    capabilities: [
      'Shot intent templates for establishing, action, and detail frames across menu narratives',
      'Composition controls for depth, focal hierarchy, motion implication, and visual continuity',
      'Lighting and tone directives that align with NextCompli visual system while avoiding generic stock-photo output',
    ],
    workflow: [
      'Select framing motif and camera behavior based on scene intent and audience',
      'Bind subject placement, action vectors, and environment signals to shot plan fields',
      'Compile framing directives into final instructions consumed by Prism generation tasks',
    ],
    outcomes: [
      'Improved storytelling clarity in generated visuals without sacrificing technical accuracy',
      'More consistent multi-image narrative flow across product and feature subitems',
      'Faster reviewer approval cycles due to explicit framing choices and reduced visual drift',
    ],
    sources: [
      { label: 'Cinematic Language (Wikipedia)', href: 'https://en.wikipedia.org/wiki/Cinematic_techniques' },
      { label: 'Film Style and Grammar Primer', href: 'https://www.studiobinder.com/blog/film-techniques/' },
      { label: 'Confluence Search: menu video generation', href: 'https://winanscreek.atlassian.net/wiki/search?text=menu%20video%20generation' },
    ],
  },
  'screening-api-platform/generation-output-artifacts': {
    summary:
      'Generation output artifacts package render outputs and runtime evidence so every image or frame can be traced from input prompt to final delivered asset.',
    businessValue:
      'Operations and audit teams can diagnose quality issues quickly and prove content provenance using run directory, progress telemetry, logs, and manifest metadata.',
    capabilities: [
      'Task-level runtime metadata capture (run directory, progress state, log file, last output)',
      'Stable-path copy workflow for website assets and versioned manifest emission',
      'Traceability links across task configuration, source prompt, and rendered raster outputs',
    ],
    workflow: [
      'Execute Prism generation task with scene instructions and style/runtime controls',
      'Collect task progress and output metadata from Prism runtime into manifest records',
      'Copy selected outputs into website image paths with deterministic names and source pointers',
    ],
    outcomes: [
      'End-to-end provenance from text prompt to raster image suitable for compliance review',
      'Faster incident triage when generation results regress or differ from expected composition',
      'Repeatable deployment of image assets with clear change history and operational context',
    ],
    sources: [
      { label: 'Confluence: Prism Image Generation Runbook', href: 'https://winanscreek.atlassian.net/wiki/spaces/NC/pages/52232193' },
      { label: 'Confluence Search: Prism image generation', href: 'https://winanscreek.atlassian.net/wiki/search?text=Prism%20image%20generation' },
      { label: 'JSON Schema Specification', href: 'https://json-schema.org/specification' },
    ],
  },
  'ofac-compliance/sdn-and-general-license-screening': {
    summary:
      'SDN and general license screening combines blocked-party checks, list-source traceability, and license context into one repeatable decision workflow for both onboarding and ongoing reviews, including current Venezuela GL 52C conditions for certain PdVSA activity.',
    businessValue:
      'Teams reduce false escalations and missed risk by reviewing SDN matches and license applicability together, then publishing decisions with traceable evidence and accountable ownership. This creates faster triage, clearer approvals, and cleaner audit handoffs across operations, legal, and policy teams.',
    workflow: [
      'Ingest current SDN and Consolidated list datasets from OFAC Sanctions List Service and bind screening scope to policy requirements',
      'Run exact and fuzzy matching with documented thresholds, then evaluate match context alongside applicable general licenses such as Venezuela GL 52C',
      'Confirm counterparties, payment path, and reporting evidence before publishing disposition, then route remaining hits to analyst review',
    ],
    outcomes: [
      'Faster, more consistent OFAC decisions with less analyst rework',
      'Lower chance of missed restricted-party exposure during onboarding and periodic reviews',
      'Stronger defensibility through explicit workflow records and source-linked evidence',
    ],
    sources: [
      { label: 'OFAC Sanctions List Search Tool', href: 'https://ofac.treasury.gov/sanctions-list-search-tool' },
      { label: 'OFAC Sanctions List Service', href: 'https://ofac.treasury.gov/sanctions-list-service' },
      { label: 'OFAC Selected General Licenses', href: 'https://ofac.treasury.gov/selected-general-licenses-issued-ofac' },
      { label: 'OFAC General License 52C (PdVSA)', href: 'https://ofac.treasury.gov/media/936926/download?inline=' },
    ],
  },
  'ofac-compliance/consolidated-and-capta-list-coverage': {
    summary:
      'Consolidated and CAPTA list coverage expands sanctions screening beyond SDN to capture non-SDN restrictions and foreign-bank correspondent-account controls that still affect whether a financial relationship is permitted.',
    businessValue:
      'Teams close a common sanctions gap by screening for correspondent account or payable-through account restrictions alongside OFAC consolidated list records, instead of assuming SDN coverage alone is sufficient. That leads to better banking-partner decisions, fewer missed escalations, and clearer evidence when a foreign financial institution requires review.',
    workflow: [
      'Ingest OFAC consolidated non-SDN datasets and CAPTA-related banking restrictions from the Sanctions List Service and bind them to customer and counterparty screening scope',
      'Screen foreign financial institutions, owners, and related entities for non-SDN restrictions that affect U.S. correspondent or payable-through account access',
      'Escalate potential hits with source-linked evidence so operations, sanctions, and legal teams can approve, restrict, or decline the relationship consistently',
    ],
    outcomes: [
      'Better visibility into foreign-bank and non-SDN sanctions exposure',
      'Lower chance of missing banking restrictions that do not appear on SDN-only checks',
      'Stronger review packages for compliance, audit, and banking-partner examination',
    ],
    sources: [
      { label: 'OFAC Sanctions List Service', href: 'https://ofac.treasury.gov/sanctions-list-service' },
      { label: 'OFAC Consolidated Sanctions List', href: 'https://ofac.treasury.gov/consolidated-sanctions-list-non-sdn-lists' },
      { label: 'OFAC CAPTA List', href: 'https://ofac.treasury.gov/sanctions-programs-and-country-information/counter-terrorism/capta-list' },
    ],
  },
  'ofac-compliance/continuous-ofac-monitoring': {
    summary:
      'Continuous OFAC monitoring is CompliAdvantage coverage: screened parties stay under watch after onboarding because list changes, program updates, and new sanctions actions trigger an automatic book rescreen. CompliCore operators run Rescreen book from Live Feed after the same notice.',
    businessValue:
      'Teams do not have to rely on stale point-in-time results. The monitoring loop surfaces changed risk faster, triggers targeted rescreens, and gives analysts an alert queue that is tied to the exact source update that caused the review.',
    workflow: [
      'Track relevant OFAC list, program, and enforcement-source changes after the initial screen is complete',
      'Compare source deltas against monitored entities and trigger rescreen events only where exposure may have changed',
      'Route alerts into review with the triggering change, updated match results, and a fresh disposition path',
    ],
    outcomes: [
      'Faster detection of sanctions exposure that appears after onboarding',
      'Less manual rescreening waste because alerts are tied to actual source changes',
      'Clearer evidence that ongoing sanctions monitoring is active and repeatable',
    ],
    sources: [
      { label: 'OFAC Recent Actions', href: 'https://ofac.treasury.gov/recent-actions' },
      { label: 'OFAC Sanctions List Service', href: 'https://ofac.treasury.gov/sanctions-list-service' },
      { label: 'OFAC Search FAQs', href: 'https://ofac.treasury.gov/faqs/topic/1636' },
    ],
  },
  'ofac-compliance/real-time-and-batch-ofac-checks': {
    summary:
      'Real-time and batch OFAC checks are CompliAdvantage coverage: one screening model for immediate payment-party decisions and scheduled high-volume CSV or portfolio reviews, so policy does not drift between the two modes. CompliCore screens named parties one at a time.',
    businessValue:
      'Operations teams get low-latency screening for live transactions and onboarding, while compliance teams run bulk rescreens across customer populations with the same rules, thresholds, and evidence structure. That avoids conflicting outcomes between live and offline review programs.',
    workflow: [
      'Accept single-party or transaction requests for immediate screening while also supporting queued file or portfolio submissions for bulk review',
      'Run both modes through the same sanctions data, match logic, and decision policy so scores and dispositions stay aligned',
      'Publish one decision record format with timestamps, source references, and analyst-ready evidence regardless of screening mode',
    ],
    outcomes: [
      'Consistent sanctions decisions across live operations and batch review programs',
      'Higher throughput for periodic rescreening without creating a second policy stack',
      'Cleaner audit evidence because both modes land in the same decision model',
    ],
    sources: [
      { label: 'OFAC Sanctions List Service', href: 'https://ofac.treasury.gov/sanctions-list-service' },
      { label: 'OFAC Search Tool Guidance', href: 'https://ofac.treasury.gov/faqs/topic/1636' },
      { label: 'OFAC Sanctions List Search Tool', href: 'https://ofac.treasury.gov/sanctions-list-search-tool' },
    ],
  },
  'website-compliance/privacy-and-data-governance': {
    summary:
      'Privacy and data governance standardizes consent, disclosures, and data-rights handling so website operations remain transparent under GDPR, CCPA/CPRA, COPPA, and similar rules.',
    businessValue:
      'Teams reduce legal exposure and increase customer trust by running one repeatable model for privacy notices, consent decisions, and user-rights requests.',
    capabilities: [
      'Consent controls for cookie and tracking opt-in or opt-out paths, including Global Privacy Control where required',
      'Classified cookie/tracker inventory, script blocking until consent, and stored proof-of-consent logs',
      'Google Consent Mode v2 and geo-targeted banners, plus scheduled homepage rescans',
      'User-rights workflows for data access, correction, deletion, opt-out, and request logs',
      'Policy governance for Privacy, Cookie, and Terms-of-Service alignment plus a COPPA audience determination',
    ],
    workflow: [
      'Map website data collection and classify personal-data obligations by jurisdiction',
      'Apply consent and disclosure controls with legal exception routing',
      'Fulfill and evidence user-rights requests for audit and regulator response',
    ],
    outcomes: [
      'Lower risk from inconsistent privacy disclosures and handling',
      'Faster user-rights fulfillment with clearer ownership',
      'Audit-ready records for policy, consent, and request decisions',
    ],
    sources: [
      { label: 'GDPR Portal', href: 'https://gdpr.eu/' },
      { label: 'California Consumer Privacy Act (CCPA)', href: 'https://oag.ca.gov/privacy/ccpa' },
      { label: 'FTC COPPA', href: 'https://www.ftc.gov/legal-library/browse/rules/childrens-online-privacy-protection-rule-coppa' },
    ],
  },
  'website-compliance/accessibility-and-user-experience': {
    summary:
      'Accessibility and user experience ensures content is usable, readable, and compliant with ADA and WCAG expectations before and after release.',
    businessValue:
      'Teams lower accessibility liability and improve conversion by delivering inclusive interactions across assistive technologies and device contexts.',
    capabilities: [
      'Accessibility checks for keyboard navigation, semantics, and form labeling',
      'Content checks for alt text, contrast, readability, and hierarchy',
      'Published accessibility statement plus a record that overlay widgets are not a WCAG substitute',
      'UX checks to prevent dark patterns in consent and critical flows',
    ],
    workflow: [
      'Evaluate templates and page content against WCAG-based criteria',
      'Assign remediation for blockers to design and engineering owners',
      'Re-validate pre-release and monitor drift after publication',
    ],
    outcomes: [
      'Reduced ADA and accessibility compliance risk',
      'Higher user success through clearer, inclusive interactions',
      'Higher release confidence with repeatable validation',
    ],
    sources: [
      { label: 'WCAG Overview (W3C)', href: 'https://www.w3.org/WAI/standards-guidelines/wcag/' },
      { label: 'ADA.gov Accessibility Guidance', href: 'https://www.ada.gov/resources/web-guidance/' },
    ],
  },
  'website-compliance/security-and-risk-protection': {
    summary:
      'Security and risk protection hardens the public website with HTTPS, headers, patching, incident contact, and PCI DSS when cards are accepted. CompliAdvantage adds a WAF and recurring scans; CompliComplete adds ASVS-depth application review.',
    businessValue:
      'Teams reduce incident probability and remediation cost by enforcing baseline controls on CompliCore and adding monitored, then governed, depth as the subscription rises.',
    capabilities: [
      'HTTPS and header verification on public pages (HSTS, CSP, X-Content-Type-Options)',
      'Patch cadence, incident contact, and PCI DSS scope when checkout accepts cards',
      'CompliAdvantage: WAF or equivalent edge filter plus recurring header/TLS scans in the alert workflow',
      'CompliComplete: scoped ASVS Level 2/3 review filed in the audit workspace',
    ],
    workflow: [
      'Assess controls and identify gaps in encryption, headers, and configuration',
      'Prioritize remediation with owners and due dates',
      'Run recurring scans on CompliAdvantage and ASVS-depth reviews on CompliComplete',
    ],
    outcomes: [
      'Lower breach and outage risk from unaddressed vulnerabilities',
      'Faster remediation cycles with clear accountability',
      'Improved posture through repeatable evidence-backed security checks matched to the plan',
    ],
    sources: [
      { label: 'OWASP Top 10', href: 'https://owasp.org/www-project-top-ten/' },
      { label: 'OWASP ASVS', href: 'https://owasp.org/www-project-application-security-verification-standard/' },
      { label: 'PCI DSS', href: 'https://www.pcisecuritystandards.org/standards/pci-dss/' },
    ],
  },
  'website-compliance/email-authenticity-and-deliverability': {
    summary:
      'Email authenticity and deliverability keeps website and product mail out of spam by publishing SPF that lists every sender, one DKIM key, and DMARC with a report mailbox, then a short action note so nobody has to live in that inbox.',
    businessValue:
      'Teams protect the brand inbox, meet Gmail/Yahoo/Microsoft sender rules, and stop spoofed mail from using the customer domain.',
    capabilities: [
      'CompliCore: SPF that lists the live mail host and every other sender, one DKIM TXT, DMARC at p=none with a report mailbox, an action note for mail that needs a decision, plus CAN-SPAM identity and unsubscribe',
      'CompliAdvantage: DMARC p=quarantine, RFC 8058 one-click unsubscribe, dedicated marketing subdomains, complaint-rate watch under 0.3%',
      'CompliComplete: DMARC p=reject, BIMI with a Verified Mark Certificate when brand marks matter, multi-domain report review with embedded consultants',
      'Homepage scan compares apex SPF to the mail exchange and marks DMARC done only when the policy and the report mailbox are both published',
    ],
    workflow: [
      'Inventory every sender (Workspace and the transactional vendor) and publish one SPF record and one DKIM TXT',
      'Start DMARC at p=none with a mailbox someone reads. Trash spam, mark ordinary mail read, and send an action note for decisions. Move to quarantine and reject only when reports are clean',
      'Separate marketing from transactional mail and keep complaint rates below mailbox-provider limits',
    ],
    outcomes: [
      'Fewer legitimate messages landing in spam',
      'Harder spoofing of the customer domain',
      'Audit-ready DNS and unsubscribe evidence matched to the plan',
    ],
    sources: [
      { label: 'Gmail email sender guidelines', href: 'https://support.google.com/mail/answer/81126' },
      { label: 'FTC CAN-SPAM', href: 'https://www.ftc.gov/business-guidance/resources/can-spam-act-compliance-guide-business' },
      { label: 'BIMI Group', href: 'https://bimigroup.org/' },
    ],
  },
  'website-compliance/zero-trust-and-system-hardening': {
    summary:
      'Zero Trust and system hardening checks the cloud environments, containers, websites, and git repos that actually serve the customer. Never trust by default; verify identity, lock down source, and keep workloads free of secrets.',
    businessValue:
      'Teams close the gap between a polished homepage and an open cloud account, leaked git secret, or unpatched container that attackers actually use.',
    capabilities: [
      'CompliCore (CIS IG1 / CISA traditional-to-initial): MFA on cloud, git, and CMS; asset inventory; private repos with secret scanning; containers without embedded secrets or root',
      'CompliAdvantage (CIS IG2 / CISA advanced): CIS Benchmark logging, protected branches with push protection, image vulnerability scans and SBOM, live monitoring of those controls',
      'CompliComplete (CIS IG3 / CISA optimal): CSPM, workload identity, signed images at admission, git SSO/SCIM, immutable audit, quarterly embedded-consultant review',
    ],
    workflow: [
      'Inventory cloud accounts, git orgs, container registries, and public origins',
      'Turn on MFA and remove secrets from git history and images',
      'Add monitored CIS/SBOM controls on CompliAdvantage and governed CSPM/signing on CompliComplete',
    ],
    outcomes: [
      'Fewer standing-admin and leaked-credential incidents',
      'Known software ingredients for every release',
      'Evidence that matches how CISA and CIS describe Zero Trust maturity',
    ],
    sources: [
      { label: 'CISA Zero Trust Maturity Model', href: 'https://www.cisa.gov/zero-trust-maturity-model' },
      { label: 'CIS Controls Implementation Groups', href: 'https://www.cisecurity.org/controls/implementation-groups' },
      { label: 'OpenSSF SCM best practices', href: 'https://github.com/ossf/wg-best-practices-os-developers/blob/main/docs/SCM-BestPractices/README.md' },
    ],
  },
  'website-compliance/ai-ml-zero-trust-and-guardrails': {
    summary:
      'AI/ML Zero Trust and guardrails treat every public chat or generative assistant as untrusted input and untrusted model output. Input rails intercept toxic, political/off-topic, and injection prompts before the LLM; output rails and owner-led evaluation deepen by plan.',
    businessValue:
      'Teams keep brand-safe, on-topic assistants without waiting for a long model reply — and they keep evidence that matches OWASP LLM Top 10 and NIST AI RMF practice.',
    capabilities: [
      'CompliCore: dated inventory of public AI features (or a dated none note) with an off-switch owner',
      'CompliAdvantage: pre-LLM input rails (toxicity, topic boundary, prompt injection) using stacks such as NeMo Guardrails + Llama Guard or equivalent; output filtering',
      'CompliComplete: named owner, acceptable-use rule, quarterly refusal eval, and cross-link to AI Compliance governance',
    ],
    workflow: [
      'Inventory every public generative feature and name who can disable it',
      'Place input rails before the model API; publish allowed topics and refuse the rest',
      'Add output rails and a small quarterly toxic / off-topic / injection test set on CompliComplete',
    ],
    outcomes: [
      'Toxic, political, and off-topic prompts stopped before they burn tokens or brand',
      'Clear proof of which open-source or hosted guardrail layer runs first',
      'Zero Trust posture: never trust the visitor prompt or the model alone',
    ],
    sources: [
      { label: 'OWASP Top 10 for LLM Applications', href: 'https://genai.owasp.org/llm-top-10/' },
      { label: 'NVIDIA NeMo Guardrails', href: 'https://github.com/NVIDIA-NeMo/Guardrails' },
      { label: 'NIST AI RMF 1.0', href: 'https://doi.org/10.6028/NIST.AI.100-1' },
      { label: 'CISA Zero Trust Maturity Model', href: 'https://www.cisa.gov/zero-trust-maturity-model' },
    ],
  },
  'website-compliance/intellectual-property-and-dmca': {
    summary:
      'Intellectual property and DMCA operationalizes copyright notices, 17 U.S.C. § 512 designated-agent registration, notice and counter-notice intake, and open-source attribution.',
    businessValue:
      'Teams reduce copyright-infringement and safe-harbor risk by publishing agent details, running a documented takedown process, and keeping license obligations current.',
    capabilities: [
      'Copyright and ownership notices on public pages',
      'DMCA designated-agent registration and on-site contact details',
      'Notice / counter-notice intake, SLA, case log, and repeat-infringer plus OSS attribution tracking',
    ],
    workflow: [
      'Publish copyright notices and register the designated agent with the U.S. Copyright Office',
      'Route incoming § 512 notices and counter-notices through a named owner with a response SLA',
      'Record dispositions, tabletop the process annually, and keep OSS attribution with shipped software',
    ],
    outcomes: [
      'Clearer safe-harbor posture when third-party or user content is hosted',
      'Faster, consistent takedown and counter-notice handling',
      'Audit-ready evidence for copyright, agent, and license reviews',
    ],
    sources: [
      { label: 'U.S. Copyright Office — DMCA § 512', href: 'https://www.copyright.gov/512/' },
      { label: 'DMCA designated agent directory', href: 'https://www.copyright.gov/dmca-directory/' },
    ],
  },
  'website-compliance/marketing-terms-and-consumer-rules': {
    summary:
      'Marketing, terms, and consumer rules covers E-SIGN clickwrap, FTC advertising and endorsements, auto-renewal and cancellation disclosures, CAN-SPAM, and acceptable-use boundaries.',
    businessValue:
      'Teams reduce unenforceable-contract, deceptive-advertising, and subscription-law exposure by standardizing assent, claims, billing copy, and prohibited-use enforcement.',
    capabilities: [
      'Clickwrap assent tied to a versioned Terms record (15 U.S.C. § 7001)',
      'Claim substantiation and endorsement-disclosure controls',
      'ROSCA / negative-option billing disclosures, CAN-SPAM email hygiene, and AUP enforcement',
    ],
    workflow: [
      'Require affirmative assent and store account, timestamp, and terms version',
      'Review public claims and testimonials against evidence before publish',
      'Match checkout, renewal, and cancel paths to Terms and keep commercial email compliant',
    ],
    outcomes: [
      'Stronger contract enforceability when disputes arise',
      'Lower FTC and state AG risk from unsupported claims or buried cancel paths',
      'Clearer prohibited-use enforcement and email opt-out evidence',
    ],
    sources: [
      { label: 'FTC Advertising and Marketing', href: 'https://www.ftc.gov/business-guidance/advertising-marketing' },
      { label: 'FTC Negative Option Rule', href: 'https://www.ftc.gov/legal-library/browse/rules/negative-option-rule' },
      { label: 'FTC CAN-SPAM', href: 'https://www.ftc.gov/business-guidance/resources/can-spam-act-compliance-guide-business' },
    ],
  },
  'dod-contracts-compliance/entity-eligibility': {
    summary:
      'Entity eligibility is the award gate: formed LLC, EIN, business bank, SAM.gov UEI/CAGE, NAICS and representations, and insurance.',
    businessValue:
      'Teams stop wasting bid effort on notices they cannot accept because the entity record, banking, or SAM registration is incomplete.',
    capabilities: [
      'Legal-entity and EIN evidence tracking',
      'SAM.gov UEI, CAGE, NAICS, and representations checklist',
      'Banking and E&O/CGL insurance gates before first invoice',
    ],
    workflow: [
      'Confirm the bidder is the formed LLC using the EIN-letter legal name',
      'Register in SAM.gov, capture UEI and CAGE, complete reps and NAICS',
      'Open LLC banking and bind insurance required by primes and RFPs',
    ],
    outcomes: [
      'Contracting officers can find and award the entity in SAM',
      'EFT and invoice payment will not fail for missing banking',
      'Proposals include current representations instead of last-minute scrambles',
    ],
    sources: [
      { label: 'SAM.gov', href: 'https://sam.gov/' },
      { label: 'SBA APEX Accelerators', href: 'https://www.sba.gov/counseling/local-assistance/targeted-support-and-services/#find-an-apex-accelerator' },
    ],
  },
  'dod-contracts-compliance/cybersecurity': {
    summary:
      'Cybersecurity starts with CMMC Level 1 self-assessment for Federal Contract Information on CompliCore. CompliAdvantage unlocks NIST 800-171, numeric SPRS, and DFARS 7012 playbooks without storing CUI. CompliComplete adds SSP/POA&M, GCC High/Entra/Intune pointers, and a PreVeil CUI mailbox. Phase II C3PAO requirements are paused as of July 2026.',
    businessValue:
      'Teams reduce breach risk and contract ineligibility by operating one repeatable FCI control model, with a separate CUI path when a notice actually requires it.',
    capabilities: [
      'Company / project / contract taxonomy with a rules+AI CMMC level calculator',
      'CMMC Level 1 / FAR 52.204-21 practice tracking and SPRS affirmation on CompliCore',
      'NIST 800-171 Level 2 tracker and DFARS 7012 playbook on CompliAdvantage (CUI stays off this SaaS)',
      'SSP/POA&M, GCC High/Entra/Intune pointers, and PreVeil CUI mailbox on CompliComplete when CUI is in scope',
    ],
    workflow: [
      'Decide FCI vs CUI from the solicitation before scoping systems',
      'Implement FAR 52.204-21 practices, enter Level 1 in SPRS, and affirm annually',
      'Only then build NIST 800-171 SSP/POA&M if a notice requires CUI',
    ],
    outcomes: [
      'Eligible for Phase I CMMC self-assessment clauses on FCI work',
      'No wasted C3PAO spend while Phase II remains paused',
      'Documented evidence for contracting officer and prime flow-down reviews',
    ],
    sources: [
      { label: 'DoD CMMC program', href: 'https://dodcio.defense.gov/cmmc/' },
      { label: 'FAR 52.204-21', href: 'https://www.acquisition.gov/far/52.204-21' },
      { label: 'NIST SP 800-171 Rev. 2', href: 'https://csrc.nist.gov/pubs/sp/800/171/r2/final' },
    ],
  },
  'dod-contracts-compliance/contract-compliance': {
    summary:
      'Contract compliance operationalizes FAR and DFARS obligations, clause flow-downs, export controls, and documentation requirements for defense contracts.',
    businessValue:
      'Teams reduce contractual and regulatory exposure by standardizing how obligations are interpreted, assigned, and evidenced across programs.',
    capabilities: [
      'Clause tracking for FAR/DFARS obligations and updates',
      'Section 889 covered-telecom and US-person / FOCI access rules',
      'Flow-down, export-control, and government-property checkpoints',
    ],
    workflow: [
      'Map contract clauses into actionable obligations by owner and due date',
      'Verify flow-down language and controls across subcontractor agreements',
      'Maintain evidence records for obligations, changes, and closure status',
    ],
    outcomes: [
      'Fewer missed obligations and late remediations during contract execution',
      'Higher audit readiness for clause and documentation reviews',
      'Clearer accountability across legal, program, and operations teams',
    ],
    sources: [
      { label: 'Federal Acquisition Regulation (FAR)', href: 'https://www.acquisition.gov/browse/index/far' },
      { label: 'Defense Federal Acquisition Regulation Supplement (DFARS)', href: 'https://www.acquisition.gov/dfars' },
    ],
  },
  'dod-contracts-compliance/supply-chain-assurance': {
    summary:
      'Supply chain assurance validates subcontractor compliance, prohibited-source restrictions, and traceability controls across defense delivery partners.',
    businessValue:
      'Teams reduce inherited risk from third parties by enforcing vendor controls that align with prime-contract compliance obligations.',
    capabilities: [
      'Subcontractor compliance tracking for cyber and clause flow-down controls',
      'Prohibited-source screening and supplier risk review checkpoints',
      'Traceability records for vendors, components, and handling controls',
    ],
    workflow: [
      'Classify suppliers by contract risk and required compliance controls',
      'Verify vendor attestations, controls, and prohibited-source restrictions',
      'Monitor vendor posture continuously and escalate non-compliance exceptions',
    ],
    outcomes: [
      'Lower risk of prime-contract violations caused by supplier failures',
      'Improved confidence in third-party control coverage and evidence',
      'Faster risk response when vendor posture changes',
    ],
    sources: [
      { label: 'DoD Trusted Capital / Supply Chain Risk Resources', href: 'https://www.acq.osd.mil/cmmc/' },
      { label: 'Acquisition.gov DFARS', href: 'https://www.acquisition.gov/dfars' },
    ],
  },
  'dod-contracts-compliance/financial-and-ethics-controls': {
    summary:
      'Financial and ethics controls align DCAA-ready accounting, cost allowability, billing integrity, and disclosure obligations for DoD programs.',
    businessValue:
      'Teams reduce audit findings and legal exposure by standardizing financial controls, ethics reporting, and evidence-backed contract performance records.',
    capabilities: [
      'DCAA-aligned accounting and allowable-cost control checks',
      'Timekeeping, billing, and incurred-cost submission validation',
      'Ethics-program and mandatory-disclosure governance workflows',
    ],
    workflow: [
      'Validate accounting controls and cost-treatment rules against contract terms',
      'Run recurring billing and labor-charge checks before submission cycles',
      'Capture ethics and disclosure events with review, approval, and audit trails',
    ],
    outcomes: [
      'Lower risk of disallowed costs and repayment actions',
      'Improved readiness for pre-award and post-award audits',
      'Stronger legal defensibility for ethics and disclosure obligations',
    ],
    sources: [
      { label: 'Defense Contract Audit Agency', href: 'https://www.dcaa.mil/' },
      { label: 'DOJ False Claims Act', href: 'https://www.justice.gov/civil/false-claims-act' },
    ],
  },
  'dod-contracts-compliance/pursuit-readiness': {
    summary:
      'Pursuit readiness turns eligibility into a first award: capability statement, DSBS, SAM opportunity watch, APEX counseling, and a subcontract-first past-performance path.',
    businessValue:
      'New IT consultancies win faster by chasing notices they can actually perform and by building past performance through primes instead of cold prime bids.',
    capabilities: [
      'Capability-statement and DSBS profile tracking',
      'SAM.gov saved-search cadence for DoD IT NAICS',
      'APEX Accelerator and subcontract-first pursuit checklist',
    ],
    workflow: [
      'Publish a one-page capability statement with UEI/CAGE once SAM is Active',
      'Watch SAM for DoD 541512 sources-sought and set-asides that are FCI-only until Gate 2 is ready',
      'Engage APEX and target primes who already hold DoD IT vehicles',
    ],
    outcomes: [
      'Fewer no-bid surprises from missing CAGE or CMMC Level 1 records',
      'Faster past performance via subcontracting',
      'A documented weekly opportunity cadence leadership can inspect',
    ],
    sources: [
      { label: 'SAM.gov opportunities', href: 'https://sam.gov/content/opportunities' },
      { label: 'DoD OSBP', href: 'https://business.defense.gov/' },
    ],
  },
  'education-compliance/campus-restricted-party-screening': {
    summary:
      'Campus restricted-party screening runs OFAC SDN / CAPTA checks on visiting scholars, MOUs, foreign universities, and shipments, then keeps cleared parties on monitoring.',
    businessValue:
      'Research offices stop treating a one-time clear as permanent and can show an auditor why a collaboration was allowed to proceed.',
    capabilities: [
      'Person and entity screens with purpose recorded (scholar, MOU, shipment)',
      'Deep link into the existing OFAC screening console',
      'Ongoing monitoring for parties that remain on campus or under an active agreement',
    ],
    workflow: [
      'Identify the person or legal entity before access, signature, or shipment',
      'Screen in OFAC, escalate matches, and record the decision',
      'Enroll cleared parties that stay in scope on list-change alerts',
    ],
    outcomes: [
      'Fewer collaborations that start before a sanctions check',
      'Evidence that matches Visual Compliance-style campus SOPs',
      'One screening stack shared with the rest of CompliCore',
    ],
    sources: [
      { label: 'OFAC compliance', href: 'https://ofac.treasury.gov/compliance' },
    ],
  },
  'education-compliance/research-export-and-cui': {
    summary:
      'Research export and CUI classify FCI vs CUI vs export-controlled work and reuse the DoD console for 800-171 / CMMC evidence when a grant requires it.',
    businessValue:
      'Campuses avoid a second CUI workbench. Export-control and research-security staff use the same gates as DoD contractors.',
    capabilities: [
      'Activity classification before foreign-national access or cloud hosting',
      'Deemed-export / US-person access tracking',
      'Deep link to DoD contracts compliance for SPRS, SSP, and CMMC',
    ],
    workflow: [
      'Classify the project from the grant or contract clauses',
      'Document who can reach the lab, enclave, or source code',
      'Complete DoD console evidence only when CUI or CMMC is in scope',
    ],
    outcomes: [
      'CUI tooling appears when the clause requires it, not by default',
      'Export-control decisions sit next to the screening record',
      'NIST 800-171 evidence is not duplicated in a campus-only UI',
    ],
    sources: [
      { label: 'DoD CMMC program', href: 'https://dodcio.defense.gov/cmmc/' },
      { label: 'NIST SP 800-171', href: 'https://csrc.nist.gov/pubs/sp/800/171/r3/final' },
    ],
  },
  'education-compliance/hecvat-and-vendor-trust': {
    summary:
      'HECVAT and vendor trust collect official EDUCAUSE Full/Lite packets from EdTech vendors and publish NextCompli’s campus assurance pack. The questionnaire is not copied into the product.',
    businessValue:
      'Procurement and CISO reviewers get a Trust Center answer instead of an ad-hoc PDF dump, and institutions can inventory their own EdTech vendors the same way.',
    capabilities: [
      'Link to the official EDUCAUSE HECVAT 4 toolkit',
      'Vendor inventory for HECVAT, SOC 2, and DPA status',
      'Public NextCompli campus assurance pack on /trust',
    ],
    workflow: [
      'Download HECVAT Full or Lite from EDUCAUSE',
      'Store completed vendor workbooks in the institution’s evidence store',
      'Share /trust and the campus pack for NextCompli diligence',
    ],
    outcomes: [
      'No forked HECVAT text that would require an EDUCAUSE GRC license',
      'Faster campus vendor reviews',
      'A public pack reviewers can bookmark',
    ],
    sources: [
      { label: 'EDUCAUSE HECVAT toolkit', href: 'https://www.educause.edu/higher-education-community-vendor-assessment-toolkit' },
    ],
  },
  'education-compliance/ferpa-and-glba-program': {
    summary:
      'FERPA and GLBA program hygiene names owners for the annual notice, 45-day records-access path, 34 CFR 99.31 disclosure log, and Title IV Safeguards WISP.',
    businessValue:
      'Registrar and aid offices keep their systems of record. NextCompli tracks who owns the program so GLBA / FERPA findings are not orphaned.',
    capabilities: [
      'Annual FERPA notice owner and URL',
      '45-day access runbook without storing education records',
      'GLBA WISP pointer for Title IV schools (16 CFR 314)',
    ],
    workflow: [
      'Name the FERPA notice and disclosure-log owners',
      'Point at the access-request runbook',
      'Attach the GLBA WISP and FSA cyber contact for Title IV institutions',
    ],
    outcomes: [
      'Program gaps show up as checklist items, not missing case files',
      'Student PII stays out of this console',
      'Title IV cyber findings have a named owner',
    ],
    sources: [
      { label: 'Student Privacy Policy Office', href: 'https://studentprivacy.ed.gov/' },
      { label: 'FSA GLBA cybersecurity update', href: 'https://fsapartners.ed.gov/knowledge-center/library/electronic-announcements/2023-02-09/updates-gramm-leach-bliley-act-cybersecurity-requirements' },
    ],
  },
};

const PRODUCT_SUBDETAIL_CONTENT: ProductSubDetailContent[] = PRODUCT_DETAIL_CONTENT.flatMap((product) =>
  product.highlights.map((highlight) => {
    const subSlug = toProductSubSlug(product.slug, highlight);
    const contentKey = `${product.slug}/${subSlug}`;
    const laneLabel = highlight.toLowerCase();
    const contentOverride = PRODUCT_SUBITEM_CONTENT_OVERRIDES[contentKey];
    const referenceSources = PRODUCT_SUBITEM_REFERENCE_SOURCES[product.slug] ?? [];
    const mergedSources = [
      ...product.sources,
      ...referenceSources,
      ...PRODUCT_SUBITEM_RISK_SOURCE_REFERENCES,
      ...(contentOverride?.sources ?? []),
    ];
    const uniqueSources = mergedSources.filter(
      (source, index) => mergedSources.findIndex((candidate) => candidate.href === source.href) === index,
    );

    return {
      id: `${product.slug}-${subSlug}`,
      parentSlug: product.slug,
      parentLabel: product.label,
      subSlug,
      label: highlight,
      title: highlight,
      to: websitePath(`/products/${product.slug}/${subSlug}`),
      summary:
        contentOverride?.summary ??
        `${highlight} operationalizes ${product.label} into a measurable, evidence-ready delivery lane with explicit controls, source traceability, and decision support.`,
      businessValue:
        contentOverride?.businessValue ??
        `Teams reduce manual interpretation risk by standardizing how ${laneLabel} is configured, reviewed, and escalated across operations and engineering workflows. This improves decision consistency, audit readiness, and release confidence without forcing tool sprawl.`,
      businessRisk:
        contentOverride?.businessRisk ??
        buildProductSubitemBusinessRisk(product.slug, highlight),
      capabilities: contentOverride?.capabilities ?? [
        `Control surface dedicated to ${laneLabel} with consistent route-level behavior and source attribution`,
        'Structured output blocks for business value, capabilities, workflow steps, and outcomes',
        'Reference links to official source systems and implementation guidance',
      ],
      workflow: contentOverride?.workflow ?? [
        'Define screening or review objective and bind required source dependencies',
        'Execute configured checks with traceable analyst or system actions',
        'Publish decision-ready results and capture evidence for audit and follow-up',
      ],
      outcomes: contentOverride?.outcomes ?? [
        'Faster decision throughput with less ambiguity in escalation paths',
        'Higher consistency between policy intent and operational execution',
        'Improved audit defensibility through explicit, repeatable evidence trails',
      ],
      sources: uniqueSources,
    };
  }),
);

const PRODUCT_LABEL_BY_SLUG: Record<string, string> = PRODUCT_DETAIL_CONTENT.reduce((acc, item) => {
  acc[item.slug] = item.label;
  return acc;
}, {} as Record<string, string>);

const PRODUCT_SUBLABEL_BY_KEY: Record<string, string> = PRODUCT_SUBDETAIL_CONTENT.reduce((acc, item) => {
  acc[`${item.parentSlug}/${item.subSlug}`] = item.label;
  return acc;
}, {} as Record<string, string>);

const PRODUCT_SLIDEOUT_GROUPS: SlideoutGroup[] = [
  {
    header: 'By job',
    items: [
      {
        id: 'job-ofac',
        to: websitePath('/products/ofac-compliance'),
        label: 'Screen a name',
        summary: 'Start with OFAC blocked-party checks and license context before a payment or partnership moves.',
        subItems: [
          { label: 'OFAC SDN & General Licenses', to: websitePath('/products/ofac-sdn-general-licenses') },
          { label: 'CAPTA + Consolidated Lists', to: websitePath('/products/capta-consolidated-lists') },
        ],
      },
      {
        id: 'job-government',
        to: websitePath('/products/dod-contracts-compliance'),
        label: 'Run government homework',
        summary: 'Company, project, and CMMC Level 1 for everyday contract papers (FCI). Extra-secret CUI tools appear only when a notice requires them. NextCompli does not host CUI.',
        subItems: [
          { label: 'Entity Eligibility', to: websitePath('/products/dod-contracts-compliance/entity-eligibility') },
          { label: 'Cybersecurity', to: websitePath('/products/dod-contracts-compliance/cybersecurity') },
        ],
      },
      {
        id: 'job-website',
        to: websitePath('/products/website-compliance'),
        label: 'Check a public website',
        summary: 'Privacy, accessibility, email authenticity, and public-site checks for the pages customers already see.',
        subItems: [
          { label: 'Privacy & Data Governance', to: websitePath('/products/website-compliance/privacy-and-data-governance') },
          { label: 'Accessibility & User Experience', to: websitePath('/products/website-compliance/accessibility-and-user-experience') },
        ],
      },
      {
        id: 'job-cloud',
        to: websitePath('/products/cloud-compliance'),
        label: 'Check the cloud',
        summary: 'A written checklist for the cloud account the client owns. A higher plan can add a once-a-day read-only review. This is not the public website scan.',
        subItems: [
          { label: 'Written cloud checklist', to: websitePath('/products/cloud-compliance/written-cloud-checklist') },
          { label: 'The client cloud account', to: websitePath('/products/cloud-compliance/the-client-cloud-account') },
        ],
      },
      {
        id: 'job-organization',
        to: websitePath('/products/business-creation-compliance'),
        label: 'Legal Entity Formation',
        summary: 'Name the organization first. A business, a nonprofit, and a campus all start here. NextCompli writes the steps and does not file them with the state.',
        subItems: [
          { label: 'Business, nonprofit, or campus', to: websitePath('/products/business-creation-compliance/business-nonprofit-or-campus') },
          { label: 'A checklist, not a filing desk', to: websitePath('/products/business-creation-compliance/a-checklist-not-a-filing-desk') },
        ],
      },
      {
        id: 'job-wills',
        to: websitePath('/products/wills-trusts-compliance'),
        label: 'Wills & Trusts',
        summary: 'Name the person first. The answers become a checklist for a will or a revocable trust. A lawyer writes the papers. NextCompli does not.',
        subItems: [
          { label: 'Will or revocable trust', to: websitePath('/products/wills-trusts-compliance/will-or-revocable-trust') },
          { label: 'A checklist, not a law office', to: websitePath('/products/wills-trusts-compliance/a-checklist-not-a-law-office') },
        ],
      },
      {
        id: 'job-team',
        to: websitePath('/subscribe'),
        label: 'Invite the team',
        summary: 'Pay for a plan, then the subscription admin invites teammates. People live on the subscription, not in NextCompli access management.',
        subItems: [
          { label: 'Plans and pricing', to: websitePath('/plans') },
          { label: 'Request a demo', to: DEMO_REQUEST_DESTINATION },
        ],
      },
    ],
  },
  {
    header: 'Products',
    items: [
      {
        id: 'support-basic',
        to: websitePath('/products/basic'),
        label: 'CompliCore (Starting at $99/month)',
        summary: 'For smaller teams needing fast coverage, CompliCore bundles sanctions, website, cloud, government, education, and organization checklists into one starting plan at $99/month.',
        subItems: [
          { label: 'OFAC Compliance', to: websitePath('/products/ofac-compliance') },
          { label: 'Website Compliance', to: websitePath('/products/website-compliance') },
          { label: 'Cloud Compliance', to: websitePath('/products/cloud-compliance') },
          { label: 'Government Compliance', to: websitePath('/products/dod-contracts-compliance') },
          { label: 'Legal Entity Formation', to: websitePath('/products/business-creation-compliance') },
          { label: 'Wills & Trusts', to: websitePath('/products/wills-trusts-compliance') },
          { label: 'Education Compliance', to: websitePath('/products/education-compliance') },
          { label: 'AI Compliance', to: websitePath('/products/ai-compliance') },
          { label: 'Pursuit CRM', to: websitePath('/products/pursuit') },
          { label: 'Research Console', to: CONSOLE_RESEARCH_PATH },
        ],
      },
      {
        id: 'support-advanced',
        to: websitePath('/products/advanced'),
        label: 'CompliAdvantage (Starting at $999/month)',
        summary: 'For teams that outgrow manual review, CompliAdvantage adds real-time monitoring and incident alerts to scale response without adding analyst overhead.',
        subItems: [
          { label: 'Everything in CompliCore', to: websitePath('/products/basic') },
          { label: 'Live Environment Monitoring', to: websitePath('/products/live-environment-monitoring') },
          { label: 'Real-Time Alerts', to: websitePath('/products/real-time-alerts') },
          { label: 'Usage-based Pricing Plans', to: websitePath('/products/usage-based-pricing-plans') },
        ],
      },
      {
        id: 'support-enterprise',
        to: websitePath('/products/enterprise'),
        label: 'CompliComplete (Contact for estimate)',
        summary: 'For complex organizations with strict oversight, CompliComplete adds embedded experts and executive reporting. Each client receives a branded program name (e.g., ATTCompli).',
        subItems: [
          { label: 'Everything in CompliAdvantage', to: websitePath('/products/advanced') },
          { label: 'Embedded Consultants', to: websitePath('/products/embedded-consultants') },
          { label: 'Executive Compliance Reporting', to: websitePath('/products/executive-compliance-reporting') },
          { label: 'Scheduled Audits', to: websitePath('/products/scheduled-audits') },
        ],
      },
    ],
  },
  {
    header: 'Services',
    items: [
      {
        id: 'service-ofac-compliance',
        to: websitePath('/products/ofac-compliance'),
        label: 'OFAC Compliance',
        summary: 'For sanctions teams managing blocked-party risk, OFAC coverage combines SDN checks with license context to reduce false escalations.',
        subItems: [
          { label: 'OFAC SDN & General Licenses', to: websitePath('/products/ofac-sdn-general-licenses') },
          { label: 'CAPTA + Consolidated Lists', to: websitePath('/products/capta-consolidated-lists') },
          { label: 'Continuous Monitoring', to: websitePath('/products/continuous-monitoring') },
          { label: 'Real-time + Batch Screening', to: websitePath('/products/real-time-batch-screening') },
        ],
      },
      {
        id: 'service-cloud-compliance',
        to: websitePath('/products/cloud-compliance'),
        label: 'Cloud Compliance',
        summary: 'For the cloud account the client owns. CompliCore is the written checklist. A higher plan can read that account once a day after the client grants a narrow role.',
        subItems: [
          { label: 'Written cloud checklist', to: websitePath('/products/cloud-compliance/written-cloud-checklist') },
          { label: 'The client cloud account', to: websitePath('/products/cloud-compliance/the-client-cloud-account') },
          { label: 'Once-a-day read on a higher plan', to: websitePath('/products/cloud-compliance/once-a-day-read-on-a-higher-plan') },
        ],
      },
      {
        id: 'service-organization',
        to: websitePath('/products/business-creation-compliance'),
        label: 'Legal Entity Formation',
        summary: 'For a new business, nonprofit, or campus. The questions become the filing, tax, license, and report steps. NextCompli does not send those papers to a government office.',
        subItems: [
          { label: 'Business, nonprofit, or campus', to: websitePath('/products/business-creation-compliance/business-nonprofit-or-campus') },
          { label: 'One question at a time', to: websitePath('/products/business-creation-compliance/one-question-at-a-time') },
          { label: 'A checklist, not a filing desk', to: websitePath('/products/business-creation-compliance/a-checklist-not-a-filing-desk') },
        ],
      },
      {
        id: 'service-wills-trusts',
        to: websitePath('/products/wills-trusts-compliance'),
        label: 'Wills & Trusts',
        summary: 'For a will or a revocable trust. The questions become the checklist. A lawyer licensed in that state writes and reviews the papers. NextCompli does not write them and does not file them.',
        subItems: [
          { label: 'Will or revocable trust', to: websitePath('/products/wills-trusts-compliance/will-or-revocable-trust') },
          { label: 'One question at a time', to: websitePath('/products/wills-trusts-compliance/one-question-at-a-time') },
          { label: 'A checklist, not a law office', to: websitePath('/products/wills-trusts-compliance/a-checklist-not-a-law-office') },
        ],
      },
      {
        id: 'service-website-compliance',
        to: websitePath('/products/website-compliance'),
        label: 'Website Compliance',
        summary: 'For legal and digital teams, website compliance covers privacy, accessibility, website security, email authenticity, and Zero Trust checks of cloud, containers, git, and the public site — with deeper coverage on higher plans.',
        subItems: [
          { label: 'Privacy & Data Governance', to: websitePath('/products/website-compliance/privacy-and-data-governance') },
          { label: 'Accessibility & User Experience', to: websitePath('/products/website-compliance/accessibility-and-user-experience') },
          { label: 'Security & Risk Protection', to: websitePath('/products/website-compliance/security-and-risk-protection') },
          { label: 'Email Authenticity & Deliverability', to: websitePath('/products/website-compliance/email-authenticity-and-deliverability') },
          { label: 'Zero Trust & System Hardening', to: websitePath('/products/website-compliance/zero-trust-and-system-hardening') },
          { label: 'AI/ML Zero Trust & Guardrails', to: websitePath('/products/website-compliance/ai-ml-zero-trust-and-guardrails') },
          { label: 'Intellectual Property & DMCA', to: websitePath('/products/website-compliance/intellectual-property-and-dmca') },
          { label: 'Marketing, Terms & Consumer Rules', to: websitePath('/products/website-compliance/marketing-terms-and-consumer-rules') },
        ],
      },
      {
        id: 'service-dod-contracts-compliance',
        to: websitePath('/products/dod-contracts-compliance'),
        label: 'Government Compliance',
        summary: 'For defense contractors and new IT consultancies, government compliance spans company/project/contract taxonomy, CMMC Level 1 for FCI, and local, county, state, and federal vendor lists. CUI tooling appears only when a notice requires it.',
        subItems: [
          { label: 'Entity Eligibility', to: websitePath('/products/dod-contracts-compliance/entity-eligibility') },
          { label: 'Cybersecurity', to: websitePath('/products/dod-contracts-compliance/cybersecurity') },
          { label: 'Contract Compliance', to: websitePath('/products/dod-contracts-compliance/contract-compliance') },
          { label: 'Supply Chain Assurance', to: websitePath('/products/dod-contracts-compliance/supply-chain-assurance') },
          { label: 'Financial & Ethics Controls', to: websitePath('/products/dod-contracts-compliance/financial-and-ethics-controls') },
          { label: 'Pursuit Readiness', to: websitePath('/products/dod-contracts-compliance/pursuit-readiness') },
        ],
      },
      {
        id: 'service-ai-compliance',
        to: websitePath('/products/ai-compliance'),
        label: 'AI Compliance',
        summary: 'For teams already using AI tools. The checklist records the inventory, the use rule, and the owner. It does not certify NIST AI RMF or ISO/IEC 42001.',
        subItems: [
          { label: 'Inventory and use rule', to: websitePath('/products/ai-compliance/inventory-and-use-rule') },
          { label: 'Vendor review and human check', to: websitePath('/products/ai-compliance/vendor-review-and-human-check') },
          { label: 'Impact and scope', to: websitePath('/products/ai-compliance/impact-and-scope') },
        ],
      },
      {
        id: 'service-education-compliance',
        to: websitePath('/products/education-compliance'),
        label: 'Education Compliance',
        summary: 'For research offices and campus CISOs, Education Compliance packages restricted-party screening, CUI evidence, HECVAT vendor trust, and FERPA / GLBA program hygiene.',
        subItems: [
          { label: 'Campus Restricted-Party Screening', to: websitePath('/products/education-compliance/campus-restricted-party-screening') },
          { label: 'Research Export and CUI', to: websitePath('/products/education-compliance/research-export-and-cui') },
          { label: 'HECVAT and Vendor Trust', to: websitePath('/products/education-compliance/hecvat-and-vendor-trust') },
          { label: 'FERPA and GLBA Program', to: websitePath('/products/education-compliance/ferpa-and-glba-program') },
        ],
      },
    ],
  },
  {
    header: 'Features',
    items: [
      {
        id: 'feature-prism',
        to: websitePath('/products/screening-api-platform'),
        label: 'Prism',
        summary: 'Prism takes structured text inputs (menu summaries and subitems), compiles them into a scene instruction, applies style/runtime controls, and runs generation tasks per scene. Each task emits runtime metadata (run directory, progress, log file), and outputs can be copied into website image assets with a manifest that preserves traceability from text prompt to final raster image.',
        subItems: [
          { label: 'Text decomposition', to: websitePath('/products/screening-api-platform/text-decomposition') },
          { label: 'Cinematic framing', to: websitePath('/products/screening-api-platform/cinematic-framing') },
          { label: 'Generation output artifacts', to: websitePath('/products/screening-api-platform/generation-output-artifacts') },
        ],
      },
      {
        id: 'feature-dashboards',
        to: websitePath('/products/dashboards'),
        label: 'Dashboards',
        summary: 'For managers and analysts, dashboards show source health, risk scoring, and compliance posture in one view to speed decisions.',
        subItems: [
          { label: 'Data Source Health Monitoring', to: websitePath('/products/dashboards/data-source-health-monitoring') },
          { label: 'Risk-scoring', to: websitePath('/products/dashboards/risk-scoring') },
          { label: 'Compliance Posture Overview', to: websitePath('/products/dashboards/executive-kpi-rollups') },
        ],
      },
      {
        id: 'feature-live-monitoring',
        to: websitePath('/products/live-environment-monitoring'),
        label: 'Live Environment Monitoring',
        summary: 'For teams that need both speed and coverage, live monitoring blends immediate checks with scheduled scans and audit-ready workflow history.',
        subItems: [
          { label: 'Real-time + Batch Screening', to: websitePath('/products/real-time-batch-screening/low-latency-api-checks') },
          { label: 'Audit Trail + Alert Workflow', to: websitePath('/products/audit-trail-alert-workflow') },
          { label: 'Monitoring Operations Console', to: websitePath('/products/real-time-batch-screening/bulk-screening-jobs') },
        ],
      },
      {
        id: 'feature-real-time-alerts',
        to: websitePath('/products/real-time-alerts'),
        label: 'Real-Time Alerts',
        summary: 'For incident owners, real-time alerts prioritize policy-sensitive events and route action to the right responder without delay.',
        subItems: [
          { label: 'Alerts & Incident Workflow', to: websitePath('/products/real-time-alerts/alerts-incident-workflow') },
          { label: 'Priority alert pipelines', to: websitePath('/products/real-time-alerts/priority-alert-pipelines') },
          { label: 'Incident response actions', to: websitePath('/products/real-time-alerts/incident-response-actions') },
        ],
      },
      {
        id: 'feature-embedded-consultants',
        to: websitePath('/products/embedded-consultants'),
        label: 'Embedded Consultants',
        summary: 'For teams short on specialist capacity, embedded consultants accelerate implementation while building internal governance capability.',
        subItems: [
          { label: 'Program design guidance', to: websitePath('/products/embedded-consultants/program-design-guidance') },
          { label: 'Execution support with your teams', to: websitePath('/products/embedded-consultants/execution-support-with-your-teams') },
          { label: 'Governance operating model', to: websitePath('/products/embedded-consultants/governance-operating-model') },
        ],
      },
      {
        id: 'feature-scheduled-audits',
        to: websitePath('/products/scheduled-audits'),
        label: 'Scheduled Audits',
        summary: 'For audit-bound teams, scheduled audits monitor local files and public websites with traceable evidence collected throughout the cycle.',
        subItems: [
          { label: 'Liability Compliance Monitoring', to: websitePath('/products/scheduled-audits/liability-compliance-monitoring') },
          { label: 'Audit Evidence Management', to: websitePath('/products/scheduled-audits/audit-evidence-management') },
          { label: 'Case & Audit Workspace', to: websitePath('/products/case-audit-workspace') },
          { label: 'Evidence-linked decision logs', to: CLAIMS_EVIDENCE_DESTINATION },
        ],
      },
      {
        id: 'feature-exec-reporting',
        to: websitePath('/products/executive-compliance-reporting'),
        label: 'Executive Compliance Reporting',
        summary: 'For leadership stakeholders, executive reporting turns compliance activity into concise risk, trend, and ownership updates.',
        subItems: [
          { label: 'Leadership dashboard snapshots', to: websitePath('/products/executive-compliance-reporting/leadership-dashboard-snapshots') },
          { label: 'Board-ready compliance reporting', to: websitePath('/products/executive-compliance-reporting/board-ready-compliance-reporting') },
          { label: 'Trend and KPI rollups', to: websitePath('/products/executive-compliance-reporting/compliance-trend-commentary') },
        ],
      },
      {
        id: 'feature-research-reports',
        to: websitePath('/products/investigative-reporting'),
        label: 'Research Reports & Investigations',
        summary: 'For due-diligence and investigations teams, one research-report workflow manages people and company background findings with evidence-linked outcomes.',
        subItems: [
          { label: 'Submit people and entities', to: websitePath('/products/investigative-reporting/submit-people-and-entities') },
          { label: 'Company and people background dossier', to: websitePath('/products/investigative-reporting/company-and-people-background-dossiers') },
          { label: 'Evidence-linked investigation package', to: websitePath('/products/investigative-reporting/evidence-linked-investigation-package') },
          { label: 'Research Reports workspace', to: websitePath('/products/investigative-reporting/research-reports-workspace') },
        ],
      },
      {
        id: 'feature-usage-pricing',
        to: websitePath('/products/usage-based-pricing-plans'),
        label: 'Usage-based Pricing Plans',
        summary: 'For buyers evaluating total cost, usage-based pricing makes each tier clear with visible entry points and enterprise scaling options.',
        subItems: [
          { label: 'Basic starting at $99/month', to: websitePath('/products/usage-based-pricing-plans/basic-starting-at-99-month') },
          { label: 'Advanced starting at $999/month', to: websitePath('/products/usage-based-pricing-plans/advanced-starting-at-999-month') },
          { label: 'CompliComplete contact for estimate', to: websitePath('/products/usage-based-pricing-plans/enterprise-contact-for-estimate') },
        ],
      },
    ],
  },
];

const INDUSTRY_CATALOG = [
  'Automotive',
  'Communications',
  'Construction & Real Estate',
  'Consumer Goods',
  'Education',
  'Energy & Utilities',
  'Financial Services',
  'Healthcare',
  'Life Sciences',
  'Manufacturing',
  'Media',
  'Nonprofit',
  'Professional Services',
  'Public Sector',
  'Retail',
  'Technology',
  'Travel, Transportation & Hospitality',
] as const;

type IndustryContentOverride = {
  title?: string;
  heroSubtitle: string;
  ctaLabel: string;
  ctaTo?: string;
  subItems: string[];
};

const INDUSTRY_CONTENT_OVERRIDES: Record<string, IndustryContentOverride> = {
  automotive: {
    title: 'Automotive',
    heroSubtitle: 'Drive a lifetime of loyalty.',
    ctaLabel: 'Explore automotive solutions',
    subItems: ['OEM', 'Dealer/Retailers', 'Captive', 'Software-Defined Vehicle (SDV)', 'Automotive Cloud', 'Automotive Pricing'],
  },
  communications: {
    title: 'Communications',
    heroSubtitle: 'Deliver the connected future, faster.',
    ctaLabel: 'Explore communications solutions',
    subItems: [
      'Unify the Telecom Customer Journey',
      'Accelerate Communication Sales',
      'Simplify Wholesale Transactions',
      'Light Up Fiber Experiences',
      'Fulfill the Perfect Order',
      'Optimize Telecom Customer Service',
    ],
  },
  'construction-and-real-estate': {
    title: 'Engineering, Construction & Real Estate',
    heroSubtitle: 'Achieve operational excellence.',
    ctaLabel: 'Explore engineering, construction, and real estate solutions',
    subItems: ['Real Estate', 'Engineering & Construction', 'Residential & Commercial Services'],
  },
  'consumer-goods': {
    title: 'Consumer Goods',
    heroSubtitle: 'Deliver profitable growth.',
    ctaLabel: 'Explore consumer goods solutions',
    subItems: ['Fast-Moving Consumer Goods', 'Home Durables', 'Distributors', 'Apparel, Footwear, and Accessories', 'Beauty'],
  },
  education: {
    title: 'Education',
    heroSubtitle: 'Campus restricted-party screening, research-export / CUI, HECVAT vendor trust, and FERPA / GLBA program hygiene.',
    ctaLabel: 'Open Education Compliance',
    ctaTo: websitePath('/products/education-compliance'),
    subItems: [
      'Campus Restricted-Party Screening',
      'Research Export and CUI',
      'HECVAT and Vendor Trust',
      'FERPA and GLBA Program',
    ],
  },
  'energy-and-utilities': {
    title: 'Energy & Utilities',
    heroSubtitle: 'Energy-market movement is not a permitted transaction. Screen counterparties and apply current general-license context, including Venezuela GL 52C, before treating a signal as a deal.',
    ctaLabel: 'Open OFAC Compliance',
    ctaTo: websitePath('/products/ofac-compliance'),
    subItems: ['Power & Utilities', 'Oil & Gas'],
  },
  'financial-services': {
    title: 'Financial Services',
    heroSubtitle: 'Empower your customers’ financial success with trusted AI.',
    ctaLabel: 'Explore financial services solutions',
    subItems: [
      'Scale Service',
      'Streamline Onboarding',
      'Personalize Engagement',
      'Modernize Insurance Sales',
      'Next Gen Service for Banking',
      'Next Gen Advisor for Wealth',
    ],
  },
  healthcare: {
    title: 'Healthcare',
    heroSubtitle: 'Accelerate outcomes with AI-powered workflows for Health.',
    ctaLabel: 'Explore healthcare solutions',
    subItems: ['Provider', 'Payer', 'Public Sector Health'],
  },
  'life-sciences': {
    title: 'Life Sciences',
    heroSubtitle: 'Improve clinical and commercial outcomes with AI-powered workflows for Life Sciences.',
    ctaLabel: 'Explore life sciences solutions',
    subItems: ['Pharma', 'MedTech'],
  },
  manufacturing: {
    title: 'Manufacturing',
    heroSubtitle: 'Connect the value chain.',
    ctaLabel: 'Explore manufacturing solutions',
    subItems: ['Modernize Commercial Operations', 'Transform the Service Experience', 'Simplify Partner Engagement'],
  },
  media: {
    title: 'Media',
    heroSubtitle: 'Cultivate loyal audiences faster.',
    ctaLabel: 'Explore media solutions',
    subItems: [
      'Accelerate Advertising Sales',
      'Automate Media and Entertainment Processes',
      'Grow Subscriber Relationships',
      'Maximize Audience Engagement',
      'Streamline Production and Operations',
    ],
  },
  nonprofit: {
    title: 'Nonprofit',
    heroSubtitle: 'Build relationships that drive change.',
    ctaLabel: 'Explore nonprofit solutions',
    subItems: [
      'Fundraising',
      'Marketing and Engagement',
      'Program and Outcome Management',
      'IT Operations for Nonprofit',
      'Grantmaking',
      'Volunteer Management',
    ],
  },
  'professional-services': {
    title: 'Professional Services',
    heroSubtitle: 'Increase client trust.',
    ctaLabel: 'Explore professional services solutions',
    subItems: ['Accounting, Tax, and Audit', 'Consulting', 'Staffing and Recruiting', 'Legal'],
  },
  technology: {
    title: 'Technology',
    heroSubtitle: 'Drive cost-efficient growth with intelligent automated processes.',
    ctaLabel: 'Explore technology solutions',
    subItems: ['Unlock Partner Growth', 'Drive Recruiting Revenue'],
  },
  'travel-transportation-and-hospitality': {
    title: 'Travel, Transportation & Hospitality',
    heroSubtitle: 'Deliver connected experiences.',
    ctaLabel: 'Explore travel, transportation, and hospitality experiences',
    subItems: ['Travel', 'Transportation and Logistics', 'Hospitality'],
  },
};

const INDUSTRY_INSIGHT_IMAGE_IDS: Record<string, number[]> = {
  automotive: [1071, 1072, 1073, 1074],
  communications: [1075, 1076, 1077, 1078],
  'construction-and-real-estate': [1040, 1041, 1042, 1043],
  'consumer-goods': [1080, 1081, 1082, 1083],
  education: [1005, 1006, 1008, 1010],
  'energy-and-utilities': [1011, 1014, 1018, 1020],
  'financial-services': [1024, 1026, 1027, 1033],
  healthcare: [1025, 1035, 1060, 1066],
  'life-sciences': [1003, 1009, 1039, 1057],
  manufacturing: [1029, 1031, 1037, 1050],
  media: [1044, 1045, 1047, 1048],
  nonprofit: [1052, 1053, 1055, 1056],
  'professional-services': [1061, 1062, 1064, 1065],
  'public-sector': [1012, 1016, 1021, 1036],
  retail: [1069, 1070, 1084, 1086],
  technology: [180, 201, 225, 240],
  'travel-transportation-and-hospitality': [28, 42, 52, 88],
};

const INDUSTRY_PRODUCT_IMAGE_IDS: Record<string, number[]> = {
  automotive: [1068, 1070, 1074, 1084],
  communications: [1027, 1048, 1063, 1076],
  'construction-and-real-estate': [1031, 1036, 1043, 1050],
  'consumer-goods': [1060, 1062, 1081, 1083],
  education: [1005, 1010, 1037, 1057],
  'energy-and-utilities': [1011, 1014, 1018, 1020],
  'financial-services': [1024, 1026, 1033, 1064],
  healthcare: [1025, 1035, 1066, 1080],
  'life-sciences': [1003, 1009, 1039, 1055],
  manufacturing: [1029, 1034, 1041, 1052],
  media: [1044, 1045, 1047, 1077],
  nonprofit: [1053, 1056, 1061, 1082],
  'professional-services': [1062, 1065, 1067, 1079],
  'public-sector': [1012, 1016, 1021, 1040],
  retail: [1069, 1084, 1086, 1090],
  technology: [180, 201, 225, 240],
  'travel-transportation-and-hospitality': [28, 42, 52, 88],
};

const DEFAULT_INDUSTRY_INSIGHT_IMAGE_IDS = [1079, 1085, 1087, 1090];
const DEFAULT_INDUSTRY_PRODUCT_IMAGE_IDS = [1035, 1043, 1068, 1072];

const INDUSTRY_DETAIL_CONTENT: IndustryDetailContent[] = INDUSTRY_CATALOG.map((label) => {
  const slug = toSlug(label);
  const override = INDUSTRY_CONTENT_OVERRIDES[slug];
  return {
    slug,
    label,
    title: override?.title ?? `${label} Industry`,
    summary: withBrand(`Industry-specific operating model for ${label} teams on {brand}.`),
    heroSubtitle: override?.heroSubtitle ?? `Industry-specific operating model and modernization path for ${label} teams.`,
    ctaLabel: override?.ctaLabel ?? 'Explore industries',
    ctaTo: override?.ctaTo,
  };
});

const INDUSTRY_SUBDETAIL_CONTENT: IndustrySubDetailContent[] = INDUSTRY_DETAIL_CONTENT.flatMap((industry) => {
  const subItemLabels = INDUSTRY_CONTENT_OVERRIDES[industry.slug]?.subItems ?? [];

  return subItemLabels.map((subItemLabel) => {
    const subSlug = toSlug(subItemLabel);
    const normalizedLabel = subItemLabel.toLowerCase();

    return {
      id: `${industry.slug}-${subSlug}`,
      parentSlug: industry.slug,
      parentLabel: industry.label,
      subSlug,
      label: subItemLabel,
      title: subItemLabel,
      to: websitePath(`/industries/${industry.slug}/${subSlug}`),
      summary: `${subItemLabel} capabilities align ${industry.label} workflows across customer lifecycle, commercial execution, and operational modernization priorities.`,
      pillars: [`${subItemLabel} operating model alignment`, 'Connected customer lifecycle orchestration', 'AI-assisted workflow execution'],
      details: [
        `Use ${subItemLabel} to standardize how ${industry.label} teams manage high-impact workflows with consistent data, service, and engagement orchestration.`,
        `This page defines implementation patterns and rollout checkpoints for ${normalizedLabel} initiatives inside ${industry.label.toLowerCase()} programs.`,
      ],
    };
  });
});

const INDUSTRY_LABEL_BY_SLUG: Record<string, string> = INDUSTRY_DETAIL_CONTENT.reduce((acc, item) => {
  acc[item.slug] = item.label;
  return acc;
}, {} as Record<string, string>);

const INDUSTRY_SUBLABEL_BY_KEY: Record<string, string> = INDUSTRY_SUBDETAIL_CONTENT.reduce((acc, item) => {
  acc[`${item.parentSlug}/${item.subSlug}`] = item.label;
  return acc;
}, {} as Record<string, string>);

const INDUSTRY_SLIDEOUT_ITEMS: SlideoutItem[] = INDUSTRY_DETAIL_CONTENT.map((industry) => ({
  id: `industry-${industry.slug}`,
  to: websitePath(`/industries/${industry.slug}`),
  label: industry.label,
  summary: industry.summary,
  subItems: INDUSTRY_SUBDETAIL_CONTENT.filter((subItem) => subItem.parentSlug === industry.slug).map((subItem) => ({
    label: subItem.label,
    to: subItem.to,
  })),
  ctaLabel: industry.ctaLabel,
  iconKey: INDUSTRY_ICON_KEY_BY_SLUG[industry.slug],
  iconTone: industryTileTone(industry.label),
}));

const inferGatewayBaseUrl = (): string => {
  const configured = (process.env.REACT_APP_GATEWAY_URL || '').trim();
  if (configured) {
    // Prefer IPv4 loopback locally — Cloudflare WARP / IPv6 localhost can stall fetches.
    if (/^https?:\/\/localhost(?::|\/|$)/i.test(configured)) {
      return configured.replace(/localhost/i, '127.0.0.1').replace(/\/$/, '');
    }
    return configured.replace(/\/$/, '');
  }

  if (typeof window === 'undefined') {
    return 'http://127.0.0.1:8000';
  }

  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1';
  if (isLocal) {
    return 'http://127.0.0.1:8000';
  }

  const apexHost = host.replace(/^www\./, '').replace(/^dashboard\./, '').replace(/^metrics\./, '');
  return `https://${apexHost}`;
};

const inferKeycloakAdminUrl = (): string | null => {
  const configuredAdminUrl = (process.env.REACT_APP_KEYCLOAK_ADMIN_URL || '').trim();
  if (configuredAdminUrl) {
    return configuredAdminUrl;
  }

  const issuer = (process.env.REACT_APP_KEYCLOAK_ISSUER || '').trim().replace(/\/$/, '');
  if (!issuer) {
    return null;
  }

  const issuerMatch = issuer.match(/^(https?:\/\/[^\s]+?)\/realms\/([^/\s]+)$/i);
  if (!issuerMatch) {
    return null;
  }

  const issuerBase = issuerMatch[1].replace(/\/$/, '');
  const realm = issuerMatch[2];
  return `${issuerBase}/admin/${realm}/console/`;
};

const GATEWAY_BASE_URL = inferGatewayBaseUrl();
const KEYCLOAK_ADMIN_URL = inferKeycloakAdminUrl();
const GATEWAY_CHAT_URL = `${GATEWAY_BASE_URL}/chat`;
const AUTH_ACCEPT_INVITE_URL = `${GATEWAY_BASE_URL}/api/v1/auth/invitations/accept`;
const AUTH_FORGOT_PASSWORD_URL = `${GATEWAY_BASE_URL}/api/v1/auth/forgot-password`;
const AUTH_RESET_PASSWORD_URL = `${GATEWAY_BASE_URL}/api/v1/auth/reset-password`;
const NEWSLETTER_SIGNUP_URL = (process.env.REACT_APP_NEWSLETTER_SIGNUP_URL || '/api/marketing/newsletter-signups').trim();
const NEWSLETTER_AUDIENCE = (process.env.REACT_APP_NEWSLETTER_AUDIENCE || 'nextcompli-product-updates').trim();
const SALES_CRM_FLOW_URL = (process.env.REACT_APP_CRM_LEAD_FLOW_URL || `${GATEWAY_BASE_URL}/api/crm/leads`).trim();

const PROMPT_INJECTION_PATTERNS = [
  /ignore\s+(all|previous|prior)\s+instructions/i,
  /reveal\s+(the\s+)?system\s+prompt/i,
  /show\s+(me\s+)?(your\s+)?hidden\s+instructions/i,
  /developer\s+message/i,
  /jailbreak/i,
  /bypass\s+guardrails/i,
];

type AssistantRequestOptions = {
  prompt: string;
  sessionId: string;
  contextLines?: string[];
  fallbackReply: string;
};

const sanitizeAssistantPrompt = (rawPrompt: string, maxLength = 1200): string =>
  Array.from(rawPrompt)
    .map((character) => {
      const code = character.charCodeAt(0);
      return code < 32 || code === 127 ? ' ' : character;
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength);

const isPromptPotentiallyUnsafe = (prompt: string): boolean =>
  PROMPT_INJECTION_PATTERNS.some((pattern) => pattern.test(prompt));

const requestAssistantReply = async ({
  prompt,
  sessionId,
  contextLines = [],
  fallbackReply,
}: AssistantRequestOptions): Promise<string> => {
  const sanitizedPrompt = sanitizeAssistantPrompt(prompt);
  if (!sanitizedPrompt) {
    return fallbackReply;
  }

  if (isPromptPotentiallyUnsafe(sanitizedPrompt)) {
    return 'I can help with product, implementation, and compliance workflows, but I can’t assist with bypassing safety rules or exposing hidden system instructions.';
  }

  const guardedPrompt = [
    'You are the NextCompli assistant. Provide concise, practical guidance and avoid requesting or exposing secrets or personal data.',
    ...contextLines,
    `User question: ${sanitizedPrompt}`,
  ].join('\n');

  try {
    const response = await fetch(GATEWAY_CHAT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt: guardedPrompt,
        session_id: sessionId,
      }),
    });

    if (!response.ok) {
      return fallbackReply;
    }

    const payload = (await response.json()) as GatewayChatResponse;
    const reply = typeof payload.reply === 'string' ? payload.reply.trim() : '';
    return reply || fallbackReply;
  } catch {
    return fallbackReply;
  }
};

const ARCHITECTURE_SECTIONS = [
  {
    title: 'Deployment topology',
    bullets: [
      'Single-region, multi-zone baseline for controlled rollout and lower operational complexity.',
      'Multi-region option for resilience and data residency alignment (for example US-East, US-West, EU-West).',
      'Consistent deployment model from local Docker to Kubernetes clusters and cloud-managed runtime.',
    ],
  },
  {
    title: 'Gateway and API boundary',
    bullets: [
      'One gateway endpoint with auth, request governance, and downstream screening-service fan-out.',
      'Normalized response contracts reduce client-side integration drift across providers.',
      'Rate limiting, schema validation, and explicit error contracts reduce API misuse risk.',
    ],
  },
  {
    title: 'Security controls',
    bullets: [
      'Encryption in transit and at rest with managed key controls in supported environments.',
      'JWT/API-key access patterns, role-based permissions, and least-privilege service identities.',
      'Threat-model and secure-development practices mapped to recognized control frameworks.',
    ],
  },
  {
    title: 'Data governance and evidence',
    bullets: [
      'Source lineage, processing traceability, and immutable audit events for review workflows.',
      'Retention and deletion controls aligned to policy and contractual requirements.',
      'Evidence packages support customer due diligence and external audit readiness.',
    ],
  },
  {
    title: 'Observability and operations',
    bullets: [
      'Metrics, logs, and traces for service health, latency, and screening workflow quality.',
      'Runbook-driven incident response and post-incident corrective action tracking.',
      'SLO-oriented operational reviews to prioritize reliability improvements.',
    ],
  },
  {
    title: 'Resilience and recovery',
    bullets: [
      'Failure-domain isolation, backup strategy, and staged recovery runbooks.',
      'Disaster recovery drills on a planned cadence with documented outcomes.',
      'Progressive rollout controls and rollback levers to reduce deployment risk.',
    ],
  },
];

const ARCHITECTURE_VISUAL_CARDS = [
  {
    id: 'trusted-apps',
    badge: 'Events & Insights',
    title: 'Catch all the latest platform and AI highlights.',
    description:
      'Keep your teams current with release briefings, rollout playbooks, and operating guidance for AI-powered compliance programs.',
    cta: 'Stream it now',
    to: websitePath('/platform'),
    image: 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'Conference attendees celebrating and taking a group selfie',
  },
  {
    id: 'data-everywhere',
    badge: 'Community',
    title: 'Collaborate with customers, partners, and product experts.',
    description:
      'Get practical implementation help, integration patterns, and deployment checklists from experienced operators.',
    cta: 'Join the community',
    to: websitePath('/products/continuous-monitoring'),
    image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'Two professionals collaborating and smiling',
  },
  {
    id: 'agentic-workforce',
    badge: 'Ecosystem',
    title: 'Get the most out of your stack with partners, apps, and agents.',
    description:
      'Extend your workflows with trusted integrations, vetted apps, and reusable AI agents built for production operations.',
    cta: 'Discover integrations',
    to: websitePath('/customers'),
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'Product and analytics tools on a modern screen',
  },
];

const ARCHITECTURE_REFERENCE_LINKS = [
  { label: 'AWS Well-Architected Framework', href: 'https://docs.aws.amazon.com/wellarchitected/latest/framework/welcome.html' },
  { label: 'AWS Security Reference Architecture', href: 'https://docs.aws.amazon.com/prescriptive-guidance/latest/security-reference-architecture/welcome.html' },
  { label: 'NIST Cybersecurity Framework 2.0', href: 'https://www.nist.gov/cyberframework' },
  { label: 'NIST SP 800-53 Rev. 5', href: 'https://csrc.nist.gov/pubs/sp/800/53/r5/upd1/final' },
  { label: 'OWASP API Security Top 10 (2023)', href: 'https://owasp.org/API-Security/editions/2023/en/0x11-t10/' },
  { label: 'OWASP ASVS', href: 'https://owasp.org/www-project-application-security-verification-standard/' },
  { label: 'Kubernetes Security Overview', href: 'https://kubernetes.io/docs/concepts/security/overview/' },
  { label: 'OpenTelemetry Documentation', href: 'https://opentelemetry.io/docs/' },
  { label: 'OpenAPI Specification', href: 'https://spec.openapis.org/oas/latest.html' },
  { label: 'RFC 9457 Problem Details for HTTP APIs', href: 'https://www.rfc-editor.org/rfc/rfc9457' },
];

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout, user, accessToken } = useAuth();
  const chromeHydratedForTokenRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    installUxTelemetry();
    installPostludeVisit();
    installSiteTraffic();
  }, []);
  React.useEffect(() => {
    noteLocation(location.pathname);
    notePostludeVisit('page', { path: location.pathname });
    noteSitePageview(location.pathname);
  }, [location.pathname]);
  const siteHeaderRef = React.useRef<HTMLElement | null>(null);
  const languageMenuRef = React.useRef<HTMLDivElement | null>(null);
  const themeMenuRef = React.useRef<HTMLDivElement | null>(null);
  const userMenuRef = React.useRef<HTMLDivElement | null>(null);
  const [language, setLanguage] = React.useState<WebsiteLanguage>(() => detectInitialWebsiteLanguage());
  const [websiteLogoPath, setWebsiteLogoPath] = React.useState<string>(() => loadWebsiteLogo());
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = React.useState(false);
  const [isThemeMenuOpen, setIsThemeMenuOpen] = React.useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);
  const [appearancePreferences, setAppearancePreferences] = React.useState<AppearancePreferences>(
    () => loadAppearancePreferences(),
  );
  const [isProductsPinnedOpen, setIsProductsPinnedOpen] = React.useState(false);
  const [isProductsHovered, setIsProductsHovered] = React.useState(false);
  const [isIndustriesPinnedOpen, setIsIndustriesPinnedOpen] = React.useState(false);
  const [isIndustriesHovered, setIsIndustriesHovered] = React.useState(false);
  const [isCustomersPinnedOpen, setIsCustomersPinnedOpen] = React.useState(false);
  const [isCustomersHovered, setIsCustomersHovered] = React.useState(false);
  const [isLearningPinnedOpen, setIsLearningPinnedOpen] = React.useState(false);
  const [isLearningHovered, setIsLearningHovered] = React.useState(false);
  const [isSupportPinnedOpen, setIsSupportPinnedOpen] = React.useState(false);
  const [isSupportHovered, setIsSupportHovered] = React.useState(false);
  const [isCompanyPinnedOpen, setIsCompanyPinnedOpen] = React.useState(false);
  const [isCompanyHovered, setIsCompanyHovered] = React.useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = React.useState(false);
  const allProductMenuGroups = React.useMemo(
    () => localizeSlideoutGroups(PRODUCT_SLIDEOUT_GROUPS, language),
    [language],
  );
  const allProductMenuItems = React.useMemo(() => allProductMenuGroups.flatMap((group) => group.items), [allProductMenuGroups]);
  const allIndustryMenuItems = React.useMemo(
    () => localizeSlideoutItems(INDUSTRY_SLIDEOUT_ITEMS, language),
    [language],
  );
  const allCustomerMenuItems = React.useMemo(
    () => localizeSlideoutItems(CUSTOMER_SLIDEOUT_ITEMS, language),
    [language],
  );
  const allLearningMenuItems = React.useMemo(
    () => (language === 'es' ? LEARNING_SLIDEOUT_ITEMS_ES : LEARNING_SLIDEOUT_ITEMS),
    [language],
  );
  const allSupportMenuItems = React.useMemo(
    () => (language === 'es' ? SUPPORT_SLIDEOUT_ITEMS_ES : SUPPORT_SLIDEOUT_ITEMS),
    [language],
  );
  const allCompanyMenuItems = React.useMemo(
    () => (language === 'es' ? COMPANY_SLIDEOUT_ITEMS_ES : COMPANY_SLIDEOUT_ITEMS),
    [language],
  );
  const [activeProductItemId, setActiveProductItemId] = React.useState(allProductMenuItems[0]?.id ?? '');
  const [activeIndustryItemId, setActiveIndustryItemId] = React.useState(allIndustryMenuItems[0]?.id ?? '');
  const [activeCustomerItemId, setActiveCustomerItemId] = React.useState(allCustomerMenuItems[0]?.id ?? '');
  const [activeLearningItemId, setActiveLearningItemId] = React.useState(allLearningMenuItems[0]?.id ?? '');
  const [activeSupportItemId, setActiveSupportItemId] = React.useState(allSupportMenuItems[0]?.id ?? '');
  const [activeCompanyItemId, setActiveCompanyItemId] = React.useState(allCompanyMenuItems[0]?.id ?? '');
  const [activeIndustrySubitemLabel, setActiveIndustrySubitemLabel] = React.useState(allIndustryMenuItems[0]?.subItems[0]?.label ?? '');
  const [activeCustomerSubitemLabel, setActiveCustomerSubitemLabel] = React.useState(allCustomerMenuItems[0]?.subItems[0]?.label ?? '');
  const [activeLearningSubitemLabel, setActiveLearningSubitemLabel] = React.useState(allLearningMenuItems[0]?.subItems[0]?.label ?? '');
  const [activeSupportSubitemLabel, setActiveSupportSubitemLabel] = React.useState(allSupportMenuItems[0]?.subItems[0]?.label ?? '');
  const [activeCompanySubitemLabel, setActiveCompanySubitemLabel] = React.useState(allCompanyMenuItems[0]?.subItems[0]?.label ?? '');
  const [slideoutTop] = React.useState(84);
  const [supportsHoverMenus, setSupportsHoverMenus] = React.useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  });
  const productsMenuRef = React.useRef<HTMLDivElement | null>(null);
  const productsHoverCloseTimeoutRef = React.useRef<number | null>(null);
  const industriesHoverCloseTimeoutRef = React.useRef<number | null>(null);
  const customersHoverCloseTimeoutRef = React.useRef<number | null>(null);
  const learningHoverCloseTimeoutRef = React.useRef<number | null>(null);
  const supportHoverCloseTimeoutRef = React.useRef<number | null>(null);
  const companyHoverCloseTimeoutRef = React.useRef<number | null>(null);
  const isProductsOpen = isProductsPinnedOpen || isProductsHovered;
  const isIndustriesOpen = isIndustriesPinnedOpen || isIndustriesHovered;
  const isCustomersOpen = isCustomersPinnedOpen || isCustomersHovered;
  const isLearningOpen = isLearningPinnedOpen || isLearningHovered;
  const isSupportOpen = isSupportPinnedOpen || isSupportHovered;
  const isCompanyOpen = isCompanyPinnedOpen || isCompanyHovered;
  const closeHeaderOverlays = React.useCallback(() => {
    setIsLanguageMenuOpen(false);
    setIsThemeMenuOpen(false);
    setIsUserMenuOpen(false);
    setIsProductsPinnedOpen(false);
    setIsProductsHovered(false);
    setIsIndustriesPinnedOpen(false);
    setIsIndustriesHovered(false);
    setIsCustomersPinnedOpen(false);
    setIsCustomersHovered(false);
    setIsLearningPinnedOpen(false);
    setIsLearningHovered(false);
    setIsSupportPinnedOpen(false);
    setIsSupportHovered(false);
    setIsCompanyPinnedOpen(false);
    setIsCompanyHovered(false);
  }, []);
  const normalizedPathname = location.pathname.replace(/\/+$/, '') || '/';
  const isConsoleSurfaceRoute =
    normalizedPathname.startsWith(CONSOLE_PATH) ||
    normalizedPathname.startsWith(APP_DASHBOARD_PATH) ||
    normalizedPathname.startsWith('/app/');
  const isPostludeEmbedRoute = normalizedPathname === POSTLUDE_EMBED_PATH;
  const isRazzmatazzEmbedRoute = normalizedPathname === RAZZMATAZZ_EMBED_PATH;
  const isStandaloneAuthRoute =
    isPostludeEmbedRoute ||
    isRazzmatazzEmbedRoute ||
    normalizedPathname === websitePath('/login') ||
    normalizedPathname === '/login' ||
    normalizedPathname === websitePath(ACCEPT_INVITE_PATH) ||
    normalizedPathname === ACCEPT_INVITE_PATH;
  const websiteBreadcrumbs = React.useMemo<SiteBreadcrumb[]>(() => {
    if (isStandaloneAuthRoute || isConsoleSurfaceRoute) {
      return [];
    }

    if (normalizedPathname === websitePath() || normalizedPathname === '/') {
      return [];
    }

    const segments = normalizedPathname.split('/').filter(Boolean);
    if (segments.length === 0) {
      return [];
    }

    const crumbs: SiteBreadcrumb[] = [{ label: localizeMenuText('Home', language), to: websitePath() }];
    let builtPath = '';

    segments.forEach((segment, index) => {
      builtPath += `/${segment}`;
      const isLast = index === segments.length - 1;
      let label = STATIC_BREADCRUMB_LABELS[builtPath] ?? formatBreadcrumbSegment(segment);

      if (segments[0] === 'products' && index === 1) {
        label = PRODUCT_LABEL_BY_SLUG[segment] ?? label;
      }
      if (segments[0] === 'products' && index === 2) {
        const key = `${segments[1]}/${segment}`;
        label = PRODUCT_SUBLABEL_BY_KEY[key] ?? label;
      }
      if (segments[0] === 'industries' && index === 1) {
        label = INDUSTRY_LABEL_BY_SLUG[segment] ?? label;
      }
      if (segments[0] === 'industries' && index === 2) {
        const key = `${segments[1]}/${segment}`;
        label = INDUSTRY_SUBLABEL_BY_KEY[key] ?? label;
      }

      crumbs.push({
        label: localizeMenuText(label, language),
        to: isLast ? undefined : builtPath,
      });
    });

    return crumbs;
  }, [isStandaloneAuthRoute, isConsoleSurfaceRoute, normalizedPathname, language]);
  const activeProductMenuItem =
    allProductMenuItems.find((item) => item.id === activeProductItemId) || allProductMenuItems[0] || null;
  const activeProductVisual = activeProductMenuItem
    ? getProductSlideoutVisual(activeProductMenuItem.id)
    : DEFAULT_PRODUCT_SLIDEOUT_VISUAL;
  const activeProductVisualTiles = React.useMemo(
    () => buildProductVisualTiles(activeProductMenuItem, activeProductVisual.icon, language),
    [activeProductMenuItem, activeProductVisual.icon, language],
  );
  const activeProductMemeCopy = activeProductMenuItem
    ? getProductMenuMemeCopy(activeProductMenuItem.id, activeProductMenuItem.label, language)
    : getProductMenuMemeCopy('default', localizeMenuText('Discover NextCompli products', language), language);
  const activeIndustryMenuItem =
    allIndustryMenuItems.find((item) => item.id === activeIndustryItemId) || allIndustryMenuItems[0] || null;
  const activeCustomerMenuItem =
    allCustomerMenuItems.find((item) => item.id === activeCustomerItemId) || allCustomerMenuItems[0] || null;
  const activeLearningMenuItem =
    allLearningMenuItems.find((item) => item.id === activeLearningItemId) || allLearningMenuItems[0] || null;
  const activeSupportMenuItem =
    allSupportMenuItems.find((item) => item.id === activeSupportItemId) || allSupportMenuItems[0] || null;
  const activeCompanyMenuItem =
    allCompanyMenuItems.find((item) => item.id === activeCompanyItemId) || allCompanyMenuItems[0] || null;
  const t = React.useCallback(
    (key: string) =>
      WEBSITE_COPY[language][key]
      ?? SCREEN_COPY[language]?.[key]
      ?? WEBSITE_COPY.en[key]
      ?? SCREEN_COPY.en[key]
      ?? key,
    [language],
  );

  React.useEffect(() => {
    setActiveIndustrySubitemLabel(activeIndustryMenuItem?.subItems[0]?.label ?? '');
    setActiveCustomerSubitemLabel(activeCustomerMenuItem?.subItems[0]?.label ?? '');
    setActiveLearningSubitemLabel(activeLearningMenuItem?.subItems[0]?.label ?? '');
    setActiveSupportSubitemLabel(activeSupportMenuItem?.subItems[0]?.label ?? '');
    setActiveCompanySubitemLabel(activeCompanyMenuItem?.subItems[0]?.label ?? '');
  }, [
    activeIndustryMenuItem?.id,
    activeIndustryMenuItem?.subItems,
    activeCustomerMenuItem?.id,
    activeCustomerMenuItem?.subItems,
    activeLearningMenuItem?.id,
    activeLearningMenuItem?.subItems,
    activeSupportMenuItem?.id,
    activeSupportMenuItem?.subItems,
    activeCompanyMenuItem?.id,
    activeCompanyMenuItem?.subItems,
  ]);

  React.useEffect(() => {
    captureAttributionFromWindow();
  }, [location.search]);

  React.useEffect(() => {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    const secureCookieAttr = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${encodeURIComponent(LANGUAGE_COOKIE_KEY)}=${encodeURIComponent(language)}; path=/; max-age=31536000; SameSite=Lax${secureCookieAttr}`;
    document.documentElement.lang = language;
    // Same-tab listeners (Dashboard) do not see `storage` events; notify them directly.
    window.dispatchEvent(new Event('nextcompli-language-change'));
  }, [language]);

  React.useEffect(() => {
    if (!isAuthenticated || !accessToken || accessToken === '__legacy__') {
      chromeHydratedForTokenRef.current = null;
      return undefined;
    }
    if (chromeHydratedForTokenRef.current === accessToken) {
      return undefined;
    }
    let cancelled = false;
    const hydrate = async () => {
      const remote = await loadUiChromeFromAccount(GATEWAY_BASE_URL, accessToken, language);
      if (cancelled) {
        return;
      }
      chromeHydratedForTokenRef.current = accessToken;
      if (!remote) {
        void saveUiChromeToAccount(
          GATEWAY_BASE_URL,
          accessToken,
          buildUiChromePreference(language, appearancePreferences),
        );
        return;
      }
      setLanguage(remote.language);
      setAppearancePreferences(applyUiChromeLocally(remote));
    };
    void hydrate();
    return () => {
      cancelled = true;
    };
    // Intentionally omit language/appearance so a remote hydrate is not re-triggered by local edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, isAuthenticated]);

  React.useEffect(() => {
    if (!isAuthenticated || !accessToken || accessToken === '__legacy__') {
      return;
    }
    if (chromeHydratedForTokenRef.current !== accessToken) {
      return;
    }
    void saveUiChromeToAccount(
      GATEWAY_BASE_URL,
      accessToken,
      buildUiChromePreference(language, appearancePreferences),
    );
  }, [accessToken, appearancePreferences, isAuthenticated, language]);

  React.useEffect(() => {
    setIsMobileNavOpen(false);
    closeHeaderOverlays();
  }, [closeHeaderOverlays, location.pathname]);

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const media = window.matchMedia('(min-width: 901px)');
    const syncDesktopState = () => {
      if (media.matches) {
        setIsMobileNavOpen(false);
      }
    };
    syncDesktopState();
    media.addEventListener('change', syncDesktopState);
    return () => media.removeEventListener('change', syncDesktopState);
  }, []);

  React.useEffect(() => {
    const urlLanguage = new URLSearchParams(window.location.search).get('lang');
    const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    const cookieLanguage = getCookieValue(LANGUAGE_COOKIE_KEY).toLowerCase();
    if (urlLanguage === 'es' || urlLanguage === 'en' || storedLanguage === 'es' || storedLanguage === 'en' || cookieLanguage === 'es' || cookieLanguage === 'en') {
      return;
    }

    const controller = new AbortController();
    const loadTrustedGeoLanguage = async () => {
      try {
        const response = await fetch(`${GATEWAY_BASE_URL}${GEO_LANGUAGE_DEFAULT_PATH}`, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (!response.ok) {
          return;
        }
        const payload = (await response.json()) as { language?: string };
        const nextLanguage = payload.language === 'es' ? 'es' : payload.language === 'en' ? 'en' : null;
        if (!nextLanguage) {
          return;
        }

        const preferenceAfterFetch = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (preferenceAfterFetch === 'es' || preferenceAfterFetch === 'en') {
          return;
        }
        setLanguage(nextLanguage);
      } catch {
        // Keep existing language behavior if geo signal is unavailable.
      }
    };

    void loadTrustedGeoLanguage();
    return () => {
      controller.abort();
    };
  }, []);

  React.useEffect(() => {
    const syncLogo = () => {
      setWebsiteLogoPath(loadWebsiteLogo());
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key === WEBSITE_LOGO_STORAGE_KEY) {
        syncLogo();
      }
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('nextcompli:logo-updated', syncLogo as EventListener);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('nextcompli:logo-updated', syncLogo as EventListener);
    };
  }, []);

  const hasDesignerAccess = React.useMemo(() => {
    if (!user) {
      return false;
    }
    const roles = new Set([...(user.platform_roles || []), ...(user.effective_roles || [])]);
    return user.is_platform_admin || roles.has('designer') || roles.has('design_admin');
  }, [user]);

  const hasSuperUserAccess = React.useMemo(() => {
    return hasPlatformAdminAccess(user);
  }, [user]);

  const languageContextValue = React.useMemo(
    () => ({ language, setLanguage, t }),
    [language, t],
  );

  const signedInIdentity = React.useMemo(() => {
    if (!user) {
      return { primary: '', secondary: '' };
    }

    const email = String(user.email || '').trim();
    const derivedName = email.includes('@') ? email.split('@')[0] : email;
    return {
      primary: derivedName || 'User',
      secondary: email,
    };
  }, [user]);

  const signedInInitials = React.useMemo(() => {
    const base = `${signedInIdentity.primary} ${signedInIdentity.secondary}`.replace(/[^A-Za-z0-9]+/g, ' ').trim();
    if (!base) {
      return 'U';
    }
    const parts = base.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }, [signedInIdentity]);

  const slideoutPanelStyle = React.useMemo(
    () => ({ top: `${slideoutTop}px`, height: `calc(100vh - ${slideoutTop}px)` }),
    [slideoutTop],
  );

  const openProductsHover = React.useCallback(() => {
    if (!supportsHoverMenus) {
      return;
    }
    if (productsHoverCloseTimeoutRef.current !== null) {
      window.clearTimeout(productsHoverCloseTimeoutRef.current);
      productsHoverCloseTimeoutRef.current = null;
    }
    setIsProductsHovered(true);
  }, [supportsHoverMenus]);

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      setSupportsHoverMenus(true);
      return;
    }

    const mediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    const handleChange = (event: MediaQueryListEvent) => {
      setSupportsHoverMenus(event.matches);
    };

    setSupportsHoverMenus(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const closeProductsHover = React.useCallback(() => {
    if (!supportsHoverMenus) {
      return;
    }
    if (productsHoverCloseTimeoutRef.current !== null) {
      window.clearTimeout(productsHoverCloseTimeoutRef.current);
    }
    productsHoverCloseTimeoutRef.current = window.setTimeout(() => {
      setIsProductsHovered(false);
      productsHoverCloseTimeoutRef.current = null;
    }, 80);
  }, [supportsHoverMenus]);

  const openIndustriesHover = React.useCallback(() => {
    if (!supportsHoverMenus) {
      return;
    }
    if (industriesHoverCloseTimeoutRef.current !== null) {
      window.clearTimeout(industriesHoverCloseTimeoutRef.current);
      industriesHoverCloseTimeoutRef.current = null;
    }
    setIsIndustriesHovered(true);
  }, [supportsHoverMenus]);

  const closeIndustriesHover = React.useCallback(() => {
    if (!supportsHoverMenus) {
      return;
    }
    if (industriesHoverCloseTimeoutRef.current !== null) {
      window.clearTimeout(industriesHoverCloseTimeoutRef.current);
    }
    industriesHoverCloseTimeoutRef.current = window.setTimeout(() => {
      setIsIndustriesHovered(false);
      industriesHoverCloseTimeoutRef.current = null;
    }, 80);
  }, [supportsHoverMenus]);

  const openCustomersHover = React.useCallback(() => {
    if (!supportsHoverMenus) {
      return;
    }
    if (customersHoverCloseTimeoutRef.current !== null) {
      window.clearTimeout(customersHoverCloseTimeoutRef.current);
      customersHoverCloseTimeoutRef.current = null;
    }
    setIsCustomersHovered(true);
  }, [supportsHoverMenus]);

  const closeCustomersHover = React.useCallback(() => {
    if (!supportsHoverMenus) {
      return;
    }
    if (customersHoverCloseTimeoutRef.current !== null) {
      window.clearTimeout(customersHoverCloseTimeoutRef.current);
    }
    customersHoverCloseTimeoutRef.current = window.setTimeout(() => {
      setIsCustomersHovered(false);
      customersHoverCloseTimeoutRef.current = null;
    }, 80);
  }, [supportsHoverMenus]);

  const openLearningHover = React.useCallback(() => {
    if (!supportsHoverMenus) {
      return;
    }
    if (learningHoverCloseTimeoutRef.current !== null) {
      window.clearTimeout(learningHoverCloseTimeoutRef.current);
      learningHoverCloseTimeoutRef.current = null;
    }
    setIsLearningHovered(true);
  }, [supportsHoverMenus]);

  const closeLearningHover = React.useCallback(() => {
    if (!supportsHoverMenus) {
      return;
    }
    if (learningHoverCloseTimeoutRef.current !== null) {
      window.clearTimeout(learningHoverCloseTimeoutRef.current);
    }
    learningHoverCloseTimeoutRef.current = window.setTimeout(() => {
      setIsLearningHovered(false);
      learningHoverCloseTimeoutRef.current = null;
    }, 80);
  }, [supportsHoverMenus]);

  const openSupportHover = React.useCallback(() => {
    if (!supportsHoverMenus) {
      return;
    }
    if (supportHoverCloseTimeoutRef.current !== null) {
      window.clearTimeout(supportHoverCloseTimeoutRef.current);
      supportHoverCloseTimeoutRef.current = null;
    }
    setIsSupportHovered(true);
  }, [supportsHoverMenus]);

  const closeSupportHover = React.useCallback(() => {
    if (!supportsHoverMenus) {
      return;
    }
    if (supportHoverCloseTimeoutRef.current !== null) {
      window.clearTimeout(supportHoverCloseTimeoutRef.current);
    }
    supportHoverCloseTimeoutRef.current = window.setTimeout(() => {
      setIsSupportHovered(false);
      supportHoverCloseTimeoutRef.current = null;
    }, 80);
  }, [supportsHoverMenus]);

  const openCompanyHover = React.useCallback(() => {
    if (!supportsHoverMenus) {
      return;
    }
    if (companyHoverCloseTimeoutRef.current !== null) {
      window.clearTimeout(companyHoverCloseTimeoutRef.current);
      companyHoverCloseTimeoutRef.current = null;
    }
    setIsCompanyHovered(true);
  }, [supportsHoverMenus]);

  const closeCompanyHover = React.useCallback(() => {
    if (!supportsHoverMenus) {
      return;
    }
    if (companyHoverCloseTimeoutRef.current !== null) {
      window.clearTimeout(companyHoverCloseTimeoutRef.current);
    }
    companyHoverCloseTimeoutRef.current = window.setTimeout(() => {
      setIsCompanyHovered(false);
      companyHoverCloseTimeoutRef.current = null;
    }, 80);
  }, [supportsHoverMenus]);

  React.useEffect(() => {
    const syncAppearance = (event?: Event) => {
      const detail = (event as CustomEvent<AppearancePreferences> | undefined)?.detail;
      setAppearancePreferences(detail ? { ...detail } : loadAppearancePreferences());
    };
    window.addEventListener(APPEARANCE_UPDATED_EVENT, syncAppearance as EventListener);
    window.addEventListener('storage', syncAppearance);
    return () => {
      window.removeEventListener(APPEARANCE_UPDATED_EVENT, syncAppearance as EventListener);
      window.removeEventListener('storage', syncAppearance);
    };
  }, []);

  const setThemeMode = React.useCallback((themeMode: AppearanceThemeMode) => {
    setAppearancePreferences((current) => saveAppearancePreferences({
      ...current,
      themeMode,
    }));
  }, []);

  React.useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) {
        return;
      }

      if (languageMenuRef.current && !languageMenuRef.current.contains(target)) {
        setIsLanguageMenuOpen(false);
      }

      if (themeMenuRef.current && !themeMenuRef.current.contains(target)) {
        setIsThemeMenuOpen(false);
      }

      if (userMenuRef.current && !userMenuRef.current.contains(target)) {
        setIsUserMenuOpen(false);
      }

      if (siteHeaderRef.current && !siteHeaderRef.current.contains(target)) {
        setIsMobileNavOpen(false);
      }
    };

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileNavOpen(false);
        closeHeaderOverlays();
      }
    };

    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      if (productsHoverCloseTimeoutRef.current !== null) {
        window.clearTimeout(productsHoverCloseTimeoutRef.current);
      }
      if (industriesHoverCloseTimeoutRef.current !== null) {
        window.clearTimeout(industriesHoverCloseTimeoutRef.current);
      }
      if (customersHoverCloseTimeoutRef.current !== null) {
        window.clearTimeout(customersHoverCloseTimeoutRef.current);
      }
      if (learningHoverCloseTimeoutRef.current !== null) {
        window.clearTimeout(learningHoverCloseTimeoutRef.current);
      }
      if (supportHoverCloseTimeoutRef.current !== null) {
        window.clearTimeout(supportHoverCloseTimeoutRef.current);
      }
      if (companyHoverCloseTimeoutRef.current !== null) {
        window.clearTimeout(companyHoverCloseTimeoutRef.current);
      }
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [closeHeaderOverlays]);

  return (
    <WebsiteLanguageContext.Provider value={languageContextValue}>
    <div className={`site-shell ${isStandaloneAuthRoute ? 'site-shell-auth' : ''} ${isConsoleSurfaceRoute ? 'site-shell-console' : ''} ${WEBSITE_SUBMENU_TEXT_ONLY_MODE ? 'site-shell-text-only' : ''}`} lang={language}>
      {!isStandaloneAuthRoute && (
      <header className={`site-header ${isMobileNavOpen ? 'mobile-open' : ''}`} ref={siteHeaderRef}>
        <div className="brand" onClick={() => navigate(websitePath())}>
          <BrandLockup path={websiteLogoPath} />
        </div>
        <div className="header-settings" aria-label={t('header.settingsChrome')}>
          <div className="header-language" ref={languageMenuRef}>
            <button
              type="button"
              className={`header-language-trigger ${isLanguageMenuOpen ? 'active' : ''}`}
              aria-haspopup="menu"
              aria-expanded={isLanguageMenuOpen}
              aria-label={t('header.languageMenu')}
              onClick={() => {
                setIsThemeMenuOpen(false);
                setIsLanguageMenuOpen((previous) => !previous);
              }}
            >
              <span className="header-language-icon" aria-hidden="true">
                🌐
              </span>
            </button>
            <div
              className={`header-language-menu ${isLanguageMenuOpen ? 'open' : ''}`}
              role="menu"
              aria-label={t('header.languageMenu')}
              aria-hidden={!isLanguageMenuOpen}
            >
              <button
                type="button"
                role="menuitemradio"
                aria-checked={language === 'en'}
                className={`header-language-option ${language === 'en' ? 'active' : ''}`}
                onClick={() => {
                  setLanguage('en');
                  setIsLanguageMenuOpen(false);
                }}
              >
                {t('header.languageEnglish')}
              </button>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={language === 'es'}
                className={`header-language-option ${language === 'es' ? 'active' : ''}`}
                onClick={() => {
                  setLanguage('es');
                  setIsLanguageMenuOpen(false);
                }}
              >
                {t('header.languageSpanish')}
              </button>
            </div>
          </div>
          <div className="header-theme" ref={themeMenuRef}>
            <button
              type="button"
              className={`header-theme-trigger ${isThemeMenuOpen ? 'active' : ''}`}
              aria-haspopup="menu"
              aria-expanded={isThemeMenuOpen}
              aria-label={`${t('header.themeMenu')}. ${t('header.theme')}: ${
                appearancePreferences.themeMode === 'system'
                  ? t('header.themeSystem')
                  : appearancePreferences.themeMode === 'dark'
                    ? t('header.themeDark')
                    : t('header.themeLight')
              }`}
              title={t('header.themeMenu')}
              onClick={() => {
                setIsLanguageMenuOpen(false);
                setIsThemeMenuOpen((previous) => !previous);
              }}
            >
              <span className="header-theme-icon" aria-hidden="true">
                {resolveThemeMode(appearancePreferences.themeMode) === 'dark' ? (
                  <svg viewBox="0 0 24 24" width="18" height="18" focusable="false">
                    <path
                      fill="currentColor"
                      d="M21 14.5A8.5 8.5 0 0 1 9.5 3a7 7 0 1 0 11.5 11.5Z"
                    />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="18" height="18" focusable="false">
                    <circle cx="12" cy="12" r="4" fill="currentColor" />
                    <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none">
                      <path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5.2 5.2l1.6 1.6M17.2 17.2l1.6 1.6M5.2 18.8l1.6-1.6M17.2 6.8l1.6-1.6" />
                    </g>
                  </svg>
                )}
              </span>
            </button>
            <div
              className={`header-theme-menu ${isThemeMenuOpen ? 'open' : ''}`}
              role="menu"
              aria-label={t('header.themeMenu')}
              aria-hidden={!isThemeMenuOpen}
            >
              {(
                [
                  { value: 'system', label: t('header.themeSystem'), hint: t('header.themeSystemHint') },
                  { value: 'light', label: t('header.themeLight') },
                  { value: 'dark', label: t('header.themeDark') },
                ] as Array<{ value: AppearanceThemeMode; label: string; hint?: string }>
              ).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={appearancePreferences.themeMode === option.value}
                  className={`header-theme-option ${appearancePreferences.themeMode === option.value ? 'active' : ''}`}
                  onClick={() => {
                    setThemeMode(option.value);
                    setIsThemeMenuOpen(false);
                  }}
                >
                  <span className="header-theme-option-label">{option.label}</span>
                  {option.hint ? <span className="header-theme-option-hint">{option.hint}</span> : null}
                </button>
              ))}
            </div>
          </div>
          {isAuthenticated && user ? (
            <Link
              to={`${CONSOLE_PATH}/home`}
              className="header-console-cta"
              onClick={() => {
                setIsUserMenuOpen(false);
                setIsMobileNavOpen(false);
              }}
            >
              {t('header.console')}
            </Link>
          ) : null}
        </div>
        <button
          type="button"
          className={`mobile-nav-toggle ${isMobileNavOpen ? 'active' : ''}`}
          aria-expanded={isMobileNavOpen}
          aria-controls="site-primary-navigation"
          aria-label={language === 'es' ? 'Abrir o cerrar menú del sitio' : 'Open or close site menu'}
          onClick={() => {
            if (isMobileNavOpen) {
              closeHeaderOverlays();
            }
            setIsMobileNavOpen((current) => !current);
          }}
        >
          <span aria-hidden="true">☰</span>
          <span>{t('header.menu')}</span>
        </button>
        <nav
          id="site-primary-navigation"
          className={`site-nav ${isAuthenticated ? 'site-nav-authenticated' : ''}`}
          ref={productsMenuRef}
          onClickCapture={(event) => {
            const target = event.target as HTMLElement | null;
            if (target?.closest('a')) {
              setIsMobileNavOpen(false);
            }
          }}
        >
          <button
            className={`nav-link nav-link-button products-trigger ${isProductsOpen ? 'active' : ''}`}
            type="button"
            aria-haspopup="menu"
            aria-expanded={isProductsOpen}
            aria-controls="products-slideout"
            onMouseEnter={openProductsHover}
            onMouseLeave={closeProductsHover}
            onPointerDown={(event) => {
              if (event.pointerType === 'mouse') {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsIndustriesPinnedOpen(false);
              setIsIndustriesHovered(false);
              setIsCustomersPinnedOpen(false);
              setIsCustomersHovered(false);
              setIsLearningPinnedOpen(false);
              setIsLearningHovered(false);
              setIsSupportPinnedOpen(false);
              setIsSupportHovered(false);
              setIsCompanyPinnedOpen(false);
              setIsCompanyHovered(false);
              setIsProductsPinnedOpen((prev) => !prev);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setIsIndustriesPinnedOpen(false);
                setIsIndustriesHovered(false);
                setIsCustomersPinnedOpen(false);
                setIsCustomersHovered(false);
                setIsLearningPinnedOpen(false);
                setIsLearningHovered(false);
                setIsSupportPinnedOpen(false);
                setIsSupportHovered(false);
                setIsCompanyPinnedOpen(false);
                setIsCompanyHovered(false);
                setIsProductsPinnedOpen((prev) => !prev);
              }
            }}
          >
            {t('nav.products')}
          </button>
          <div
            id="products-slideout"
            className={`products-slideout ${isProductsOpen ? 'open' : ''}`}
            role="menu"
            aria-label="Products"
            aria-hidden={!isProductsOpen}
            style={slideoutPanelStyle}
            onMouseEnter={openProductsHover}
            onMouseLeave={closeProductsHover}
          >
            <button
              type="button"
              className="slideout-dismiss-button"
              aria-label={t('header.closeSlideoutMenu')}
              onClick={() => {
                setIsProductsPinnedOpen(false);
                setIsProductsHovered(false);
              }}
            >
              <span className="slideout-dismiss-icon" aria-hidden="true">◀</span>
            </button>
            <div className="products-slideout-links">
              {allProductMenuGroups.map((group) => (
                <section key={group.header} className="products-slideout-group">
                  <h3>{group.header}</h3>
                  {group.items.map((item) => (
                    <button
                      key={`${group.header}-${item.label}`}
                      type="button"
                      role="menuitem"
                      className={`products-slideout-link ${activeProductItemId === item.id ? 'active' : ''}`}
                      onMouseEnter={() => setActiveProductItemId(item.id)}
                      onFocus={() => setActiveProductItemId(item.id)}
                    >
                      <span className="products-slideout-link-main">
                        <span className="products-slideout-item-icon">
                          <ProductSlideoutIcon iconKey={getProductSlideoutVisual(item.id).icon} className="products-slideout-item-icon-svg" />
                        </span>
                        <span>{formatSlideoutProductLabel(item.label, item.id)}</span>
                      </span>
                      <span className="products-slideout-link-chevron" aria-hidden="true">
                        ›
                      </span>
                    </button>
                  ))}
                </section>
              ))}
            </div>
            <div className="products-slideout-feature">
              <h4 className={`products-slideout-feature-title ${activeProductMenuItem && !isSupportSlideoutItem(activeProductMenuItem.id) ? 'is-center-icon' : ''}`}>
                <span className="products-slideout-item-icon">
                  <ProductSlideoutIcon iconKey={activeProductVisual.icon} className="products-slideout-item-icon-svg" />
                </span>
                {activeProductMenuItem ? (
                  (() => {
                    const parts = getSlideoutProductLabelParts(activeProductMenuItem.label, activeProductMenuItem.id);
                    return (
                      <>
                        <span className="products-slideout-feature-title-main">{parts.title}</span>
                        {parts.subtitle ? <span className="products-slideout-feature-title-subtitle">{parts.subtitle}</span> : null}
                      </>
                    );
                  })()
                ) : (
                  <span className="products-slideout-feature-title-main">{localizeMenuText('Discover NextCompli products', language)}</span>
                )}
              </h4>
              <p>{activeProductMenuItem ? activeProductMenuItem.summary : localizeMenuText('Browse products, services, and features.', language)}</p>
              {activeProductMenuItem && activeProductMenuItem.subItems.length > 0 && (
                <ul className="products-slideout-subitems" aria-label={`${activeProductMenuItem.label} subitems`}>
                  {activeProductMenuItem.subItems.map((subItem) => (
                    <li key={subItem.label}>
                      <Link
                        to={subItem.to}
                        className="products-slideout-subitem-link"
                        target={subItem.opensInNewWindow ? '_blank' : undefined}
                        rel={subItem.opensInNewWindow ? 'noreferrer' : undefined}
                        onClick={() => {
                          setIsProductsPinnedOpen(false);
                          setIsProductsHovered(false);
                        }}
                      >
                        {subItem.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              <Link
                to={activeProductMenuItem ? activeProductMenuItem.to : websitePath('/platform')}
                className="products-slideout-cta"
                onClick={() => {
                  setIsProductsPinnedOpen(false);
                  setIsProductsHovered(false);
                }}
              >
                {activeProductMenuItem
                  ? (language === 'es' ? `Explorar ${activeProductMenuItem.label}` : toExplorePlatformLabel(activeProductMenuItem.label))
                  : localizeMenuText('Explore Platform', language)}
              </Link>
              {!isSupportSlideoutItem(activeProductMenuItem?.id) ? (
                <div className="products-slideout-meme" role="note" aria-label={language === 'es' ? 'Resumen visual del producto' : 'Product visual summary'}>
                  <strong>{activeProductMemeCopy[0]}</strong>
                  <span>{activeProductMemeCopy[1]}</span>
                </div>
              ) : null}
              <div className="slideout-text-summary" role="note" aria-label={language === 'es' ? 'Áreas de enfoque del producto' : 'Product focus areas'}>
                {activeProductVisualTiles.map((tile) => (
                  <span key={`tile-${tile.label}-${tile.icon}`} className="slideout-text-summary-chip">
                    {tile.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <button
            className={`nav-link nav-link-button products-trigger industries-trigger ${isIndustriesOpen ? 'active' : ''}`}
            type="button"
            aria-haspopup="menu"
            aria-expanded={isIndustriesOpen}
            aria-controls="industries-slideout"
            onMouseEnter={openIndustriesHover}
            onMouseLeave={closeIndustriesHover}
            onPointerDown={(event) => {
              if (event.pointerType === 'mouse') {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsProductsPinnedOpen(false);
              setIsProductsHovered(false);
              setIsCustomersPinnedOpen(false);
              setIsCustomersHovered(false);
              setIsLearningPinnedOpen(false);
              setIsLearningHovered(false);
              setIsSupportPinnedOpen(false);
              setIsSupportHovered(false);
              setIsCompanyPinnedOpen(false);
              setIsCompanyHovered(false);
              setIsIndustriesPinnedOpen((prev) => !prev);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setIsProductsPinnedOpen(false);
                setIsProductsHovered(false);
                setIsCustomersPinnedOpen(false);
                setIsCustomersHovered(false);
                setIsLearningPinnedOpen(false);
                setIsLearningHovered(false);
                setIsSupportPinnedOpen(false);
                setIsSupportHovered(false);
                setIsCompanyPinnedOpen(false);
                setIsCompanyHovered(false);
                setIsIndustriesPinnedOpen((prev) => !prev);
              }
            }}
          >
            {t('nav.industries')}
          </button>
          <div
            id="industries-slideout"
            className={`products-slideout industries-slideout ${isIndustriesOpen ? 'open' : ''}`}
            role="menu"
            aria-label="Industries"
            aria-hidden={!isIndustriesOpen}
            style={slideoutPanelStyle}
            onMouseEnter={openIndustriesHover}
            onMouseLeave={closeIndustriesHover}
          >
            <button
              type="button"
              className="slideout-dismiss-button"
              aria-label={t('header.closeSlideoutMenu')}
              onClick={() => {
                setIsIndustriesPinnedOpen(false);
                setIsIndustriesHovered(false);
              }}
            >
              <span className="slideout-dismiss-icon" aria-hidden="true">◀</span>
            </button>
            <div className="products-slideout-links">
              <section className="products-slideout-group">
                <h3>{localizeMenuText('Industries', language)}</h3>
                {allIndustryMenuItems.map((item) => (
                  <Link
                    key={`industry-slideout-${item.id}`}
                    to={item.to}
                    role="menuitem"
                    className={`products-slideout-link ${activeIndustryItemId === item.id ? 'active' : ''}`}
                    onMouseEnter={() => setActiveIndustryItemId(item.id)}
                    onFocus={() => setActiveIndustryItemId(item.id)}
                    onClick={() => {
                      setIsIndustriesPinnedOpen(false);
                      setIsIndustriesHovered(false);
                    }}
                  >
                    <span className="industry-slideout-link-main">
                      <span className={`industry-slideout-icon industries-overview-icon--${item.iconTone ?? 'violet'}`}>
                        {item.iconKey && <IndustryIcon iconKey={item.iconKey} className="industry-icon-svg" />}
                      </span>
                      <span>{item.label}</span>
                    </span>
                    <span className="products-slideout-link-chevron" aria-hidden="true">
                      ›
                    </span>
                  </Link>
                ))}
              </section>
            </div>
            <div className="products-slideout-feature">
              <h4 className="products-slideout-feature-title">
                {activeIndustryMenuItem && (
                  <span className={`industry-slideout-feature-icon industries-overview-icon--${activeIndustryMenuItem.iconTone ?? 'violet'}`}>
                    {activeIndustryMenuItem.iconKey && (
                      <IndustryIcon iconKey={activeIndustryMenuItem.iconKey} className="industry-icon-svg" />
                    )}
                  </span>
                )}
                <span>{activeIndustryMenuItem ? activeIndustryMenuItem.label : localizeMenuText('Explore industries', language)}</span>
              </h4>
              <p>
                {activeIndustryMenuItem
                  ? activeIndustryMenuItem.summary
                  : localizeMenuText('Get industry-specific innovations to modernize your business and lower costs.', language)}
              </p>
              {activeIndustryMenuItem && activeIndustryMenuItem.subItems.length > 0 && (
                <ul className="products-slideout-subitems" aria-label={`${activeIndustryMenuItem.label} subitems`}>
                  {activeIndustryMenuItem.subItems.map((subItem) => (
                    <li key={subItem.label}>
                      <Link
                        to={subItem.to}
                        className="products-slideout-subitem-link"
                        onMouseEnter={() => setActiveIndustrySubitemLabel(subItem.label)}
                        onFocus={() => setActiveIndustrySubitemLabel(subItem.label)}
                        onClick={() => {
                          setIsIndustriesPinnedOpen(false);
                          setIsIndustriesHovered(false);
                        }}
                      >
                        {subItem.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              <Link
                to={activeIndustryMenuItem ? activeIndustryMenuItem.to : websitePath('/industries')}
                className="products-slideout-cta"
                onClick={() => {
                  setIsIndustriesPinnedOpen(false);
                  setIsIndustriesHovered(false);
                }}
              >
                {activeIndustryMenuItem?.ctaLabel ?? localizeMenuText('Explore industries', language)}
              </Link>
              <div className="products-slideout-meme" role="note" aria-label={language === 'es' ? 'Enfoque de la industria' : 'Industry focus'}>
                <strong>{language === 'es' ? 'Enfoque actual' : 'Current focus'}</strong>
                <span>{activeIndustrySubitemLabel || activeIndustryMenuItem?.summary || localizeMenuText('Explore industries', language)}</span>
              </div>
            </div>
          </div>
          <button
            className={`nav-link nav-link-button products-trigger customers-trigger ${isCustomersOpen ? 'active' : ''}`}
            type="button"
            aria-haspopup="menu"
            aria-expanded={isCustomersOpen}
            aria-controls="customers-slideout"
            onMouseEnter={openCustomersHover}
            onMouseLeave={closeCustomersHover}
            onPointerDown={(event) => {
              if (event.pointerType === 'mouse') {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsProductsPinnedOpen(false);
              setIsProductsHovered(false);
              setIsIndustriesPinnedOpen(false);
              setIsIndustriesHovered(false);
              setIsLearningPinnedOpen(false);
              setIsLearningHovered(false);
              setIsSupportPinnedOpen(false);
              setIsSupportHovered(false);
              setIsCompanyPinnedOpen(false);
              setIsCompanyHovered(false);
              setIsCustomersPinnedOpen((prev) => !prev);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setIsProductsPinnedOpen(false);
                setIsProductsHovered(false);
                setIsIndustriesPinnedOpen(false);
                setIsIndustriesHovered(false);
                setIsLearningPinnedOpen(false);
                setIsLearningHovered(false);
                setIsSupportPinnedOpen(false);
                setIsSupportHovered(false);
                setIsCompanyPinnedOpen(false);
                setIsCompanyHovered(false);
                setIsCustomersPinnedOpen((prev) => !prev);
              }
            }}
          >
            {t('nav.customers')}
          </button>
          <div
            id="customers-slideout"
            className={`products-slideout customers-slideout ${isCustomersOpen ? 'open' : ''}`}
            role="menu"
            aria-label="Customers"
            aria-hidden={!isCustomersOpen}
            style={slideoutPanelStyle}
            onMouseEnter={openCustomersHover}
            onMouseLeave={closeCustomersHover}
          >
            <button
              type="button"
              className="slideout-dismiss-button"
              aria-label={t('header.closeSlideoutMenu')}
              onClick={() => {
                setIsCustomersPinnedOpen(false);
                setIsCustomersHovered(false);
              }}
            >
              <span className="slideout-dismiss-icon" aria-hidden="true">◀</span>
            </button>
            <div className="products-slideout-links">
              <section className="products-slideout-group">
                <h3>{localizeMenuText('Customers', language)}</h3>
                {allCustomerMenuItems.map((item) => (
                  <Link
                    key={`customer-slideout-${item.id}`}
                    to={item.to}
                    role="menuitem"
                    className={`products-slideout-link ${activeCustomerItemId === item.id ? 'active' : ''}`}
                    onMouseEnter={() => setActiveCustomerItemId(item.id)}
                    onFocus={() => setActiveCustomerItemId(item.id)}
                    onClick={() => {
                      setIsCustomersPinnedOpen(false);
                      setIsCustomersHovered(false);
                    }}
                  >
                    <span>{item.label}</span>
                    <span className="products-slideout-link-chevron" aria-hidden="true">
                      ›
                    </span>
                  </Link>
                ))}
              </section>
            </div>
            <div className="products-slideout-feature">
              <h4>{activeCustomerMenuItem ? activeCustomerMenuItem.label : localizeMenuText('Customer Stories', language)}</h4>
              <p>
                {activeCustomerMenuItem
                  ? activeCustomerMenuItem.summary
                  : localizeMenuText('Explore success stories from customers of all sizes, in every industry.', language)}
              </p>
              <Link
                to={activeCustomerMenuItem ? activeCustomerMenuItem.to : websitePath('/customers')}
                className="products-slideout-cta"
                onClick={() => {
                  setIsCustomersPinnedOpen(false);
                  setIsCustomersHovered(false);
                }}
              >
                {activeCustomerMenuItem?.ctaLabel ?? localizeMenuText('See all stories', language)}
              </Link>
              {activeCustomerMenuItem && activeCustomerMenuItem.subItems.length > 0 && (
                <ul className="products-slideout-subitems" aria-label={`${activeCustomerMenuItem.label} subitems`}>
                  {activeCustomerMenuItem.subItems.map((subItem) => (
                    <li key={subItem.label}>
                      <Link
                        to={subItem.to}
                        className="products-slideout-subitem-link"
                        onMouseEnter={() => setActiveCustomerSubitemLabel(subItem.label)}
                        onFocus={() => setActiveCustomerSubitemLabel(subItem.label)}
                        onClick={() => {
                          setIsCustomersPinnedOpen(false);
                          setIsCustomersHovered(false);
                        }}
                      >
                        {subItem.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              <div className="products-slideout-meme" role="note" aria-label={language === 'es' ? 'Ruta del cliente' : 'Customer path'}>
                <strong>{language === 'es' ? 'Historia actual' : 'Current story'}</strong>
                <span>{activeCustomerSubitemLabel || activeCustomerMenuItem?.summary || localizeMenuText('See all stories', language)}</span>
              </div>
            </div>
          </div>
          <button
            className={`nav-link nav-link-button products-trigger learning-trigger ${isLearningOpen ? 'active' : ''}`}
            type="button"
            aria-haspopup="menu"
            aria-expanded={isLearningOpen}
            aria-controls="learning-slideout"
            onMouseEnter={openLearningHover}
            onMouseLeave={closeLearningHover}
            onPointerDown={(event) => {
              if (event.pointerType === 'mouse') {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsProductsPinnedOpen(false);
              setIsProductsHovered(false);
              setIsIndustriesPinnedOpen(false);
              setIsIndustriesHovered(false);
              setIsCustomersPinnedOpen(false);
              setIsCustomersHovered(false);
              setIsSupportPinnedOpen(false);
              setIsSupportHovered(false);
              setIsCompanyPinnedOpen(false);
              setIsCompanyHovered(false);
              setIsLearningPinnedOpen((prev) => !prev);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setIsProductsPinnedOpen(false);
                setIsProductsHovered(false);
                setIsIndustriesPinnedOpen(false);
                setIsIndustriesHovered(false);
                setIsCustomersPinnedOpen(false);
                setIsCustomersHovered(false);
                setIsSupportPinnedOpen(false);
                setIsSupportHovered(false);
                setIsCompanyPinnedOpen(false);
                setIsCompanyHovered(false);
                setIsLearningPinnedOpen((prev) => !prev);
              }
            }}
          >
            {t('nav.learning')}
          </button>
          <div
            id="learning-slideout"
            className={`products-slideout learning-slideout ${isLearningOpen ? 'open' : ''}`}
            role="menu"
            aria-label="Learning"
            aria-hidden={!isLearningOpen}
            style={slideoutPanelStyle}
            onMouseEnter={openLearningHover}
            onMouseLeave={closeLearningHover}
          >
            <button
              type="button"
              className="slideout-dismiss-button"
              aria-label={t('header.closeSlideoutMenu')}
              onClick={() => {
                setIsLearningPinnedOpen(false);
                setIsLearningHovered(false);
              }}
            >
              <span className="slideout-dismiss-icon" aria-hidden="true">◀</span>
            </button>
            <div className="products-slideout-links">
              <section className="products-slideout-group">
                <h3>{localizeMenuText('Learning', language)}</h3>
                {allLearningMenuItems.map((item) => (
                  <button
                    key={`learning-slideout-${item.id}`}
                    type="button"
                    role="menuitem"
                    className={`products-slideout-link ${activeLearningItemId === item.id ? 'active' : ''}`}
                    onMouseEnter={() => setActiveLearningItemId(item.id)}
                    onFocus={() => setActiveLearningItemId(item.id)}
                  >
                    <span>{item.label}</span>
                    <span className="products-slideout-link-chevron" aria-hidden="true">
                      ›
                    </span>
                  </button>
                ))}
              </section>
            </div>
            <div className="products-slideout-feature">
              <h4>{activeLearningMenuItem ? activeLearningMenuItem.label : localizeMenuText('Learning', language)}</h4>
              {activeLearningMenuItem?.summary ? <p>{activeLearningMenuItem.summary}</p> : null}
              <Link
                to={activeLearningMenuItem ? activeLearningMenuItem.to : websitePath('/blog')}
                className="products-slideout-cta"
                onClick={() => {
                  setIsLearningPinnedOpen(false);
                  setIsLearningHovered(false);
                }}
              >
                {activeLearningMenuItem?.ctaLabel ?? localizeMenuText('Explore Learning', language)}
              </Link>
              {activeLearningMenuItem && activeLearningMenuItem.subItems.length > 0 && (
                <ul className="products-slideout-subitems learning-slideout-subitems" aria-label={`${activeLearningMenuItem.label} subitems`}>
                  {activeLearningMenuItem.subItems.map((subItem) => (
                    <li key={subItem.label}>
                      <Link
                        to={subItem.to}
                        className="products-slideout-subitem-link learning-slideout-subitem-link"
                        target={subItem.opensInNewWindow ? '_blank' : undefined}
                        rel={subItem.opensInNewWindow ? 'noreferrer' : undefined}
                        onMouseEnter={() => setActiveLearningSubitemLabel(subItem.label)}
                        onFocus={() => setActiveLearningSubitemLabel(subItem.label)}
                        onClick={() => {
                          setIsLearningPinnedOpen(false);
                          setIsLearningHovered(false);
                        }}
                      >
                        <strong>{subItem.label}</strong>
                        {subItem.description && <span>{subItem.description}</span>}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              <div className="products-slideout-meme" role="note" aria-label={language === 'es' ? 'Enfoque de aprendizaje' : 'Learning focus'}>
                <strong>{language === 'es' ? 'Recurso actual' : 'Current resource'}</strong>
                <span>{activeLearningSubitemLabel || activeLearningMenuItem?.summary || t('learning.header')}</span>
              </div>
            </div>
          </div>
          <button
            className={`nav-link nav-link-button products-trigger support-trigger ${isSupportOpen ? 'active' : ''}`}
            type="button"
            aria-haspopup="menu"
            aria-expanded={isSupportOpen}
            aria-controls="support-slideout"
            onMouseEnter={openSupportHover}
            onMouseLeave={closeSupportHover}
            onPointerDown={(event) => {
              if (event.pointerType === 'mouse') {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsProductsPinnedOpen(false);
              setIsProductsHovered(false);
              setIsIndustriesPinnedOpen(false);
              setIsIndustriesHovered(false);
              setIsCustomersPinnedOpen(false);
              setIsCustomersHovered(false);
              setIsLearningPinnedOpen(false);
              setIsLearningHovered(false);
              setIsCompanyPinnedOpen(false);
              setIsCompanyHovered(false);
              setIsSupportPinnedOpen((prev) => !prev);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setIsProductsPinnedOpen(false);
                setIsProductsHovered(false);
                setIsIndustriesPinnedOpen(false);
                setIsIndustriesHovered(false);
                setIsCustomersPinnedOpen(false);
                setIsCustomersHovered(false);
                setIsLearningPinnedOpen(false);
                setIsLearningHovered(false);
                setIsCompanyPinnedOpen(false);
                setIsCompanyHovered(false);
                setIsSupportPinnedOpen((prev) => !prev);
              }
            }}
          >
            {t('nav.support')}
          </button>
          <div
            id="support-slideout"
            className={`products-slideout support-slideout ${isSupportOpen ? 'open' : ''}`}
            role="menu"
            aria-label={t('support.header')}
            aria-hidden={!isSupportOpen}
            style={slideoutPanelStyle}
            onMouseEnter={openSupportHover}
            onMouseLeave={closeSupportHover}
          >
            <button
              type="button"
              className="slideout-dismiss-button"
              aria-label={t('header.closeSlideoutMenu')}
              onClick={() => {
                setIsSupportPinnedOpen(false);
                setIsSupportHovered(false);
              }}
            >
              <span className="slideout-dismiss-icon" aria-hidden="true">◀</span>
            </button>
            <div className="products-slideout-links">
              <section className="products-slideout-group">
                <h3>{t('support.header')}</h3>
                {allSupportMenuItems.map((item) => (
                  <button
                    key={`support-slideout-${item.id}`}
                    type="button"
                    role="menuitem"
                    className={`products-slideout-link ${activeSupportItemId === item.id ? 'active' : ''}`}
                    onMouseEnter={() => setActiveSupportItemId(item.id)}
                    onFocus={() => setActiveSupportItemId(item.id)}
                  >
                    <span>{item.label}</span>
                    <span className="products-slideout-link-chevron" aria-hidden="true">
                      ›
                    </span>
                  </button>
                ))}
              </section>
            </div>
            <div className="products-slideout-feature">
              <h4>{activeSupportMenuItem ? activeSupportMenuItem.label : 'Support'}</h4>
              {activeSupportMenuItem?.summary ? <p>{activeSupportMenuItem.summary}</p> : null}
              <Link
                to={activeSupportMenuItem ? activeSupportMenuItem.to : websitePath('/support')}
                className="products-slideout-cta"
                onClick={() => {
                  setIsSupportPinnedOpen(false);
                  setIsSupportHovered(false);
                }}
              >
                {activeSupportMenuItem?.ctaLabel ?? 'Get started'}
              </Link>
              {activeSupportMenuItem && activeSupportMenuItem.subItems.length > 0 && (
                <ul className="products-slideout-subitems support-slideout-subitems" aria-label={`${activeSupportMenuItem.label} subitems`}>
                  {activeSupportMenuItem.subItems.map((subItem) => (
                    <li key={subItem.label}>
                      <Link
                        to={subItem.to}
                        className="products-slideout-subitem-link support-slideout-subitem-link"
                        onMouseEnter={() => setActiveSupportSubitemLabel(subItem.label)}
                        onFocus={() => setActiveSupportSubitemLabel(subItem.label)}
                        onClick={() => {
                          setIsSupportPinnedOpen(false);
                          setIsSupportHovered(false);
                        }}
                      >
                        <strong>{subItem.label}</strong>
                        {subItem.description && <span>{subItem.description}</span>}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              <div className="products-slideout-meme" role="note" aria-label={language === 'es' ? 'Enfoque del plan' : 'Plan focus'}>
                <strong>{language === 'es' ? 'Ruta actual' : 'Current path'}</strong>
                <span>{activeSupportSubitemLabel || activeSupportMenuItem?.summary || 'Support'}</span>
              </div>
            </div>
          </div>
          <button
            className={`nav-link nav-link-button products-trigger company-trigger ${isCompanyOpen ? 'active' : ''}`}
            type="button"
            aria-haspopup="menu"
            aria-expanded={isCompanyOpen}
            aria-controls="company-slideout"
            onMouseEnter={openCompanyHover}
            onMouseLeave={closeCompanyHover}
            onPointerDown={(event) => {
              if (event.pointerType === 'mouse') {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsProductsPinnedOpen(false);
              setIsProductsHovered(false);
              setIsIndustriesPinnedOpen(false);
              setIsIndustriesHovered(false);
              setIsCustomersPinnedOpen(false);
              setIsCustomersHovered(false);
              setIsLearningPinnedOpen(false);
              setIsLearningHovered(false);
              setIsSupportPinnedOpen(false);
              setIsSupportHovered(false);
              setIsCompanyPinnedOpen((prev) => !prev);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setIsProductsPinnedOpen(false);
                setIsProductsHovered(false);
                setIsIndustriesPinnedOpen(false);
                setIsIndustriesHovered(false);
                setIsCustomersPinnedOpen(false);
                setIsCustomersHovered(false);
                setIsLearningPinnedOpen(false);
                setIsLearningHovered(false);
                setIsSupportPinnedOpen(false);
                setIsSupportHovered(false);
                setIsCompanyPinnedOpen((prev) => !prev);
              }
            }}
          >
            {t('nav.company')}
          </button>
          <div
            id="company-slideout"
            className={`products-slideout company-slideout ${isCompanyOpen ? 'open' : ''}`}
            role="menu"
            aria-label="Company"
            aria-hidden={!isCompanyOpen}
            style={slideoutPanelStyle}
            onMouseEnter={openCompanyHover}
            onMouseLeave={closeCompanyHover}
          >
            <button
              type="button"
              className="slideout-dismiss-button"
              aria-label={t('header.closeSlideoutMenu')}
              onClick={() => {
                setIsCompanyPinnedOpen(false);
                setIsCompanyHovered(false);
              }}
            >
              <span className="slideout-dismiss-icon" aria-hidden="true">◀</span>
            </button>
            <div className="products-slideout-links">
              <section className="products-slideout-group">
                <h3>{localizeMenuText('Company', language)}</h3>
                {allCompanyMenuItems.map((item) => (
                  <button
                    key={`company-slideout-${item.id}`}
                    type="button"
                    role="menuitem"
                    className={`products-slideout-link ${activeCompanyItemId === item.id ? 'active' : ''}`}
                    onMouseEnter={() => setActiveCompanyItemId(item.id)}
                    onFocus={() => setActiveCompanyItemId(item.id)}
                  >
                    <span>{item.label}</span>
                    <span className="products-slideout-link-chevron" aria-hidden="true">
                      ›
                    </span>
                  </button>
                ))}
              </section>
            </div>
            <div className="products-slideout-feature">
              <h4>{activeCompanyMenuItem ? activeCompanyMenuItem.label : localizeMenuText('Company', language)}</h4>
              {activeCompanyMenuItem?.summary ? <p>{activeCompanyMenuItem.summary}</p> : null}
              <Link
                to={activeCompanyMenuItem ? activeCompanyMenuItem.to : COMPANY_DESTINATION}
                className="products-slideout-cta"
                onClick={() => {
                  setIsCompanyPinnedOpen(false);
                  setIsCompanyHovered(false);
                }}
              >
                {activeCompanyMenuItem?.ctaLabel ?? localizeMenuText('Learn more', language)}
              </Link>
              {activeCompanyMenuItem && activeCompanyMenuItem.subItems.length > 0 && (
                <ul className="products-slideout-subitems company-slideout-subitems" aria-label={`${activeCompanyMenuItem.label} subitems`}>
                  {activeCompanyMenuItem.subItems.map((subItem) => (
                    <li key={subItem.label}>
                      <Link
                        to={subItem.to}
                        className="products-slideout-subitem-link company-slideout-subitem-link"
                        target={subItem.opensInNewWindow ? '_blank' : undefined}
                        rel={subItem.opensInNewWindow ? 'noreferrer' : undefined}
                        onMouseEnter={() => setActiveCompanySubitemLabel(subItem.label)}
                        onFocus={() => setActiveCompanySubitemLabel(subItem.label)}
                        onClick={() => {
                          setIsCompanyPinnedOpen(false);
                          setIsCompanyHovered(false);
                        }}
                      >
                        {subItem.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              <div className="products-slideout-meme" role="note" aria-label={language === 'es' ? 'Enfoque de la compañía' : 'Company focus'}>
                <strong>{language === 'es' ? 'Tema actual' : 'Current topic'}</strong>
                <span>{activeCompanySubitemLabel || activeCompanyMenuItem?.summary || localizeMenuText('Company', language)}</span>
              </div>
            </div>
          </div>
        </nav>
        <div
          className="header-actions"
          aria-label={t('header.actions')}
          onClickCapture={(event) => {
            const target = event.target as HTMLElement | null;
            if (target?.closest('a')) {
              setIsMobileNavOpen(false);
            }
          }}
        >
          <Link to={CONTACT_DESTINATION} className="header-utility-link">
            {t('header.contactUs')}
          </Link>
          {HAS_SUPPORT_PHONE ? (
            <a href={SUPPORT_PHONE_TEL} className="header-phone" aria-label={`Call support at ${SUPPORT_PHONE_DISPLAY}`}>
              {t('header.phone')}
            </a>
          ) : null}
          {!isAuthenticated ? (
            <>
              <Link to="/login" className="header-utility-link">
                {t('header.login')}
              </Link>
              <Link to={`${SUBSCRIBE_DESTINATION}?plan=basic`} className="btn btn-primary header-cta-link">
                {t('header.startPlan')}
              </Link>
            </>
          ) : null}
          {isAuthenticated && user ? (
            <div className="header-user-menu" ref={userMenuRef}>
              <button
                type="button"
                className={`header-user-chip ${isUserMenuOpen ? 'active' : ''}`}
                aria-haspopup="menu"
                aria-expanded={isUserMenuOpen}
                aria-label={`${t('header.signedInAs')} ${signedInIdentity.secondary || signedInIdentity.primary}`}
                title={signedInIdentity.secondary || signedInIdentity.primary}
                onClick={() => {
                  setIsLanguageMenuOpen(false);
                  setIsThemeMenuOpen(false);
                  setIsUserMenuOpen((previous) => !previous);
                }}
              >
                <span className="header-user-avatar" aria-hidden="true">{signedInInitials}</span>
                <span className="header-user-meta">
                  <span className="header-user-label">{t('header.signedInAs')}</span>
                  <span className="header-user-value">{signedInIdentity.secondary || signedInIdentity.primary}</span>
                </span>
              </button>
              <div
                className={`header-user-dropdown ${isUserMenuOpen ? 'open' : ''}`}
                role="menu"
                aria-label={t('header.signedInAs')}
                aria-hidden={!isUserMenuOpen}
              >
                <Link
                  to={`${CONSOLE_PATH}/home`}
                  role="menuitem"
                  className="header-user-menu-item"
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    setIsMobileNavOpen(false);
                  }}
                >
                  {t('header.console')}
                </Link>
                {hasDesignerAccess ? (
                  <Link
                    to={CONSOLE_STYLE_DESIGNER_PATH}
                    role="menuitem"
                    className="header-user-menu-item"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      setIsMobileNavOpen(false);
                    }}
                  >
                    {t('header.designSystem')}
                  </Link>
                ) : null}
                {hasSuperUserAccess && KEYCLOAK_ADMIN_URL ? (
                  <a
                    href={KEYCLOAK_ADMIN_URL}
                    role="menuitem"
                    className="header-user-menu-item"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    {t('header.keycloakAdmin')}
                  </a>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  className="header-user-menu-item header-logout-btn"
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    logout();
                    navigate(websitePath('/login'));
                  }}
                >
                  {t('header.logout')}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </header>
      )}

      {!isStandaloneAuthRoute && websiteBreadcrumbs.length > 1 ? (
        <nav className="site-breadcrumbs" aria-label="Breadcrumb">
          {websiteBreadcrumbs.map((crumb, index) => {
            const isCurrent = index === websiteBreadcrumbs.length - 1;
            return (
              <React.Fragment key={`${crumb.label}-${index}`}>
                {index > 0 ? <span className="site-breadcrumb-separator" aria-hidden="true">/</span> : null}
                {crumb.to && !isCurrent ? (
                  <Link to={crumb.to}>{crumb.label}</Link>
                ) : (
                  <span className={isCurrent ? 'site-breadcrumb-current' : ''}>{crumb.label}</span>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      ) : null}

      <LinkedInInsightTag />
      <main className={`site-main ${isStandaloneAuthRoute ? 'site-main-auth' : ''}`}>{children}</main>

      {!isStandaloneAuthRoute && (
      <footer className="site-footer">
        <div className="footer-main">
          <div className="footer-brand-block">
            <div className="footer-cloud-logo">
              <BrandLockup path={websiteLogoPath} variant="footer" />
            </div>
            <div className="footer-social" aria-label={t('footer.socialLinks')}>
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noreferrer" aria-label="Facebook">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M13.62 21.99v-9.1h3.06l.46-3.55h-3.52V7.08c0-1.03.28-1.73 1.76-1.73h1.88V2.18c-.33-.04-1.45-.14-2.76-.14-2.73 0-4.6 1.66-4.6 4.72v2.58H6.83v3.55h3.07v9.1h3.72z" />
                </svg>
              </a>
              <a href={SOCIAL_LINKS.x} target="_blank" rel="noreferrer" aria-label="X">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M18.9 3h2.9l-6.34 7.26L23 21h-5.87l-4.6-6.04L7.26 21H4.34l6.78-7.75L1 3h6.02l4.16 5.48L18.9 3zm-1.03 16.23h1.61L6.15 4.68H4.42l13.45 14.55z" />
                </svg>
              </a>
              <a href={SOCIAL_LINKS.linkedIn} target="_blank" rel="noreferrer" aria-label="LinkedIn">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6.94 8.5H3.56V20h3.38V8.5zM5.25 3C4.14 3 3.24 3.9 3.24 5.01s.9 2.01 2.01 2.01 2.01-.9 2.01-2.01C7.26 3.9 6.36 3 5.25 3zM20.76 13.39c0-3.15-1.68-5.19-4.55-5.19-2.1 0-3.04 1.16-3.57 1.98V8.5H9.26V20h3.38v-6.39c0-1.68.32-3.31 2.4-3.31 2.06 0 2.09 1.93 2.09 3.42V20h3.38v-6.61z" />
                </svg>
              </a>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noreferrer" aria-label="Instagram">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4c0 3.2-2.6 5.8-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8C2 4.6 4.6 2 7.8 2zm8.2 1.9H8C5.8 3.9 3.9 5.8 3.9 8v8c0 2.2 1.9 4.1 4.1 4.1h8c2.2 0 4.1-1.9 4.1-4.1V8c0-2.2-1.9-4.1-4.1-4.1zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 1.9a3.1 3.1 0 1 0 0 6.2 3.1 3.1 0 0 0 0-6.2zm5.25-3a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4z" />
                </svg>
              </a>
              <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noreferrer" aria-label="YouTube">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M21.58 7.19c-.23-.88-.91-1.57-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.78.39c-.89.23-1.57.92-1.8 1.8C2 8.78 2 12 2 12s0 3.22.42 4.81c.23.88.91 1.57 1.8 1.8C5.8 19 12 19 12 19s6.2 0 7.78-.39c.89-.23 1.57-.92 1.8-1.8C22 15.22 22 12 22 12s0-3.22-.42-4.81zM10 15.5v-7l6 3.5-6 3.5z" />
                </svg>
              </a>
              <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M14.5 3h2.54c.17 1.43 1.02 2.64 2.33 3.32.8.43 1.71.67 2.63.69v2.64a8.03 8.03 0 0 1-4.96-1.69v6.29c0 1.32-.42 2.61-1.21 3.67a6.64 6.64 0 0 1-5.34 2.75A6.77 6.77 0 0 1 7.08 8.23a6.67 6.67 0 0 1 4.58-1.31v2.73a3.97 3.97 0 0 0-1.38-.07c-1.71.24-2.98 1.7-2.98 3.45 0 2.03 1.65 3.68 3.68 3.68 1.89 0 3.47-1.43 3.66-3.31.03-.24.03-.49.03-.73V3z" />
                </svg>
              </a>
            </div>
            {HAS_SUPPORT_PHONE ? <p className="footer-phone">{t('footer.callUsAt')}</p> : null}
            <Link className="footer-contact-link" to={websitePath('/support')}>
              {t('footer.supportOptions')}
            </Link>
          </div>

          <div className="footer-grid">
            <div className="footer-column">
              <strong>{t('footer.col.products')}</strong>
              <div className="footer-links">
                <Link to={websitePath('/products/ofac-compliance')}>{t('footer.products.ofac')}</Link>
                <Link to={DOD_CONTRACTS_DESTINATION}>{t('footer.products.government')}</Link>
                <Link to={websitePath('/products/website-compliance')}>{t('footer.products.website')}</Link>
                <Link to={websitePath('/products/education-compliance')}>{t('footer.products.education')}</Link>
                <Link to={websitePath('/products/ai-compliance')}>{t('footer.products.ai')}</Link>
                <Link to={SUPPORT_DESTINATION}>{t('footer.products.plans')}</Link>
              </div>
            </div>
            <div className="footer-column">
              <strong>{t('footer.col.solutions')}</strong>
              <div className="footer-links">
                <Link to={SIGNUP_DESTINATION}>{t('footer.solutions.smallBiz')}</Link>
                <Link to={websitePath('/products/enterprise')}>{t('footer.solutions.enterprise')}</Link>
                <Link to={DOD_CONTRACTS_DESTINATION}>{t('footer.solutions.defense')}</Link>
                <Link to={websitePath('/industries')}>{t('footer.popular.dreamforce')}</Link>
              </div>
            </div>
            <div className="footer-column">
              <strong>{t('footer.col.frameworks')}</strong>
              <div className="footer-links">
                <Link to={websitePath('/products/ofac-compliance')}>{t('footer.frameworks.ofac')}</Link>
                <Link to={websitePath('/products/dod-contracts-compliance/cybersecurity')}>{t('footer.frameworks.cmmc')}</Link>
                <Link to={websitePath('/products/website-compliance/privacy-and-data-governance')}>{t('footer.frameworks.website')}</Link>
                <Link to={websitePath('/products/education-compliance/ferpa-and-glba-program')}>{t('footer.frameworks.ferpa')}</Link>
              </div>
            </div>
            <div className="footer-column">
              <strong>{t('footer.col.resources')}</strong>
              <div className="footer-links">
                <Link to={websitePath('/blog')}>{t('footer.resources.blog')}</Link>
                <Link to={websitePath('/news')}>{t('footer.resources.news')}</Link>
                <Link to={TRUST_DESTINATION}>{t('footer.resources.trust')}</Link>
                <Link to={RELEASE_DESTINATION}>{t('footer.resources.release')}</Link>
                <Link to={websitePath('/company')}>{t('footer.resources.company')}</Link>
                <Link to={DASHBOARDS_VALUE_DESTINATION}>{t('footer.resources.dashboards')}</Link>
                <Link to={LIABILITY_MONITORING_DESTINATION}>{t('footer.resources.liability')}</Link>
              </div>
            </div>
            <div className="footer-column">
              <strong>{t('footer.col.support')}</strong>
              <div className="footer-links">
                <Link to={HELP_DESTINATION}>{t('footer.support.help')}</Link>
                <Link to={DEMO_REQUEST_DESTINATION}>{t('footer.support.demo')}</Link>
                <Link to="/login">{t('footer.support.login')}</Link>
                <Link to={CONTACT_DESTINATION}>{t('footer.support.contact')}</Link>
              </div>
            </div>
          </div>

        </div>

        <div className="footer-bottom-bar">
          <div className="footer-bottom-inner">
            <div className="footer-bottom-topline">
              <span>{t('footer.worldwide')}</span>
              <div className="footer-inline-links" aria-label="Footer legal links">
                <Link to={LEGAL_DESTINATION}>{t('footer.legal')}</Link>
                <Link to={PRIVACY_DESTINATION}>{t('footer.privacy')}</Link>
                <Link to={TERMS_DESTINATION}>{t('footer.terms')}</Link>
                <Link to={RESPONSIBLE_DISCLOSURE_DESTINATION}>{t('footer.responsibleDisclosure')}</Link>
                <Link to={CONTACT_DESTINATION}>{t('footer.contact')}</Link>
                <Link to={TRUST_DESTINATION}>{t('footer.about.trust')}</Link>
                <Link to={COOKIE_PREFERENCES_DESTINATION}>{t('footer.cookiePreferences')}</Link>
              </div>
            </div>
            <p className="footer-copyright">
              {t('footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
      )}
    </div>
    <NextCompliAssistant />
    </WebsiteLanguageContext.Provider>
  );
};

const PageTemplate: React.FC<{
  title: string;
  subtitle: string;
  bullets: string[];
  details?: string[];
  ctaLabel?: string;
  ctaTo?: string;
}> = ({ title, subtitle, bullets, details, ctaLabel, ctaTo }) => {
  const { t } = useWebsiteLanguage();
  return (
  <section className="page-section">
    <h1>{title}</h1>
    <p className="subtitle">{subtitle}</p>
    <div className="card-grid">
      {bullets.map((bullet) => (
        <article key={bullet} className="feature-card">
          <h3>{bullet}</h3>
        </article>
      ))}
    </div>
    {details && (
      <div className="page-stack">
        {details.map((detail) => (
          <p key={detail}>{detail}</p>
        ))}
      </div>
    )}
    {ctaLabel && ctaTo && (
      <div className="cta-band">
        <p>{t('page.readyCta')}</p>
        <Link to={ctaTo} className="btn btn-primary">
          {ctaLabel}
        </Link>
      </div>
    )}
  </section>
  );
};

const PRODUCT_VALUE_PROPOSITIONS: Record<string, { title: string; paragraphs: string[]; bullets: string[] }> = {
  'continuous-monitoring': {
    title: 'Value proposition',
    paragraphs: [
      'Continuous Monitoring gives compliance teams an always-on control loop that catches source changes and newly relevant risk conditions between scheduled batch reviews.',
      'By standardizing recurring checks, delta detection, and escalation handoffs, organizations reduce blind spots that can emerge when lists, licenses, or disclosures change unexpectedly.',
      'This operating model improves response speed and governance confidence by turning monitoring into a measurable, repeatable workflow rather than an ad hoc activity.',
    ],
    bullets: [],
  },
  'ofac-sdn-general-licenses': {
    title: 'Value proposition',
    paragraphs: [
      'OFAC SDN & General Licenses creates one operating view for list exposure and applicable licensing relief, so teams can make defensible decisions without splitting review across disconnected tools.',
      'Instead of treating SDN hits and general-license context as separate workflows, this model keeps source updates, policy interpretation, and analyst actions in one traceable lifecycle. Venezuela GL 52C is current license context, not a skip of SDN or reporting.',
      'The result is lower decision latency, stronger audit readiness, and fewer avoidable escalations caused by incomplete sanctions context at the time of review.',
    ],
    bullets: [
      'Detects sanctions-relevant changes earlier and routes them into consistent triage workflows.',
      'Improves decision quality by pairing blocked-party signals with current general-license applicability context.',
      'Produces auditable evidence trails for reviews, remediation actions, and policy-aligned outcomes.',
    ],
  },
};

const ProductDetailPage: React.FC<{ content: ProductDetailContent }> = ({ content }) => {
  const { t, language } = useWebsiteLanguage();
  const businessRisk = buildProductBusinessRiskOverview(content.slug, content.label);
  const isOfacProduct = content.slug === 'ofac-compliance';
  const plainLanguage = PRODUCT_PLAIN_LANGUAGE_EXPLAINERS[content.slug] ?? {
    what: `${content.label} is a defined operating capability that translates policy intent into repeatable controls and evidence-backed decisions.`,
    why: 'It is needed to reduce ambiguity in high-stakes compliance workflows and to improve consistency, traceability, and audit readiness.',
  };
  const businessRiskGraphic = React.useMemo(
    () => (content.slug === 'ofac-compliance' ? buildBusinessRiskIcebergGraphicDataUri() : null),
    [content.slug],
  );
  const productHeroGraphic = React.useMemo(
    () => buildSemanticPageGraphicDataUri({
      title: content.title,
      summary: content.summary,
      bullets: content.highlights,
      contextLabel: content.label,
      pageKey: `product:${content.slug}`,
    }),
    [content.highlights, content.label, content.slug, content.summary, content.title],
  );
  const productHeroGraphicSrc =
    PRODUCT_PNG_HERO_BY_SLUG[content.slug] ?? productHeroGraphic;

  return (
    <section className="page-section product-platform-explore-page">
      <div className="menu-item-page-hero">
        <div className="menu-item-page-hero-copy">
          <h1 className="product-platform-explore-title">{toExplorePageTitle(content.title)}</h1>
          <p className="subtitle"><DodTermText text={content.summary} language={language} /></p>
          <div className="product-platform-overview-cta product-platform-overview-cta--left">
            <Link to={DEMO_REQUEST_DESTINATION} className="btn btn-primary product-platform-overview-cta-link">
              Request platform walkthrough
            </Link>
          </div>
        </div>
        <div className={`menu-item-page-hero-visual${isOfacProduct ? ' is-ofac-generated' : ''}`} aria-hidden="true">
          <div className="menu-item-page-hero-orb" />
          <div className={`menu-item-page-hero-frame${isOfacProduct ? ' is-ofac-generated' : ''}`}>
            <img
              src={productHeroGraphicSrc}
              alt=""
              className={`menu-item-page-hero-image menu-item-page-hero-image--semantic${isOfacProduct ? ' is-ofac-generated' : ''}`}
              loading="lazy"
            />
          </div>
          {!isOfacProduct && (
            <aside className="menu-item-page-hero-insight">
              <p>Focus areas</p>
              <ul>
                {content.highlights.slice(0, 3).map((highlight) => (
                  <li key={highlight}>{highlight}</li>
                ))}
              </ul>
            </aside>
          )}
        </div>
      </div>
      <div className="page-stack">
        <h2>{t('product.whatThisIs')}</h2>
        <p><DodTermText text={plainLanguage.what} language={language} /></p>
        <h2>{t('product.whyNeeded')}</h2>
        <p><DodTermText text={plainLanguage.why} language={language} /></p>
      </div>
      {content.slug === 'ofac-compliance' && (
        <div className="page-stack">
          <h2>{t('product.ofacFlowStory')}</h2>
          <ul className="feature-list">
            {OFAC_FLOW_STORY.map((item) => (
              <li key={item.title}><strong>{item.title}:</strong> {item.meaning}</li>
            ))}
          </ul>
        </div>
      )}
      {content.slug === 'ofac-compliance' && (
        <OfacVenezuelaWindow language={language} variant="product" demoTo={DEMO_REQUEST_DESTINATION} />
      )}
      {content.slug === 'ofac-compliance' && (
        <OfacPlanCoverage language={language} variant="product" />
      )}
      {PRODUCT_VALUE_PROPOSITIONS[content.slug] && (
        <div className="page-stack">
          <h2>{PRODUCT_VALUE_PROPOSITIONS[content.slug].title}</h2>
          {PRODUCT_VALUE_PROPOSITIONS[content.slug].paragraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          {PRODUCT_VALUE_PROPOSITIONS[content.slug].bullets.length > 0 && (
            <ul className="feature-list">
              {PRODUCT_VALUE_PROPOSITIONS[content.slug].bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="page-stack">
        <h2>{t('product.businessRisk')}</h2>
        {businessRiskGraphic && (
          <img
            src={businessRiskGraphic}
            alt="Iceberg visual showing visible and hidden costs of non-compliance"
            loading="lazy"
            style={{ width: '100%', maxWidth: 860, borderRadius: 16, marginBottom: 16 }}
          />
        )}
        <ul className="feature-list">
          {businessRisk.map((risk) => (
            <li key={risk}>{risk}</li>
          ))}
        </ul>
      </div>
      <div className="product-platform-submenu-grid" aria-label={`${content.label} sub menu links`}>
        {content.highlights.map((highlight) => (
          <Link
            key={highlight}
            to={websitePath(`/products/${content.slug}/${toProductSubSlug(content.slug, highlight)}`)}
            className="product-platform-submenu-card"
          >
            <span
              className={`product-platform-submenu-icon product-platform-submenu-icon--${productTileTone(content.slug, highlight)}`}
              aria-hidden="true"
            >
              <ProductTileGlyph productSlug={content.slug} highlight={highlight} className="product-platform-submenu-icon-svg" />
            </span>
            <span className="product-platform-submenu-label">{highlight}</span>
          </Link>
        ))}
      </div>
    </section>
  );
};

const ProductSubDetailPage: React.FC<{ content: ProductSubDetailContent }> = ({ content }) => {
  const { t, language } = useWebsiteLanguage();
  const navigate = useNavigate();
  const isOfacSubpage = content.parentSlug === 'ofac-compliance';
  const subitemExplainer = PRODUCT_SUBITEM_PLAIN_LANGUAGE_EXPLAINERS[`${content.parentSlug}/${content.subSlug}`] ?? {
    what: `${content.label} is a practical control lane within ${content.parentLabel} that clarifies how to execute this workflow consistently.`,
    why: 'It is needed to reduce interpretation drift, speed analyst actions, and improve traceability for internal and external review.',
  };
  const [chatPrompt, setChatPrompt] = React.useState('');
  const [openFaqIndex, setOpenFaqIndex] = React.useState(0);
  const subpageHeroGraphic = React.useMemo(
    () => {
      const subitemGraphicKey = `${content.parentSlug}/${content.subSlug}`;
      const forcedPngHero = PRODUCT_SUBITEM_PNG_HERO_BY_KEY[subitemGraphicKey];
      if (forcedPngHero) {
        return forcedPngHero;
      }
      if (content.parentSlug === 'ofac-compliance') {
        const variant = OFAC_SUBPAGE_VARIANT_BY_SLUG[content.subSlug] ?? 'overview';
        return variant === 'overview'
          ? OFAC_OVERVIEW_GRAPHIC_PATH
          : buildOfacIconCollageGraphicDataUri(variant);
      }
      return buildSubitemExplainerGraphicDataUri({
        pageKey: `product-sub:${content.parentSlug}/${content.subSlug}`,
        title: content.title,
        summary: content.summary,
        parentLabel: content.parentLabel,
        workflow: content.workflow,
        capabilities: content.capabilities,
        outcomes: content.outcomes,
      });
    },
    [
      content.capabilities,
      content.outcomes,
      content.parentLabel,
      content.parentSlug,
      content.subSlug,
      content.summary,
      content.title,
      content.workflow,
    ],
  );

  const testimonials = [
    {
      quote: `${content.title} helped us cut investigation time by 43% while improving escalation consistency across teams.`,
      author: 'A. Rivera',
      role: 'Director of Compliance Operations',
    },
    {
      quote: `We moved from fragmented checks to a repeatable workflow with stronger source traceability and audit readiness.`,
      author: 'M. Chen',
      role: 'VP, Risk Technology',
    },
    {
      quote: `The rollout gave analysts clearer decision paths and faster collaboration between engineering and policy stakeholders.`,
      author: 'S. Patel',
      role: 'Global Screening Program Lead',
    },
  ];

  const faqItems = [
    {
      question: `What is ${content.label}?`,
      answer:
        `${content.label} is an operational capability in our ${content.parentLabel} portfolio that helps teams standardize execution, evidence, and decision quality across production workflows.`,
    },
    {
      question: `How does ${content.label} help my business?`,
      answer:
        `It improves analyst throughput, reduces avoidable manual rework, and gives risk and compliance stakeholders a clearer, auditable operating model for high-confidence decisions.`,
    },
    {
      question: `How do I choose the right rollout model for ${content.label}?`,
      answer:
        `Start with one scoped workflow, define measurable outcomes, and use staged rollout controls to expand from pilot to broader production use once performance and review quality are validated.`,
    },
    {
      question: `Can ${content.label} work with our existing tooling?`,
      answer:
        `Yes. The implementation is designed to integrate with existing systems via API-first patterns so you can connect current case, alert, and reporting processes without replacing core infrastructure.`,
    },
    {
      question: `Is ${content.label} suitable for both real-time and batch operations?`,
      answer:
        `Yes. Teams can run low-latency decision flows for immediate checks while also supporting scheduled high-volume processing for periodic portfolio or customer-base reviews.`,
    },
    {
      question: `How quickly can we launch ${content.label}?`,
      answer:
        `Most teams begin with a focused deployment in days, then expand in phases as rules, thresholds, and governance controls are tuned for production-scale operations.`,
    },
    {
      question: `What workflow should we follow for ${content.label}?`,
      answer:
        `Define required sources and decision criteria, execute checks with traceable actions, then publish evidence-linked outcomes with clear ownership for follow-up and periodic review.`,
    },
  ];

  return (
    <section className="page-section product-subpage">
      <div className="product-subpage-hero">
        <div className="product-subpage-hero-grid">
          <div className="product-subpage-hero-copy">
            <p className="product-subpage-eyebrow">{content.parentLabel}</p>
            <h1>{content.title}</h1>
            <p className="subtitle"><DodTermText text={content.summary} language={language} /></p>
            <div className="product-subpage-hero-actions">
              <a href="#workflow-readiness" className="btn btn-primary">
                {t('product.watchDemo')}
              </a>
            </div>
            <div className="product-subpage-hero-secondary-links">
              <Link to={DEMO_REQUEST_DESTINATION} className="btn btn-secondary">
                Request demo
              </Link>
              <Link to={websitePath(`/products/${content.parentSlug}`)} className="btn btn-secondary">
                Back to {content.parentLabel}
              </Link>
            </div>
          </div>

          <div className={`product-subpage-hero-visual${isOfacSubpage ? ' is-ofac-generated' : ''}`} aria-hidden="true">
            <img
              src={subpageHeroGraphic}
              alt=""
              className={`product-subpage-hero-semantic-graphic${isOfacSubpage ? ' is-ofac-generated' : ''}`}
              loading="lazy"
            />
          </div>
        </div>
      </div>

      <div className="product-subpage-layout">
        <article className="product-subpage-panel">
          <h2>{t('product.whatThisIs')}</h2>
          <p><DodTermText text={subitemExplainer.what} language={language} /></p>
          <h2>{t('product.whyNeeded')}</h2>
          <p><DodTermText text={subitemExplainer.why} language={language} /></p>
        </article>

        {content.parentSlug === 'ofac-compliance' && content.subSlug === 'sdn-and-general-license-screening' ? (
          <article className="product-subpage-panel product-subpage-panel--wide">
            <OfacVenezuelaWindow language={language} variant="subpage" demoTo={DEMO_REQUEST_DESTINATION} />
          </article>
        ) : null}
        {content.parentSlug === 'ofac-compliance' && (
          content.subSlug === 'continuous-ofac-monitoring'
          || content.subSlug === 'real-time-and-batch-ofac-checks'
        ) ? (
          <article className="product-subpage-panel product-subpage-panel--wide">
            <OfacPlanCoverage language={language} variant="subpage" />
          </article>
        ) : null}

        <article className="product-subpage-panel">
          <h2>{t('product.businessValue')}</h2>
          <p><DodTermText text={content.businessValue} language={language} /></p>
        </article>

        <article className="product-subpage-panel">
          <h2>{t('product.businessRisk')}</h2>
          <ul className="product-subpage-list">
            {content.businessRisk.map((risk) => (
              <li key={risk}>{risk}</li>
            ))}
          </ul>
        </article>

        <article className="product-subpage-panel">
          <h2>{t('product.capabilities')}</h2>
          <ul className="product-subpage-list">
            {content.capabilities.map((capability) => (
              <li key={capability}><DodTermText text={capability} language={language} /></li>
            ))}
          </ul>
        </article>

        <article className="product-subpage-panel">
          <h2>{t('product.workflow')}</h2>
          <ol className="product-subpage-list ordered">
            {content.workflow.map((step) => (
              <li key={step}><DodTermText text={step} language={language} /></li>
            ))}
          </ol>
        </article>

        <article className="product-subpage-panel">
          <h2>{t('product.expectedOutcomes')}</h2>
          <ul className="product-subpage-list">
            {content.outcomes.map((outcome) => (
              <li key={outcome}><DodTermText text={outcome} language={language} /></li>
            ))}
          </ul>
        </article>
      </div>

      <div className="product-subpage-media-layout">
        <article id="workflow-readiness" className="product-subpage-panel product-subpage-video-panel">
          <h2>{t('product.watchDemo')}</h2>
          <p>Video walkthroughs are still being finalized. Use this rollout checklist to align stakeholders before a guided session.</p>
          <ol className="product-subpage-list ordered">
            {content.workflow.slice(0, 3).map((step) => (
              <li key={`${content.id}-workflow-readiness-${step}`}>{step}</li>
            ))}
          </ol>
          <Link to={DEMO_REQUEST_DESTINATION} className="btn btn-primary">
            Request guided walkthrough
          </Link>
        </article>

        <article className="product-subpage-panel product-subpage-testimonials-panel">
          <h2>{t('product.testimonials')}</h2>
          <div className="product-subpage-testimonials">
            {testimonials.map((testimonial) => (
              <blockquote key={`${content.id}-${testimonial.author}`} className="product-subpage-testimonial-card">
                <p>{testimonial.quote}</p>
                <footer>
                  <strong>{testimonial.author}</strong>
                  <span>{testimonial.role}</span>
                </footer>
              </blockquote>
            ))}
          </div>
        </article>
      </div>

      <section className="product-subpage-faq" aria-label="Frequently asked questions">
        <h2>{t('product.faq')}</h2>
        <div className="product-subpage-faq-list">
          {faqItems.map((item, index) => {
            const isOpen = openFaqIndex === index;
            return (
              <article key={`${content.id}-${item.question}`} className={`product-subpage-faq-item ${isOpen ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="product-subpage-faq-trigger"
                  aria-expanded={isOpen}
                  onClick={() => setOpenFaqIndex((previous) => (previous === index ? -1 : index))}
                >
                  <span>{item.question}</span>
                  <span className="product-subpage-faq-icon" aria-hidden="true">
                    {isOpen ? '−' : '+'}
                  </span>
                </button>
                {isOpen && (
                  <div className="product-subpage-faq-answer">
                    <p>{item.answer}</p>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <aside className="product-subpage-chatbox" aria-label={language === 'es' ? '¿Necesitas ayuda? Pregunta al asistente.' : 'Need help? Ask the assistant.'}>
        <div className="product-subpage-chatbox-header">{language === 'es' ? '¿Necesitas ayuda? Pregunta al asistente.' : 'Need help? Ask the assistant.'}</div>
        <p className="product-subpage-chatbox-scope">
          {language === 'es' ? 'Asistente de IA para preguntas de producto y precios.' : 'AI assistant for product and pricing questions.'}
        </p>
        <form
          className="product-subpage-chatbox-form"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmedPrompt = chatPrompt.trim();
            if (!trimmedPrompt) {
              return;
            }
            navigate(SALES_ASSISTANT_DESTINATION, { state: { prompt: trimmedPrompt } });
          }}
        >
          <textarea
            aria-label={language === 'es' ? 'Cuéntame tu caso y te ayudo a empezar' : 'Tell me your use case and I will help you start'}
            aria-describedby="product-chatbox-trust"
            placeholder={
              language === 'es'
                ? 'Lidero cumplimiento para un equipo de 40 personas. ¿Qué debo automatizar primero?'
                : 'I run compliance for a 40-person team. What should I automate first?'
            }
            value={chatPrompt}
            onChange={(event) => setChatPrompt(event.target.value)}
            rows={2}
            maxLength={500}
          />
          <button type="submit" className="btn btn-primary">
            {language === 'es' ? 'Enviar' : 'Send'}
          </button>
        </form>
        <p className="product-subpage-chatbox-trust" id="product-chatbox-trust">
          {language === 'es' ? 'La IA puede equivocarse. Verifica detalles importantes y evita compartir secretos.' : 'AI can make mistakes. Verify important details and avoid sharing secrets.'}
        </p>
        <div className="product-subpage-chatbox-quick-actions">
          <Link to={SALES_ASSISTANT_DESTINATION}>{language === 'es' ? 'Conéctame con ventas' : 'Connect me with a sales rep'}</Link>
          <Link to={DEMO_REQUEST_DESTINATION}>{language === 'es' ? 'Solicitar una guía' : 'Request a walkthrough'}</Link>
          <Link to={SIGNUP_DESTINATION}>{language === 'es' ? '¿Cómo puede ayudar a mi negocio?' : 'How can this help my business?'}</Link>
        </div>
      </aside>
    </section>
  );
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t, language } = useWebsiteLanguage();
  const [isSignupSubmitted, setIsSignupSubmitted] = React.useState(false);
  const [newsletterEmail, setNewsletterEmail] = React.useState('');
  const [isNewsletterSubmitting, setIsNewsletterSubmitting] = React.useState(false);
  const [newsletterError, setNewsletterError] = React.useState<string | null>(null);
  const [homeAgentPrompt, setHomeAgentPrompt] = React.useState('');
  const submitHomeAgentPrompt = React.useCallback((rawPrompt: string) => {
    const trimmedPrompt = rawPrompt.trim();
    if (!trimmedPrompt) {
      return;
    }
    notePostludeVisit('search', { note: trimmedPrompt });

    const comparePlansLabels = new Set([
      t('home.askStarterOne'),
      'Compare plans',
      'Comparar planes',
    ]);
    if (comparePlansLabels.has(trimmedPrompt)) {
      navigate(SUPPORT_DESTINATION);
      return;
    }

    navigate(SALES_ASSISTANT_DESTINATION, { state: { prompt: trimmedPrompt } });
  }, [navigate, t]);
  const [isSalesBotOpen, setIsSalesBotOpen] = React.useState(false);
  const [salesBotInput, setSalesBotInput] = React.useState('');
  const [salesBotStep, setSalesBotStep] = React.useState(0);
  const [salesBotSubmitting, setSalesBotSubmitting] = React.useState(false);
  const [salesBotCompleted, setSalesBotCompleted] = React.useState(false);
  const [salesBotMessages, setSalesBotMessages] = React.useState<Array<{ role: 'bot' | 'user'; text: string }>>([
    { role: 'bot', text: 'Hi! I can help route your request to sales. What is your first and last name?' },
  ]);
  const [salesBotAnswers, setSalesBotAnswers] = React.useState({
    fullName: '',
    workEmail: '',
    phone: '',
    company: '',
    employees: '',
    useCase: '',
  });

  const salesBotQuestions = [
    { key: 'fullName', question: 'What is your first and last name?', placeholder: 'Full name', type: 'text' },
    { key: 'workEmail', question: 'Great. What is your work email?', placeholder: 'Work email', type: 'email' },
    { key: 'phone', question: 'What is the best phone number for follow-up?', placeholder: 'Phone', type: 'tel' },
    { key: 'company', question: 'What company are you with?', placeholder: 'Company', type: 'text' },
    { key: 'employees', question: 'Roughly how many employees are in your organization?', placeholder: 'Number of employees', type: 'text' },
    { key: 'useCase', question: 'What are you trying to solve right now?', placeholder: 'Primary use case', type: 'text' },
  ] as const;

  const queueLeadForRetry = (payload: Record<string, string>) => {
    try {
      const existing = JSON.parse(window.localStorage.getItem('nextcompli.crm.pendingLeads') || '[]') as Array<Record<string, string>>;
      existing.push(payload);
      window.localStorage.setItem('nextcompli.crm.pendingLeads', JSON.stringify(existing));
    } catch {
      // Ignore storage failures.
    }
  };

  const submitLeadToCrmFlow = async (answers: Record<string, string>) => {
    const payload = {
      ...answers,
      ...getAttribution(),
      source: 'website-connect-sales-bot',
      locale: language,
      page_path: window.location.pathname,
      submitted_at: new Date().toISOString(),
    };

    try {
      const response = await fetch(SALES_CRM_FLOW_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('CRM flow temporarily unavailable');
      }

      setSalesBotMessages((previous) => ([
        ...previous,
        {
          role: 'bot',
          text: 'Thanks — your details were submitted to our CRM flow. A sales specialist will follow up shortly.',
        },
      ]));
    } catch {
      queueLeadForRetry(payload);
      setSalesBotMessages((previous) => ([
        ...previous,
        {
          role: 'bot',
          text: 'Thanks — we captured your details and queued them for CRM sync. A sales specialist will still follow up.',
        },
      ]));
    }
  };

  const submitSalesBotAnswer = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const answer = salesBotInput.trim();
    if (!answer || salesBotSubmitting || salesBotCompleted) {
      return;
    }

    const stepConfig = salesBotQuestions[salesBotStep];
    const nextAnswers = {
      ...salesBotAnswers,
      [stepConfig.key]: answer,
    };

    setSalesBotAnswers(nextAnswers);
    setSalesBotMessages((previous) => ([
      ...previous,
      { role: 'user', text: answer },
    ]));
    setSalesBotInput('');

    const aiGuideReply = await requestAssistantReply({
      prompt: `Customer is in a lead-qualification flow and answered: "${answer}". Give a short, helpful follow-up under 2 sentences.`,
      sessionId: 'website-sales-lead-bot',
      contextLines: [
        `Current step: ${stepConfig.key}`,
        'Goal: keep user engaged and move toward practical implementation guidance.',
      ],
      fallbackReply: 'Thanks — that helps. I can tailor implementation guidance as we continue.',
    });

    setSalesBotMessages((previous) => ([
      ...previous,
      { role: 'bot', text: aiGuideReply },
    ]));

    if (salesBotStep >= salesBotQuestions.length - 1) {
      setSalesBotSubmitting(true);
      await submitLeadToCrmFlow(nextAnswers);
      setSalesBotSubmitting(false);
      setSalesBotCompleted(true);
      return;
    }

    const upcomingStep = salesBotStep + 1;
    setSalesBotStep(upcomingStep);
    setSalesBotMessages((previous) => ([
      ...previous,
      { role: 'bot', text: salesBotQuestions[upcomingStep].question },
    ]));
  };

  const closeSalesBot = () => {
    setIsSalesBotOpen(false);
  };

  const submitNewsletterSignup = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedEmail = newsletterEmail.trim();
    if (!normalizedEmail) {
      return;
    }

    setIsNewsletterSubmitting(true);
    setNewsletterError(null);

    try {
      const response = await fetch(NEWSLETTER_SIGNUP_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: normalizedEmail,
          audience: NEWSLETTER_AUDIENCE,
          source: 'website-home-signup',
          locale: language,
          consent: true,
          subscribed_at: new Date().toISOString(),
          page_path: window.location.pathname,
          ...getAttribution(),
        }),
      });

      if (!response.ok) {
        let detail = language === 'es'
          ? 'No pudimos guardar tu suscripción. Inténtalo de nuevo.'
          : 'We could not save your signup. Please try again.';
        try {
          const payload = await response.json();
          if (typeof payload?.detail === 'string' && payload.detail.trim()) {
            detail = payload.detail;
          }
        } catch {
          // Ignore non-JSON error bodies.
        }
        throw new Error(detail);
      }

      setIsSignupSubmitted(true);
      identifySiteVisitor({
        email: normalizedEmail,
        source: 'newsletter',
        path: window.location.pathname || '/',
      });
      setNewsletterEmail('');
    } catch (submissionError) {
      const defaultMessage = language === 'es'
        ? 'No pudimos guardar tu suscripción. Inténtalo de nuevo.'
        : 'We could not save your signup. Please try again.';
      const message = submissionError instanceof Error && submissionError.message.trim()
        ? submissionError.message
        : defaultMessage;
      setNewsletterError(message);
    } finally {
      setIsNewsletterSubmitting(false);
    }
  };

  return (
    <section className="home-page">
    <div className="home-ai-strip">
      <div className="home-ai-content">
        <div className="home-ai-shell">
          <div className="home-ai-bot" aria-hidden="true">
            <div className="home-ai-bot-face">
              <span className="home-ai-bot-eye" />
              <span className="home-ai-bot-eye" />
              <span className="home-ai-bot-mouth" />
            </div>
            <span className="home-ai-bot-spark home-ai-bot-spark-one" />
            <span className="home-ai-bot-spark home-ai-bot-spark-two" />
          </div>

          <div className="home-ai-main">
            <h2>{t('home.askHeading')}</h2>
            <p className="home-ai-scope">{t('home.askScope')}</p>
            <form
              className="home-ai-input"
              onSubmit={(event) => {
                event.preventDefault();
                submitHomeAgentPrompt(homeAgentPrompt);
              }}
            >
              <span className="home-ai-input-icon" aria-hidden="true">
                🤖
              </span>
              <textarea
                className="home-ai-input-field"
                aria-label={language === 'es' ? 'Describe tu caso de uso' : 'Describe your use case'}
                rows={2}
                placeholder={t('home.askPlaceholder')}
                value={homeAgentPrompt}
                onChange={(event) => setHomeAgentPrompt(event.target.value)}
                maxLength={500}
              />
              <button className="home-ai-input-send" type="submit" aria-label="Submit agent question" disabled={!homeAgentPrompt.trim()}>
                {t('header.askAction')}
              </button>
            </form>
            <div className="home-ai-starters" aria-label="Suggested prompts">
              <button
                type="button"
                className="home-ai-starter"
                onClick={() => {
                  const prompt = t('home.askStarterOne');
                  setHomeAgentPrompt(prompt);
                  submitHomeAgentPrompt(prompt);
                }}
              >
                {t('home.askStarterOne')}
              </button>
              <button
                type="button"
                className="home-ai-starter"
                onClick={() => {
                  const prompt = t('home.askStarterTwo');
                  setHomeAgentPrompt(prompt);
                  submitHomeAgentPrompt(prompt);
                }}
              >
                {t('home.askStarterTwo')}
              </button>
              <button
                type="button"
                className="home-ai-starter"
                onClick={() => {
                  const prompt = t('home.askStarterThree');
                  setHomeAgentPrompt(prompt);
                  submitHomeAgentPrompt(prompt);
                }}
              >
                {t('home.askStarterThree')}
              </button>
            </div>
            <p className="home-ai-trust">{t('home.askTrust')}</p>
            <div className="home-ai-actions">
              <button className="home-ai-btn" type="button" onClick={() => navigate(SALES_ASSISTANT_DESTINATION)}>
                {t('home.connectSales')}
              </button>
              <button className="home-ai-btn home-ai-btn-primary" type="button" onClick={() => navigate(`${SUBSCRIBE_DESTINATION}?plan=basic`)}>
                {t('home.startPlan')}
              </button>
              <button className="home-ai-btn" type="button" onClick={() => navigate(SUPPORT_DESTINATION)}>
                {t('home.choosePlan')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="home-hero">
      <div className="home-hero-inner">
        <p className="hero-kicker">{t('home.heroKicker')}</p>
        <h1>
          {t('home.heroTitleLead')}
          <span> {t('home.heroTitleAccent')}</span>
        </h1>
        <p className="hero-copy">
          {t('home.heroCopy')}
        </p>
        <p className="hero-copy muted">
          {t('home.heroCopyMuted')}
        </p>

        <div className="hero-cta-row">
          <button className="btn btn-primary" type="button" onClick={() => navigate(`${SUBSCRIBE_DESTINATION}?plan=basic`)}>
            {t('home.startPlan')}
          </button>
          <button className="btn btn-secondary" type="button" onClick={() => navigate(SUPPORT_DESTINATION)}>
            {t('home.seePlans')}
          </button>
        </div>
        <p className="hero-quiet-link">
          <button type="button" onClick={() => navigate(ARCHITECTURE_DESTINATION)}>
            {t('home.viewArchitecture')}
          </button>
        </p>
      </div>

      <div className="home-showcase-row">
        <article className="showcase-card">
          <div className="showcase-image">
            <img src="/home/operational-visibility.webp" alt="Operational visibility dashboard" loading="lazy" />
          </div>
          <div className="showcase-copy">
            <h3>Operational Visibility</h3>
            <p>Track service health, risk posture, and performance from one unified dashboard.</p>
          </div>
        </article>
        <article className="showcase-card">
          <div className="showcase-image">
            <img src="/home/composable-cloud.webp" alt="Composable cloud deployment architecture" loading="lazy" />
          </div>
          <div className="showcase-copy">
            <h3>Composable Deployment</h3>
            <p>Use the same architecture from local testing through cloud production environments.</p>
          </div>
        </article>
        <article className="showcase-card">
          <div className="showcase-image">
            <img src="/home/regulated-control.webp" alt="Regulated-ready policy control framework" loading="lazy" />
          </div>
          <div className="showcase-copy">
            <h3>Regulated-Ready Control</h3>
            <p>Enable only required compliance services and preserve clear operational governance.</p>
          </div>
        </article>
      </div>
    </div>
    <section className="page-section">
      <div className="cta-band">
        <p>{t('home.deploymentPlan')}</p>
        <button className="btn btn-primary" type="button" onClick={() => navigate(DEMO_REQUEST_DESTINATION)}>
          {t('home.requestDemoCta')}
        </button>
      </div>

      <div className="cta-band cta-band-newsletter" id="email-updates">
        {!isSignupSubmitted ? (
          <form
            className="home-signup-form"
            onSubmit={submitNewsletterSignup}
            aria-labelledby="home-email-updates-title"
          >
            <p className="home-signup-kicker">{t('home.signupKicker')}</p>
            <h2 id="home-email-updates-title" className="home-signup-title">{t('home.signupPrompt')}</h2>
            <p className="home-signup-hint">{t('home.signupHint')}</p>
            <div className="home-signup-row">
              <input
                id="home-signup-email"
                placeholder={t('home.signupPlaceholder')}
                aria-label={t('home.signupPlaceholder')}
                type="email"
                autoComplete="email"
                value={newsletterEmail}
                onChange={(event) => setNewsletterEmail(event.target.value)}
                required
              />
              <button className="btn btn-primary" type="submit" disabled={isNewsletterSubmitting || !newsletterEmail.trim()}>
                {isNewsletterSubmitting ? (language === 'es' ? 'Enviando…' : 'Submitting…') : t('home.signupButton')}
              </button>
            </div>
            {newsletterError ? <p className="signup-error" role="alert">{newsletterError}</p> : null}
          </form>
        ) : (
          <div className="home-signup-success" role="status">
            <h2 className="home-signup-title">{t('home.signupSuccessTitle')}</h2>
            <p className="home-signup-hint">{t('home.signupSuccessBody')}</p>
            <button className="btn btn-secondary home-signup-again" type="button" onClick={() => setIsSignupSubmitted(false)}>
              {t('home.signupAddAnother')}
            </button>
          </div>
        )}
      </div>
    </section>

    {isSalesBotOpen ? (
      <div className="sales-lead-bot-backdrop" role="presentation" onClick={closeSalesBot}>
        <section className="sales-lead-bot" role="dialog" aria-modal="true" aria-label="Sales assistant bot" onClick={(event) => event.stopPropagation()}>
          <header className="sales-lead-bot-header">
            <h3>NextCompli sales assistant</h3>
            <button type="button" className="sales-lead-bot-close" onClick={closeSalesBot} aria-label="Close sales bot">×</button>
          </header>
          <div className="sales-lead-bot-thread" aria-live="polite">
            {salesBotMessages.map((message, index) => (
              <p key={`${message.role}-${index}`} className={`sales-lead-bot-msg sales-lead-bot-msg-${message.role}`}>
                {message.text}
              </p>
            ))}
          </div>
          <form className="sales-lead-bot-form" onSubmit={submitSalesBotAnswer}>
            <input
              type={salesBotQuestions[salesBotStep]?.type || 'text'}
              placeholder={salesBotQuestions[salesBotStep]?.placeholder || 'Your answer'}
              value={salesBotInput}
              onChange={(event) => setSalesBotInput(event.target.value)}
              disabled={salesBotSubmitting || salesBotCompleted}
              required
            />
            <button className="btn btn-primary" type="submit" disabled={salesBotSubmitting || salesBotCompleted || !salesBotInput.trim()}>
              {salesBotCompleted ? 'Completed' : salesBotSubmitting ? 'Submitting…' : 'Send'}
            </button>
          </form>
          <p className="sales-lead-bot-note">Lead details are routed to CRM flow: <strong>{SALES_CRM_FLOW_URL}</strong></p>
        </section>
      </div>
    ) : null}
    </section>
  );
};

type NewsCardItem = {
  title: string;
  summary: string;
  href: string;
  icon: string;
  source?: string;
  publishedAt?: string;
};

const RELEASE_CARDS: NewsCardItem[] = [
  {
    title: 'Venezuela GL 52C',
    summary: 'OFAC General License 52C (14 Sep 2026) authorizes certain PdVSA activity for established U.S. entities. It still requires SDN screening, payment routing, and reporting.',
    href: 'https://ofac.treasury.gov/media/936926/download?inline=',
    icon: '🛢️',
  },
  {
    title: 'OFAC Enforcement',
    summary: 'Syrian sanctions settlement highlights expectations for documented controls and escalation practices.',
    href: 'https://ofac.treasury.gov/recent-actions/20260225_66',
    icon: '⚖️',
  },
  {
    title: 'Sanctions List Updates',
    summary: 'Iran and non-proliferation designation changes affecting screening and monitoring workflows.',
    href: 'https://ofac.treasury.gov/recent-actions/20260225',
    icon: '📋',
  },
  {
    title: 'Cyber & Russia Designations',
    summary: 'New OFAC cyber and Russia-related designations for watchlist and risk model updates.',
    href: 'https://ofac.treasury.gov/recent-actions/20260224',
    icon: '🛡️',
  },
  {
    title: 'SEC Enforcement Manual',
    summary: 'SEC enforcement manual updates relevant to governance, investigations, and compliance documentation.',
    href: 'https://www.sec.gov/newsroom/press-releases/2026-20-secs-division-enforcement-announces-updates-enforcement-manual',
    icon: '🏛️',
  },
  {
    title: 'SEC Reporting Rules',
    summary: 'Proposed amendments on fund portfolio holdings that impact compliance reporting programs.',
    href: 'https://www.sec.gov/newsroom/press-releases/2026-19-sec-proposes-amendments-reduce-burdens-reporting-fund-portfolio-holdings',
    icon: '📈',
  },
  {
    title: 'NIST CSF 2.0',
    summary: 'Two-year CSF 2.0 update with practical guidance for cyber risk and control operations.',
    href: 'https://www.nist.gov/blogs/cybersecurity-insights/celebrating-two-years-csf-20',
    icon: '🔐',
  },
  {
    title: 'NIST Cyber AI Profile',
    summary: 'Community profile work on cybersecurity of AI and AI-enabled cyber defense use cases.',
    href: 'https://www.nccoe.nist.gov/projects/cyber-ai-profile',
    icon: '🤖',
  },
  {
    title: 'OFAC General Licenses',
    summary: 'Track newly issued and amended licenses that affect operations and permissible transactions.',
    href: 'https://ofac.treasury.gov/selected-general-licenses-issued-ofac',
    icon: '🌐',
  },
];

const LIVE_NEWS_FALLBACK: NewsCardItem[] = [
  {
    title: 'Venezuela GL 52C',
    summary: 'OFAC General License 52C (14 Sep 2026) authorizes certain PdVSA activity for established U.S. entities. It still requires SDN screening, payment routing, and reporting.',
    href: 'https://ofac.treasury.gov/media/936926/download?inline=',
    icon: '🛢️',
    source: 'OFAC',
  },
  {
    title: 'SEC Newsroom',
    summary: 'Latest SEC press and compliance-related rulemaking updates from official newsroom sources.',
    href: 'https://www.sec.gov/newsroom/press-releases',
    icon: '🏛️',
    source: 'SEC',
  },
  {
    title: 'OFAC Recent Actions',
    summary: 'Current sanctions actions and designation announcements from OFAC.',
    href: 'https://ofac.treasury.gov/recent-actions',
    icon: '⚖️',
    source: 'OFAC',
  },
  {
    title: 'NIST Cybersecurity Insights',
    summary: 'Operational cybersecurity and governance guidance from NIST.',
    href: 'https://www.nist.gov/blogs/cybersecurity-insights',
    icon: '🔐',
    source: 'NIST',
  },
  {
    title: 'CISA Alerts',
    summary: 'Government cybersecurity alerts, advisories, and key mitigation notices.',
    href: 'https://www.cisa.gov/news-events/alerts',
    icon: '🛡️',
    source: 'CISA',
  },
];

const LIVE_NEWS_FEEDS = [
  {
    url: 'https://news.google.com/rss/search?q=compliance%20OR%20sanctions%20OR%20regulation&hl=en-US&gl=US&ceid=US:en',
    source: 'Google News',
  },
  {
    url: 'https://www.sec.gov/news/pressreleases.rss',
    source: 'SEC',
  },
];

const newsIconByIndex = (index: number): string => {
  const icons = ['📰', '⚖️', '🏛️', '🔐', '📊', '🌍', '🛡️', '📈'];
  return icons[index % icons.length];
};

const stripHtml = (value: string): string => value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const parseNewsItemsFromXml = (xmlText: string, fallbackSource: string): NewsCardItem[] => {
  const xml = new DOMParser().parseFromString(xmlText, 'application/xml');
  const parserError = xml.querySelector('parsererror');
  if (parserError) {
    return [];
  }

  return Array.from(xml.querySelectorAll('item'))
    .map((item, index) => {
      const title = item.querySelector('title')?.textContent?.trim() || 'Untitled update';
      const href = item.querySelector('link')?.textContent?.trim() || '';
      const descriptionRaw = item.querySelector('description')?.textContent || '';
      const summary = stripHtml(descriptionRaw).slice(0, 180) || 'Open the source link for complete details.';
      const publishedAt = item.querySelector('pubDate')?.textContent?.trim();
      const source = item.querySelector('source')?.textContent?.trim() || fallbackSource;
      return {
        title,
        href,
        summary,
        publishedAt,
        source,
        icon: newsIconByIndex(index),
      };
    })
    .filter((item) => /^https?:\/\//i.test(item.href));
};

const formatNewsMeta = (item: NewsCardItem): string => {
  const normalizedSource = item.source?.trim();
  const parsedDate = item.publishedAt ? new Date(item.publishedAt) : null;
  const hasValidDate = Boolean(parsedDate && !Number.isNaN(parsedDate.getTime()));
  const dateLabel = hasValidDate ? parsedDate!.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

  if (normalizedSource && dateLabel) {
    return `${dateLabel} | ${normalizedSource}`;
  }
  if (dateLabel) {
    return dateLabel;
  }
  if (normalizedSource) {
    return normalizedSource;
  }
  return 'Official source';
};

const ReleasePage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  return (
  <section className="page-section news-page">
    <div className="news-hero">
      <h1>{t('page.releaseNotes')}</h1>
      <p className="subtitle">
        {t('page.releaseNotesSubtitle')}
      </p>
    </div>

    <section className="news-card-grid" aria-label={t('page.releaseAria')}>
      {RELEASE_CARDS.map((item) => (
        <article key={item.href} className="news-card">
          <h3>{item.title}</h3>
          <p>{item.summary}</p>
          <a href={item.href} target="_blank" rel="noreferrer">{t('page.learnMore')}</a>
          <span className="news-card-icon" aria-hidden="true">{item.icon}</span>
        </article>
      ))}
    </section>
  </section>
  );
};

const NewsPage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  const [newsCards, setNewsCards] = React.useState<NewsCardItem[]>(LIVE_NEWS_FALLBACK);
  const [isLoadingNews, setIsLoadingNews] = React.useState(true);
  const [newsError, setNewsError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let isCancelled = false;

    const loadLiveNews = async () => {
      setIsLoadingNews(true);
      setNewsError(null);

      try {
        const feedResults = await Promise.allSettled(
          LIVE_NEWS_FEEDS.map(async (feed) => {
            const response = await fetch(`https://api.allorigins.win/raw?url=${encodeURIComponent(feed.url)}`);
            if (!response.ok) {
              throw new Error(`Failed to load feed: ${feed.source}`);
            }
            const xml = await response.text();
            return parseNewsItemsFromXml(xml, feed.source);
          }),
        );

        const merged = feedResults
          .flatMap((result) => (result.status === 'fulfilled' ? result.value : []))
          .filter((item, index, array) => array.findIndex((candidate) => candidate.href === item.href) === index)
          .slice(0, 8);

        if (!merged.length) {
          throw new Error('No live news items returned.');
        }

        if (!isCancelled) {
          setNewsCards(merged);
        }
      } catch {
        if (!isCancelled) {
          setNewsCards(LIVE_NEWS_FALLBACK);
          setNewsError('unavailable');
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingNews(false);
        }
      }
    };

    void loadLiveNews();

    return () => {
      isCancelled = true;
    };
  }, []);

  return (
    <section className="page-section news-page">
      <div className="news-hero">
        <h1>{t('page.newsroom')}</h1>
        <p className="subtitle">
          {t('page.newsroomSubtitle')}
        </p>
        {isLoadingNews ? <p className="subtitle">{t('page.newsroomLoading')}</p> : null}
        {newsError ? <p className="subtitle">{t('page.newsFeedError')}</p> : null}
      </div>

      <section className="news-card-grid" aria-label={t('page.newsAria')}>
        {newsCards.map((item) => (
          <article key={item.href} className="news-card">
            <p className="blog-card-meta">{formatNewsMeta(item)}</p>
            <h3>{item.title}</h3>
            <p>{item.summary}</p>
            <a href={item.href} target="_blank" rel="noreferrer">{t('page.learnMore')}</a>
            <span className="news-card-icon" aria-hidden="true">{item.icon}</span>
          </article>
        ))}
      </section>
    </section>
  );
};

const ArchitecturePage: React.FC = () => {
  const { t } = useWebsiteLanguage();

  return (
    <section className="page-section architecture-page">
      <div className="architecture-graphic-stage">
        <div className="architecture-graphic-board" aria-label={t('architecture.graphicAria')}>
          <h1 className="architecture-graphic-title">{t('architecture.heroTitle')}</h1>
          <div className="architecture-graphic-grid">
            {ARCHITECTURE_VISUAL_CARDS.map((card) => (
              <article key={card.id} className="architecture-graphic-card">
                <div className="architecture-graphic-image-wrap">
                  <img src={card.image} alt={card.imageAlt} loading="lazy" />
                </div>
                <div className="architecture-graphic-copy">
                  <span className="architecture-graphic-badge">{card.badge}</span>
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                  <Link to={card.to}>{card.cta}</Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>

      <div className="card-grid architecture-grid">
        {ARCHITECTURE_SECTIONS.map((section) => (
          <article key={section.title} className="architecture-card">
            <p className="architecture-card-eyebrow">{t('architecture.domain')}</p>
            <h3>{section.title}</h3>
            <ul className="architecture-list">
              {section.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          </article>
        ))}
      </div>

      <div className="architecture-disclaimer">
        <p>{t('page.architectureDisclaimer')}</p>
      </div>

      <div className="architecture-references">
        <h2>{t('architecture.references')}</h2>
        <div className="card-grid">
          {ARCHITECTURE_REFERENCE_LINKS.map((reference) => (
            <article key={reference.href} className="architecture-reference-card">
              <h3>{reference.label}</h3>
              <a href={reference.href} target="_blank" rel="noreferrer">{t('architecture.openReference')}</a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const IndustriesPage: React.FC = () => {
  const { language } = useWebsiteLanguage();

  return (
  <section className="industries-overview-page">
    <section className="industries-overview-hero">
      <div className="industries-overview-shell">
        <p className="industries-overview-eyebrow">{language === 'es' ? 'Asistente de IA para Industrias' : 'AI Assistant for Industries'}</p>
        <h1>{language === 'es' ? 'Innova más rápido con IA + Datos + CRM' : 'Innovate faster with AI + Data + CRM'}</h1>
        <p>
          {language === 'es'
            ? 'Aumenta ingresos, mejora eficiencia y ofrece mejores experiencias con agentes autónomos de IA.'
            : 'Grow revenue, improve efficiency, and drive better customer experiences with autonomous AI agents.'}
        </p>
      </div>
    </section>

    <section className="industries-overview-catalog page-section">
      <h2>{language === 'es' ? 'Elige tu industria para empezar.' : 'Choose your industry to get started.'}</h2>
      <div className="industries-overview-grid" aria-label="Industry catalog">
        {INDUSTRY_DETAIL_CONTENT.map((industry) => {
          const tileTone = industryTileTone(industry.label);
          const tileIconKey = INDUSTRY_ICON_KEY_BY_SLUG[industry.slug];

          return (
            <Link
              key={industry.slug}
              to={websitePath(`/industries/${industry.slug}`)}
              className="industries-overview-card"
              aria-label={industry.label}
            >
              <span className={`industries-overview-icon industries-overview-icon--${tileTone}`} aria-hidden="true">
                {tileIconKey && <IndustryIcon iconKey={tileIconKey} className="industry-icon-svg industry-icon-svg--overview" />}
              </span>
              <span className="industries-overview-label">{industry.label}</span>
            </Link>
          );
        })}
      </div>
    </section>
  </section>
  );
};

const IndustryDetailPage: React.FC<{ content: IndustryDetailContent }> = ({ content }) => {
  const { language } = useWebsiteLanguage();
  const industrySubItems = INDUSTRY_SUBDETAIL_CONTENT.filter((subItem) => subItem.parentSlug === content.slug);
  const industryHeroGraphic = React.useMemo(
    () => buildSemanticPageGraphicDataUri({
      title: content.title,
      summary: content.heroSubtitle,
      bullets: industrySubItems.slice(0, 4).map((subItem) => subItem.label),
      contextLabel: content.label,
      pageKey: `industry:${content.slug}`,
    }),
    [content.heroSubtitle, content.label, content.slug, content.title, industrySubItems],
  );

  if (industrySubItems.length === 0) {
    return (
      <PageTemplate
        title={content.title}
        subtitle={content.summary}
        bullets={['Industry operations', 'Customer engagement workflows', 'AI-aligned enablement']}
        details={[
          withBrand(`${content.label} pages describe how {brand} fits that industry's compliance work.`),
          'Use these pages to align internal rollout planning with industry-specific requirements and solution mapping.',
        ]}
        ctaLabel={withBrand('View {brand} industry page')}
        ctaTo={websitePath('/support')}
      />
    );
  }

  return (
    <section className="page-section product-platform-explore-page">
      <div className="menu-item-page-hero">
        <div className="menu-item-page-hero-copy">
          <h1 className="product-platform-explore-title">{content.title}</h1>
          <p className="subtitle">{content.heroSubtitle}</p>
          <div className="product-platform-overview-cta product-platform-overview-cta--left">
            <Link to={content.ctaTo ?? websitePath('/support')} className="btn btn-primary product-platform-overview-cta-link">
              {content.ctaLabel}
            </Link>
          </div>
        </div>
        <div className="menu-item-page-hero-visual" aria-hidden="true">
          <div className="menu-item-page-hero-orb" />
          <div className="menu-item-page-hero-frame">
            <img src={industryHeroGraphic} alt="" className="menu-item-page-hero-image menu-item-page-hero-image--semantic" loading="lazy" />
          </div>
          <aside className="menu-item-page-hero-insight">
            <p>Industry priorities</p>
            <ul>
              {industrySubItems.slice(0, 3).map((subItem) => (
                <li key={subItem.id}>{subItem.label}</li>
              ))}
            </ul>
          </aside>
        </div>
      </div>
      <div className="product-platform-submenu-grid" aria-label={`${content.label} sub menu links`}>
        {industrySubItems.map((subItem) => (
          <Link key={subItem.id} to={subItem.to} className="product-platform-submenu-card">
            <span
              className={`product-platform-submenu-icon product-platform-submenu-icon--${productTileTone(content.slug, subItem.label)}`}
              aria-hidden="true"
            >
              <ProductTileGlyph productSlug={content.slug} highlight={subItem.label} className="product-platform-submenu-icon-svg" />
            </span>
            <span className="product-platform-submenu-label">{subItem.label}</span>
          </Link>
        ))}
      </div>
      {content.slug === 'energy-and-utilities' ? (
        <OfacVenezuelaWindow language={language} variant="industry" demoTo={DEMO_REQUEST_DESTINATION} />
      ) : null}
    </section>
  );
};

const IndustrySubDetailPage: React.FC<{ content: IndustrySubDetailContent }> = ({ content }) => {
  const { language } = useWebsiteLanguage();
  const isOilAndGas = content.parentSlug === 'energy-and-utilities' && content.subSlug === 'oil-and-gas';
  const siblingSolutions = INDUSTRY_SUBDETAIL_CONTENT.filter(
    (item) => item.parentSlug === content.parentSlug && item.subSlug !== content.subSlug,
  );
  const parentLabelLower = content.parentLabel.toLowerCase();
  const curatedImageIds = INDUSTRY_INSIGHT_IMAGE_IDS[content.parentSlug] ?? DEFAULT_INDUSTRY_INSIGHT_IMAGE_IDS;
  const curatedProductImageIds = INDUSTRY_PRODUCT_IMAGE_IDS[content.parentSlug] ?? DEFAULT_INDUSTRY_PRODUCT_IMAGE_IDS;
  const productCards = content.pillars.slice(0, 4).map((pillar, index) => ({
    id: `${content.id}-pillar-${index}`,
    title: pillar,
    description:
      content.details[index] ??
      `${pillar} supports ${content.parentLabel} teams with practical implementation patterns and measurable outcomes.`,
    ctaLabel: index === 0 ? 'Get the guide' : 'Learn more',
    imageUrl: `https://picsum.photos/id/${curatedProductImageIds[index % curatedProductImageIds.length]}/640/360`,
  }));
  const insightCards = [
    {
      id: `${content.id}-insight-primary`,
      title: content.label,
      badge: 'Article',
      imageUrl: `https://picsum.photos/id/${curatedImageIds[0]}/640/360`,
    },
    ...siblingSolutions.slice(0, 3).map((solution, index) => ({
      id: `${content.id}-insight-${index}`,
      title: solution.label,
      badge: index % 2 === 0 ? 'Webinar' : 'Video',
      to: solution.to,
      imageUrl: `https://picsum.photos/id/${curatedImageIds[(index + 1) % curatedImageIds.length]}/640/360`,
    })),
  ];
  const industrySubHeroGraphic = React.useMemo(
    () => buildSubitemExplainerGraphicDataUri({
      pageKey: `industry-sub:${content.parentSlug}/${content.subSlug}`,
      title: content.title,
      summary: content.summary,
      parentLabel: content.parentLabel,
      workflow: content.details,
      capabilities: content.pillars,
      outcomes: content.pillars,
    }),
    [content.details, content.parentLabel, content.parentSlug, content.subSlug, content.pillars, content.summary, content.title],
  );

  return (
    <section className="page-section industry-solution-page">
      <div className="industry-solution-hero-wrap">
        <div className="industry-solution-hero">
          <div className="industry-solution-hero-copy">
            <p className="industry-solution-eyebrow">{content.parentLabel}</p>
            <h1>{content.title}</h1>
            <p className="industry-solution-subtitle">{content.summary}</p>
            <div className="industry-solution-actions">
              {isOilAndGas ? (
                <>
                  <Link to={websitePath('/products/ofac-compliance')} className="btn btn-primary industry-solution-cta-primary">
                    Open OFAC Compliance
                  </Link>
                  <Link to={DEMO_REQUEST_DESTINATION} className="btn btn-secondary industry-solution-cta-secondary">
                    Request demo
                  </Link>
                </>
              ) : (
                <>
                  <Link to={websitePath('/support')} className="btn btn-primary industry-solution-cta-primary">
                    Read the article
                  </Link>
                  <Link to={websitePath('/support')} className="btn btn-secondary industry-solution-cta-secondary">
                    Contact us
                  </Link>
                </>
              )}
            </div>
          </div>
          <div className="industry-solution-hero-visual" aria-hidden="true">
            <div className="industry-solution-hero-orb" />
            <div className="industry-solution-hero-card industry-solution-hero-card-main">
              <img src={industrySubHeroGraphic} alt="" className="industry-solution-hero-card-image" loading="lazy" />
            </div>
            <div className="industry-solution-hero-card industry-solution-hero-card-chat">
              <p>{content.parentLabel} teams</p>
              <span>Connected service and growth workflows</span>
            </div>
          </div>
        </div>
      </div>

      {isOilAndGas ? (
        <div className="page-section">
          <OfacVenezuelaWindow language={language} variant="industry" demoTo={DEMO_REQUEST_DESTINATION} />
        </div>
      ) : null}

      <section className="industry-solution-products">
        <h2>{withBrand(`Build your ${parentLabelLower} compliance work with these {brand} products.`)}</h2>
        <div className="industry-solution-product-grid">
          {productCards.map((card, index) => (
            <article key={card.id} className={`industry-solution-product-card accent-${index % 4}`}>
              <img src={card.imageUrl} alt={`${card.title} product visual`} className="industry-solution-product-media" loading="lazy" />
              <h3>{card.title}</h3>
              <p>{card.description}</p>
              <Link to={websitePath('/support')}>{card.ctaLabel}</Link>
            </article>
          ))}
        </div>
      </section>

      <section className="industry-solution-insights" aria-label={`Latest ${content.parentLabel} insights`}>
        <h2>Stay on top of transition with the latest {parentLabelLower} trends and insights</h2>
        <div className="industry-solution-insights-grid">
          {insightCards.map((insight, index) => {
            const CardTag = insight.to ? Link : 'article';
            const cardProps = insight.to ? { to: insight.to } : {};

            return (
              <CardTag
                key={insight.id}
                className={`industry-solution-insight-card insight-tone-${index % 4}`}
                {...cardProps}
              >
                <img
                  src={insight.imageUrl}
                  alt={`${insight.title} insight visual`}
                  className="industry-solution-insight-media"
                  loading="lazy"
                />
                <div className="industry-solution-insight-content">
                  <span className="industry-solution-insight-badge">{insight.badge}</span>
                  <h3>{insight.title}</h3>
                </div>
              </CardTag>
            );
          })}
        </div>
      </section>
    </section>
  );
};

const PlatformPage: React.FC = () => {
  const { t } = useWebsiteLanguage();

  return (
  <PageTemplate
    title={t('page.platformTitle')}
    subtitle={t('page.platformSubtitle')}
    bullets={[
      t('platform.bullet1'),
      t('platform.bullet2'),
      t('platform.bullet3'),
      t('platform.bullet4'),
    ]}
    details={[
      t('platform.detail1'),
      t('platform.detail2'),
    ]}
    ctaLabel={t('page.requestDemo')}
    ctaTo={websitePath('/support')}
  />
  );
};

const BlogPage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  const blogPosts = [
    {
      date: 'Feb 25, 2026',
      category: 'Sanctions',
      title: 'Making sanctions monitoring operational, not reactive',
      summary: 'How teams convert daily regulatory updates into predictable control workflows with clear ownership.',
      href: 'https://ofac.treasury.gov/recent-actions',
      image:
        'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80',
    },
    {
      date: 'Feb 24, 2026',
      category: 'Governance',
      title: 'What SEC enforcement updates mean for compliance teams',
      summary: 'A practical read on translating enforcement-process changes into better internal governance and evidence.',
      href: 'https://www.sec.gov/newsroom/press-releases/2026-20-secs-division-enforcement-announces-updates-enforcement-manual',
      image:
        'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1200&q=80',
    },
    {
      date: 'Feb 24, 2026',
      category: 'Cybersecurity',
      title: 'Applying NIST CSF 2.0 updates to compliance operations',
      summary: 'Steps for aligning risk, incident readiness, and reporting workflows to the latest NIST guidance.',
      href: 'https://www.nist.gov/cyberframework/updates-archive',
      image:
        'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=1200&q=80',
    },
  ];

  return (
    <section className="page-section blog-page">
      <div className="blog-page-header">
        <h1>{t('blog.title')}</h1>
        <p>{t('blog.subtitle')}</p>
      </div>

      <section className="blog-card-grid" aria-label={t('blog.aria')}>
        {blogPosts.map((post) => (
          <article key={post.title} className="blog-card">
            <img src={post.image} alt={post.title} loading="lazy" />
            <div className="blog-card-body">
              <p className="blog-card-meta">{post.date} | {post.category}</p>
              <h3>{post.title}</h3>
              <p>{post.summary}</p>
              <a href={post.href} target="_blank" rel="noreferrer">{t('blog.readMore')}</a>
            </div>
          </article>
        ))}
      </section>
    </section>
  );
};

const CUSTOMER_STORY_CONTENT: CustomerStoryContent[] = [
  {
    slug: 'customer-highlights-from-earnings',
    label: 'Customer Highlights from Earnings',
    title: 'Customer Highlights from Earnings',
    subtitle: 'Representative lessons from public-company style operating updates: leaders want fewer disconnected compliance facts and more decision-ready narrative.',
    industry: 'Executive reporting',
    challenge: 'Leadership teams need compliance reporting that connects operational signal, audit evidence, and business impact without relying on fragmented spreadsheets or status updates.',
    approach: [
      'Normalize monitoring, alert, and case outputs into one reporting cadence.',
      'Translate control activity into board-ready themes such as response speed, evidence quality, and remediation progress.',
      'Keep executive narratives tied to source-backed operating facts instead of anecdotal updates.',
    ],
    outcomes: [
      'Sharper executive visibility into risk trendlines and control performance.',
      'Faster preparation for earnings, board, and investor-facing review cycles.',
      'Clearer accountability for remediation owners and unresolved exposure.',
    ],
    sources: [
      { label: 'NIST Risk Management Framework', href: 'https://csrc.nist.gov/projects/risk-management/about-rmf' },
      { label: 'FTC Business Guidance', href: 'https://www.ftc.gov/business-guidance' },
    ],
  },
  {
    slug: 'analytics-stories',
    label: 'Analytics Stories',
    title: 'Analytics Stories',
    subtitle: 'A pattern for analytics-led compliance teams that need reporting surfaces operators trust and executives can act on quickly.',
    industry: 'Analytics operations',
    challenge: 'Teams often have data, but not a shared operational view that turns source health, risk posture, and workload signals into one clear story.',
    approach: [
      'Design dashboards around operating decisions, not just raw metrics.',
      'Expose source freshness, trend movement, and unresolved escalation volume in the same workspace.',
      'Use repeatable KPI definitions so board, compliance, and engineering see the same truth.',
    ],
    outcomes: [
      'Higher confidence in weekly and monthly compliance reporting.',
      'Less time spent reconciling metrics across tools and teams.',
      'Faster prioritization of the controls that materially affect exposure.',
    ],
    sources: [
      { label: 'NIST Risk Management Framework', href: 'https://csrc.nist.gov/projects/risk-management/about-rmf' },
      { label: 'NIST Privacy Framework', href: 'https://www.nist.gov/privacy-framework' },
    ],
  },
  {
    slug: 'integration-stories',
    label: 'Integration Stories',
    title: 'Integration Stories',
    subtitle: 'A pattern for integration-heavy teams that need reliable handoffs between upstream systems, decision services, and downstream case workflows.',
    industry: 'Integration delivery',
    challenge: 'Compliance programs lose time and consistency when source systems, analysts, and case-management steps are connected through brittle manual handoffs.',
    approach: [
      'Use API-first integration boundaries to keep screening, alerting, and evidence capture synchronized.',
      'Standardize payloads so the same decision context is preserved from initial check through final case closure.',
      'Reduce environment drift by keeping rollout contracts consistent from sandbox through production.',
    ],
    outcomes: [
      'Cleaner system-to-system traceability for audits and incident reviews.',
      'Lower manual rework when workflows cross application boundaries.',
      'Faster rollout of new controls without rewriting every integration path.',
    ],
    sources: [
      { label: 'NIST Cybersecurity Framework', href: 'https://www.nist.gov/cyberframework' },
      { label: 'OFAC Compliance Resources', href: 'https://ofac.treasury.gov/compliance' },
    ],
  },
  {
    slug: 'collaboration-stories',
    label: 'Collaboration Stories',
    title: 'Collaboration Stories',
    subtitle: 'A pattern for collaboration-centered teams that need response workflows to move quickly without losing governance discipline.',
    industry: 'Incident coordination',
    challenge: 'Important compliance events frequently stall because ownership is unclear, escalation context is incomplete, or response updates are scattered across channels.',
    approach: [
      'Route alerts into a single response workflow with explicit ownership and due dates.',
      'Carry evidence, source links, and recommended actions into the same collaboration thread or case.',
      'Give legal, compliance, and engineering the same event context so remediation is aligned from the first escalation.',
    ],
    outcomes: [
      'Shorter time to assign and escalate high-risk events.',
      'Less ambiguity in incident ownership and next-step execution.',
      'Stronger decision history for internal review and external examinations.',
    ],
    sources: [
      { label: 'NIST Cybersecurity Framework', href: 'https://www.nist.gov/cyberframework' },
      { label: 'FTC Data Breach Response Guide', href: 'https://www.ftc.gov/business-guidance/resources/data-breach-response-guide-business' },
    ],
  },
  {
    slug: 'platform-stories',
    label: 'Platform Stories',
    title: 'Platform Stories',
    subtitle: 'A pattern for platform teams that need faster iteration without introducing avoidable compliance drift across environments.',
    industry: 'Platform delivery',
    challenge: 'Fast-moving teams need staging, production, and governance controls to stay aligned while still shipping frequently and responding to change.',
    approach: [
      'Keep environment assumptions explicit so the same control model can move from test to production.',
      'Use rollout checklists that pair delivery speed with evidence capture and approval gates.',
      'Treat compliance configuration as part of platform operations instead of a late-stage documentation task.',
    ],
    outcomes: [
      'Faster release cycles with fewer last-minute compliance surprises.',
      'Better consistency between deployed controls and documented expectations.',
      'Stronger audit readiness for teams operating across multiple environments.',
    ],
    sources: [
      { label: 'NIST Risk Management Framework', href: 'https://csrc.nist.gov/projects/risk-management/about-rmf' },
      { label: 'NIST Privacy Framework', href: 'https://www.nist.gov/privacy-framework' },
    ],
  },
];

const CUSTOMER_STORY_BY_SLUG: Record<string, CustomerStoryContent> = CUSTOMER_STORY_CONTENT.reduce((acc, story) => {
  acc[story.slug] = story;
  return acc;
}, {} as Record<string, CustomerStoryContent>);

const CustomersPage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  const featuredStory = CUSTOMER_STORY_CONTENT[0];

  return (
    <section className="page-section customers-page">
      <div className="customers-hero">
        <p className="customers-eyebrow">{t('customers.eyebrow')}</p>
        <h1>{t('customers.title')}</h1>
        <p className="subtitle">
          {t('customers.subtitle')}
        </p>
      </div>

      <section className="customers-featured-story" aria-label={t('customers.featuredAria')}>
        <div className="customers-featured-copy">
          <p className="customers-featured-label">{t('customers.featured')}</p>
          <h2>{featuredStory.title}</h2>
          <p>{featuredStory.challenge}</p>
          <p className="customers-featured-attribution">{t('customers.attribution').replace('{industry}', featuredStory.industry.toLowerCase())}</p>
          <Link to={websitePath(`/customers/${featuredStory.slug}`)} className="btn btn-secondary customers-featured-link">
            {t('customers.readPlaybook')}
          </Link>
        </div>
        <div className="customers-featured-metrics">
          {featuredStory.outcomes.map((outcome) => (
            <article key={outcome}>
              <strong>{t('customers.outcome')}</strong>
              <span>{outcome}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="customers-story-grid" aria-label={t('customers.moreAria')}>
        {CUSTOMER_STORY_CONTENT.map((story) => (
          <Link key={story.slug} to={websitePath(`/customers/${story.slug}`)} className="customers-story-card customers-story-card-link">
            <p className="customers-story-industry">{story.industry}</p>
            <h3>{story.label}</h3>
            <p>{story.challenge}</p>
            <p className="customers-story-result">{t('customers.outcome')}: {story.outcomes[0]}</p>
          </Link>
        ))}
      </section>

      <div className="cta-band">
        <p>{t('customers.cta')}</p>
        <Link to={DEMO_REQUEST_DESTINATION} className="btn btn-primary">
          {t('customers.talkTeam')}
        </Link>
      </div>
    </section>
  );
};

const CustomerStoryPage: React.FC<{ content: CustomerStoryContent }> = ({ content }) => {
  const { t } = useWebsiteLanguage();
  return (
  <section className="page-section customers-page customer-story-page">
    <div className="customers-hero">
      <p className="customers-eyebrow">{t('customers.storyEyebrow')}</p>
      <h1>{content.title}</h1>
      <p className="subtitle">{content.subtitle}</p>
    </div>

    <section className="customers-featured-story" aria-label={`${content.label} overview`}>
      <div className="customers-featured-copy">
        <p className="customers-featured-label">{t('customers.challenge')}</p>
        <h2>{content.challenge}</h2>
        <p>{t('customers.pattern').replace('{industry}', content.industry.toLowerCase())}</p>
      </div>
      <div className="customers-featured-metrics">
        {content.outcomes.map((outcome) => (
          <article key={outcome}>
            <strong>{t('customers.outcome')}</strong>
            <span>{outcome}</span>
          </article>
        ))}
      </div>
    </section>

    <section className="customers-story-grid" aria-label={`${content.label} implementation approach`}>
      {content.approach.map((step) => (
        <article key={step} className="customers-story-card">
          <p className="customers-story-industry">{t('customers.implementation')}</p>
          <p>{step}</p>
        </article>
      ))}
    </section>

    <div className="page-stack">
      <h2>{t('customers.guidance')}</h2>
      <p>
        {t('customers.guidanceBody')}
      </p>
      <ul className="feature-list">
        {content.sources.map((source) => (
          <li key={source.href}>
            <a href={source.href} target="_blank" rel="noreferrer">{source.label}</a>
          </li>
        ))}
      </ul>
    </div>

    <div className="cta-band">
      <p>{t('customers.needPattern')}</p>
      <Link to={DEMO_REQUEST_DESTINATION} className="btn btn-primary">
        {t('customers.walkthrough')}
      </Link>
    </div>
  </section>
  );
};

const CompanyPage: React.FC = () => {
  const { language } = useWebsiteLanguage();
  const isSpanish = language === 'es';

  const pillars = isSpanish
    ? [
      {
        title: 'Flujos de trabajo operativos',
        body: 'Convertimos tareas fragmentadas de cumplimiento en flujos claros para revisión, escalamiento y seguimiento.',
      },
      {
        title: 'Evidencia y trazabilidad',
        body: 'Diseñamos para contexto verificable, registros listos para auditoría y una historia clara de qué pasó y qué sigue.',
      },
      {
        title: 'Control y visibilidad',
        body: 'Ayudamos a operaciones, cumplimiento, legal y liderazgo a ver señales, riesgo y estado sin depender de hojas de cálculo dispersas.',
      },
    ]
    : [
      {
        title: 'Operational workflows',
        body: 'We turn fragmented compliance tasks into structured workflows for review, escalation, and follow-through.',
      },
      {
        title: 'Evidence and auditability',
        body: 'We design for traceable context, audit-ready records, and a clear history of what happened and what needs to happen next.',
      },
      {
        title: 'Control and visibility',
        body: 'We help operations, compliance, legal, and leadership teams see signal, risk, and status without relying on scattered spreadsheets.',
      },
    ];

  const outcomes = isSpanish
    ? [
      'Flujos guiados para alertas, revisiones e investigaciones.',
      'Registros listos para auditoría con mejor continuidad de evidencia.',
      'Visibilidad ejecutiva sobre estado, tendencias y trabajo pendiente.',
      'Implementación práctica para equipos que necesitan control, no solo dashboards.',
    ]
    : [
      'Guided workflows for alerts, reviews, and investigations.',
      'Audit-ready records with stronger evidence continuity.',
      'Executive visibility into status, trends, and unresolved work.',
      'Practical implementation for teams that need control, not just dashboards.',
    ];

  const leadershipPrinciples = isSpanish
    ? [
      'Decisiones de producto guiadas por resultados auditables y evidencia útil para operaciones.',
      'Priorización basada en reducción de riesgo operativo, claridad de propiedad y velocidad de respuesta.',
      'Estándar de afirmaciones concretas: describimos capacidades implementadas, no promesas vagas.',
    ]
    : [
      'Product decisions are guided by auditable outcomes and evidence that operations can actually use.',
      'Prioritization is based on operational risk reduction, ownership clarity, and response speed.',
      'We hold a concrete-claims standard: we describe implemented capabilities, not vague promises.',
    ];

  const trustPillars = isSpanish
    ? [
      'Transparencia de flujos de trabajo para revisión y escalamiento.',
      'Trazabilidad de acciones y continuidad de evidencia para auditorías.',
      'Controles operativos visibles para equipos de cumplimiento y liderazgo.',
    ]
    : [
      'Workflow transparency for review and escalation decisions.',
      'Action traceability and evidence continuity for audits.',
      'Visible operational controls for compliance and leadership teams.',
    ];

  const customerProofHighlights = isSpanish
    ? [
      { label: 'Orientado a operaciones', value: 'Flujos de trabajo estructurados para equipos reales' },
      { label: 'Orientado a evidencia', value: 'Registros auditables y contexto trazable' },
      { label: 'Orientado a decisiones', value: 'Visibilidad clara de riesgos, estado y próximos pasos' },
    ]
    : [
      { label: 'Operations-first', value: 'Structured workflows for real teams' },
      { label: 'Evidence-first', value: 'Auditable records with traceable context' },
      { label: 'Decision-ready', value: 'Clear visibility into risk, status, and next actions' },
    ];

  return (
    <section className="page-section company-page" aria-label={isSpanish ? 'Acerca de NextCompli' : 'About NextCompli'}>
      <div className="company-hero">
        <p className="company-eyebrow">{isSpanish ? 'Acerca de nosotros' : 'About us'}</p>
        <h1>
          {isSpanish
            ? 'NextCompli ayuda a equipos regulados a operar con claridad, control y evidencia.'
            : 'NextCompli helps regulated teams run compliance operations with clarity, control, and evidence.'}
        </h1>
        <p className="subtitle">
          {isSpanish
            ? 'Combinamos automatización de flujo de trabajo, monitoreo operativo y registros auditables para que las decisiones de cumplimiento no dependan de herramientas dispersas ni contexto perdido.'
            : 'We combine workflow automation, operational monitoring, and auditable records so compliance decisions do not depend on disconnected tools or lost context.'}
        </p>
      </div>

      <div className="company-pillars" aria-label={isSpanish ? 'Misión, visión y valores' : 'Mission, vision, and values'}>
        <article className="company-pillar-card">
          <h3>{isSpanish ? 'Visión' : 'Vision'}</h3>
          <p>
            {isSpanish
              ? 'Ser la plataforma más fácil para que cualquier equipo se mantenga en cumplimiento, responda más rápido y siga listo para una auditoría mientras crece.'
              : 'Be the easiest platform for any team to stay compliant, respond faster, and remain audit-ready as they scale.'}
          </p>
        </article>
        <article className="company-pillar-card">
          <h3>{isSpanish ? 'Misión' : 'Mission'}</h3>
          <p>
            {isSpanish
              ? 'Hacer que las verificaciones de sanciones y cumplimiento sean simples, rápidas y confiables para equipos que operan de verdad.'
              : 'Make sanctions and compliance checks simple, fast, and reliable for teams running real operations.'}
          </p>
        </article>
        <article className="company-pillar-card">
          <h3>{isSpanish ? 'Valores' : 'Values'}</h3>
          <p>
            {isSpanish
              ? 'Operar con precios transparentes, documentación clara, una interfaz moderna y ejecución responsable ante clientes, socios y reguladores.'
              : 'Operate with transparent pricing, clear documentation, modern UX, and accountable execution for customers, partners, and regulators.'}
          </p>
        </article>
      </div>

      <div className="company-value-proposition">
        <h2>{isSpanish ? 'Qué hacemos' : 'What we do'}</h2>
        <p>
          {isSpanish
            ? 'La dirección interna del producto y los documentos de controles de la compañía enfatizan la misma prioridad: hacer que el trabajo de cumplimiento sea operable, medible y defendible. Por eso el producto se centra en flujos estructurados, señales de monitoreo y evidencia reutilizable.'
            : 'The product direction and internal controls material in this repository point to the same priority: make compliance work operational, measurable, and defensible. That is why the product focuses on structured workflows, monitoring signals, and reusable evidence.'}
        </p>
        <ul className="feature-list">
          {outcomes.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </div>

      <div className="company-pillars">
        {pillars.map((pillar) => (
          <article key={pillar.title} className="company-pillar-card">
            <h3>{pillar.title}</h3>
            <p>{pillar.body}</p>
          </article>
        ))}
      </div>

      <div className="company-evidence">
        <h2>{isSpanish ? 'Cómo trabajamos' : 'How we work'}</h2>
        <p>
          {isSpanish
            ? 'Preferimos afirmaciones concretas sobre capacidades reales. Eso significa diseñar experiencias con mejor trazabilidad, controles más claros y resultados que operaciones, cumplimiento y liderazgo puedan revisar juntos.'
            : 'We prefer concrete claims tied to real capabilities. In practice that means designing experiences with better traceability, clearer controls, and outcomes that operations, compliance, and leadership can review together.'}
        </p>
        <div className="company-reference-links footer-inline-links">
          <Link to={DEMO_REQUEST_DESTINATION}>{isSpanish ? 'Solicitar una demostración' : 'Request a demo'}</Link>
          <Link to={TRUST_DESTINATION}>{isSpanish ? 'Centro de confianza' : 'Trust center'}</Link>
          <Link to={CONTACT_DESTINATION}>{isSpanish ? 'Hablar con nuestro equipo' : 'Talk to our team'}</Link>
        </div>
      </div>

      <section className="company-leadership" aria-label={isSpanish ? 'Enfoque de liderazgo' : 'Leadership approach'}>
        <h2>{isSpanish ? 'Liderazgo y enfoque' : 'Leadership and approach'}</h2>
        <ul className="feature-list">
          {leadershipPrinciples.map((principle) => (
            <li key={principle}>{principle}</li>
          ))}
        </ul>
      </section>

      <section className="company-trust-section" aria-label={isSpanish ? 'Fundamentos de confianza' : 'Trust fundamentals'}>
        <h2>{isSpanish ? 'Fundamentos de confianza' : 'Trust fundamentals'}</h2>
        <div className="company-proof-grid">
          {trustPillars.map((pillar) => (
            <article key={pillar} className="company-proof-card">
              <p>{pillar}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="company-customer-proof" aria-label={isSpanish ? 'Prueba de valor para clientes' : 'Customer proof'}>
        <h2>{isSpanish ? 'Prueba de valor para clientes' : 'Customer proof'}</h2>
        <div className="company-proof-grid">
          {customerProofHighlights.map((proof) => (
            <article key={proof.label} className="company-proof-card">
              <strong>{proof.label}</strong>
              <p>{proof.value}</p>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
};

const LegalPage: React.FC = () => {
  const { t, language } = useWebsiteLanguage();
  const es = language === 'es';
  return (
  <section className="page-section policy-page">
    <div className="policy-hero">
      <h1>{t('page.legalTitle')}</h1>
      <p className="subtitle">{t('page.legalSubtitle')}</p>
    </div>
    <div className="page-stack policy-stack">
      <p>
        {t('legal.hubIntro')}
      </p>
      <p>
        {t('legal.hubReview')}
      </p>
      <div className="policy-link-grid" aria-label={es ? 'Enlaces de documentos legales' : 'Legal document links'}>
        <article className="policy-link-card">
          <h3>{t('footer.privacy')}</h3>
          <p>{es ? 'Cómo se recopilan, usan, conservan y protegen los datos personales.' : 'How personal data is collected, used, retained, and protected.'}</p>
          <Link to={PRIVACY_DESTINATION}>{es ? 'Abrir Política de Privacidad' : 'Open Privacy Policy'}</Link>
        </article>
        <article className="policy-link-card">
          <h3>{t('footer.terms')}</h3>
          <p>{es ? 'Términos del servicio, uso aceptable y canales de solicitudes legales.' : 'Service terms, acceptable use expectations, and legal request channels.'}</p>
          <Link to={TERMS_DESTINATION}>{es ? 'Abrir Términos del Servicio' : 'Open Terms of Service'}</Link>
        </article>
        <article className="policy-link-card">
          <h3>{t('footer.responsibleDisclosure')}</h3>
          <p>{es ? 'Cómo reportar vulnerabilidades con remediación coordinada.' : 'Guidelines for reporting vulnerabilities with coordinated remediation.'}</p>
          <Link to={RESPONSIBLE_DISCLOSURE_DESTINATION}>{es ? 'Abrir política de divulgación' : 'Open Disclosure Policy'}</Link>
        </article>
        <article className="policy-link-card">
          <h3>{es ? 'Confianza y controles' : 'Trust & Controls'}</h3>
          <p>{es ? 'Gobernanza de seguridad y aseguramiento operativo para clientes.' : 'Security governance and operational assurance overview for customers.'}</p>
          <Link to={TRUST_DESTINATION}>{es ? 'Abrir centro de confianza' : 'Open Trust Center'}</Link>
        </article>
      </div>
      <ul className="feature-list">
        <li><Link to={COOKIE_PREFERENCES_DESTINATION}>{t('footer.cookiePreferences')}</Link></li>
        <li><Link to={DATA_CHOICES_DESTINATION}>{t('footer.dataChoices')}</Link></li>
        <li><Link to={DMCA_DESTINATION}>DMCA</Link></li>
        <li><Link to={ACCESSIBILITY_DESTINATION}>{es ? 'Accesibilidad' : 'Accessibility'}</Link></li>
      </ul>
    </div>
  </section>
  );
};

const PrivacyPage: React.FC = () => {
  const { t, language } = useWebsiteLanguage();
  const privacyVersion = LEGAL_POLICY_METADATA.privacy.version;
  const privacyUpdatedAt = LEGAL_POLICY_METADATA.privacy.updatedAt;

  return (
  <section className="page-section policy-page">
    <div className="policy-hero">
      <h1>{t('page.privacyTitle')}</h1>
      <p className="subtitle">{t('page.privacySubtitle')}</p>
    </div>
    <div className="page-stack privacy-policy policy-stack">
      <div className="policy-meta">
      <p><strong>{t('page.version')}:</strong> {privacyVersion}</p>
      <p><strong>{t('page.lastUpdated')}:</strong> {privacyUpdatedAt}</p>
      </div>

      <PolicyBody kind="privacy" language={language} />
      <ul className="feature-list">
        <li><a href="https://www.nist.gov/privacy-framework" target="_blank" rel="noreferrer">NIST Privacy Framework</a></li>
        <li><a href="https://www.ftc.gov/business-guidance/resources/data-breach-response-guide-business" target="_blank" rel="noreferrer">FTC Data Breach Response Guide</a></li>
        <li><a href="https://oag.ca.gov/privacy/ccpa" target="_blank" rel="noreferrer">California CCPA Consumer Rights</a></li>
      </ul>
    </div>
  </section>
  );
};

const TermsPage: React.FC = () => {
  const { t, language } = useWebsiteLanguage();
  const termsVersion = LEGAL_POLICY_METADATA.terms.version;
  const termsUpdatedAt = LEGAL_POLICY_METADATA.terms.updatedAt;
  const selectedVersion = React.useMemo(
    () => TERMS_VERSION_HISTORY.find((item) => item.version === termsVersion) ?? TERMS_VERSION_HISTORY[TERMS_VERSION_HISTORY.length - 1],
    [termsVersion],
  );

  return (
  <section className="page-section policy-page">
    <div className="policy-hero">
      <h1>{t('page.termsTitle')}</h1>
      <p className="subtitle">{t('page.termsSubtitle')}</p>
    </div>
    <div className="page-stack policy-stack">
      <div className="policy-meta">
      <p><strong>{t('page.version')}:</strong> {termsVersion}</p>
      <p><strong>{t('page.lastUpdated')}:</strong> {termsUpdatedAt}</p>
      </div>

      <PolicyBody kind="terms" language={language} />

      <h2>{t('terms.versionHistory')}</h2>
      <p><strong>{t('terms.currentHash')}</strong> {TERMS_VERSION_HASH}</p>
      <ul>
        {TERMS_VERSION_HISTORY.map((item) => (
          <li key={item.version}>
            <strong>{item.version}</strong> — {item.publishedAt} — {item.summary}
          </li>
        ))}
      </ul>
      <p>
        <strong>{t('terms.replay')}</strong> {selectedVersion.version} ({selectedVersion.publishedAt})
      </p>
    </div>
  </section>
  );
};

const CookiePreferencesPage: React.FC = () => {
    const { t } = useWebsiteLanguage();
    type CookieConsentState = {
      essential: true;
      functional: boolean;
      analytics: boolean;
      advertising: boolean;
      updatedAt: string;
      version: string;
      source: 'saved-preference' | 'accept-all' | 'reject-non-essential' | 'manual-save' | 'global-privacy-control';
    };

    const globalPrivacyControlEnabled =
      typeof navigator !== 'undefined' && Boolean((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl);

    const defaultConsentState = (): CookieConsentState => ({
      essential: true,
      functional: false,
      analytics: false,
      advertising: false,
      updatedAt: new Date().toISOString(),
      version: COOKIE_PREFERENCES_VERSION,
      source: globalPrivacyControlEnabled ? 'global-privacy-control' : 'manual-save',
    });

    const [consent, setConsent] = React.useState<CookieConsentState>(defaultConsentState);
    const [statusMessage, setStatusMessage] = React.useState<string | null>(null);

    React.useEffect(() => {
      const raw = localStorage.getItem(COOKIE_PREFERENCES_STORAGE_KEY);
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as Partial<CookieConsentState>;
          setConsent({
            essential: true,
            functional: Boolean(parsed.functional),
            analytics: Boolean(parsed.analytics),
            advertising: Boolean(parsed.advertising),
            updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : new Date().toISOString(),
            version: typeof parsed.version === 'string' ? parsed.version : COOKIE_PREFERENCES_VERSION,
            source: parsed.source === 'saved-preference' || parsed.source === 'accept-all' || parsed.source === 'reject-non-essential' || parsed.source === 'manual-save' || parsed.source === 'global-privacy-control'
              ? parsed.source
              : 'saved-preference',
          });
          setStatusMessage('loaded');
          return;
        } catch {
          localStorage.removeItem(COOKIE_PREFERENCES_STORAGE_KEY);
        }
      }

      if (globalPrivacyControlEnabled) {
        const gpcState: CookieConsentState = {
          essential: true,
          functional: false,
          analytics: false,
          advertising: false,
          updatedAt: new Date().toISOString(),
          version: COOKIE_PREFERENCES_VERSION,
          source: 'global-privacy-control',
        };
        setConsent(gpcState);
        localStorage.setItem(COOKIE_PREFERENCES_STORAGE_KEY, JSON.stringify(gpcState));
        setStatusMessage('gpc');
      }
    }, [globalPrivacyControlEnabled]);

    const persistConsent = (nextConsent: CookieConsentState, message: string) => {
      setConsent(nextConsent);
      localStorage.setItem(COOKIE_PREFERENCES_STORAGE_KEY, JSON.stringify(nextConsent));
      setStatusMessage(message);
    };

    const acceptAll = () => {
      persistConsent(
        {
          essential: true,
          functional: true,
          analytics: true,
          advertising: true,
          updatedAt: new Date().toISOString(),
          version: COOKIE_PREFERENCES_VERSION,
          source: 'accept-all',
        },
        'accept-all',
      );
    };

    const rejectNonEssential = () => {
      persistConsent(
        {
          essential: true,
          functional: false,
          analytics: false,
          advertising: false,
          updatedAt: new Date().toISOString(),
          version: COOKIE_PREFERENCES_VERSION,
          source: 'reject-non-essential',
        },
        'reject',
      );
    };

    const saveSelection = () => {
      persistConsent(
        {
          ...consent,
          essential: true,
          updatedAt: new Date().toISOString(),
          version: COOKIE_PREFERENCES_VERSION,
          source: 'manual-save',
        },
        'saved',
      );
    };

    const lastUpdatedLabel = new Date(consent.updatedAt).toLocaleString();
    const cookieStatusLabel =
      statusMessage === 'loaded' ? t('cookie.statusLoaded')
      : statusMessage === 'gpc' ? t('cookie.statusGpc')
      : statusMessage === 'accept-all' ? t('cookie.statusAcceptAll')
      : statusMessage === 'reject' ? t('cookie.statusReject')
      : statusMessage === 'saved' ? t('cookie.statusSaved')
      : t('cookie.statusDefault');

    return (
      <section className="page-section policy-page cookie-preferences-page">
        <div className="policy-hero">
          <h1>{t('cookie.title')}</h1>
          <p className="subtitle">{t('cookie.subtitle')}</p>
        </div>

        <div className="page-stack policy-stack cookie-preferences-panel" role="region" aria-label={t('cookie.title')}>
          <div className="policy-meta">
            <p><strong>{t('cookie.version')}</strong> {consent.version}</p>
            <p><strong>{t('page.lastUpdated')}:</strong> {lastUpdatedLabel}</p>
            <p><strong>{t('cookie.source')}</strong> {consent.source}</p>
          </div>

          <p className="cookie-preferences-status" role="status" aria-live="polite">{cookieStatusLabel}</p>

          <h2>{t('cookie.categories')}</h2>
          <div className="cookie-preferences-grid" role="group" aria-label={t('cookie.categories')}>
            <article className="cookie-preference-card">
              <div>
                <h3>{t('cookie.essential')}</h3>
                <p>{t('cookie.essentialBody')}</p>
              </div>
              <label className="cookie-toggle">
                <input type="checkbox" checked readOnly disabled />
                <span>{t('cookie.alwaysActive')}</span>
              </label>
            </article>

            <article className="cookie-preference-card">
              <div>
                <h3>{t('cookie.functional')}</h3>
                <p>{t('cookie.functionalBody')}</p>
              </div>
              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={consent.functional}
                  onChange={(event) => setConsent((previous) => ({ ...previous, functional: event.target.checked }))}
                />
                <span>{consent.functional ? t('cookie.on') : t('cookie.off')}</span>
              </label>
            </article>

            <article className="cookie-preference-card">
              <div>
                <h3>{t('cookie.analytics')}</h3>
                <p>{t('cookie.analyticsBody')}</p>
              </div>
              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={consent.analytics}
                  onChange={(event) => setConsent((previous) => ({ ...previous, analytics: event.target.checked }))}
                />
                <span>{consent.analytics ? t('cookie.on') : t('cookie.off')}</span>
              </label>
            </article>

            <article className="cookie-preference-card">
              <div>
                <h3>{t('cookie.advertising')}</h3>
                <p>{t('cookie.advertisingBody')}</p>
              </div>
              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={consent.advertising}
                  onChange={(event) => setConsent((previous) => ({ ...previous, advertising: event.target.checked }))}
                />
                <span>{consent.advertising ? t('cookie.on') : t('cookie.off')}</span>
              </label>
            </article>
          </div>

          <div className="cookie-preferences-actions" role="group" aria-label={t('cookie.categories')}>
            <button type="button" className="btn btn-secondary" onClick={rejectNonEssential}>{t('cookie.reject')}</button>
            <button type="button" className="btn btn-secondary" onClick={acceptAll}>{t('cookie.acceptAll')}</button>
            <button type="button" className="btn btn-primary" onClick={saveSelection}>{t('cookie.save')}</button>
          </div>

          <h2>{t('cookie.snapshot')}</h2>
          <div className="cookie-preferences-table-wrap" role="region" aria-label={t('cookie.snapshot')}>
            <table className="cookie-preferences-table">
              <thead>
                <tr>
                  <th scope="col">{t('cookie.colCookie')}</th>
                  <th scope="col">{t('cookie.colCategory')}</th>
                  <th scope="col">{t('cookie.colPurpose')}</th>
                  <th scope="col">{t('cookie.colRetention')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>session_id</td>
                  <td>{t('cookie.essentialCat')}</td>
                  <td>{t('cookie.sessionPurpose')}</td>
                  <td>{t('cookie.session')}</td>
                </tr>
                <tr>
                  <td>lang_pref</td>
                  <td>{t('cookie.functionalCat')}</td>
                  <td>{t('cookie.langPurpose')}</td>
                  <td>{t('cookie.sixMonths')}</td>
                </tr>
                <tr>
                  <td>analytics_id</td>
                  <td>{t('cookie.analyticsCat')}</td>
                  <td>{t('cookie.analyticsTablePurpose')}</td>
                  <td>{t('cookie.thirteenMonths')}</td>
                </tr>
                <tr>
                  <td>campaign_attr</td>
                  <td>{t('cookie.advertisingCat')}</td>
                  <td>{t('cookie.adPurpose')}</td>
                  <td>{t('cookie.ninetyDays')}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2>{t('cookie.bestPractice')}</h2>
          <ul className="feature-list">
            <li><strong>{t('cookie.equalChoice')}</strong> {t('cookie.equalChoiceBody')}</li>
            <li><strong>{t('cookie.granular')}</strong> {t('cookie.granularBody')}</li>
            <li><strong>{t('cookie.noPreEnabled')}</strong> {t('cookie.noPreEnabledBody')}</li>
            <li><strong>{t('cookie.withdraw')}</strong> {t('cookie.withdrawBody')}</li>
            <li><strong>{t('cookie.signal')}</strong> {t('cookie.signalBody')}</li>
          </ul>

          <p>
            {t('cookie.needRights')} <Link to={DATA_CHOICES_DESTINATION}>{t('footer.dataChoices')}</Link> {t('cookie.orReview')} <Link to={PRIVACY_DESTINATION}>{t('cookie.privacyPolicy')}</Link>.
          </p>
        </div>
      </section>
    );
};

const DashboardsValuePage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  return (
  <section className="page-section product-platform-explore-page">
    <h1 className="product-platform-explore-title">{t('dashboards.title')}</h1>
    <p className="subtitle">{t('dashboards.subtitle')}</p>
    <div className="page-stack">
      <h2>{t('dashboards.value')}</h2>
      <p>{t('dashboards.p1')}</p>
      <p>{t('dashboards.p2')}</p>
      <p>{t('dashboards.p3')}</p>
      <ul className="feature-list">
        <li>{t('dashboards.b1')}</li>
        <li>{t('dashboards.b2')}</li>
        <li>{t('dashboards.b3')}</li>
      </ul>
    </div>
    <div className="product-platform-overview-cta">
      <Link to={DEMO_REQUEST_DESTINATION} className="btn btn-primary product-platform-overview-cta-link">
        {t('dashboards.cta')}
      </Link>
    </div>
    <div className="product-platform-submenu-grid" aria-label={t('dashboards.subtitle')}>
      <article className="product-platform-submenu-card">
        <span className="product-platform-submenu-icon product-platform-submenu-icon--teal" aria-hidden="true">📊</span>
        <span className="product-platform-submenu-label">{t('dashboards.card1')}</span>
      </article>
      <article className="product-platform-submenu-card">
        <span className="product-platform-submenu-icon product-platform-submenu-icon--violet" aria-hidden="true">⚡</span>
        <span className="product-platform-submenu-label">{t('dashboards.card2')}</span>
      </article>
      <article className="product-platform-submenu-card">
        <span className="product-platform-submenu-icon product-platform-submenu-icon--cyan" aria-hidden="true">🧾</span>
        <span className="product-platform-submenu-label">{t('dashboards.card3')}</span>
      </article>
      <article className="product-platform-submenu-card">
        <span className="product-platform-submenu-icon product-platform-submenu-icon--indigo" aria-hidden="true">🧭</span>
        <span className="product-platform-submenu-label">{t('dashboards.card4')}</span>
      </article>
    </div>
  </section>
  );
};

const LiabilityMonitoringPage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  return (
  <section className="page-section">
    <h1>{t('liability.title')}</h1>
    <p className="subtitle">{t('liability.subtitle')}</p>
    <div className="page-stack">
      <h2>{t('dashboards.value')}</h2>
      <p>{t('liability.p1')}</p>
      <p>{t('liability.p2')}</p>
      <p>{t('liability.p3')}</p>
    </div>
    <section className="liability-demo-section" aria-label="Liability monitoring product demos">
      <div className="liability-demo-cta-wrap">
        <Link to={DEMO_REQUEST_DESTINATION} className="btn btn-primary liability-demo-cta">
          {t('liability.cta')}
        </Link>
      </div>
      <div className="liability-demo-grid">
        <article className="liability-demo-item">
          <span className="liability-demo-icon-ring liability-demo-icon-ring--green" aria-hidden="true">
            <img src="https://api.iconify.design/bi/search.svg?color=%2334996e" alt="" loading="lazy" />
          </span>
          <h3>Finds liability-relevant changes earlier in the content lifecycle.</h3>
        </article>
        <article className="liability-demo-item">
          <span className="liability-demo-icon-ring liability-demo-icon-ring--pink" aria-hidden="true">
            <img src="https://api.iconify.design/bi/journal-check.svg?color=%23d34d9a" alt="" loading="lazy" />
          </span>
          <h3>Provides auditable evidence of monitoring and remediation.</h3>
        </article>
        <article className="liability-demo-item">
          <span className="liability-demo-icon-ring liability-demo-icon-ring--mint" aria-hidden="true">
            <img src="https://api.iconify.design/bi/arrow-repeat.svg?color=%232f8f4e" alt="" loading="lazy" />
          </span>
          <h3>Reduces manual review burden with consistent, repeatable checks.</h3>
        </article>
      </div>
    </section>
  </section>
  );
};

const DodContractsPage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  return (
  <section className="page-section">
    <h1>{t('dod.title')}</h1>
    <p className="subtitle">{t('dod.subtitle')}</p>
    <div className="page-stack">
      <h2>{t('dashboards.value')}</h2>
      <p>{t('dod.p1')}</p>
      <p>{t('dod.p2')}</p>
      <p>{t('dod.p3')}</p>
      <p>
        References: <a href="https://sam.gov/content/opportunities" target="_blank" rel="noreferrer">SAM.gov contract opportunities</a>,{' '}
        <a href="https://www.acquisition.gov/" target="_blank" rel="noreferrer">Acquisition.gov (FAR/DFARS resources)</a>, and{' '}
        <a href="https://www.dau.edu/" target="_blank" rel="noreferrer">Defense Acquisition University</a>.
      </p>
    </div>
    <section className="liability-demo-section" aria-label="DoD contracts product demos and value outcomes">
      <div className="liability-demo-cta-wrap">
        <Link to={DEMO_REQUEST_DESTINATION} className="btn btn-primary liability-demo-cta">
          {t('liability.cta')}
        </Link>
      </div>
      <div className="liability-demo-grid">
        <article className="liability-demo-item">
          <span className="liability-demo-icon-ring liability-demo-icon-ring--green" aria-hidden="true">
            <img src="https://api.iconify.design/bi/bullseye.svg?color=%2334996e" alt="" loading="lazy" />
          </span>
          <h3>{t('dod.card1')}</h3>
        </article>
        <article className="liability-demo-item">
          <span className="liability-demo-icon-ring liability-demo-icon-ring--pink" aria-hidden="true">
            <img src="https://api.iconify.design/bi/file-earmark-text.svg?color=%23d34d9a" alt="" loading="lazy" />
          </span>
          <h3>{t('dod.card2')}</h3>
        </article>
        <article className="liability-demo-item">
          <span className="liability-demo-icon-ring liability-demo-icon-ring--mint" aria-hidden="true">
            <img src="https://api.iconify.design/bi/shield-check.svg?color=%232f8f4e" alt="" loading="lazy" />
          </span>
          <h3>Strengthens post-award performance with auditable regulatory workflows.</h3>
        </article>
      </div>
    </section>
  </section>
  );
};

const DataChoicesPage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [requestType, setRequestType] = React.useState('access');
  const [requestId, setRequestId] = React.useState('');
  const [lookupId, setLookupId] = React.useState('');
  const requestCounterRef = React.useRef(1);
  const [storedRequests, setStoredRequests] = React.useState<DsarRequest[]>(() => getStoredDsarRequests());

  React.useEffect(() => {
    setStoredRequests(getStoredDsarRequests());
  }, []);

  const createRequestId = () => {
    const next = String(requestCounterRef.current).padStart(5, '0');
    requestCounterRef.current += 1;
    return `ARPN-DSAR-${next}`;
  };

  return (
    <section className="page-section policy-page">
      <div className="policy-hero">
        <h1>{t('data.title')}</h1>
        <p className="subtitle">{t('data.subtitle')}</p>
      </div>
      <div className="page-stack policy-stack">
        <p>
          {t('data.intro')}
        </p>
        <h3>{t('data.sla')}</h3>
        <p>
          <strong>{t('data.open')}</strong>{' '}
          {storedRequests.filter((item) => item.status !== 'completed').length}
        </p>
        <p>
          <strong>{t('data.review')}</strong> {storedRequests.filter((item) => item.status === 'in_review').length}
        </p>
        <p>
          <strong>{t('data.completed')}</strong> {storedRequests.filter((item) => item.status === 'completed').length}
        </p>
        <p>
          <strong>{t('data.overdue')}</strong>{' '}
          {
            storedRequests.filter((item) => item.status !== 'completed' && new Date(item.dueAt).getTime() < Date.now())
              .length
          }
        </p>
        <label htmlFor="dsar-lookup">{t('data.lookup')}</label>
        <input
          id="dsar-lookup"
          placeholder={t('data.lookupPlaceholder')}
          value={lookupId}
          onChange={(event) => setLookupId(event.target.value)}
        />
        {lookupId.trim() && (
          <p>
            <strong>{t('data.lookupResult')}</strong>{' '}
            {storedRequests.find((item) => item.requestId.toLowerCase() === lookupId.trim().toLowerCase())?.status ?? t('data.notFound')}
          </p>
        )}
        <ul className="feature-list">
          <li><a href="https://oag.ca.gov/privacy/ccpa" target="_blank" rel="noreferrer">California CCPA rights overview</a></li>
          <li><a href="https://oag.ca.gov/privacy/ccpa/gpc" target="_blank" rel="noreferrer">Global Privacy Control (GPC)</a></li>
        </ul>
      </div>
      {!isSubmitted ? (
        <form
          className="demo-form"
          onSubmit={(event) => {
            event.preventDefault();
            const nextRequestId = createRequestId();
            const now = new Date();
            const dueAt = new Date(now.getTime());
            dueAt.setDate(dueAt.getDate() + 45);
            const request: DsarRequest = {
              requestId: nextRequestId,
              requestType,
              submittedAt: now.toISOString(),
              dueAt: dueAt.toISOString(),
              status: 'queued',
            };
            saveStoredDsarRequest(request);
            setStoredRequests(getStoredDsarRequests());
            setRequestId(nextRequestId);
            setIsSubmitted(true);
          }}
        >
          <p>You may request access, correction, deletion, export, opt-out, or sensitive-data limitation where applicable under regional privacy frameworks.</p>
          <label htmlFor="data-choice-name">Full name</label>
          <input id="data-choice-name" name="name" placeholder="Full name" required />

          <label htmlFor="data-choice-email">Work email</label>
          <input id="data-choice-email" name="email" type="email" placeholder="Work email" required />

          <label htmlFor="data-choice-type">Request type</label>
          <select
            id="data-choice-type"
            name="requestType"
            value={requestType}
            onChange={(event) => setRequestType(event.target.value)}
            required
          >
            <option value="access">Access / Right to know</option>
            <option value="correction">Correction</option>
            <option value="deletion">Deletion</option>
            <option value="export">Data portability/export</option>
            <option value="optout">Opt-out of sale/sharing</option>
            <option value="sensitive-limit">Limit use of sensitive data</option>
          </select>

          <label htmlFor="data-choice-details">Details</label>
          <textarea
            id="data-choice-details"
            name="details"
            rows={4}
            placeholder="Describe your request and any relevant account identifiers"
            required
          />

          <button className="btn btn-primary" type="submit">
            Submit data rights request
          </button>
        </form>
      ) : (
        <div className="form-success">
          <h3>{t('data.title')}</h3>
          <p>
            <strong>{t('data.lookup')}:</strong> {requestId}
          </p>
          <p>
            <strong>{t('data.review')}</strong> queued
          </p>
          <p>{t('data.responseTarget')}</p>
          <button className="btn btn-secondary" type="button" onClick={() => setIsSubmitted(false)}>
            {t('data.submitAnother')}
          </button>
        </div>
      )}
    </section>
  );
};

const ResponsibleDisclosurePage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  return (
  <section className="page-section policy-page">
    <div className="policy-hero">
      <h1>{t('disclosure.title')}</h1>
      <p className="subtitle">{t('disclosure.subtitle')}</p>
    </div>
    <div className="page-stack policy-stack">
      <h2>{t('disclosure.h1')}</h2>
      <ul className="feature-list">
        <li>{t('disclosure.b1')}</li>
        <li>{t('disclosure.b2')}</li>
        <li>{t('disclosure.b3')}</li>
      </ul>
      <h2>{t('disclosure.h2')}</h2>
      <p>{t('disclosure.p1')}</p>
      <p>{t('disclosure.p2')}</p>
      <h2>{t('disclosure.h3')}</h2>
      <ul className="feature-list">
        <li><a href="https://www.cisa.gov/coordinated-vulnerability-disclosure-process" target="_blank" rel="noreferrer">CISA Coordinated Vulnerability Disclosure</a></li>
        <li><a href="https://owasp.org/Top10/2025/" target="_blank" rel="noreferrer">OWASP Top 10 (2025)</a></li>
      </ul>
    </div>
  </section>
  );
};

const TrustPage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  return (
  <section className="page-section policy-page">
    <div className="policy-hero">
      <h1>{t('trust.title')}</h1>
      <p className="subtitle">{t('trust.subtitle')}</p>
    </div>
    <div className="page-stack policy-stack">
      <h2>{t('trust.h1')}</h2>
      <p>{t('trust.p1')}</p>
      <h2>{t('trust.h2')}</h2>
      <ul className="feature-list">
        <li>{t('trust.b1')}</li>
        <li>{t('trust.b2')}</li>
        <li>{t('trust.b3')}</li>
      </ul>
      <h2>{t('trust.h3')}</h2>
      <ul className="feature-list">
        <li><a href="https://www.nist.gov/privacy-framework" target="_blank" rel="noreferrer">NIST Privacy Framework</a></li>
        <li><a href="https://www.iso.org/isoiec-27001-information-security.html" target="_blank" rel="noreferrer">ISO/IEC 27001 information security overview</a></li>
        <li><a href="https://www.ftc.gov/business-guidance/resources/data-breach-response-guide-business" target="_blank" rel="noreferrer">FTC Data Breach Response Guide</a></li>
      </ul>
      <h2>{t('trust.h4')}</h2>
      <p>{t('trust.p2')}</p>
      <ul className="feature-list">
        <li>
          <a href="https://www.educause.edu/higher-education-community-vendor-assessment-toolkit" target="_blank" rel="noreferrer">
            {t('trust.hecvat')}
          </a>
        </li>
        <li>
          <a href="/trust/nextcompli-campus-assurance-pack.md" target="_blank" rel="noreferrer">
            {t('trust.pack')}
          </a>
        </li>
        <li>{t('trust.b4')}</li>
        <li>{t('trust.b5')}</li>
      </ul>
    </div>
  </section>
  );
};

const DmcaPage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  return (
  <section className="page-section">
    <h1>{t('dmca.title')}</h1>
    <p className="subtitle">{t('dmca.subtitle')}</p>
    <div className="page-stack">
      <p>{t('dmca.p1')}</p>
      <p>{t('dmca.p2')}</p>
      <p>{t('dmca.p3')}</p>
    </div>
  </section>
  );
};

const AccessibilityPage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  return (
  <section className="page-section">
    <h1>{t('a11y.title')}</h1>
    <p className="subtitle">{t('a11y.subtitle')}</p>
    <div className="page-stack">
      <p>{t('a11y.p1')}</p>
      <p>{t('a11y.p2')}</p>
      <p>{t('a11y.p3')}</p>
      <p>
        <strong>{t('a11y.audit')}</strong>{' '}
        <a href={ACCESSIBILITY_AUDIT_SUMMARY.reportUrl} target="_blank" rel="noreferrer">
          {ACCESSIBILITY_AUDIT_SUMMARY.reportId}
        </a>{' '}
        ({ACCESSIBILITY_AUDIT_SUMMARY.auditedAt})
      </p>
      <p>
        <strong>{t('a11y.remediation')}</strong> {ACCESSIBILITY_AUDIT_SUMMARY.remediationCompletionPercent}%
      </p>
      <p>
        <strong>{t('a11y.blockers')}</strong> {t('a11y.critical')} {ACCESSIBILITY_AUDIT_SUMMARY.openCriticalIssues}, {t('a11y.serious')} {ACCESSIBILITY_AUDIT_SUMMARY.openSeriousIssues}
      </p>
    </div>
  </section>
  );
};

const ClaimsEvidencePage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  return (
  <section className="page-section">
    <h1>{t('claims.title')}</h1>
    <p className="subtitle">{t('claims.subtitle')}</p>
    <div className="page-stack">
      {CLAIM_SUBSTANTIATION_REGISTRY.map((item) => (
        <article key={item.claimId} className="feature-card">
          <h3>{item.claimId}</h3>
          <p>{item.claimText}</p>
          <p>
            <strong>{t('claims.evidence')}</strong>{' '}
            <a href={item.evidenceUrl} target="_blank" rel="noreferrer">
              {item.evidenceUrl}
            </a>
          </p>
          <p>
            <strong>{t('claims.reviewed')}</strong> {item.lastReviewed}
          </p>
          <p>
            <strong>{t('claims.valid')}</strong> {isValidEvidenceUrl(item.evidenceUrl) ? t('claims.yes') : t('claims.no')}
          </p>
        </article>
      ))}
    </div>
  </section>
  );
};

type TroubleshootingOption = {
  id: string;
  label: string;
  response: string;
};

const CONTACT_TROUBLESHOOTING_OPTIONS: TroubleshootingOption[] = [
  {
    id: 'login',
    label: 'I cannot sign in',
    response:
      'Try resetting your password first, then clear browser cache/cookies for this site and retry in a private window. If SSO is enabled, confirm your IdP session is active and your account is assigned to the application.',
  },
  {
    id: 'alerts',
    label: 'Alerts are missing or delayed',
    response:
      'Verify monitoring is enabled for the target workflow, check source connectivity health, and confirm alert routing (email/webhook) is active. Delays usually come from paused schedules or destination delivery restrictions.',
  },
  {
    id: 'data',
    label: 'Data appears outdated',
    response:
      'Run a manual refresh from the dashboard, then review source freshness timestamps and connector status. If one source is stale, reconnect credentials and re-run a scoped sync before full monitoring resumes.',
  },
  {
    id: 'integration',
    label: 'Integration/API issue',
    response:
      'Check API base URL, token scope, and request headers first. For webhook issues, validate endpoint SSL, response status, and retry logs. Most failures come from expired tokens or mismatched environment configuration.',
  },
];

const HelpCenterPageRoute: React.FC = () => {
  const { language } = useWebsiteLanguage();
  return (
    <HelpCenterPage
      language={language}
      contactPath={CONTACT_DESTINATION}
      plansPath={SUPPORT_DESTINATION}
    />
  );
};

const ContactPage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  const navigate = useNavigate();
  const [isTroubleshootOpen, setIsTroubleshootOpen] = React.useState(false);
  const [selectedTroubleshootId, setSelectedTroubleshootId] = React.useState<string | null>(null);
  const [isTroubleshootResolved, setIsTroubleshootResolved] = React.useState<boolean | null>(null);
  const [troubleshootReply, setTroubleshootReply] = React.useState<string | null>(null);
  const [isTroubleshootReplyLoading, setIsTroubleshootReplyLoading] = React.useState(false);

  const troubleshootingOptions = [
    { id: 'login', label: t('contact.loginIssue'), response: t('contact.loginReply') },
    { id: 'alerts', label: t('contact.alertsIssue'), response: t('contact.alertsReply') },
    { id: 'data', label: t('contact.dataIssue'), response: t('contact.dataReply') },
    { id: 'integration', label: t('contact.apiIssue'), response: t('contact.apiReply') },
  ];

  const selectedTroubleshooting =
    troubleshootingOptions.find((item) => item.id === selectedTroubleshootId) ?? null;

  return (
    <section className="page-section contact-help-page">
      <div className="contact-help-hero" role="img" aria-label="Cat and dog resting together">
        <img
          src="https://images.unsplash.com/photo-1516734212186-a967f81ad0d7?auto=format&fit=crop&w=1700&q=80"
          alt="Cat and dog resting together"
          loading="lazy"
        />
      </div>

      <section className="contact-help-actions" aria-label="Contact action options">
        <h2>{t('contact.help')}</h2>
        {HAS_SUPPORT_PHONE ? (
          <a href={SUPPORT_PHONE_TEL} className="contact-help-action" aria-label={`Call toll-free ${SUPPORT_PHONE_DISPLAY}`}>
            <span className="contact-help-icon" aria-hidden="true">📞</span>
            <span>
              <strong>{t('contact.call')}</strong>
              <em>{SUPPORT_PHONE_DISPLAY}</em>
            </span>
          </a>
        ) : null}
        <button
          className="contact-help-action contact-help-action-button"
          type="button"
          onClick={() => {
            setIsTroubleshootOpen((previous) => !previous);
            setIsTroubleshootResolved(null);
          }}
          aria-label="Open troubleshooting chat"
        >
          <span className="contact-help-icon" aria-hidden="true">💬</span>
          <span>
            <strong>{t('contact.chat')}</strong>
              <em>{t('contact.askExpert')}</em>
          </span>
        </button>
        <a href="mailto:support@nextcompli.com" className="contact-help-action" aria-label="Email support">
          <span className="contact-help-icon" aria-hidden="true">✉️</span>
          <span>
            <strong>{t('contact.emailUs')}</strong>
            <em>support@nextcompli.com</em>
          </span>
        </a>
      </section>

      {isTroubleshootOpen ? (
        <section className="contact-troubleshoot-shell" aria-label="Troubleshooting chat assistant">
          <div className="contact-troubleshoot-header">
            <h3>{t('contact.troubleshootTitle')}</h3>
            <p>{t('contact.troubleshootIntro')}</p>
          </div>

          <div className="contact-troubleshoot-options" role="list" aria-label="Troubleshooting topics">
            {troubleshootingOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                role="listitem"
                className={`contact-troubleshoot-topic ${selectedTroubleshootId === option.id ? 'is-active' : ''}`}
                onClick={() => {
                  setSelectedTroubleshootId(option.id);
                  setIsTroubleshootResolved(null);
                  setTroubleshootReply(null);
                  setIsTroubleshootReplyLoading(true);
                  void (async () => {
                    const reply = await requestAssistantReply({
                      prompt: option.label,
                      sessionId: 'website-contact-troubleshoot',
                      contextLines: [
                        'Mode: troubleshooting assistant.',
                        'Response format: actionable steps first, then when to escalate.',
                      ],
                      fallbackReply: option.response,
                    });
                    setTroubleshootReply(reply);
                    setIsTroubleshootReplyLoading(false);
                  })();
                }}
              >
                {option.label}
              </button>
            ))}
          </div>

          {selectedTroubleshooting ? (
            <div className="contact-troubleshoot-thread" role="status" aria-live="polite">
              <article className="contact-troubleshoot-message contact-troubleshoot-message-user">
                <strong>{t('contact.you')}</strong> {selectedTroubleshooting.label}
              </article>
              <article className="contact-troubleshoot-message contact-troubleshoot-message-assistant">
                <strong>{t('contact.assistant')}</strong> {isTroubleshootReplyLoading ? t('contact.analyzing') : (troubleshootReply || selectedTroubleshooting.response)}
              </article>

              <div className="contact-troubleshoot-feedback" aria-label="Troubleshooting resolution feedback">
                <p>{t('contact.solved')}</p>
                <div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setIsTroubleshootResolved(true)}
                  >
                    Yes, resolved
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setIsTroubleshootResolved(false)}
                  >
                    No, still need help
                  </button>
                </div>
              </div>

              {isTroubleshootResolved === true ? (
                <p className="contact-troubleshoot-success">Great — troubleshooting solved your issue. You can reopen chat anytime if needed.</p>
              ) : null}

              {isTroubleshootResolved === false ? (
                <div className="contact-troubleshoot-escalation">
                  <p>Troubleshooting did not resolve the issue. You can now escalate to support.</p>
                  <div className="contact-troubleshoot-escalation-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => navigate(
                        `${HELP_DESTINATION}?topic=${encodeURIComponent(selectedTroubleshootId || 'other')}`,
                      )}
                    >
                      Open help ticket
                    </button>
                    <a href="mailto:support@nextcompli.com">Email support@nextcompli.com</a>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}
    </section>
  );
};

const PLANS_DEMO_VIDEOS = {
  basic: {
    id: 'ir-starter-complicore',
    planName: 'CompliCore',
    src: '/videos/implementation-readiness/ir-starter-complicore.mp4',
    poster: '/videos/implementation-readiness/posters/ir-starter-complicore.jpg',
    libraryLabel: 'CompliCore starter rollout demo video',
  },
  advanced: {
    id: 'ir-scaled-compliadvantage',
    planName: 'CompliAdvantage',
    src: '/videos/implementation-readiness/ir-scaled-compliadvantage.mp4',
    poster: '/videos/implementation-readiness/posters/ir-scaled-compliadvantage.jpg',
    libraryLabel: 'CompliAdvantage scaled monitoring demo video',
  },
} as const;

const PlansDemoPlayer: React.FC<{
  id?: string;
  src: string;
  poster: string;
  label: string;
}> = ({ id, src, poster, label }) => (
  <div id={id} className="plans-video-library-player-wrap">
    <video
      className="plans-video-library-player"
      controls
      preload="none"
      playsInline
      poster={poster}
      src={src}
      aria-label={label}
    />
  </div>
);

const scrollPlansAnchorIntoView = (anchorId: string) => {
  window.requestAnimationFrame(() => {
    const target = document.getElementById(anchorId);
    if (target instanceof HTMLElement) {
      scrollElementWithinConsole(target);
    }
  });
};

const SupportPage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const orderedSupportLevels = SUPPORT_LEVELS;
  const defaultPlanName = orderedSupportLevels.find((level) => level.featured)?.name
    || orderedSupportLevels[0]?.name
    || 'CompliAdvantage';
  const [activePlanName, setActivePlanName] = React.useState<string>(defaultPlanName);

  React.useEffect(() => {
    const hash = (location.hash || '').replace(/^#/, '');
    const hashedDemo = Object.values(PLANS_DEMO_VIDEOS).find((video) => video.id === hash);
    if (hashedDemo) {
      setActivePlanName(hashedDemo.planName);
      scrollPlansAnchorIntoView(hashedDemo.id);
    }
  }, [location.hash]);

  const openSubscribe = React.useCallback((tierId: string) => {
    if (tierId === 'enterprise') {
      navigate(DEMO_REQUEST_DESTINATION);
      return;
    }
    navigate(`${SUBSCRIBE_DESTINATION}?plan=${encodeURIComponent(tierId)}`);
  }, [navigate]);

  return (
    <section className="page-section support-levels-page plans-page">
      <div className="plans-header-wrap">
        <h1>{t('plans.title')}</h1>
        <div className="plans-tabs" role="tablist" aria-label={t('plans.tabsAria')}>
          {PLAN_CATEGORY_TABS.map((tab) => {
            const isActive = activePlanName === tab.title;
            return (
              <button
                key={tab.title}
                type="button"
                className={`plans-tab ${isActive ? 'is-active' : ''}`}
                role="tab"
                aria-selected={isActive}
                onClick={() => {
                  setActivePlanName(tab.title);
                  const tier = PRICING_TIERS.find((item) => item.name === tab.title);
                  if (tier) {
                    scrollPlansAnchorIntoView(`plan-${tier.id}`);
                  }
                }}
              >
                <strong>{tab.title}</strong>
                <span>{tab.subtitle}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="plans-grid" role="list" aria-label={t('plans.cardsAria')}>
        {orderedSupportLevels.map((level) => {
          const isFocused = activePlanName === level.name;
          return (
          <article
            className={`plans-card ${level.featured ? 'is-featured' : ''} ${isFocused ? 'is-selected' : ''}`}
            key={level.name}
            id={`plan-${level.id}`}
            role="listitem"
            onMouseEnter={() => setActivePlanName(level.name)}
            onFocus={() => setActivePlanName(level.name)}
            onClick={() => setActivePlanName(level.name)}
          >
            {level.featured ? <p className="plans-card-badge">{t('plans.mostPopular')}</p> : null}
            <header className="plans-card-header">
              <h2>{level.name}</h2>
            </header>
            <div className="plans-card-body">
              <div className={`plans-card-graphic-wrap ${level.graphicPath.includes('.webp') || level.graphicPath.includes('.png') ? 'is-raster' : ''}`}>
                <img src={level.graphicPath} alt={level.graphicAlt} className={`plans-card-graphic ${level.graphicPath.includes('.webp') || level.graphicPath.includes('.png') ? 'is-raster' : ''}`} loading="lazy" />
              </div>
              <p className="plans-card-subtitle">{level.subtitle}</p>
              <div className="plans-card-offer">
                {level.price ? (
                  <>
                    <p className="plans-card-price">{level.price}</p>
                    <p className="plans-card-cadence">{level.cadence}</p>
                  </>
                ) : (
                  <p className="plans-card-customize">Let’s customize the right plan for you</p>
                )}
                {level.seats !== level.ctaLabel ? (
                  <p className="plans-card-users">{level.seats}</p>
                ) : null}
              </div>
              {level.id === 'enterprise' ? (
                <button
                  className={`btn ${isFocused ? 'btn-success' : 'btn-secondary'} plans-card-cta`}
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openSubscribe(level.id);
                  }}
                >
                  {level.ctaLabel}
                </button>
              ) : (
                <Link
                  className={`btn ${isFocused ? 'btn-success' : 'btn-secondary'} plans-card-cta`}
                  to={`${SUBSCRIBE_DESTINATION}?plan=${encodeURIComponent(level.id)}`}
                  onClick={(event) => event.stopPropagation()}
                >
                  {level.ctaLabel}
                </Link>
              )}
              <div className="plans-card-secondary-action">
                {level.id === 'basic' || level.id === 'advanced' ? (
                  <Link
                    className="plans-card-watch"
                    to={{ pathname: SUPPORT_DESTINATION, hash: `#${PLANS_DEMO_VIDEOS[level.id].id}` }}
                    onClick={(event) => {
                      event.stopPropagation();
                      setActivePlanName(level.name);
                      scrollPlansAnchorIntoView(PLANS_DEMO_VIDEOS[level.id].id);
                    }}
                  >
                    {level.id === 'basic' ? 'Watch CompliCore rollout' : 'Watch CompliAdvantage demo'}
                  </Link>
                ) : (
                  <Link
                    className="plans-card-watch"
                    to={{ pathname: SUPPORT_DESTINATION, hash: '#plans-detail-tray' }}
                    onClick={(event) => {
                      event.stopPropagation();
                      setActivePlanName(level.name);
                      scrollPlansAnchorIntoView('plans-detail-tray');
                    }}
                  >
                    See plan details
                  </Link>
                )}
              </div>
              <ul className="plans-card-includes">
                {level.includes.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </article>
          );
        })}
      </div>

      {SUPPORT_LEVEL_DETAILS[activePlanName] ? (
        <aside
          id="plans-detail-tray"
          className="plans-detail-tray"
          aria-live="polite"
          aria-label={`${activePlanName} plan details`}
        >
          <p className="plans-detail-tray-plan">{activePlanName}</p>
          <p className="plans-detail-tray-ideal">{SUPPORT_LEVEL_DETAILS[activePlanName].idealFor}</p>
          <p className="plans-detail-tray-summary">{SUPPORT_LEVEL_DETAILS[activePlanName].expansionSummary}</p>
          <ul className="plans-detail-tray-points">
            {SUPPORT_LEVEL_DETAILS[activePlanName].expansionPoints.map((point) => (
              <li key={`${activePlanName}-${point}`}>{point}</li>
            ))}
          </ul>
        </aside>
      ) : null}

      <section
        id="implementation-readiness"
        className="plans-video-library"
        aria-label="Implementation readiness library"
      >
        <div className="plans-video-library-header">
          <h2>Implementation Readiness Library</h2>
          <p>Watch the CompliCore starter rollout and CompliAdvantage scaled monitoring demos. Use the other cards for governance scope and the best next action for rollout planning.</p>
        </div>
        <div className="plans-video-library-grid" role="list" aria-label="Implementation readiness links">
          <article className="plans-video-library-card" role="listitem">
            <h3>Starter rollout</h3>
            <p>Review the baseline operating model for smaller teams that need immediate coverage.</p>
            <PlansDemoPlayer
              id={PLANS_DEMO_VIDEOS.basic.id}
              src={PLANS_DEMO_VIDEOS.basic.src}
              poster={PLANS_DEMO_VIDEOS.basic.poster}
              label={PLANS_DEMO_VIDEOS.basic.libraryLabel}
            />
            <Link className="btn btn-secondary" to={websitePath('/products/basic')}>
              Open CompliCore plan overview
            </Link>
          </article>
          <article className="plans-video-library-card" role="listitem">
            <h3>Scaled monitoring</h3>
            <p>See how live environment monitoring and alert workflows fit together before you expand beyond manual review.</p>
            <PlansDemoPlayer
              id={PLANS_DEMO_VIDEOS.advanced.id}
              src={PLANS_DEMO_VIDEOS.advanced.src}
              poster={PLANS_DEMO_VIDEOS.advanced.poster}
              label={PLANS_DEMO_VIDEOS.advanced.libraryLabel}
            />
            <Link className="btn btn-secondary" to={websitePath('/products/advanced')}>
              Open CompliAdvantage plan overview
            </Link>
          </article>
          <article className="plans-video-library-card" role="listitem">
            <h3>Governance program</h3>
            <p>Use the CompliComplete route to align executive reporting, embedded support, and scheduled audit expectations.</p>
            <Link className="btn btn-secondary" to={websitePath('/products/enterprise')}>
              Open CompliComplete plan overview
            </Link>
          </article>
          <article className="plans-video-library-card" role="listitem">
            <h3>Guided walkthrough</h3>
            <p>When you are ready to review your own environment, request a guided session with the team.</p>
            <Link className="btn btn-secondary" to={DEMO_REQUEST_DESTINATION}>
              Request a walkthrough
            </Link>
          </article>
        </div>
      </section>

    </section>
  );
};

type SubscribeMode = 'create' | 'signin';

const subscriptionAccountName = (company: string, fullName: string, email: string): string => {
  const named = company.trim();
  if (named) {
    return named;
  }
  const person = fullName.trim();
  if (person) {
    return `${person}'s account`;
  }
  const local = email.split('@')[0]?.replace(/[._+-]+/g, ' ').trim();
  return local ? `${local}'s account` : 'My account';
};

const SubscribePage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  const location = useLocation();
  const { login, isAuthenticated, user, isLoading, ssoProvider, startSsoLogin, startGoogleLogin } = useAuth();
  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const planParam = (searchParams.get('plan') || 'basic').trim().toLowerCase();
  const tier = React.useMemo(() => {
    const match = PRICING_TIERS.find((item) => item.id === planParam || item.name.toLowerCase() === planParam);
    return match || PRICING_TIERS.find((item) => item.id === 'basic') || PRICING_TIERS[0];
  }, [planParam]);
  const checkoutCanceled = searchParams.get('checkout') === 'cancel';
  const googleSignupToken = (searchParams.get('google_signup') || '').trim();
  const subscribeSsoError = (searchParams.get('sso_error') || '').trim();
  const subscribePromo = (searchParams.get('promo') || '').trim().toLowerCase();
  const [googleIdentity, setGoogleIdentity] = React.useState<{ email: string; name: string } | null>(null);

  const [mode, setMode] = React.useState<SubscribeMode>('create');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [companyName, setCompanyName] = React.useState('');
  const [jobTitle, setJobTitle] = React.useState('');
  const [fullName, setFullName] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const checkoutStartedRef = React.useRef(false);
  const emailRef = React.useRef('');
  React.useEffect(() => {
    emailRef.current = email;
  }, [email]);
  React.useEffect(() => {
    markFunnel('subscribe', 'viewed');
    trackUxEvent({ event_type: 'funnel_step', funnel_key: 'subscribe', step: 'viewed', last_path: '/subscribe' });
    return () => {
      if (!checkoutStartedRef.current) {
        trackUxEvent({
          event_type: 'funnel_abandoned',
          funnel_key: 'subscribe',
          step: emailRef.current ? 'form' : 'viewed',
          last_path: '/subscribe',
          email: emailRef.current || undefined,
        });
      }
      clearFunnel();
    };
  }, []);

  React.useEffect(() => {
    if (!user) {
      return;
    }
    if (user.email && !email) {
      setEmail(user.email);
    }
    const company = user.companies?.[0]?.company_name;
    if (company && !companyName) {
      setCompanyName(company);
    }
  }, [user, email, companyName]);

  const startCheckout = React.useCallback(async (
    checkoutEmail: string,
    checkoutCompany: string,
    options?: { googleSignupToken?: string; password?: string; role?: string },
  ) => {
    const response = await fetch(`${GATEWAY_BASE_URL}/api/v1/billing/checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tier_id: tier.id,
        email: checkoutEmail,
        company_name: checkoutCompany,
        success_url: `${window.location.origin}${CONSOLE_PATH}/home?checkout=success&onboard=1&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}${SUBSCRIBE_DESTINATION}?plan=${encodeURIComponent(tier.id)}&checkout=cancel${subscribePromo ? `&promo=${encodeURIComponent(subscribePromo)}` : ''}`,
        ...(options?.googleSignupToken ? { google_signup_token: options.googleSignupToken } : {}),
        ...(options?.password ? { password: options.password } : {}),
        ...(options?.role ? { role: options.role } : {}),
        ...(subscribePromo ? { promo: subscribePromo } : {}),
        ...getAttribution(),
      }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body?.detail || `Checkout failed (${response.status})`);
    }
    if (!body?.url) {
      throw new Error('Checkout session did not return a URL');
    }
    checkoutStartedRef.current = true;
    clearFunnel();
    identifySiteVisitor({
      email: checkoutEmail,
      company_name: checkoutCompany,
      source: subscribePromo === 'linkedin_oidc' ? 'subscribe_linkedin' : 'subscribe',
      path: '/subscribe',
    });
    trackUxEvent({
      event_type: 'funnel_completed',
      funnel_key: 'subscribe',
      step: 'checkout',
      last_path: '/subscribe',
      email: checkoutEmail,
    });
    window.location.assign(String(body.url));
  }, [subscribePromo, tier.id]);

  React.useEffect(() => {
    if (!googleSignupToken) {
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch(
          `${GATEWAY_BASE_URL}/api/v1/auth/sso/google/signup-proof?token=${encodeURIComponent(googleSignupToken)}`,
        );
        const body = await response.json().catch(() => ({}));
        if (!response.ok || !body?.email) {
          if (!cancelled) {
            setFormError(t('subscribe.googleProofExpired'));
          }
          return;
        }
        if (cancelled) {
          return;
        }
        const confirmedEmail = String(body.email).trim().toLowerCase();
        const confirmedName = String(body.name || '').trim();
        setGoogleIdentity({ email: confirmedEmail, name: confirmedName });
        setEmail(confirmedEmail);
        if (confirmedName) {
          setFullName(confirmedName);
        }
        setMode('create');
        setFormError(null);
      } catch {
        if (!cancelled) {
          setFormError(t('subscribe.googleProofExpired'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [googleSignupToken, t]);

  const handleCreateSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    const normalizedEmail = (googleIdentity?.email || email).trim().toLowerCase();
    const normalizedCompany = subscriptionAccountName(companyName, fullName, normalizedEmail);
    if (!googleIdentity && (!normalizedEmail || !password.trim())) {
      setFormError(t('subscribe.emailRequired'));
      return;
    }
    if (!googleIdentity && password.trim().length < 8) {
      setFormError(t('subscribe.passwordLength'));
      return;
    }
    if (!normalizedEmail) {
      setFormError(t('subscribe.emailRequired'));
      return;
    }
    setIsSubmitting(true);
    try {
      // Persist local credentials for returning users on this browser, then open Stripe Checkout.
      // Gateway provisioning completes after successful payment / invite flows.
      // Google already proved this mailbox. Checkout signs them in. It does not ask for a password.
      if (!googleIdentity) {
        createLocalAccount(normalizedEmail, password.trim());
      }
      await startCheckout(normalizedEmail, normalizedCompany, {
        googleSignupToken: googleIdentity ? googleSignupToken : undefined,
        password: googleIdentity ? undefined : password.trim(),
        role: jobTitle.trim() || undefined,
      });
    } catch (error) {
      setFormError(error instanceof Error ? error.message : t('subscribe.unableStart'));
      trackUxEvent({
        event_type: 'product_error',
        funnel_key: 'subscribe',
        step: 'create_checkout',
        last_path: '/subscribe',
        email: email.trim() || undefined,
        message: error instanceof Error ? error.message : 'checkout_failed',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignInSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !password) {
      setFormError(t('subscribe.signInRequired'));
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await login(normalizedEmail, password);
      if (!result.ok) {
        setFormError(result.error || 'Unable to authenticate with the provided credentials.');
        return;
      }
      const company = subscriptionAccountName(
        companyName || user?.companies?.[0]?.company_name || '',
        fullName,
        normalizedEmail,
      );
      await startCheckout(normalizedEmail, company);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : t('subscribe.unableStart'));
      trackUxEvent({
        event_type: 'product_error',
        funnel_key: 'subscribe',
        step: 'signin_checkout',
        last_path: '/subscribe',
        email: email.trim() || undefined,
        message: error instanceof Error ? error.message : 'checkout_failed',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinueAuthenticated = async () => {
    setFormError(null);
    const normalizedEmail = (email || user?.email || '').trim().toLowerCase();
    const company = subscriptionAccountName(
      companyName || user?.companies?.[0]?.company_name || '',
      fullName || user?.email || '',
      normalizedEmail,
    );
    if (!normalizedEmail) {
      setFormError(t('subscribe.addEmail'));
      return;
    }
    setIsSubmitting(true);
    try {
      await startCheckout(normalizedEmail, company);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : t('subscribe.unableStart'));
      trackUxEvent({
        event_type: 'product_error',
        funnel_key: 'subscribe',
        step: 'continue_checkout',
        last_path: '/subscribe',
        email: normalizedEmail || undefined,
        message: error instanceof Error ? error.message : 'checkout_failed',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <section className="page-section subscribe-page">
        <div className="subscribe-shell">
          <p>{t('subscribe.checking')}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="page-section subscribe-page">
      {subscribePromo === 'linkedin_oidc' ? (
        <AssentCapture language="en" offer="linkedin_deal" />
      ) : null}
      {subscribePromo === 'linkedin_oidc' ? (
        <p className="subscribe-promo-banner" role="status">
          LinkedIn founder deal applied at checkout when configured (20% off first 3 months).
        </p>
      ) : null}
      <div className="subscribe-shell">
        <aside className="subscribe-plan-panel" aria-label="Selected plan">
          <p className="subscribe-kicker">{t('subscribe.kicker')}</p>
          <h1>{tier.name}</h1>
          <p className="subscribe-plan-price">
            {tier.priceDisplay || t('subscribe.contactEstimate')}
            {tier.cadence ? <span> {tier.cadence}</span> : null}
          </p>
          <p className="subscribe-plan-copy">{tier.subtitle}</p>
          <ul className="subscribe-plan-includes">
            {tier.includes.slice(0, 5).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <ul className="subscribe-plan-terms">
            {tier.priceDisplay ? <li>{t('subscribe.noCardYet')}</li> : null}
            <li>{t('subscribe.renews')}</li>
            <li>{t('subscribe.youAreAdmin')}</li>
            <li>
              {tier.includedLicenses == null
                ? t('subscribe.unlimitedLicenses')
                : t('subscribe.includedLicenses').replace('{count}', String(tier.includedLicenses))}
            </li>
            <li>{t('subscribe.afterPay')}</li>
          </ul>
          <Link className="subscribe-change-plan" to={SUPPORT_DESTINATION}>
            {t('subscribe.changePlan')}
          </Link>
        </aside>

        <div className="subscribe-auth-panel">
          {checkoutCanceled ? (
            <p className="subscribe-error" role="status">
              Checkout was canceled. Pick up where you left off — nothing was charged.
            </p>
          ) : null}
          {isAuthenticated ? (
            <>
              <h2>{t('subscribe.continueAs').replace('{email}', user?.email || 'your account')}</h2>
              <p className="subscribe-lead">
                {t('subscribe.signedInLead')}
              </p>
              <form
                className="subscribe-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleContinueAuthenticated();
                }}
              >
                <label htmlFor="subscribe-email-auth">{t('contact.workEmail')}</label>
                <input
                  id="subscribe-email-auth"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
                <label htmlFor="subscribe-company-auth">{t('subscribe.company')}</label>
                <input
                  id="subscribe-company-auth"
                  type="text"
                  autoComplete="organization"
                  value={companyName}
                  onChange={(event) => setCompanyName(event.target.value)}
                />
                <p className="subscribe-field-hint">{t('subscribe.companyHint')}</p>
                {formError ? <p className="subscribe-error" role="alert">{formError}</p> : null}
                <div className="subscribe-actions">
                  <button className="btn btn-success subscribe-submit" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Working…' : `Continue to ${tier.name}`}
                  </button>
                  <Link className="btn btn-secondary subscribe-cancel" to={SUPPORT_DESTINATION}>
                    {t('subscribe.cancel')}
                  </Link>
                </div>
              </form>
            </>
          ) : (
            <>
              <div className="subscribe-mode-tabs" role="tablist" aria-label="Account options">
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === 'create'}
                  className={`subscribe-mode-tab ${mode === 'create' ? 'is-active' : ''}`}
                  onClick={() => {
                    setMode('create');
                    setFormError(null);
                  }}
                >
                  Create account
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === 'signin'}
                  className={`subscribe-mode-tab ${mode === 'signin' ? 'is-active' : ''}`}
                  onClick={() => {
                    setMode('signin');
                    setFormError(null);
                  }}
                >
                  Sign in
                </button>
              </div>

              <h2>{mode === 'create' ? t('subscribe.createAccount') : t('subscribe.signInContinue')}</h2>
              <p className="subscribe-lead">
                {googleIdentity
                  ? t('subscribe.googleConfirmed').replace('{email}', googleIdentity.email)
                  : mode === 'create'
                    ? t('subscribe.createLead')
                    : t('subscribe.signinLead')}
              </p>
              {subscribeSsoError && !googleIdentity ? (
                <p className="subscribe-error" role="alert">
                  {subscribeSsoError === 'no_account'
                    ? t('subscribe.googleNoAccount')
                    : subscribeSsoError === 'account_disabled'
                      ? t('login.accountDisabled')
                      : subscribeSsoError === 'not_workspace'
                        ? t('login.googleNotWorkspace')
                        : subscribeSsoError === 'not_verified'
                          ? t('login.googleNotVerified')
                          : t('login.ssoIncomplete')}
                </p>
              ) : null}

              {ssoProvider === 'keycloak' ? (
                <button
                  type="button"
                  className="btn btn-secondary subscribe-sso"
                  onClick={() => startSsoLogin(`${SUBSCRIBE_DESTINATION}?plan=${encodeURIComponent(tier.id)}`)}
                >
                  {t('subscribe.sso')}
                </button>
              ) : null}
              <button
                type="button"
                className="login-google subscribe-sso"
                onClick={() => {
                  void startGoogleLogin(
                    `${SUBSCRIBE_DESTINATION}?plan=${encodeURIComponent(tier.id)}`,
                    { signup: mode === 'create' },
                  ).then((result) => {
                    if (!result.ok) {
                      setFormError(t('login.googleUnavailable'));
                    }
                  });
                }}
              >
                <GoogleMark />
                {mode === 'create' ? t('subscribe.googleContinue') : t('login.google')}
              </button>

              <form
                className="subscribe-form"
                onSubmit={(event) => {
                  if (mode === 'create') {
                    void handleCreateSubmit(event);
                  } else {
                    void handleSignInSubmit(event);
                  }
                }}
              >
                {mode === 'create' ? (
                  <>
                    <label htmlFor="subscribe-name">{t('subscribe.fullName')}</label>
                    <input
                      id="subscribe-name"
                      type="text"
                      autoComplete="name"
                      value={fullName}
                      onChange={(event) => setFullName(event.target.value)}
                      required
                    />
                  </>
                ) : null}
                <label htmlFor="subscribe-email">{mode === 'create' ? t('contact.workEmail') : t('login.emailLabel')}</label>
                <input
                  id="subscribe-email"
                  type={mode === 'create' ? 'email' : 'text'}
                  autoComplete="email"
                  value={googleIdentity?.email || email}
                  onChange={(event) => setEmail(event.target.value)}
                  readOnly={Boolean(googleIdentity)}
                  required
                />
                <label htmlFor="subscribe-company">{t('subscribe.company')}</label>
                <input
                  id="subscribe-company"
                  type="text"
                  autoComplete="organization"
                  value={companyName}
                  onChange={(event) => setCompanyName(event.target.value)}
                />
                <p className="subscribe-field-hint">{t('subscribe.companyHint')}</p>
                {mode === 'create' ? (
                  <>
                    <label htmlFor="subscribe-role">{t('signup.role')}</label>
                    <input
                      id="subscribe-role"
                      type="text"
                      autoComplete="organization-title"
                      placeholder={t('signup.role')}
                      value={jobTitle}
                      onChange={(event) => setJobTitle(event.target.value)}
                    />
                  </>
                ) : null}
                {googleIdentity ? null : (
                <>
                <label htmlFor="subscribe-password">{t('subscribe.password')}</label>
                <div className="subscribe-password-field">
                  <input
                    id="subscribe-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    minLength={mode === 'create' ? 8 : undefined}
                    required
                  />
                  <button
                    type="button"
                    className="subscribe-password-toggle"
                    onClick={() => setShowPassword((current) => !current)}
                    aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? t('login.hide') : t('login.show')}
                  </button>
                </div>
                </>
                )}
                {formError ? <p className="subscribe-error" role="alert">{formError}</p> : null}
                <div className="subscribe-actions">
                  <button className="btn btn-success subscribe-submit" type="submit" disabled={isSubmitting}>
                    {isSubmitting
                      ? 'Working…'
                      : googleIdentity
                        ? `Continue to ${tier.name}`
                        : mode === 'create'
                          ? `Create account & start ${tier.name}`
                          : `Sign in & start ${tier.name}`}
                  </button>
                  <Link className="btn btn-secondary subscribe-cancel" to={SUPPORT_DESTINATION}>
                    {t('subscribe.cancel')}
                  </Link>
                </div>
                {mode === 'create' ? (
                  <p className="subscribe-legal">
                    By continuing you agree to our <Link to={websitePath('/terms')}>Terms</Link> and{' '}
                    <Link to={websitePath('/privacy')}>Privacy Policy</Link>.
                  </p>
                ) : (
                  <p className="subscribe-legal">
                    Need an account?{' '}
                    <button type="button" className="subscribe-inline-link" onClick={() => setMode('create')}>
                      Create one
                    </button>
                    {' · '}
                    <Link to={websitePath('/login')}>Full login page</Link>
                  </p>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

const DemoRequestPage: React.FC = () => {
  const { t, language } = useWebsiteLanguage();
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const submittedRef = React.useRef(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const emailRef = React.useRef('');
  const [phone, setPhone] = React.useState('');
  const [company, setCompany] = React.useState('');
  const [employees, setEmployees] = React.useState('');

  React.useEffect(() => {
    emailRef.current = email;
  }, [email]);

  React.useEffect(() => {
    markFunnel('demo_request', 'viewed');
    trackUxEvent({ event_type: 'funnel_step', funnel_key: 'demo_request', step: 'viewed', last_path: '/demo-request' });
    return () => {
      if (!submittedRef.current) {
        trackUxEvent({
          event_type: 'funnel_abandoned',
          funnel_key: 'demo_request',
          step: emailRef.current ? 'form' : 'viewed',
          last_path: '/demo-request',
          email: emailRef.current || undefined,
        });
      }
      clearFunnel();
    };
  }, []);

  const handleDemoSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const response = await fetch(SALES_CRM_FLOW_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: `${firstName.trim()} ${lastName.trim()}`.trim(),
          workEmail: email.trim(),
          phone: phone.trim(),
          company: company.trim(),
          employees: employees.trim(),
          useCase: 'Requested guided product demo and onboarding walkthrough.',
          source: 'demo-request',
          locale: navigator.language || 'en-US',
          page_path: window.location.pathname,
          submitted_at: new Date().toISOString(),
          ...getAttribution(),
        }),
      });
      if (!response.ok) {
        const body = await response.text();
        throw new Error(body || `CRM lead capture failed with HTTP ${response.status}`);
      }
      submittedRef.current = true;
      setIsSubmitted(true);
      clearFunnel();
      identifySiteVisitor({
        email: email.trim(),
        full_name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        company_name: company.trim(),
        source: 'demo-request',
        path: '/demo-request',
      });
      trackUxEvent({
        event_type: 'funnel_completed',
        funnel_key: 'demo_request',
        step: 'submitted',
        last_path: '/demo-request',
        email: email.trim(),
        product: 'inlet',
      });
    } catch {
      setSubmitError(t('demo.submitError'));
      trackUxEvent({
        event_type: 'product_error',
        product: 'inlet',
        funnel_key: 'demo_request',
        step: 'submit',
        last_path: '/demo-request',
        email: email.trim() || undefined,
        message: 'Demo request submit failed',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="page-section demo-request-page">
      <AssentCapture language={language === 'es' ? 'es' : 'en'} offer="linkedin_deal" />
      <div className="demo-request-shell">
        <aside className="demo-request-left" aria-label={t('demo.kicker')}>
          <div>
            <p className="demo-request-left-kicker">{t('demo.kicker')}</p>
            <h2>{t('demo.headline')}</h2>
            <p className="demo-request-left-lead">
              <DodTermText text={t('demo.subtitle')} language={language} seen={new Set()} />
            </p>
            <ul className="demo-request-bullets">
              <li><DodTermText text={t('demo.bullet1')} language={language} seen={new Set()} /></li>
              <li><DodTermText text={t('demo.bullet2')} language={language} seen={new Set()} /></li>
              <li><DodTermText text={t('demo.bullet3')} language={language} seen={new Set()} /></li>
            </ul>
            {HAS_SUPPORT_PHONE ? <p className="demo-request-left-kicker">{`Call ${SUPPORT_PHONE_DISPLAY}`}</p> : null}
          </div>
        </aside>

        <div className="demo-request-right">
          {!isSubmitted ? (
            <>
              <h1>{t('demo.title')}</h1>
              <p className="subtitle">{t('demo.subtitle')}</p>
              <form
                className="demo-form demo-request-form"
                onSubmit={handleDemoSubmit}
              >
                <div className="demo-request-grid-two">
                  <label className="demo-field">
                    <span>{t('demo.firstName')} *</span>
                    <input required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  </label>
                  <label className="demo-field">
                    <span>{t('demo.lastName')} *</span>
                    <input required value={lastName} onChange={(e) => setLastName(e.target.value)} />
                  </label>
                </div>
                <label className="demo-field">
                  <span>{t('demo.email')} *</span>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                </label>
                <label className="demo-field">
                  <span>{t('demo.phone')} *</span>
                  <input required value={phone} onChange={(e) => setPhone(e.target.value)} />
                </label>
                <div className="demo-request-grid-two">
                  <label className="demo-field">
                    <span>{t('demo.company')} *</span>
                    <input required value={company} onChange={(e) => setCompany(e.target.value)} />
                  </label>
                  <label className="demo-field">
                    <span>{t('demo.employees')} *</span>
                    <select required value={employees} onChange={(e) => setEmployees(e.target.value)}>
                      <option value="">{t('demo.employeesSelect')}</option>
                      <option value="1-10">{t('demo.headcount.1')}</option>
                      <option value="11-50">{t('demo.headcount.2')}</option>
                      <option value="51-200">{t('demo.headcount.3')}</option>
                      <option value="201-1000">{t('demo.headcount.4')}</option>
                      <option value="1001+">{t('demo.headcount.5')}</option>
                    </select>
                  </label>
                </div>
                <button className="btn btn-primary demo-request-submit" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? t('demo.submitting') : t('demo.schedule')}
                </button>
                {submitError ? <p className="subtitle" role="alert">{submitError}</p> : null}
                <p className="demo-request-legal-note">
                  {t('demo.privacyAgree')}{' '}
                  <Link to={PRIVACY_DESTINATION}>{t('login.privacyLink')}</Link>
                </p>
              </form>
            </>
          ) : (
            <div className="form-success">
              <h3>{t('demo.thanks')}</h3>
              <p>{t('demo.thanksBody')}</p>
              <button className="btn btn-secondary" type="button" onClick={() => setIsSubmitted(false)}>
                {t('demo.another')}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const SalesAssistantPage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  const location = useLocation();
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [company, setCompany] = React.useState('');
  const [role, setRole] = React.useState('');
  const [prompt, setPrompt] = React.useState(() => {
    const initialPrompt = (location.state as { prompt?: string } | null)?.prompt;
    return typeof initialPrompt === 'string' ? initialPrompt : '';
  });
  const [loading, setLoading] = React.useState(false);
  const [response, setResponse] = React.useState<GatewayChatResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [leadCaptured, setLeadCaptured] = React.useState(false);
  const autoSubmittedPromptRef = React.useRef<string | null>(null);

  const askSalesAssistant = React.useCallback(async (trimmedPrompt: string) => {
    if (!trimmedPrompt) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(GATEWAY_CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // Keep the model prompt free of "sales/demo/pricing" filler so chat_intake
          // classifies the visitor question, not our wrapper.
          prompt: [
            'You are the NextCompli website guide. Answer clearly. Do not invent pricing.',
            name.trim() ? `Visitor name: ${name.trim()}` : '',
            email.trim() ? `Visitor email: ${email.trim()}` : '',
            company.trim() ? `Visitor company: ${company.trim()}` : '',
            role.trim() ? `Visitor role: ${role.trim()}` : '',
            `Visitor question: ${trimmedPrompt}`,
          ].filter(Boolean).join('\n'),
          session_id: 'website-sales-assistant',
          page_path: '/sales-assistant',
          full_name: name.trim() || undefined,
          work_email: email.trim() || undefined,
          company: company.trim() || undefined,
          route_intake: true,
        }),
      });

      if (!response.ok) {
        let detail = t('sales.unable');
        try {
          const errorPayload = await response.json();
          if (typeof errorPayload?.detail === 'string' && errorPayload.detail.trim()) {
            detail = errorPayload.detail;
          }
        } catch {
          // Ignore non-JSON error bodies.
        }
        throw new Error(detail);
      }

      const payload = (await response.json()) as GatewayChatResponse;
      setResponse(payload);
      if (
        name.trim()
        || email.trim()
        || company.trim()
        || role.trim()
        || (payload.intake_route && payload.intake_route !== 'noise')
      ) {
        setLeadCaptured(true);
      }
    } catch (submissionError) {
      const message = submissionError instanceof Error && submissionError.message.trim()
        ? submissionError.message
        : t('sales.unable');
      setResponse(null);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [company, email, name, role]);

  const submitSalesPrompt = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await askSalesAssistant(prompt.trim());
  };

  React.useEffect(() => {
    const initialPrompt = (location.state as { prompt?: string } | null)?.prompt;
    if (typeof initialPrompt !== 'string') {
      return;
    }
    const trimmedPrompt = initialPrompt.trim();
    if (!trimmedPrompt || autoSubmittedPromptRef.current === trimmedPrompt) {
      return;
    }
    autoSubmittedPromptRef.current = trimmedPrompt;
    void askSalesAssistant(trimmedPrompt);
  }, [askSalesAssistant, location.state]);

  return (
    <section className="page-section sales-assistant-page">
      <div className="sales-assistant-shell">
        <h1>{t('sales.title')}</h1>
        <p className="subtitle">{t('sales.subtitle')}</p>
        <form className="demo-form sales-assistant-form" onSubmit={submitSalesPrompt}>
          <div className="sales-assistant-grid">
            <input
              placeholder={t('sales.fullName')}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <input
              placeholder={t('sales.workEmail')}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <input
              placeholder="Company"
              value={company}
              onChange={(event) => setCompany(event.target.value)}
            />
            <input
              placeholder={t('sales.role')}
              value={role}
              onChange={(event) => setRole(event.target.value)}
            />
          </div>
          <textarea
            placeholder={t('sales.prompt')}
            rows={4}
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            required
          />
          <button className="btn btn-primary" type="submit" disabled={loading || !prompt.trim()}>
            {loading ? t('sales.asking') : t('sales.ask')}
          </button>
        </form>
        {leadCaptured ? <p className="sales-assistant-lead-captured">{t('sales.leadCaptured')}</p> : null}
        {response ? (
          <div className="sales-assistant-response" role="status" aria-live="polite">
            <p><strong>Provider:</strong> {response.provider}</p>
            <p>{response.reply}</p>
            {response.citations && response.citations.length > 0 ? (
              <div>
                <p><strong>Sources:</strong></p>
                <ul>
                  {response.citations.map((citation) => (
                    <li key={citation}>{citation}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
        {error ? (
          <div className="sales-assistant-response sales-assistant-response-error" role="status" aria-live="polite">
            <p>{error}</p>
          </div>
        ) : null}
      </div>
    </section>
  );
};

const SignupPage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCreated, setIsCreated] = React.useState(false);
  const createdRef = React.useRef(false);
  const [accountError, setAccountError] = React.useState<string | null>(null);
  const [emailValue, setEmailValue] = React.useState('');
  const emailRef = React.useRef('');
  const verifiedRef = React.useRef('');
  const [oauthProvider, setOauthProvider] = React.useState<string | null>(null);
  const [verifiedEmail, setVerifiedEmail] = React.useState('');
  const [oauthPromo, setOauthPromo] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    emailRef.current = emailValue;
  }, [emailValue]);
  React.useEffect(() => {
    verifiedRef.current = verifiedEmail;
  }, [verifiedEmail]);

  React.useEffect(() => {
    markFunnel('signup', 'viewed');
    trackUxEvent({ event_type: 'funnel_step', funnel_key: 'signup', step: 'viewed', last_path: '/signup' });
    return () => {
      if (!createdRef.current) {
        trackUxEvent({
          event_type: 'funnel_abandoned',
          funnel_key: 'signup',
          step: verifiedRef.current ? 'form' : 'oauth',
          last_path: '/signup',
          email: emailRef.current || undefined,
        });
      }
      clearFunnel();
    };
  }, []);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const provider = params.get('oauth_provider');
    const email = params.get('verified_email');
    const oauthError = params.get('oauth_error');
    const promo = params.get('promo');

    if (oauthError) {
      setAccountError(`OAuth verification failed: ${oauthError}`);
      trackUxEvent({
        event_type: 'login_failed',
        funnel_key: 'signup',
        step: 'oauth',
        last_path: '/signup',
        error_code: oauthError,
        message: `OAuth verification failed: ${oauthError}`,
      });
      return;
    }

    if (provider && email) {
      setOauthProvider(provider);
      setVerifiedEmail(email.trim().toLowerCase());
      setEmailValue(email.trim().toLowerCase());
      setOauthPromo(promo === 'linkedin_oidc' ? 'linkedin_oidc' : null);
      setAccountError(null);
    }
  }, [location.search]);

  const startOauthVerification = (provider: 'google' | 'microsoft' | 'linkedin') => {
    const redirectUri = `${window.location.origin}${SIGNUP_DESTINATION}`;
    const oauthStartUrl = `/api/auth/oauth2/${provider}/start?redirect_uri=${encodeURIComponent(redirectUri)}`;
    window.location.assign(oauthStartUrl);
  };

  return (
    <section className="page-section signup-page">
      <AssentCapture language="en" offer="linkedin_deal" />
      <div className="signup-shell">
        <aside className="signup-value-panel" aria-label="Account benefits">
          <div className="signup-value-rail" aria-hidden="true">
            <span>🌀</span>
            <span>🔒</span>
            <span>⚡</span>
          </div>
          <div className="signup-value-card">
            <h2>{t('signup.ready')}</h2>
            <p>
              {t('signup.value')}
            </p>
            <img
              className="signup-value-image"
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=900&q=80"
              alt="Team collaborating with growth dashboard"
              loading="lazy"
            />
            <p className="signup-plan-lead">{t('signup.planLead')}</p>
            <button className="btn btn-primary signup-plan-btn" type="button" onClick={() => navigate(`${SUBSCRIBE_DESTINATION}?plan=basic`)}>
              {t('signup.planBtn')}
            </button>
          </div>
        </aside>

        <section className="signup-form-shell" aria-label="Trial signup form">
          <h1>{t('signup.title')}</h1>
          <p className="signup-oauth-status">{t('signup.planLead')}</p>
          <button className="btn btn-primary signup-plan-btn" type="button" onClick={() => navigate(`${SUBSCRIBE_DESTINATION}?plan=basic`)}>
            {t('signup.planBtn')}
          </button>
          <p className="signup-oauth-status">{t('signup.emailHint')}</p>

          {!isCreated ? (
            <>
            <form
              className="demo-form signup-form"
              onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                const email = String(formData.get('email') ?? '').trim().toLowerCase();
                const password = String(formData.get('password') ?? '');
                const confirmPassword = String(formData.get('confirmPassword') ?? '');
                const firstName = String(formData.get('fullName') ?? '').trim();
                const lastName = String(formData.get('lastName') ?? '').trim();
                const company = String(formData.get('company') ?? '').trim();
                const role = String(formData.get('role') ?? '').trim();

                if (verifiedEmail && email !== verifiedEmail) {
                  setAccountError('The form email must match your Google, Microsoft, or LinkedIn email.');
                  return;
                }

                if (password !== confirmPassword) {
                  setAccountError('Passwords do not match.');
                  return;
                }

                setIsSubmitting(true);
                setAccountError(null);
                void (async () => {
                  try {
                    const response = await fetch(`${GATEWAY_BASE_URL}/api/v1/auth/signup`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        email,
                        password,
                        first_name: firstName,
                        last_name: lastName,
                        company,
                        role,
                      }),
                    });
                    const body = await response.json().catch(() => ({}));
                    if (!response.ok) {
                      const detail = body?.detail;
                      setAccountError(typeof detail === 'string' && detail ? detail : t('signup.unable'));
                      return;
                    }
                    createLocalAccount(email, password);
                    createdRef.current = true;
                    notePostludeVisit('signup', { note: email });
                    identifySiteVisitor({
                      email,
                      company_name: company,
                      full_name: [firstName, lastName].filter(Boolean).join(' '),
                      source: 'signup',
                      path: '/signup',
                    });
                    setIsCreated(true);
                    clearFunnel();
                    trackUxEvent({
                      event_type: 'funnel_completed',
                      funnel_key: 'signup',
                      step: 'created',
                      last_path: '/signup',
                      email,
                    });
                  } catch {
                    setAccountError(t('signup.unable'));
                  } finally {
                    setIsSubmitting(false);
                  }
                })();
              }}
            >
              <div className="signup-grid">
                <input
                  name="email"
                  placeholder="Email"
                  type="email"
                  value={emailValue}
                  onChange={(event) => setEmailValue(event.target.value)}
                  required
                />
                <input name="fullName" placeholder="First name" required />
                <input name="lastName" placeholder="Last name" required />
                <input name="company" placeholder={t('signup.company')} />
                <input name="role" placeholder={t('signup.role')} />
                <input name="password" placeholder="Set a password" type="password" minLength={8} required />
                <input name="confirmPassword" placeholder="Confirm password" type="password" minLength={8} required />
              </div>
              {accountError ? <p className="signup-error">{accountError}</p> : null}
              <button className="btn btn-primary signup-submit" type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('signup.creating') : 'Continue'}
              </button>
              <p className="signup-legal-note">
                By clicking this button, you agree to our Terms of Service and Privacy Policy.
              </p>
            </form>
            <div className="signup-divider" aria-hidden="true">OR</div>
            <div className="signup-oauth-actions" aria-label="Optional Google, Microsoft, and LinkedIn">
              <button className="login-google" type="button" onClick={() => startOauthVerification('google')}>
                <GoogleMark />
                {t('signup.google')}
              </button>
              <button className="login-google" type="button" onClick={() => startOauthVerification('microsoft')}>
                <MicrosoftMark />
                Continue with Microsoft
              </button>
              <button className="login-google" type="button" onClick={() => startOauthVerification('linkedin')}>
                Continue with LinkedIn
                <span className="signup-oauth-deal"> — 20% off first 3 months</span>
              </button>
            </div>
            {oauthProvider && verifiedEmail ? (
              <p className="signup-oauth-status">
                Verified email via {oauthProvider}: <strong>{verifiedEmail}</strong>
                {oauthPromo === 'linkedin_oidc' ? (
                  <>
                    {' '}
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => navigate(`${SUBSCRIBE_DESTINATION}?plan=basic&promo=linkedin_oidc`)}
                    >
                      Claim LinkedIn founder deal
                    </button>
                  </>
                ) : null}
              </p>
            ) : null}
            </>
          ) : (
            <div className="form-success">
              <h3>{t('signup.created')}</h3>
              <p>Use your new credentials to log in.</p>
              <button className="btn btn-secondary" type="button" onClick={() => navigate(websitePath('/login'))}>
                Go to login
              </button>
            </div>
          )}
        </section>
      </div>
    </section>
  );
};

const LoginPage: React.FC = () => {
  const { t, language } = useWebsiteLanguage();
  const { login, isAuthenticated, isLoading, ssoProvider, startSsoLogin, startGoogleLogin, startMicrosoftLogin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [loginError, setLoginError] = React.useState<string | null>(null);
  const [passwordResetRequired, setPasswordResetRequired] = React.useState(false);
  const [agreeToLegal, setAgreeToLegal] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [socialSignIn, setSocialSignIn] = React.useState({ google: true, microsoft: true });
  const [accountMode, setAccountMode] = React.useState<'signin' | 'create'>(
    () => (new URLSearchParams(location.search).get('mode') === 'create' ? 'create' : 'signin'),
  );
  const [createPlan, setCreatePlan] = React.useState<PricingTierId>('basic');
  const [createName, setCreateName] = React.useState('');
  const [createCompany, setCreateCompany] = React.useState('');
  const [createRole, setCreateRole] = React.useState('');
  React.useEffect(() => {
    let cancelled = false;
    // Same-origin / inferred gateway — never force 127.0.0.1 in production builds.
    // A localhost fallback was hiding Google when a local gateway answered google:false.
    void fetch(`${GATEWAY_BASE_URL}/api/v1/auth/sso/providers`)
      .then((response) => (response.ok ? response.json() : null))
      .then((body: { google?: boolean; microsoft?: boolean } | null) => {
        if (cancelled || !body) {
          return;
        }
        setSocialSignIn({ google: body.google === true, microsoft: body.microsoft === true });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  const fromState = (location.state as { from?: { pathname?: string } })?.from?.pathname || '';
  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const nextFromQueryRaw = (searchParams.get('next') || '').trim();
  const nextFromQuery = nextFromQueryRaw.startsWith('/') ? nextFromQueryRaw : '';
  const from = canonicalizeConsolePath(fromState || nextFromQuery || CONSOLE_PATH);
  const forceFreshSession = searchParams.get('fresh') === '1' || searchParams.get('logout') === '1';
  const loginCompletedRef = React.useRef(false);
  const usernameRef = React.useRef('');
  React.useEffect(() => {
    usernameRef.current = username;
  }, [username]);
  React.useEffect(() => {
    if (isAuthenticated && !forceFreshSession) {
      loginCompletedRef.current = true;
    }
  }, [forceFreshSession, isAuthenticated]);
  React.useEffect(() => {
    markFunnel('login', 'viewed');
    trackUxEvent({ event_type: 'funnel_step', funnel_key: 'login', step: 'viewed', last_path: '/login' });
    return () => {
      if (!loginCompletedRef.current) {
        trackUxEvent({
          event_type: 'funnel_abandoned',
          funnel_key: 'login',
          step: 'form',
          last_path: '/login',
          email: usernameRef.current || undefined,
        });
      }
      clearFunnel();
    };
  }, []);

  React.useEffect(() => {
    if (!forceFreshSession || isLoading) return;
    logout();
  }, [forceFreshSession, isLoading, logout]);

  const ssoErrorCode = (searchParams.get('sso_error') || '').trim();
  const ssoErrorMessage = React.useMemo(() => {
    if (!ssoErrorCode) {
      return '';
    }
    if (ssoErrorCode === 'token_exchange_failed') {
      return t('login.ssoUnavailable');
    }
    if (ssoErrorCode === 'expired_state') {
      return t('login.ssoExpired');
    }
    if (ssoErrorCode === 'not_workspace') {
      return t('login.googleNotWorkspace');
    }
    if (ssoErrorCode === 'no_account') {
      return t('login.googleNoAccount');
    }
    if (ssoErrorCode === 'account_disabled') {
      return t('login.accountDisabled');
    }
    if (ssoErrorCode === 'not_verified') {
      return t('login.googleNotVerified');
    }
    if (ssoErrorCode === 'identity_conflict') {
      return t('login.identityConflict');
    }
    return t('login.ssoIncomplete');
  }, [ssoErrorCode, t]);

  React.useEffect(() => {
    if (!ssoErrorCode) {
      return;
    }
    trackUxEvent({
      event_type: 'login_failed',
      funnel_key: 'login',
      step: 'sso',
      last_path: '/login',
      error_code: ssoErrorCode,
      message: ssoErrorMessage || ssoErrorCode,
    });
  }, [ssoErrorCode, ssoErrorMessage]);

  const beginGoogleLogin = React.useCallback(() => {
    setLoginError(null);
    recordAssentEvent(username.trim().toLowerCase() || 'google-sso', location.pathname);
    void startGoogleLogin(`/login?next=${encodeURIComponent(from)}`).then((result) => {
      if (!result.ok) {
        setLoginError(t('login.googleUnavailable'));
      }
    });
  }, [from, location.pathname, startGoogleLogin, t, username]);

  const beginMicrosoftLogin = React.useCallback(() => {
    setLoginError(null);
    recordAssentEvent(username.trim().toLowerCase() || 'microsoft-sso', location.pathname);
    void startMicrosoftLogin(`/login?next=${encodeURIComponent(from)}`).then((result) => {
      if (!result.ok) {
        setLoginError(t('login.microsoftUnavailable'));
      }
    });
  }, [from, location.pathname, startMicrosoftLogin, t, username]);

  const onLegalAssentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAgreeToLegal(event.target.checked);
    if (event.target.checked) {
      setLoginError(null);
    }
  };

  const beginSsoLogin = React.useCallback(() => {
    if (!agreeToLegal) {
      setLoginError(t('login.mustAgree'));
      return;
    }
    setLoginError(null);
    startSsoLogin(`/login?next=${encodeURIComponent(from)}`);
  }, [agreeToLegal, from, startSsoLogin, t]);

  const identityStack = (
    <div className="login-identity-stack">
      <p className="login-or">{t('login.or')}</p>
      {socialSignIn.google ? (
        <button className="login-google" type="button" onClick={beginGoogleLogin}>
          <GoogleMark />
          {t('login.google')}
        </button>
      ) : null}
      {socialSignIn.microsoft ? (
        <button className="login-google" type="button" onClick={beginMicrosoftLogin}>
          <MicrosoftMark />
          {t('login.microsoft')}
        </button>
      ) : null}
      {ssoProvider === 'keycloak' ? (
        <>
          <button className="login-identity-btn" type="button" onClick={beginSsoLogin}>
            <SsoMark />
            {t('login.continueSso')}
          </button>
          <p className="login-local-hint">{t('login.ssoMicrosoftNote')}</p>
        </>
      ) : null}
    </div>
  );

  const loginHelpRow = (
    <p className="login-help-row">
      <Link to={FORGOT_PASSWORD_DESTINATION}>{t('login.trouble')}</Link>
      {' · '}
      <Link to={`${SUBSCRIBE_DESTINATION}?plan=basic`}>{t('login.notCustomer')}</Link>
    </p>
  );

  if (isLoading) {
    return (
      <section className="page-section login-page">
        <div className="login-shell">
          <div className="login-panel">
            <h1>{t('login.checkingSession')}</h1>
          </div>
        </div>
      </section>
    );
  }

  if (isAuthenticated && !forceFreshSession) {
    return <Navigate to={from} replace />;
  }

  return (
    <section className="page-section login-page">
      <div className="login-shell">
        <div className="login-panel">
          <Link className="login-brand" to="/">NextCompli</Link>
          <div className="subscribe-mode-tabs" role="tablist" aria-label="Account options">
            <button
              type="button"
              role="tab"
              aria-selected={accountMode === 'create'}
              className={`subscribe-mode-tab ${accountMode === 'create' ? 'is-active' : ''}`}
              onClick={() => {
                setAccountMode('create');
                setLoginError(null);
              }}
            >
              {t('login.createTab')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={accountMode === 'signin'}
              className={`subscribe-mode-tab ${accountMode === 'signin' ? 'is-active' : ''}`}
              onClick={() => {
                setAccountMode('signin');
                setLoginError(null);
              }}
            >
              {t('login.signInTab')}
            </button>
          </div>
          {accountMode === 'create' ? (
            <form className="login-form login-create-panel" onSubmit={(event) => {
              event.preventDefault();
              const normalizedEmail = username.trim().toLowerCase();
              if (!normalizedEmail || password.trim().length < 8) {
                setLoginError(password.trim().length < 8 ? t('subscribe.passwordLength') : t('subscribe.emailRequired'));
                return;
              }
              setLoginError(null);
              setIsSubmitting(true);
              const chosen = PRICING_TIER_BY_ID[createPlan];
              void fetch(`${GATEWAY_BASE_URL}/api/v1/billing/checkout-session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  tier_id: chosen.id,
                  email: normalizedEmail,
                  company_name: subscriptionAccountName(createCompany, createName, normalizedEmail),
                  password: password.trim(),
                  role: createRole.trim() || undefined,
                  success_url: `${window.location.origin}${CONSOLE_PATH}/home?checkout=success&onboard=1&session_id={CHECKOUT_SESSION_ID}`,
                  cancel_url: `${window.location.origin}${websitePath('/login')}?mode=create`,
                }),
              })
                .then(async (response) => {
                  const body = await response.json().catch(() => ({}));
                  if (!response.ok || !body?.url) {
                    throw new Error(typeof body?.detail === 'string' ? body.detail : t('subscribe.unableStart'));
                  }
                  window.location.assign(String(body.url));
                })
                .catch((error: unknown) => {
                  setLoginError(error instanceof Error ? error.message : t('subscribe.unableStart'));
                  setIsSubmitting(false);
                });
            }}
            >
              <h1>{`Start ${PRICING_TIER_BY_ID[createPlan].name}`}</h1>
              <p className="subtitle login-subtitle">{t('login.createLead')}</p>
              <fieldset className="login-plan-list">
                <legend>{t('login.planLegend')}</legend>
                {PRICING_TIERS.map((plan) => (
                  <label key={plan.id} className={createPlan === plan.id ? 'is-selected' : undefined}>
                    <input
                      type="radio"
                      name="create-plan"
                      value={plan.id}
                      checked={createPlan === plan.id}
                      onChange={() => setCreatePlan(plan.id)}
                    />
                    <span>
                      <strong>{plan.name}</strong>
                      <small>
                        {plan.priceMonthlyUsd != null ? `$${plan.priceMonthlyUsd}/mo` : t('login.planContact')}
                        {' · '}
                        {plan.seats}
                      </small>
                    </span>
                  </label>
                ))}
              </fieldset>
              <label className="login-field-label" htmlFor="create-name">{t('subscribe.fullName')}</label>
              <input id="create-name" type="text" autoComplete="name" value={createName} onChange={(event) => setCreateName(event.target.value)} required />
              <label className="login-field-label" htmlFor="create-email">{t('contact.workEmail')}</label>
              <input id="create-email" type="email" autoComplete="email" value={username} onChange={(event) => setUsername(event.target.value)} required />
              <label className="login-field-label" htmlFor="create-company">{t('subscribe.company')}</label>
              <input id="create-company" type="text" autoComplete="organization" value={createCompany} onChange={(event) => setCreateCompany(event.target.value)} />
              <p className="login-help-row">{t('subscribe.companyHint')}</p>
              <label className="login-field-label" htmlFor="create-role">{t('signup.role')}</label>
              <input id="create-role" type="text" autoComplete="organization-title" placeholder={t('signup.role')} value={createRole} onChange={(event) => setCreateRole(event.target.value)} />
              <label className="login-field-label" htmlFor="create-password">{t('subscribe.password')}</label>
              <div className="login-password-field">
                <input
                  id="create-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-pressed={showPassword}
                >
                  {showPassword ? t('login.hide') : t('login.show')}
                </button>
              </div>
              {loginError ? <p className="login-error" role="alert">{loginError}</p> : null}
              <button className="btn btn-primary login-submit" type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('subscribe.opening') : `${t('login.createTab')} · ${PRICING_TIER_BY_ID[createPlan].name}`}
              </button>
              <p className="login-help-row">{t('subscribe.afterPay')}</p>
            </form>
          ) : (
          <>
          <h1>{t('login.title')}</h1>
          <p className="subtitle login-subtitle">
            {t('login.subtitle')}
          </p>
          {ssoProvider === 'keycloak' ? (
          <form
              className="login-form"
              onSubmit={async (event) => {
                event.preventDefault();
                if (!agreeToLegal) {
                  setLoginError(t('login.mustAgree'));
                  return;
                }
                const normalizedLogin = username.trim().toLowerCase();
                const result = await login(normalizedLogin, password);
                if (!result.ok) {
                  setLoginError(result.error || t('login.authFailed'));
                  return;
                }
                setLoginError(null);
                loginCompletedRef.current = true;
                identifySiteVisitor({
                  email: normalizedLogin.includes('@') ? normalizedLogin : '',
                  full_name: normalizedLogin.includes('@') ? '' : normalizedLogin,
                  source: 'login',
                  path: '/login',
                });
                navigate(from, { replace: true });
              }}
            >
              <label className="login-field-label" htmlFor="login-email">{t('login.emailLabel')}</label>
              <input
                id="login-email"
                placeholder={t('login.emailPlaceholder')}
                type="text"
                autoComplete="email"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                required
              />
              <label className="login-field-label" htmlFor="login-password">{t('login.password')}</label>
              <div className="login-password-field">
                <input
                  id="login-password"
                  placeholder={t('login.password')}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
                <button
                  className="login-password-toggle"
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                  aria-pressed={showPassword}
                >
                  {showPassword ? t('login.hide') : t('login.show')}
                </button>
              </div>
              {loginError && (
                <p className="login-error" role="alert">
                  {loginError}
                </p>
              )}
              <div className="login-actions-row">
                <button className="btn btn-primary login-submit" type="submit">
                  {t('login.continueEmail')}
                </button>
              </div>
            </form>
          ) : null}
          {identityStack}
          {ssoProvider === 'keycloak' ? (
            <>
            {ssoErrorMessage ? (
              <p className="login-error" role="alert">
                {ssoErrorMessage}
              </p>
            ) : null}
            <label className="login-field-label login-legal-assent" htmlFor="legal-assent-checkbox">
              <input
                className="login-legal-assent-checkbox"
                id="legal-assent-checkbox"
                type="checkbox"
                checked={agreeToLegal}
                onChange={onLegalAssentChange}
              />{' '}
              {t('login.agreePrefix')} <Link to={TERMS_DESTINATION}>{t('login.termsLink')}</Link> {t('login.agreeAnd')} <Link to={PRIVACY_DESTINATION}>{t('login.privacyLink')}</Link>.
            </label>
            {loginHelpRow}
            </>
          ) : (
            <form
              className="login-form"
              onSubmit={async (event) => {
                event.preventDefault();

                if (!agreeToLegal) {
                  setLoginError(t('login.mustAgree'));
                  return;
                }

                setIsSubmitting(true);
                setLoginError(null);
                try {
                  const normalizedLogin = username.trim().toLowerCase();
                  const result = await login(normalizedLogin, password);
                  if (!result.ok) {
                    setLoginError(result.error || t('login.authFailed'));
                    return;
                  }

                  setPasswordResetRequired(Boolean(result.passwordResetRequired));
                  recordAssentEvent(normalizedLogin, location.pathname);
                  loginCompletedRef.current = true;
                  identifySiteVisitor({
                    email: normalizedLogin.includes('@') ? normalizedLogin : '',
                    full_name: normalizedLogin.includes('@') ? '' : normalizedLogin,
                    source: 'login',
                    path: '/login',
                  });
                  navigate(from, { replace: true });
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              <label className="login-field-label" htmlFor="login-email">{t('login.emailLabel')}</label>
              <input
                id="login-email"
                placeholder={t('login.emailPlaceholder')}
                type="text"
                autoComplete="email"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                required
              />
              <label className="login-field-label" htmlFor="login-password">{t('login.password')}</label>
              <div className="login-password-field">
                <input
                  id="login-password"
                  placeholder={t('login.password')}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
                <button
                  className="login-password-toggle"
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                  aria-pressed={showPassword}
                >
                  {showPassword ? t('login.hide') : t('login.show')}
                </button>
              </div>
              <label className="login-field-label login-legal-assent" htmlFor="legal-assent-checkbox">
                <input
                  className="login-legal-assent-checkbox"
                  id="legal-assent-checkbox"
                  type="checkbox"
                  checked={agreeToLegal}
                  onChange={onLegalAssentChange}
                />{' '}
                {t('login.agreePrefix')} <Link to={TERMS_DESTINATION}>{t('login.termsLink')}</Link> {t('login.agreeAnd')} <Link to={PRIVACY_DESTINATION}>{t('login.privacyLink')}</Link>.
              </label>
              {loginError && (
                <p className="login-error" role="alert">
                  {loginError}
                </p>
              )}
              {ssoErrorMessage ? (
                <p className="login-error" role="alert">
                  {ssoErrorMessage}
                </p>
              ) : null}
              {passwordResetRequired ? <p className="login-local-hint">{t('login.passwordResetRequired')}</p> : null}
              <div className="login-actions-row">
                <button className="btn btn-primary login-submit" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? t('login.signingIn') : t('login.continueEmail')}
                </button>
                <Link className="login-forgot" to={FORGOT_PASSWORD_DESTINATION}>
                  {t('login.forgotPassword')}
                </Link>
              </div>
              {loginHelpRow}
            </form>
          )}
          </>
          )}
        </div>

        <aside className="login-visual">
          <div className="login-visual-stage">
            <LoginEmailScene />
            <div className="login-visual-copy">
              <p className="login-visual-kicker">{t('login.visualKicker')}</p>
              <h2>{t('login.visualHeadline')}</h2>
              <p>{t('login.visualBody')}</p>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
};

const extractInviteTokenFromLocation = (search: string, hash: string): string => {
  const searchParams = new URLSearchParams(search);
  const queryToken = searchParams.get('token') || searchParams.get('invite_token') || searchParams.get('inviteToken');
  if (queryToken) {
    return queryToken.trim();
  }

  const normalizedHash = hash.startsWith('#') ? hash.slice(1) : hash;
  const hashParams = new URLSearchParams(normalizedHash);
  const hashToken = hashParams.get('token') || hashParams.get('invite_token') || hashParams.get('inviteToken');
  if (hashToken) {
    return hashToken.trim();
  }

  return '';
};

const ForgotPasswordPage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  const [email, setEmail] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [devResetUrl, setDevResetUrl] = React.useState<string | null>(null);

  return (
    <section className="page-section login-page">
      <div className="login-shell">
        <div className="login-panel">
          <Link className="login-brand" to="/">NextCompli</Link>
          <h1>{t('login.forgotTitle')}</h1>
          <p className="subtitle login-subtitle">
            {t('login.forgotSubtitle')}
          </p>
          {message ? <p className="login-local-hint" role="status">{message}</p> : null}
          {error ? <p className="login-error" role="alert">{error}</p> : null}
          {devResetUrl ? (
            <p className="login-local-hint">
              Local/dev reset link:{' '}
              <a href={devResetUrl}>{devResetUrl}</a>
            </p>
          ) : null}
          <form
            className="login-form"
            onSubmit={async (event) => {
              event.preventDefault();
              setIsSubmitting(true);
              setError(null);
              setMessage(null);
              setDevResetUrl(null);
              try {
                const response = await fetch(AUTH_FORGOT_PASSWORD_URL, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: email.trim() }),
                });
                const payload = (await response.json().catch(() => ({}))) as {
                  message?: string;
                  detail?: string;
                  dev_reset_url?: string;
                };
                if (!response.ok) {
                  setError(payload.detail || t('forgot.unable'));
                  return;
                }
                setMessage(payload.message || t('forgot.sent'));
                if (payload.dev_reset_url) {
                  setDevResetUrl(payload.dev_reset_url);
                }
              } catch {
                setError(t('reset.unreachable'));
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            <label className="login-field-label" htmlFor="forgot-password-email">Email</label>
            <input
              id="forgot-password-email"
              type="email"
              autoComplete="username"
              placeholder="you@company.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <div className="login-actions-row">
              <button className="btn btn-primary login-submit" type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('login.sending') : t('login.sendReset')}
              </button>
              <Link className="login-forgot" to="/login">{t('login.backToSignIn')}</Link>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

const ResetPasswordPage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const token = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get('token') || '').trim();
  }, [location.search]);

  return (
    <section className="page-section login-page">
      <div className="login-shell">
        <div className="login-panel">
          <Link className="login-brand" to="/">NextCompli</Link>
          <h1>{t('reset.title')}</h1>
          <p className="subtitle login-subtitle">
            {t('reset.subtitle')}
          </p>
          {!token ? <p className="login-error">{t('reset.missingToken')}</p> : null}
          {message ? <p className="login-local-hint" role="status">{message}</p> : null}
          {error ? <p className="login-error" role="alert">{error}</p> : null}
          <form
            className="login-form"
            onSubmit={async (event) => {
              event.preventDefault();
              if (!token) {
                setError(t('reset.missingToken'));
                return;
              }
              if (newPassword.length < 12) {
                setError(t('reset.tooShort'));
                return;
              }
              if (newPassword !== confirmPassword) {
                setError(t('reset.mismatch'));
                return;
              }
              setIsSubmitting(true);
              setError(null);
              setMessage(null);
              try {
                const response = await fetch(AUTH_RESET_PASSWORD_URL, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ token, new_password: newPassword }),
                });
                const payload = (await response.json().catch(() => ({}))) as { detail?: string };
                if (!response.ok) {
                  setError(payload.detail || t('reset.unable'));
                  return;
                }
                setMessage(t('reset.updated'));
                window.setTimeout(() => {
                  navigate('/login', { replace: true });
                }, 1000);
              } catch {
                setError(t('reset.unreachable'));
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            <label className="login-field-label" htmlFor="reset-password-new">{t('reset.newPassword')}</label>
            <div className="login-password-field">
              <input
                id="reset-password-new"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                minLength={12}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                required
                disabled={!token || isSubmitting}
              />
              <button type="button" className="login-password-toggle" onClick={() => setShowPassword((value) => !value)}>
                {showPassword ? t('login.hide') : t('login.show')}
              </button>
            </div>
            <label className="login-field-label" htmlFor="reset-password-confirm">Confirm new password</label>
            <input
              id="reset-password-confirm"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              minLength={12}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
              disabled={!token || isSubmitting}
            />
            <div className="login-actions-row">
              <button className="btn btn-primary login-submit" type="submit" disabled={!token || isSubmitting}>
                {isSubmitting ? 'Updating…' : 'Update password'}
              </button>
              <Link className="login-forgot" to={FORGOT_PASSWORD_DESTINATION}>Request a new link</Link>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

const AcceptInvitePage: React.FC = () => {
  const { t } = useWebsiteLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, accessToken, refreshProfile } = useAuth();
  const [newPassword, setNewPassword] = React.useState('');
  const [showInvitePassword, setShowInvitePassword] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [accepted, setAccepted] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [usesGoogle, setUsesGoogle] = React.useState(false);
  const [usesMicrosoft, setUsesMicrosoft] = React.useState(false);
  const token = React.useMemo(() => extractInviteTokenFromLocation(location.search, location.hash), [location.hash, location.search]);
  const sessionEmail = String(user?.email || '').trim().toLowerCase();
  const inviteMatchesSession = Boolean(
    isAuthenticated
    && accessToken
    && inviteEmail
    && sessionEmail
    && inviteEmail.trim().toLowerCase() === sessionEmail,
  );
  const canAcceptInSession = usesGoogle && inviteMatchesSession;

  React.useEffect(() => {
    if (!token) {
      return undefined;
    }
    let cancelled = false;
    const loadPreview = async () => {
      try {
        const response = await fetch(`${GATEWAY_BASE_URL}/api/v1/auth/invitations/preview?token=${encodeURIComponent(token)}`);
        if (!response.ok) {
          return;
        }
        const body = await response.json();
        if (cancelled) {
          return;
        }
        setInviteEmail(String(body?.email || ''));
        setUsesGoogle(Boolean(body?.google));
        setUsesMicrosoft(Boolean(body?.microsoft));
      } catch {
        if (!cancelled) {
          setUsesGoogle(false);
        setUsesMicrosoft(false);
        }
      }
    };
    void loadPreview();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const startGoogleInvite = React.useCallback(() => {
    const start = new URL(`${GATEWAY_BASE_URL}/api/v1/auth/sso/google/start`);
    start.searchParams.set('redirect_uri', `${window.location.origin}/console`);
    if (token) {
      start.searchParams.set('invite_token', token);
    }
    const hinted = inviteEmail.trim().toLowerCase();
    if (hinted.includes('@')) {
      start.searchParams.set('login_hint', hinted);
    }
    window.location.assign(start.toString());
  }, [inviteEmail, token]);

  const startMicrosoftInvite = React.useCallback(() => {
    const start = new URL(`${GATEWAY_BASE_URL}/api/v1/auth/sso/microsoft/start`);
    start.searchParams.set('redirect_uri', `${window.location.origin}/console`);
    if (token) {
      start.searchParams.set('invite_token', token);
    }
    const hinted = inviteEmail.trim().toLowerCase();
    if (hinted.includes('@')) {
      start.searchParams.set('login_hint', hinted);
    }
    window.location.assign(start.toString());
  }, [inviteEmail, token]);

  const acceptWithSession = React.useCallback(async () => {
    if (!token || !accessToken) {
      setError(t('invite.missingToken'));
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await fetch(`${GATEWAY_BASE_URL}/api/v1/auth/invitations/accept-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ token }),
      });
      const payload = (await response.json().catch(() => ({}))) as { detail?: string };
      if (!response.ok) {
        setError(payload.detail || t('invite.unable'));
        return;
      }
      setAccepted(true);
      try {
        await refreshProfile();
      } catch {
        /* profile refresh is best-effort; membership is already marked accepted */
      }
      window.setTimeout(() => {
        navigate('/console/paying-subscriptions', { replace: true });
      }, 700);
    } catch {
      setError(t('invite.unreachable'));
    } finally {
      setIsSubmitting(false);
    }
  }, [accessToken, navigate, refreshProfile, t, token]);

  const inviteContinued = React.useRef(false);
  React.useEffect(() => {
    if (inviteContinued.current || !token || !usesGoogle || accepted) {
      return;
    }
    if (canAcceptInSession) {
      inviteContinued.current = true;
      void acceptWithSession();
      return;
    }
    if (!inviteEmail.trim()) {
      return;
    }
    inviteContinued.current = true;
    startGoogleInvite();
  }, [acceptWithSession, accepted, canAcceptInSession, inviteEmail, startGoogleInvite, token, usesGoogle]);

  return (
    <section className="page-section login-page">
      <div className="login-shell">
        <div className="login-panel">
          <Link className="login-brand" to="/">NextCompli</Link>
          <h1>{t('invite.title')}</h1>
          <p className="subtitle login-subtitle">
            {canAcceptInSession
              ? t('invite.googleSessionBody')
              : usesGoogle
                ? t('invite.googleBody')
                : usesMicrosoft
                  ? t('invite.microsoftBody')
                  : t('invite.subtitle')}
          </p>
          {inviteEmail ? <p className="login-local-hint">{inviteEmail}</p> : null}
          {!token ? <p className="login-error">{t('invite.missingToken')}</p> : null}
          {accepted ? (
            <p className="login-local-hint">
              {t('invite.acceptedConsole')}
            </p>
          ) : null}
          {error ? (
            <p className="login-error">
              {error === 'Invite not found'
                ? t('invite.notFound')
                : error === 'Invite is no longer active'
                ? t('invite.inactive')
                : error}
            </p>
          ) : null}
          {usesMicrosoft && !usesGoogle ? (
            <div className="login-actions-row">
              <button
                className="btn btn-primary login-submit"
                type="button"
                onClick={startMicrosoftInvite}
              >
                {t('invite.microsoftButton')}
              </button>
              <Link className="login-forgot" to="/login">Back to login</Link>
            </div>
          ) : null}
          {usesGoogle ? (
            <div className="login-actions-row">
              {canAcceptInSession ? (
                <>
                  <button
                    className="btn btn-primary login-submit"
                    type="button"
                    disabled={isSubmitting || !token || accepted}
                    onClick={() => { void acceptWithSession(); }}
                  >
                    {isSubmitting ? (
                      <span className="password-submit-loading" aria-live="polite">
                        <span className="password-submit-spinner" aria-hidden="true" />
                        <span>{t('invite.sessionButton')}</span>
                      </span>
                    ) : t('invite.sessionButton')}
                  </button>
                  <button
                    className="login-forgot"
                    type="button"
                    onClick={startGoogleInvite}
                  >
                    {t('invite.otherGoogle')}
                  </button>
                </>
              ) : (
                <button
                  className="btn btn-primary login-submit"
                  type="button"
                  onClick={startGoogleInvite}
                >
                  {t('invite.googleButton')}
                </button>
              )}
              <Link className="login-forgot" to="/login">Back to login</Link>
            </div>
          ) : usesMicrosoft ? null : (
          <form
            className="login-form"
            onSubmit={async (event) => {
              event.preventDefault();
              if (!token) {
                setError(t('invite.missingToken'));
                return;
              }
              if (newPassword.length < 12) {
                setError(t('reset.tooShort'));
                return;
              }

              setIsSubmitting(true);
              setError(null);
              try {
                const response = await fetch(AUTH_ACCEPT_INVITE_URL, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ token, new_password: newPassword }),
                });
                const payload = (await response.json().catch(() => ({}))) as { detail?: string; access_token?: string };
                if (!response.ok) {
                  setError(payload.detail || t('invite.unable'));
                  return;
                }
                setAccepted(true);
                if (payload.access_token) {
                  localStorage.setItem('nextcompli.auth.token', payload.access_token);
                  localStorage.setItem('nextcompli.authenticated', 'true');
                  window.location.assign(`${CONSOLE_PATH}/home`);
                  return;
                }
                window.setTimeout(() => {
                  navigate(`${CONSOLE_PATH}/home`, { replace: true });
                }, 1000);
              } catch {
                setError(t('invite.unreachable'));
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            <label className="login-field-label" htmlFor="accept-invite-password">{t('reset.newPassword')}</label>
            <div className="login-password-field">
              <input
                id="accept-invite-password"
                placeholder={t('invite.placeholder')}
                type={showInvitePassword ? 'text' : 'password'}
                autoComplete="new-password"
                minLength={12}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                required
              />
              <button
                type="button"
                className="login-password-toggle"
                onClick={() => setShowInvitePassword((current) => !current)}
                aria-pressed={showInvitePassword}
              >
                {showInvitePassword ? t('login.hide') : t('login.show')}
              </button>
            </div>
            <div className="login-actions-row">
              <button className="btn btn-primary login-submit" type="submit" disabled={isSubmitting || !token}>
                {isSubmitting ? (
                  <span className="password-submit-loading" aria-live="polite">
                    <span className="password-submit-spinner" aria-hidden="true" />
                    <span>Accepting invite...</span>
                  </span>
                ) : 'Accept invite'}
              </button>
              <Link className="login-forgot" to="/login">Back to login</Link>
            </div>
          </form>
          )}
        </div>
      </div>
    </section>
  );
};

const NotFoundPage: React.FC = () => <Navigate to={websitePath()} replace />;

const LegacyWebsiteRedirect: React.FC = () => {
  const location = useLocation();
  const strippedPath = location.pathname.replace(/^\/website/, '') || '/';

  return <Navigate to={`${strippedPath}${location.search}${location.hash}`} replace />;
};

const LegacyCrmRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={canonicalizeConsolePath(location.pathname)} replace />;
};

const DashboardPage: React.FC = () => {
  return <Dashboard initialNavItem="home" />;
};

const ConsolePage: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const hasSuperUserAccess = React.useMemo(() => {
    return hasPlatformAdminAccess(user);
  }, [user]);

  const initialNavItem = React.useMemo(() => {
    const path = location.pathname;
    if (path.startsWith(CONSOLE_EMAIL_ACCOUNTS_PATH)) {
      return 'emailInbox' as const;
    }
    if (path === `${CONSOLE_PATH}/home` || path === `${CONSOLE_PATH}/`) {
      return 'home' as const;
    }
    if (path.startsWith(`${CONSOLE_PATH}/personal`)) {
      return 'personal' as const;
    }
    if (path.startsWith(`${CONSOLE_PATH}/dashboards`) || path === APP_DASHBOARD_PATH || path.startsWith(`${APP_DASHBOARD_PATH}/`)) {
      // CompliCore subscribers no longer have a separate Dashboards landing; admins still do.
      return hasSuperUserAccess ? ('dashboards' as const) : ('home' as const);
    }
    if (path.startsWith(`${CONSOLE_PATH}/security`)) {
      return 'security' as const;
    }
    if (path.startsWith(`${CONSOLE_PATH}/team`)) {
      return 'team' as const;
    }
    if (path.startsWith(`${CONSOLE_PATH}/payments`)) {
      return 'payments' as const;
    }
    if (path.startsWith(CONSOLE_BILLING_PATH)) {
      return 'billing' as const;
    }
    if (path.startsWith(CONSOLE_SUBSCRIPTION_HUB_PATH)) {
      return 'subscriptionHub' as const;
    }
    if (path.startsWith(CONSOLE_SUBSCRIPTION_LEVELS_PATH)) {
      return 'subscriptionLevels' as const;
    }
    if (path.startsWith(CONSOLE_PAYING_SUBSCRIPTIONS_PATH)) {
      return 'payingSubscriptions' as const;
    }
    if (path.startsWith(CONSOLE_EXPIRED_SUBSCRIPTIONS_PATH)) {
      return 'expiredSubscriptions' as const;
    }
    if (path.startsWith(CONSOLE_MY_SUBSCRIPTIONS_PATH)) {
      return 'mySubscriptions' as const;
    }
    if (path.startsWith(CONSOLE_SUBSCRIPTIONS_PATH)) {
      return 'subscriptions' as const;
    }
    if (path.startsWith(`${CONSOLE_PATH}/research`)) {
      return 'research' as const;
    }
    if (path.startsWith(CONSOLE_COMPLIDESK_PATH)) {
      return 'complidesk' as const;
    }
    if (path.startsWith(CONSOLE_POSTLUDE_PATH)) {
      return 'postlude' as const;
    }
    if (path.startsWith(CONSOLE_AGENT_PATH)) {
      return 'agent' as const;
    }
    if (isCrmNavHighlightPath(path)) {
      return 'crm' as const;
    }
    if (isInletOperatorPath(path)) {
      return 'inlet' as const;
    }
    if (path.startsWith(CONSOLE_OFAC_LIVE_FEED_PATH)) {
      return 'ofacLiveFeed' as const;
    }
    if (path.startsWith(CONSOLE_OFAC_SCREENING_PATH)) {
      return 'ofacScreening' as const;
    }
    if (path.startsWith(CONSOLE_OFAC_INVESTIGATIONS_PATH)) {
      return 'ofacInvestigations' as const;
    }
    if (path.startsWith(CONSOLE_OFAC_WHITELIST_PATH)) {
      return 'ofacWhitelist' as const;
    }
    if (path.startsWith(CONSOLE_OFAC_CAPTA_PATH)) {
      return 'ofacCapta' as const;
    }
    if (path.startsWith(CONSOLE_OFAC_DASHBOARDS_PATH)) {
      return 'ofacDashboards' as const;
    }
    if (path.startsWith(CONSOLE_OFAC_COMPLIANCE_PATH) || path.startsWith(CONSOLE_OFAC_PATH)) {
      return 'ofacCompliance' as const;
    }
    if (path.startsWith(CONSOLE_WEBSITE_COMPLIANCE_PATH)) {
      return 'websiteCompliance' as const;
    }
    if (path.startsWith(CONSOLE_CLOUD_COMPLIANCE_PATH)) {
      return 'cloudCompliance' as const;
    }
    if (path.startsWith(CONSOLE_DOD_CONTRACTS_PATH) || path.startsWith(CONSOLE_GOVERNMENT_COMPLIANCE_PATH)) {
      return 'dodContracts' as const;
    }
    if (path.startsWith(CONSOLE_EDUCATION_COMPLIANCE_PATH)) {
      return 'educationCompliance' as const;
    }
    if (path.startsWith(CONSOLE_AI_COMPLIANCE_PATH)) {
      return 'aiCompliance' as const;
    }
    if (path.startsWith(CONSOLE_BUSINESS_FORMATION_PATH)) {
      return 'businessFormation' as const;
    }
    if (path.startsWith(`${CONSOLE_PATH}/compliance-calendar`)) {
      return 'complianceCalendar' as const;
    }
    if (path.startsWith(CONSOLE_AUDITS_PATH)) {
      return 'audits' as const;
    }
    if (path.startsWith(CONSOLE_STYLE_PATH)) {
      return 'style' as const;
    }
    if (path.startsWith(ADMIN_VOICE_MESSAGES_PATH)) {
      return 'voiceMessages' as const;
    }
    if (path.startsWith(ADMIN_KEYCLOAK_PATH)) {
      return 'keycloakAdmin' as const;
    }
    if (path.startsWith(`${CONSOLE_PATH}/socials`)) {
      return 'socials' as const;
    }
    if (path.startsWith(`${CONSOLE_PATH}/marketing-studio`) || path.startsWith(`${CONSOLE_PATH}/razzmatazz`)) {
      return 'razzmatazz' as const;
    }
    if (path.startsWith(ADMIN_CONSOLE_PATH) || new URLSearchParams(location.search).get('openAdmin') === '1') {
      return 'admin' as const;
    }

    return hasSuperUserAccess ? ('admin' as const) : ('home' as const);
  }, [hasSuperUserAccess, location.pathname, location.search]);

  return <Dashboard initialNavItem={initialNavItem} />;
};

const ConsoleHomeRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={`${CONSOLE_PATH}/home${location.search}${location.hash}`} replace />;
};

const RoutedApp: React.FC = () => (
  <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    <Shell>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path={ARCHITECTURE_DESTINATION} element={<ArchitecturePage />} />
        <Route path={websitePath('/platform')} element={<PlatformPage />} />
        <Route path={websitePath('/industries')} element={<IndustriesPage />} />
        {INDUSTRY_DETAIL_CONTENT.map((industry) => (
          <Route
            key={`industry-${industry.slug}`}
            path={websitePath(`/industries/${industry.slug}`)}
            element={<IndustryDetailPage content={industry} />}
          />
        ))}
        {INDUSTRY_SUBDETAIL_CONTENT.map((subItem) => (
          <Route
            key={`industry-subdetail-${subItem.id}`}
            path={websitePath(`/industries/${subItem.parentSlug}/${subItem.subSlug}`)}
            element={<IndustrySubDetailPage content={subItem} />}
          />
        ))}
        <Route path={websitePath('/customers')} element={<CustomersPage />} />
        <Route
          path={websitePath('/customers/customer-highlights-from-earnings')}
          element={<CustomerStoryPage content={CUSTOMER_STORY_BY_SLUG['customer-highlights-from-earnings']} />}
        />
        <Route
          path={websitePath('/customers/analytics-stories')}
          element={<CustomerStoryPage content={CUSTOMER_STORY_BY_SLUG['analytics-stories']} />}
        />
        <Route
          path={websitePath('/customers/integration-stories')}
          element={<CustomerStoryPage content={CUSTOMER_STORY_BY_SLUG['integration-stories']} />}
        />
        <Route
          path={websitePath('/customers/collaboration-stories')}
          element={<CustomerStoryPage content={CUSTOMER_STORY_BY_SLUG['collaboration-stories']} />}
        />
        <Route
          path={websitePath('/customers/platform-stories')}
          element={<CustomerStoryPage content={CUSTOMER_STORY_BY_SLUG['platform-stories']} />}
        />
        <Route path={RELEASE_DESTINATION} element={<ReleasePage />} />
        <Route path={websitePath('/news')} element={<NewsPage />} />
        <Route path={websitePath('/blog')} element={<BlogPage />} />
        <Route path={websitePath('/events')} element={<Navigate to={websitePath('/news')} replace />} />
        <Route path={websitePath('/learning')} element={<Navigate to={websitePath('/blog')} replace />} />
        <Route path={SUPPORT_DESTINATION} element={<SupportPage />} />
        <Route path={SUBSCRIBE_DESTINATION} element={<SubscribePage />} />
        <Route path={HELP_DESTINATION} element={<HelpCenterPageRoute />} />
        <Route path={websitePath('/support')} element={<Navigate to={HELP_DESTINATION} replace />} />
        <Route path={DASHBOARDS_VALUE_DESTINATION} element={<DashboardsValuePage />} />
        <Route path={LIABILITY_MONITORING_DESTINATION} element={<LiabilityMonitoringPage />} />
        <Route path={DOD_CONTRACTS_DESTINATION} element={<DodContractsPage />} />
        <Route path={DEMO_REQUEST_DESTINATION} element={<DemoRequestPage />} />
        <Route path={SALES_ASSISTANT_DESTINATION} element={<SalesAssistantPage />} />
        <Route path={SIGNUP_DESTINATION} element={<SignupPage />} />
        <Route path={websitePath('/company')} element={<CompanyPage />} />
        <Route path={LEGAL_DESTINATION} element={<LegalPage />} />
        <Route path={PRIVACY_DESTINATION} element={<PrivacyPage />} />
        <Route path={TERMS_DESTINATION} element={<TermsPage />} />
        <Route path={CONTACT_DESTINATION} element={<ContactPage />} />
        <Route path={TRUST_DESTINATION} element={<TrustPage />} />
        <Route path={COOKIE_PREFERENCES_DESTINATION} element={<CookiePreferencesPage />} />
        <Route path={DATA_CHOICES_DESTINATION} element={<DataChoicesPage />} />
        <Route path={CLAIMS_EVIDENCE_DESTINATION} element={<ClaimsEvidencePage />} />
        <Route path={RESPONSIBLE_DISCLOSURE_DESTINATION} element={<ResponsibleDisclosurePage />} />
        <Route path={DMCA_DESTINATION} element={<DmcaPage />} />
        <Route path={ACCESSIBILITY_DESTINATION} element={<AccessibilityPage />} />
        <Route path="/website" element={<LegacyWebsiteRedirect />} />
        <Route path="/website/*" element={<LegacyWebsiteRedirect />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path={ACCEPT_INVITE_PATH} element={<AcceptInvitePage />} />
        <Route path={FORGOT_PASSWORD_DESTINATION} element={<ForgotPasswordPage />} />
        <Route path={RESET_PASSWORD_DESTINATION} element={<ResetPasswordPage />} />
        <Route path={POSTLUDE_EMBED_PATH} element={<PostludeEmbedPage />} />
        <Route path={RAZZMATAZZ_EMBED_PATH} element={<RazzmatazzEmbedPage />} />
        {PRODUCT_DETAIL_CONTENT.map((item) => (
          <Route
            key={item.slug}
            path={websitePath(`/products/${item.slug}`)}
            element={<ProductDetailPage content={item} />}
          />
        ))}
        {PRODUCT_SUBDETAIL_CONTENT.map((subItem) => (
          <Route
            key={subItem.id}
            path={websitePath(`/products/${subItem.parentSlug}/${subItem.subSlug}`)}
            element={<ProductSubDetailPage content={subItem} />}
          />
        ))}
        <Route
          path={websitePath('/products/ofac-compliance/ofac-sdn-general-licenses')}
          element={<Navigate to={websitePath('/products/ofac-compliance/sdn-and-general-license-screening')} replace />}
        />
        <Route
          path={websitePath('/products/ofac-compliance/capta-consolidated-lists')}
          element={<Navigate to={websitePath('/products/ofac-compliance/consolidated-and-capta-list-coverage')} replace />}
        />
        <Route
          path={websitePath('/products/ofac-compliance/continuous-monitoring')}
          element={<Navigate to={websitePath('/products/ofac-compliance/continuous-ofac-monitoring')} replace />}
        />
        <Route
          path={websitePath('/products/ofac-compliance/real-time-batch-screening')}
          element={<Navigate to={websitePath('/products/ofac-compliance/real-time-and-batch-ofac-checks')} replace />}
        />

        <Route path="/deployment" element={<Navigate to={websitePath('/news')} replace />} />
        <Route path="/security" element={<Navigate to={websitePath('/customers')} replace />} />
        <Route path="/pricing" element={<Navigate to={SUPPORT_DESTINATION} replace />} />
        <Route path="/developers" element={<Navigate to={websitePath('/blog')} replace />} />
        <Route path="/resources" element={<Navigate to={websitePath('/company')} replace />} />
        <Route path="/contact" element={<Navigate to={CONTACT_DESTINATION} replace />} />
        <Route path="/app/account" element={<Navigate to={CONSOLE_PATH} replace />} />
        <Route path="/account" element={<Navigate to={CONSOLE_PATH} replace />} />
        <Route path="/account/settings" element={<Navigate to={CONSOLE_PATH} replace />} />
        <Route path="/app/account/admin" element={<Navigate to={ADMIN_CONSOLE_PATH} replace />} />
        <Route path="/account/admin" element={<Navigate to={ADMIN_CONSOLE_PATH} replace />} />
        <Route path="/account/admin/users" element={<Navigate to={ADMIN_USERS_PATH} replace />} />
        <Route path="/account/admin/groups" element={<Navigate to={ADMIN_GROUPS_PATH} replace />} />
        <Route path="/account/admin/roles" element={<Navigate to={ADMIN_ROLES_PATH} replace />} />
        <Route path="/account/admin/cost-optimization" element={<Navigate to={ADMIN_COST_OPTIMIZATION_PATH} replace />} />
        <Route path="/account/admin/observability" element={<Navigate to={ADMIN_OBSERVABILITY_PATH} replace />} />
        <Route path="/account/admin/experience" element={<Navigate to={ADMIN_EXPERIENCE_PATH} replace />} />
        <Route path="/account/admin/assignments" element={<Navigate to={ADMIN_USERS_PATH} replace />} />
        <Route path="/account/design-system" element={<Navigate to={CONSOLE_STYLE_DESIGNER_PATH} replace />} />
        <Route path={ADMIN_CONSOLE_PATH} element={<Navigate to={ADMIN_GROUPS_PATH} replace />} />
        <Route path="/app/dashboard" element={<Navigate to={APP_DASHBOARD_PATH} replace />} />

        <Route element={<ProtectedRoute />}>
          <Route path={CONSOLE_PATH} element={<ConsoleHomeRedirect />} />
          <Route path={LEGACY_CONSOLE_CRM_PATH} element={<LegacyCrmRedirect />} />
          <Route path={`${LEGACY_CONSOLE_CRM_PATH}/*`} element={<LegacyCrmRedirect />} />
          <Route path={CONSOLE_PURSUIT_PATH} element={<ConsolePage />} />
          <Route path={`${CONSOLE_PATH}/*`} element={<ConsolePage />} />
          <Route path={CONSOLE_RESEARCH_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_OFAC_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_OFAC_COMPLIANCE_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_OFAC_DASHBOARDS_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_OFAC_LIVE_FEED_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_OFAC_SCREENING_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_OFAC_INVESTIGATIONS_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_OFAC_WHITELIST_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_OFAC_CAPTA_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_WEBSITE_COMPLIANCE_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_CLOUD_COMPLIANCE_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_DOD_CONTRACTS_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_GOVERNMENT_COMPLIANCE_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_EDUCATION_COMPLIANCE_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_AI_COMPLIANCE_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_BUSINESS_FORMATION_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_SUBSCRIPTION_HUB_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_SUBSCRIPTION_LEVELS_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_PAYING_SUBSCRIPTIONS_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_EXPIRED_SUBSCRIPTIONS_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_MY_SUBSCRIPTIONS_PATH} element={<ConsolePage />} />
          <Route path={CONSOLE_TEAM_PATH} element={<ConsolePage />} />
          <Route path={APP_DASHBOARD_PATH} element={<Navigate to={`${CONSOLE_PATH}/home`} replace />} />
          {/* Classic live-feed SPA stays at /dashboard/index.html for iframe + new-tab use. */}
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Shell>
  </BrowserRouter>
);

const App: React.FC = () => (
  <AuthProvider>
    <RoutedApp />
  </AuthProvider>
);

export default App;
